// App.js
/* eslint-disable no-useless-escape */
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Landing from "./Landing";
import Auth from "./Auth";
import Onboarding from "./Onboarding";
import ProWelcome from "./ProWelcome";
import SummitSubmark from "./Summit-submark-white.png";
import { useState, useRef, useEffect } from "react";
import { supabase } from "./supabaseClient";

const SQA_SUBJECTS = [
  "Biology", "Business Management", "Chemistry", "Computing Science",
  "Design and Manufacture", "Drama", "Economics", "English",
  "Engineering Science", "French", "Geography", "German", "Graphic Communication",
  "History", "Home Economics", "Human Biology", "Latin", "Mathematics",
  "Applications of Mathematics", "Modern Studies", "Music", "Philosophy", "Physical Education",
  "Physics", "Psychology", "Religious, Moral and Philosophical Studies",
  "Sociology", "Spanish", "Art and Design", "Administration and IT"
];

const SCIENCE_SUBJECTS = ["Biology", "Chemistry", "Physics", "Human Biology"];
const MATHS_SUBJECTS = ["Mathematics", "Applications of Mathematics"];

const LEVELS = ["National 5", "Higher", "Advanced Higher"];
const BACKEND = "https://sqastudybuddy-production.up.railway.app";
const FREE_DAILY_LIMIT = 10;

const SUBJECT_CHIPS = {
  "Business Management": {
    chat: ["Explain Maslow's hierarchy","What does the 'explain' command word mean?","Explain the role of a manager"],
    paper: ["Check my answer on motivation theory","Give feedback on this delegation question","Mark my SWOT analysis answer"],
    drill: ["Drill me on motivation theories","Quiz me on SWOT and PESTLE","Test me on business decisions"],
    mock: ["Generate a Higher Business mock","Generate a National 5 Business mock","Generate an Advanced Higher Business mock"]
  },
  "Computing Science": {
    chat: ["Explain binary search","What is a parameter in a function?","Explain the purpose of a while loop"],
    paper: ["Check my answer on algorithms","Give feedback on my database question","Mark my answer on networking"],
    drill: ["Drill me on data types","Quiz me on sorting algorithms","Test me on SQL queries"],
    mock: ["Generate a Higher Computing mock","Generate a National 5 Computing mock","Generate an Advanced Higher Computing mock"]
  },
  "Physical Education": {
    chat: ["Explain the four factors affecting performance","What is the difference between intrinsic and extrinsic feedback?","Explain what a PDP is and how to structure one","What development principles should I apply to mental factors?"],
    paper: [
      "Check my Section 1 answer on mental factors",
      "Mark my Section 2 PDP reflection",
      "Give feedback on my Section 3 scenario question",
      "Mark my answer on methods of collecting information",
      "Check my evaluation of a development approach"
    ],
    drill: [
      "Drill me on methods of collecting information (quantitative vs qualitative)",
      "Quiz me on the four factors (mental, emotional, social, physical) and how they interact",
      "Test me on development principles (progressive overload, specificity, frequency, etc.)",
      "Drill me on types of feedback (intrinsic, extrinsic, terminal, concurrent)"
    ],
    mock: ["Generate a Higher PE mock (all 3 sections)","Generate Section 1 questions only","Generate a Section 3 scenario question"]
  },
  "Biology": {
    chat: ["Explain DNA replication","What is osmosis?","Explain the role of enzymes"],
    paper: ["Check my answer on cell biology","Give feedback on my genetics question","Mark my answer on photosynthesis"],
    drill: ["Drill me on cell division","Quiz me on the nervous system","Test me on inheritance"],
    mock: ["Paper 1 — Multiple Choice","Paper 2 — Extended answers","Generate a full Biology mock"]
  },
  "Human Biology": {
    chat: ["Explain the immune system","What is homeostasis?","Explain the nervous system"],
    paper: ["Check my answer on immunology","Give feedback on my genetics question","Mark my answer on reproduction"],
    drill: ["Drill me on the immune system","Quiz me on hormones","Test me on cell biology"],
    mock: ["Paper 1 — Multiple Choice","Paper 2 — Extended answers","Generate a full Human Biology mock"]
  },
  "Chemistry": {
    chat: [
      "Explain covalent bonding and the types of structures it forms",
      "What is the mole and how do I use n = m/GFM?",
      "Explain oxidation and reduction in terms of electron transfer",
      "What's the difference between endothermic and exothermic reactions?"
    ],
    paper: [
      "Check my answer on this titration calculation",
      "Give feedback on my explanation of covalent bonding",
      "Mark my answer on the factors affecting reaction rate",
      "Check my ion-electron equation for this redox reaction"
    ],
    drill: [
      "Drill me on naming organic compounds (aldehydes, ketones, esters)",
      "Quiz me on writing and balancing ion-electron equations",
      "Test me on mole and concentration calculations",
      "Drill me on the periodic table trends (IE, electronegativity, covalent radius)"
    ],
    mock: [
      "Paper 1 — Multiple Choice",
      "Paper 2 — Extended Answers",
      "Generate a full Chemistry mock"
    ]
  },
  "Physics": {
    chat: ["Explain Newton's laws of motion","What is the photoelectric effect?","How does time dilation work in special relativity?","Explain the difference between elastic and inelastic collisions"],
    paper: ["Check my answer on electricity","Give feedback on my mechanics question","Mark my answer on waves and interference","Check my nuclear decay equation"],
    drill: ["Drill me on equations of motion (SUVAT)","Quiz me on electricity (Ohm's law, EMF, capacitors)","Test me on waves (interference, grating, refraction)","Drill me on nuclear and particle physics"],
    mock: ["Paper 1 — Multiple Choice","Paper 2 — Structured","Generate a full Physics mock (both papers)"]
  },
  "Mathematics": {
    chat: ["Explain differentiation","What is the quadratic formula?","Explain trigonometric identities"],
    paper: ["Check my working on this algebra question","Give feedback on my calculus answer","Mark my statistics answer"],
    drill: ["Drill me on integration","Quiz me on vectors","Test me on logarithms"],
    mock: ["Non-calculator paper","Calculator paper","Generate a full Maths mock"]
  },
  "Applications of Mathematics": {
    chat: ["Explain how to calculate expected value","What is the difference between mean and median?","Explain how to read a box plot","How do I calculate compound interest?"],
    paper: ["Check my financial maths answer","Give feedback on my statistics question","Mark my probability answer","Check my activity network/PERT chart"],
    drill: ["Drill me on financial maths (NI, tax, pay)","Quiz me on statistics (mean, SD, box plots)","Test me on probability","Drill me on tolerance calculations"],
    mock: ["Generate a National 5 Applications of Maths mock","Generate a Higher Applications of Maths mock","Generate a National 5 Applications of Maths mock — Paper 2"]
  },
  "English": {
    chat: ["What is a theme?","Explain how to analyse a poem","What is symbolism?"],
    paper: ["Check my critical essay on a text","Give feedback on my close reading","Mark my personal writing"],
    drill: ["Drill me on literary techniques","Quiz me on critical essay structure","Test me on close reading skills"],
    mock: ["Generate a Higher English mock","Generate a National 5 English mock","Generate an Advanced Higher English mock"]
  },
  "History": {
    chat: ["Explain the causes of WW1","What is the difference between knowledge and analysis in a History essay?","How do I write a strong conclusion for a 22-mark essay?","Explain what 'evaluation' means in History"],
    paper: [
      "Mark my essay — Wars of Independence",
      "Mark my essay — Nazi Germany",
      "Mark my essay — Cold War",
      "Give feedback on my source question",
      "Mark my essay — Russian Revolution",
      "Mark my essay — Appeasement"
    ],
    drill: ["Drill me on WW1 causes","Quiz me on the Russian Revolution","Test me on Nazi Germany","Drill me on the Wars of Independence","Quiz me on the Treaty of Union"],
    mock: ["Generate a Higher History mock","Generate a National 5 History mock","Generate an Advanced Higher History mock"]
  },
  "Geography": {
    chat: ["Explain glaciation","What causes earthquakes?","Explain urban change"],
    paper: ["Check my answer on development","Give feedback on my physical geography question","Mark my answer on climate change"],
    drill: ["Drill me on map skills","Quiz me on natural hazards","Test me on population"],
    mock: ["Generate a Higher Geography mock","Generate a National 5 Geography mock","Generate an Advanced Higher Geography mock"]
  },
  "Modern Studies": {
    chat: [
      "Explain the difference between AMS and FPTP",
      "What is the difference between analyse and evaluate in Modern Studies?",
      "Explain why health inequalities exist in Scotland",
      "What is the individualist vs collectivist debate?"
    ],
    paper: [
      "Mark my 20-mark essay on democracy",
      "Mark my 20-mark essay on social inequality",
      "Give feedback on my 12-mark analyse question",
      "Mark my N5 describe question",
      "Check my source conclusions answer",
      "Mark my support and oppose answer"
    ],
    drill: [
      "Drill me on Scottish Parliament and AMS",
      "Quiz me on causes of crime and criminology theories",
      "Test me on health inequalities and government responses",
      "Drill me on world power political systems"
    ],
    mock: ["Generate a Higher Modern Studies Paper 1 mock","Generate a Higher Modern Studies Paper 2 mock","Generate a National 5 Modern Studies mock","Generate an Advanced Higher Modern Studies mock"]
  },
  "Engineering Science": {
    chat: ["Explain forces and structures","What is Ohm's law in engineering?","Explain mechanical advantage"],
    paper: ["Check my answer on structures","Give feedback on my electronics question","Mark my answer on energy"],
    drill: ["Drill me on forces","Quiz me on electronics","Test me on mechanisms"],
    mock: ["Generate a Higher Engineering Science mock","Generate a National 5 Engineering Science mock","Generate an Advanced Higher Engineering Science mock"]
  },
  "Administration and IT": {
    chat: [
      "What does the Computer Misuse Act cover?",
      "Explain the features of good customer service",
      "What is GDPR and what are an organisation's responsibilities?"
    ],
    paper: [
      "Check my answer on customer service",
      "Give feedback on my health and safety question",
      "Mark my answer on time management"
    ],
    drill: [
      "Drill me on legislation (GDPR, Computer Misuse Act, H&S)",
      "Quiz me on features of database software",
      "Test me on meeting documentation and procedures"
    ],
    mock: [
      "Generate a Higher Admin & IT mock",
      "Generate a National 5 Admin & IT mock",
      "Generate a Section 1 scenario question"
    ]
  },
  "Drama": {
    chat: [
      "Explain proxemics and how a director uses them",
      "What does status mean in drama and how is it shown?",
      "Explain the difference between naturalistic and non-naturalistic"
    ],
    paper: [
      "Check my Section 1 answer on themes",
      "Give feedback on my performance analysis",
      "Mark my Section 2 directing concepts answer"
    ],
    drill: [
      "Drill me on acting concepts (voice, movement, facial expression)",
      "Quiz me on design concepts (lighting, sound, costume, set)",
      "Test me on drama terminology and conventions"
    ],
    mock: [
      "Generate a Higher Drama mock",
      "Generate a National 5 Drama mock",
      "Generate a Section 2 devising stimulus question"
    ]
  },
  "Religious, Moral and Philosophical Studies": {
    chat: [
      "What is the difference between analysis and evaluation in RMPS?",
      "Explain the cosmological argument for God's existence",
      "What is the utilitarian view on capital punishment?",
      "Explain what a theodicy is"
    ],
    paper: [
      "Mark my 20-mark World Religion question",
      "Check my 10-mark analysis question",
      "Give feedback on my Paper 2 essay",
      "Mark my Morality & Belief answer",
      "Check my evaluation on the existence of God"
    ],
    drill: [
      "Drill me on World Religion beliefs",
      "Quiz me on Morality & Belief ethical frameworks",
      "Test me on Religious & Philosophical Questions",
      "Drill me on sources and quotes"
    ],
    mock: [
      "Generate an N5 RMPS mock",
      "Generate a Higher RMPS Paper 1 mock",
      "Generate a Higher RMPS Paper 2 essay question"
    ]
  },
  "Graphic Communication": {
    chat: [
      "Explain the difference between vector and raster graphics",
      "What is the difference between extrude and revolve in 3D CAD?",
      "Explain what tolerance means in technical drawing",
      "What DTP techniques create a professional magazine layout?"
    ],
    paper: [
      "Check my answer on design elements and principles",
      "Give feedback on my CAD modelling description",
      "Mark my DTP terminology answer",
      "Check my answer on drawing standards and conventions"
    ],
    drill: [
      "Drill me on design elements and principles",
      "Quiz me on DTP terms and techniques",
      "Test me on 3D CAD modelling features and edits",
      "Drill me on drawing standards (line types, dimensioning)"
    ],
    mock: [
      "Generate an N5 Graphic Comm knowledge mock",
      "Generate a Higher Graphic Comm knowledge mock",
      "Generate an AH Graphic Comm knowledge mock"
    ]
  }
};
const DEFAULT_CHIPS = {
  chat: ["What does 'evaluate' mean in SQA?","How do I structure an explain answer?","What does 1 mark mean in SQA?"],
  paper: ["Check my answer on this question","Give feedback on my essay","Mark my extended response"],
  drill: ["Drill me on key topics","Quiz me on the main concepts","Test me on past paper questions"],
  mock: ["Generate a mock exam paper","Create a practice test","Build me a full paper"]
};

// ── Helpers ───────────────────────────────────────────────────────────

// ── Chemistry Data Booklet (SQA Higher & Advanced Higher, 2021 edition) ──
const CHEMISTRY_DATA_BOOKLET = {
  relationships: [
    { label: "Moles from mass", formula: "n = m / GFM" },
    { label: "Moles from volume (solution)", formula: "n = c × V  (V in litres)" },
    { label: "Moles from volume (gas, molar volume)", formula: "n = V / molar volume  (molar volume = 24 litres/mol at RTP)" },
    { label: "Titration", formula: "c₁V₁ / n₁ = c₂V₂ / n₂" },
    { label: "Percentage by mass", formula: "% = (mass of element / GFM) × 100" },
    { label: "Atom economy", formula: "% atom economy = (mass of desired product / total mass of reactants) × 100" },
    { label: "Percentage yield", formula: "% yield = (actual yield / theoretical yield) × 100" },
    { label: "Reaction rate", formula: "rate = 1 / t" },
    { label: "pH and [H⁺]", formula: "pH = −log₁₀[H⁺]" },
    { label: "pOH", formula: "pOH = −log₁₀[OH⁻]" },
    { label: "pH + pOH", formula: "pH + pOH = 14" },
    { label: "Buffer pH", formula: "pH = pKₐ ± 1  (approximate range)" },
    { label: "Gibbs free energy", formula: "ΔG = ΔH − TΔS" },
    { label: "Entropy change", formula: "ΔS° = ΣΔS°(products) − ΣΔS°(reactants)" },
    { label: "Free energy change", formula: "ΔG° = ΣΔG°f(products) − ΣΔG°f(reactants)" },
    { label: "Energy of photon", formula: "E = Lhf  (L = Avogadro's constant, h = Planck's constant, f = frequency)" },
  ],
  atomicMasses: [
    { el: "Hydrogen", sym: "H", ram: 1.0 }, { el: "Helium", sym: "He", ram: 4.0 },
    { el: "Lithium", sym: "Li", ram: 6.9 }, { el: "Beryllium", sym: "Be", ram: 9.0 },
    { el: "Boron", sym: "B", ram: 10.8 }, { el: "Carbon", sym: "C", ram: 12.0 },
    { el: "Nitrogen", sym: "N", ram: 14.0 }, { el: "Oxygen", sym: "O", ram: 16.0 },
    { el: "Fluorine", sym: "F", ram: 19.0 }, { el: "Neon", sym: "Ne", ram: 20.2 },
    { el: "Sodium", sym: "Na", ram: 23.0 }, { el: "Magnesium", sym: "Mg", ram: 24.3 },
    { el: "Aluminium", sym: "Al", ram: 27.0 }, { el: "Silicon", sym: "Si", ram: 28.1 },
    { el: "Phosphorus", sym: "P", ram: 31.0 }, { el: "Sulfur", sym: "S", ram: 32.1 },
    { el: "Chlorine", sym: "Cl", ram: 35.5 }, { el: "Argon", sym: "Ar", ram: 39.9 },
    { el: "Potassium", sym: "K", ram: 39.1 }, { el: "Calcium", sym: "Ca", ram: 40.1 },
    { el: "Iron", sym: "Fe", ram: 55.8 }, { el: "Copper", sym: "Cu", ram: 63.5 },
    { el: "Zinc", sym: "Zn", ram: 65.4 }, { el: "Bromine", sym: "Br", ram: 79.9 },
    { el: "Silver", sym: "Ag", ram: 107.9 }, { el: "Iodine", sym: "I", ram: 126.9 },
    { el: "Barium", sym: "Ba", ram: 137.3 }, { el: "Lead", sym: "Pb", ram: 207.2 },
  ],
  electronegativities: [
    { el: "H", val: 2.2 }, { el: "Li", val: 1.0 }, { el: "Be", val: 1.6 },
    { el: "B", val: 2.0 }, { el: "C", val: 2.5 }, { el: "N", val: 3.0 },
    { el: "O", val: 3.5 }, { el: "F", val: 4.0 }, { el: "Na", val: 0.9 },
    { el: "Mg", val: 1.3 }, { el: "Al", val: 1.6 }, { el: "Si", val: 1.9 },
    { el: "P", val: 2.2 }, { el: "S", val: 2.6 }, { el: "Cl", val: 3.2 },
    { el: "K", val: 0.8 }, { el: "Ca", val: 1.0 }, { el: "Br", val: 3.0 },
    { el: "I", val: 2.7 },
  ],
  electrochemical: [
    { half: "Li⁺(aq) + e⁻ → Li(s)", e0: "−3.03" },
    { half: "K⁺(aq) + e⁻ → K(s)", e0: "−2.92" },
    { half: "Ca²⁺(aq) + 2e⁻ → Ca(s)", e0: "−2.87" },
    { half: "Na⁺(aq) + e⁻ → Na(s)", e0: "−2.71" },
    { half: "Mg²⁺(aq) + 2e⁻ → Mg(s)", e0: "−2.36" },
    { half: "Al³⁺(aq) + 3e⁻ → Al(s)", e0: "−1.66" },
    { half: "Zn²⁺(aq) + 2e⁻ → Zn(s)", e0: "−0.76" },
    { half: "Fe²⁺(aq) + 2e⁻ → Fe(s)", e0: "−0.44" },
    { half: "Ni²⁺(aq) + 2e⁻ → Ni(s)", e0: "−0.25" },
    { half: "Sn²⁺(aq) + 2e⁻ → Sn(s)", e0: "−0.14" },
    { half: "H⁺(aq) + e⁻ → ½H₂(g)", e0: "0.00" },
    { half: "Cu²⁺(aq) + 2e⁻ → Cu(s)", e0: "+0.34" },
    { half: "½O₂(g) + H₂O(l) + 2e⁻ → 2OH⁻(aq)", e0: "+0.40" },
    { half: "I₂(s) + 2e⁻ → 2I⁻(aq)", e0: "+0.54" },
    { half: "Fe³⁺(aq) + e⁻ → Fe²⁺(aq)", e0: "+0.77" },
    { half: "Ag⁺(aq) + e⁻ → Ag(s)", e0: "+0.80" },
    { half: "Br₂(l) + 2e⁻ → 2Br⁻(aq)", e0: "+1.07" },
    { half: "½O₂(g) + 2H⁺(aq) + 2e⁻ → H₂O(l)", e0: "+1.23" },
    { half: "Cl₂(g) + 2e⁻ → 2Cl⁻(aq)", e0: "+1.36" },
    { half: "MnO₄⁻(aq) + 8H⁺(aq) + 5e⁻ → Mn²⁺(aq) + 4H₂O(l)", e0: "+1.51" },
    { half: "F₂(g) + 2e⁻ → 2F⁻(aq)", e0: "+2.87" },
  ],
  dissociationConstants: [
    { species: "H₂O", ka: "1.00 × 10⁻¹⁴ (Kw)" },
    { species: "Methanoic acid (HCOOH)", ka: "1.78 × 10⁻⁴" },
    { species: "Ethanoic acid (CH₃COOH)", ka: "1.74 × 10⁻⁵" },
    { species: "Propanoic acid (C₂H₅COOH)", ka: "1.35 × 10⁻⁵" },
    { species: "Carbonic acid (H₂CO₃)", ka: "4.47 × 10⁻⁷" },
    { species: "Phosphoric acid (H₃PO₄)", ka: "7.25 × 10⁻³" },
    { species: "Hydrofluoric acid (HF)", ka: "6.76 × 10⁻⁴" },
    { species: "Ammonium ion (NH₄⁺)", ka: "5.62 × 10⁻¹⁰" },
    { species: "Phenol (C₆H₅OH)", ka: "1.26 × 10⁻¹⁰" },
  ],
  irTable: [
    { bond: "O−H (alcohol)", wavenumber: "3230–3550" },
    { bond: "O−H (carboxylic acid)", wavenumber: "2500–3300 (broad)" },
    { bond: "N−H (amine/amide)", wavenumber: "3300–3500" },
    { bond: "C−H", wavenumber: "2850–3090" },
    { bond: "C≡N (nitrile)", wavenumber: "2200–2260" },
    { bond: "C=O (aldehyde)", wavenumber: "1720–1740" },
    { bond: "C=O (ketone)", wavenumber: "1705–1725" },
    { bond: "C=O (carboxylic acid)", wavenumber: "1700–1725" },
    { bond: "C=O (ester)", wavenumber: "1735–1750" },
    { bond: "C=O (amide)", wavenumber: "1630–1700" },
    { bond: "C=C (alkene)", wavenumber: "1620–1680" },
    { bond: "C−O (alcohol/ester)", wavenumber: "1000–1300" },
  ],
};

// ── Applications of Mathematics Pre-release Data Booklet ─────────────────────
function AppsMathsDataBooklet() {
  const [section, setSection] = useState("tax");
  const ff = "'Trebuchet MS', sans-serif";
  const thStyle = { padding: "6px 10px", background: "#f0f0f0", border: "1px solid #ccc", fontSize: 11, fontWeight: 700, textAlign: "left", fontFamily: ff };
  const tdStyle = { padding: "5px 10px", border: "1px solid #ddd", fontSize: 11, fontFamily: ff };
  const sections = [
    { id: "tax", label: "Tax & NI" },
    { id: "isa", label: "Lifetime ISA" },
    { id: "r", label: "R Commands" },
  ];
  return (
    <div style={{ fontFamily: ff, fontSize: 12, color: "#111" }}>
      <div style={{ display: "flex", gap: 1, flexWrap: "wrap", marginBottom: 12, borderBottom: "2px solid #000", paddingBottom: 8 }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ padding: "4px 10px", fontSize: 11, border: section === s.id ? "1px solid #000" : "1px solid #ccc", background: section === s.id ? "#000" : "#f5f5f5", color: section === s.id ? "white" : "#333", cursor: "pointer", fontFamily: ff, borderRadius: 3 }}>
            {s.label}
          </button>
        ))}
      </div>

      {section === "tax" && (
        <>
          <p style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Scottish Tax Bands 2024/25</p>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead><tr><th style={thStyle}>Band</th><th style={thStyle}>Taxable Income</th><th style={thStyle}>Rate</th></tr></thead>
            <tbody>
              {[
                ["Personal Allowance", "Up to £12,570", "0%"],
                ["Starter rate", "£12,570 – £14,876", "19%"],
                ["Basic rate", "£14,876 – £26,561", "20%"],
                ["Intermediate rate", "£26,561 – £43,662", "21%"],
                ["Higher rate", "£43,662 – £75,000", "42%"],
                ["Advanced rate", "£75,000 – £125,140", "45%"],
                ["Top rate", "Over £125,140", "48%"],
              ].map(([band, income, rate], i) => (
                <tr key={i}><td style={tdStyle}>{band}</td><td style={tdStyle}>{income}</td><td style={{ ...tdStyle, fontWeight: 700 }}>{rate}</td></tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>National Insurance Contributions (2024/25)</p>
          <p style={{ fontSize: 11, marginBottom: 4 }}>NI is calculated on gross salary <strong>before</strong> pension deductions.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Monthly Earnings</th><th style={thStyle}>NI Rate</th></tr></thead>
            <tbody>
              {[
                ["Up to £1,048", "0%"],
                ["£1,048 – £4,189", "8%"],
                ["Over £4,189", "2%"],
              ].map(([band, rate], i) => (
                <tr key={i}><td style={tdStyle}>{band}</td><td style={{ ...tdStyle, fontWeight: 700 }}>{rate}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {section === "isa" && (
        <div style={{ fontSize: 11, lineHeight: 1.7 }}>
          <p style={{ fontWeight: 700, marginBottom: 6 }}>Lifetime ISA — Key Rules</p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>Must be <strong>aged 18–39</strong> to open. First deposit must be before age 40.</li>
            <li>Can make further deposits until the day before your <strong>50th birthday</strong>.</li>
            <li>Save up to <strong>£4,000 per year</strong>.</li>
            <li>Government adds a <strong>25% bonus</strong> on deposits — maximum bonus £1,000 per year.</li>
            <li>Bonus is paid <strong>monthly</strong> on new deposits made that month.</li>
            <li>Once the bonus is in the account it becomes part of your savings and <strong>earns interest</strong>.</li>
            <li>At age 50, no more deposits or bonuses — but account stays open and earns interest.</li>
          </ul>
          <p style={{ fontWeight: 700, marginTop: 10, marginBottom: 4 }}>Withdrawals</p>
          <p>You can withdraw penalty-free if: buying your first home, aged 60 or over, or terminally ill.</p>
          <p>Otherwise: a <strong>25% withdrawal charge</strong> is applied (this recovers the government bonus).</p>
        </div>
      )}

      {section === "r" && (
        <div style={{ fontSize: 10, fontFamily: "monospace", lineHeight: 1.8 }}>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>Helpful R / RStudio Commands</p>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Reading data</p>
          <p>mydata &lt;- read.csv("file.csv")</p>
          <p>attach(mydata) &nbsp;&nbsp; detach(mydata)</p>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, margin: "8px 0 4px" }}>Graphics</p>
          <p>hist(X) &nbsp; boxplot(X) &nbsp; boxplot(X,Y) &nbsp; plot(X,Y)</p>
          <p>pie(table(X)) &nbsp; barplot(table(X))</p>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, margin: "8px 0 4px" }}>Descriptive Statistics</p>
          <p>mean(X) &nbsp; sd(X) &nbsp; summary(X) &nbsp; IQR(X)</p>
          <p>table(X) &nbsp; prop.table(table(X)) &nbsp; table(X,Y)</p>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, margin: "8px 0 4px" }}>Correlation & Regression</p>
          <p>cor.test(X,Y) &nbsp; lm(Y~X) &nbsp; abline(lm(Y~X)) &nbsp; summary(lm(Y~X))</p>
          <p>predict(lm(Y~X), newdata=data.frame(X=C), interval="pred")</p>
          <p style={{ fontFamily: ff, fontSize: 11, fontWeight: 700, margin: "8px 0 4px" }}>Hypothesis Testing</p>
          <p>t.test(X,Y) &nbsp; t.test(X,Y,paired=TRUE)</p>
          <p>prop.test(x=c(a,b), n=c(n1,n2))</p>
        </div>
      )}
    </div>
  );
}

function ChemistryDataBooklet({ level }) {
  const [section, setSection] = useState("relationships");
  const db = CHEMISTRY_DATA_BOOKLET;
  const ff = "'Trebuchet MS', sans-serif";
  const thStyle = { padding: "6px 10px", background: "#f0f0f0", border: "1px solid #ccc", fontSize: 11, fontWeight: 700, textAlign: "left", fontFamily: ff };
  const tdStyle = { padding: "5px 10px", border: "1px solid #ddd", fontSize: 11, fontFamily: ff };
  const sections = [
    { id: "relationships", label: "Relationships" },
    { id: "atomicMasses", label: "Atomic Masses" },
    { id: "electronegativities", label: "Electronegativities" },
    { id: "electrochemical", label: "Electrochemical Series" },
    { id: "dissociation", label: "Dissociation Constants" },
    { id: "ir", label: "IR Correlation" },
  ];
  return (
    <div style={{ fontFamily: ff, fontSize: 12, color: "#111" }}>
      <div style={{ display: "flex", gap: 1, flexWrap: "wrap", marginBottom: 12, borderBottom: "2px solid #000", paddingBottom: 8 }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ padding: "4px 10px", fontSize: 11, border: section === s.id ? "1px solid #000" : "1px solid #ccc", background: section === s.id ? "#000" : "#f5f5f5", color: section === s.id ? "white" : "#333", cursor: "pointer", fontFamily: ff, borderRadius: 3 }}>
            {s.label}
          </button>
        ))}
      </div>

      {section === "relationships" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Quantity</th><th style={thStyle}>Relationship</th></tr></thead>
          <tbody>{db.relationships.map((r, i) => (
            <tr key={i}><td style={tdStyle}>{r.label}</td><td style={{ ...tdStyle, fontStyle: "italic" }}>{r.formula}</td></tr>
          ))}</tbody>
        </table>
      )}

      {section === "atomicMasses" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Element</th><th style={thStyle}>Symbol</th><th style={thStyle}>Relative Atomic Mass</th></tr></thead>
          <tbody>{db.atomicMasses.map((a, i) => (
            <tr key={i}><td style={tdStyle}>{a.el}</td><td style={{ ...tdStyle, fontWeight: 700 }}>{a.sym}</td><td style={tdStyle}>{a.ram}</td></tr>
          ))}</tbody>
        </table>
      )}

      {section === "electronegativities" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Pauling scale. Difference &gt;1.8 = ionic; 0.4–1.8 = polar covalent; &lt;0.4 = pure covalent.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Element</th><th style={thStyle}>Electronegativity</th></tr></thead>
            <tbody>{db.electronegativities.map((e, i) => (
              <tr key={i}><td style={tdStyle}>{e.el}</td><td style={tdStyle}>{e.val}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}

      {section === "electrochemical" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Standard reduction potentials (E°/V). More negative = stronger reducing agent (top). More positive = stronger oxidising agent (bottom). Spontaneous reaction: more positive E° at bottom reacts with more negative E° at top.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Half-equation (reduction)</th><th style={thStyle}>E°/V</th></tr></thead>
            <tbody>{db.electrochemical.map((e, i) => (
              <tr key={i}><td style={tdStyle}>{e.half}</td><td style={{ ...tdStyle, fontWeight: 700, whiteSpace: "nowrap" }}>{e.e0}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}

      {section === "dissociation" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Dissociation constants (Ka) at 298 K. Larger Ka = stronger acid.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Species</th><th style={thStyle}>Ka</th></tr></thead>
            <tbody>{db.dissociationConstants.map((d, i) => (
              <tr key={i}><td style={tdStyle}>{d.species}</td><td style={tdStyle}>{d.ka}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}

      {section === "ir" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Wavenumber ranges (cm⁻¹). Absorptions below 1500 cm⁻¹ are the fingerprint region.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Bond</th><th style={thStyle}>Wavenumber / cm⁻¹</th></tr></thead>
            <tbody>{db.irTable.map((r, i) => (
              <tr key={i}><td style={tdStyle}>{r.bond}</td><td style={tdStyle}>{r.wavenumber}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}
    </div>
  );
}

function PhysicsDataSheet({ level }) {
  const [section, setSection] = useState("common");
  const ff = "'Trebuchet MS', sans-serif";
  const thStyle = { padding: "5px 8px", background: "#e8eef6", border: "1px solid #b0c4de", fontSize: 11, fontWeight: 700, textAlign: "left", fontFamily: ff };
  const tdStyle = { padding: "4px 8px", border: "1px solid #c8d8e8", fontSize: 11, fontFamily: ff };

  const commonConstants = [
    { q: "Speed of light in vacuum", sym: "c", val: "3.00 × 10⁸ m s⁻¹" },
    { q: "Magnitude of charge on electron", sym: "e", val: "1.60 × 10⁻¹⁹ C" },
    { q: "Universal Constant of Gravitation", sym: "G", val: "6.67 × 10⁻¹¹ m³ kg⁻¹ s⁻²" },
    { q: "Gravitational acceleration on Earth", sym: "g", val: "9.8 m s⁻²" },
    { q: "Planck's constant", sym: "h", val: "6.63 × 10⁻³⁴ J s" },
    { q: "Mass of electron", sym: "mₑ", val: "9.11 × 10⁻³¹ kg" },
    { q: "Mass of neutron", sym: "mₙ", val: "1.675 × 10⁻²⁷ kg" },
    { q: "Mass of proton", sym: "mₚ", val: "1.673 × 10⁻²⁷ kg" },
    { q: "Hubble's constant", sym: "H₀", val: "2.3 × 10⁻¹⁸ s⁻¹" },
    { q: "Speed of sound in air", sym: "vₐᵢᵣ", val: "3.40 × 10² m s⁻¹" },
  ];

  const gravitational = [
    { body: "Earth", g: "9.8" }, { body: "Moon", g: "1.6" }, { body: "Mars", g: "3.7" },
    { body: "Jupiter", g: "23" }, { body: "Sun", g: "270" },
  ];

  const radiation = [
    { type: "alpha", wR: "20" }, { type: "beta", wR: "1" }, { type: "fast neutrons", wR: "10" },
    { type: "gamma", wR: "1" }, { type: "slow neutrons", wR: "3" }, { type: "X-rays", wR: "1" },
  ];

  const refractive = [
    { substance: "Diamond", n: "2.42" }, { substance: "Crown glass", n: "1.50" },
    { substance: "Water", n: "1.33" }, { substance: "Air", n: "1.00" },
  ];

  const spectral = [
    { element: "Hydrogen", wavelength: "656 nm", colour: "Red" },
    { element: "Hydrogen", wavelength: "486 nm", colour: "Blue-green" },
    { element: "Hydrogen", wavelength: "434 nm", colour: "Blue-violet" },
    { element: "Sodium", wavelength: "589 nm", colour: "Yellow" },
  ];

  const sections = [
    { id: "common", label: "Constants" },
    { id: "gravitational", label: "Gravity" },
    { id: "radiation", label: "Radiation wR" },
    { id: "refractive", label: "Refractive Indices" },
    { id: "spectral", label: "Spectral Lines" },
  ];

  return (
    <div style={{ fontFamily: ff, fontSize: 12, color: "#111" }}>
      <p style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Key values from the SQA Physics data sheet. The full relationships sheet is also provided in the real exam.</p>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 10, borderBottom: "2px solid #1a3a5c", paddingBottom: 8 }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ padding: "4px 10px", fontSize: 11, border: section === s.id ? "1px solid #1a3a5c" : "1px solid #b0c4de", background: section === s.id ? "#1a3a5c" : "#f0f4ff", color: section === s.id ? "white" : "#333", cursor: "pointer", fontFamily: ff, borderRadius: 3 }}>
            {s.label}
          </button>
        ))}
      </div>
      {section === "common" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Quantity</th><th style={thStyle}>Symbol</th><th style={thStyle}>Value</th></tr></thead>
          <tbody>{commonConstants.map((r, i) => <tr key={i}><td style={tdStyle}>{r.q}</td><td style={{ ...tdStyle, fontWeight: 700 }}>{r.sym}</td><td style={tdStyle}>{r.val}</td></tr>)}</tbody>
        </table>
      )}
      {section === "gravitational" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={thStyle}>Body</th><th style={thStyle}>g (N kg⁻¹)</th></tr></thead>
          <tbody>{gravitational.map((r, i) => <tr key={i}><td style={tdStyle}>{r.body}</td><td style={tdStyle}>{r.g}</td></tr>)}</tbody>
        </table>
      )}
      {section === "radiation" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>Radiation weighting factors (wR). Used in: H = D × wR</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Type of radiation</th><th style={thStyle}>wR</th></tr></thead>
            <tbody>{radiation.map((r, i) => <tr key={i}><td style={tdStyle}>{r.type}</td><td style={tdStyle}>{r.wR}</td></tr>)}</tbody>
          </table>
        </>
      )}
      {section === "refractive" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>Refractive indices for sodium light (589 nm) at 273 K.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Substance</th><th style={thStyle}>Refractive index</th></tr></thead>
            <tbody>{refractive.map((r, i) => <tr key={i}><td style={tdStyle}>{r.substance}</td><td style={tdStyle}>{r.n}</td></tr>)}</tbody>
          </table>
        </>
      )}
      {section === "spectral" && (
        <>
          <p style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>Key spectral lines — used for identifying elements in stars and calculating redshift.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Element</th><th style={thStyle}>Wavelength</th><th style={thStyle}>Colour</th></tr></thead>
            <tbody>{spectral.map((r, i) => <tr key={i}><td style={tdStyle}>{r.element}</td><td style={tdStyle}>{r.wavelength}</td><td style={tdStyle}>{r.colour}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </div>
  );
}

function getSystemPrompt(subject, level, mode, userData) {
  const weakSubjects = userData ? Object.entries(userData.confidence || {}).filter(([,v]) => v <= 2).map(([k]) => k) : [];

  // ── Administration and IT ─────────────────────────────────────────────────
  if (subject === "Administration and IT") {
    const modeInstructions = {
      chat: `The student wants to understand an Admin & IT topic. Explain clearly using SQA ${level} Administration and IT curriculum. Be concise and use correct command words.`,
      paper: `The student will paste a question and/or their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point the SQA scheme would award. Format: "✓ [point] — [brief explanation]". Show total marks available.

---STUDENT SCORE---
X / Y marks. 2-3 sentences on what they got right and what cost them marks. Note command word compliance (outline vs describe vs discuss vs justify vs compare). End with: WEAK TOPIC: [specific topic, 2-5 words]

---WORKED EXAMPLE---
A complete model answer that would achieve full marks using the correct command word structure.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Administration and IT question with command word and mark allocation clearly stated. After they answer, give SQA-style marking feedback. Then offer another question.`,
      mock: `You are generating a complete SQA-style mock for ${level} Administration and IT.`
    };
    return `You are Summit, an expert SQA tutor specialising in ${level} Administration and IT.
You know the SQA curriculum, command words, marking schemes, and examiner expectations inside out.

CRITICAL SQA COMMAND WORDS:
- "outline" = bullet points, no explanation required (2-4 marks)
- "describe" = state + relevant detail (3-5 marks)
- "explain" = cause AND effect using because/therefore (3-4 marks)
- "justify" = give reasons supporting a decision (2-4 marks)
- "discuss" = advantages AND disadvantages + overall conclusion (4-6 marks)
- "compare" = similarities AND differences between two specific things (2-4 marks)
- Mark allocation: 1 mark = 1 distinct point.

KEY LEGISLATION:
GDPR/UK GDPR: data must be accurate, kept securely, not retained longer than needed, only used for stated purpose, individuals have right of access. Breach must be reported to ICO within 72 hours. Consequences: fines up to £17.5m or 4% global turnover, reputational damage, criminal prosecution.
Computer Misuse Act 1990: THREE offences: (1) unauthorised access to computer material, (2) unauthorised access with intent to commit further offences, (3) unauthorised modification of computer material (includes deleting files, spreading viruses).
Health & Safety at Work Act 1974: Employers must provide safe equipment, adequate training, risk assessments, welfare facilities, first aid provision. Employees must take reasonable care of themselves and others, not interfere with safety equipment, report hazards.
Display Screen Equipment Regulations: regular screen breaks, adjustable chairs/screens, eye tests provided, workstation risk assessments.
Fire Safety Regulations: clear evacuation routes, fire doors, alarm testing, named marshals, posted evacuation procedures.
Copyright, Designs and Patents Act: cannot copy software, music, images, text without a licence.
Freedom of Information Act 2000: public bodies must provide information within 20 working days of a written request.

CUSTOMER SERVICE:
Features: written policy, trained staff, complaints procedure, feedback mechanisms (surveys, mystery shopper, focus groups, suggestion schemes, online forums).
Benefits: customer loyalty, repeat business, positive word-of-mouth, competitive advantage, increased sales, reduced complaints, improved staff morale.
Consequences of poor service: lost customers, negative publicity, decreased sales, increased complaints, lower staff morale, reputational damage.

EFFECTIVE TEAMS (Belbin): Coordinator, Shaper, Plant, Monitor-Evaluator, Implementer, Teamworker, Resource Investigator, Completer-Finisher, Specialist. Need mix of roles.
Tuckman stages: Forming, Storming, Norming, Performing.
Features: clear shared goals, appropriate size, good communication, mutual trust, complementary skills, strong leadership.

TIME AND TASK MANAGEMENT:
Gantt chart: visual bar chart showing tasks over time — good for projects, shows start/end dates.
Action plan: table with task, person responsible, deadline, resources needed.
To-do list: prioritised tasks for a day or week.
E-diary: shared digital calendar for scheduling, reminders, bookings.
Time stealers: unnecessary meetings, poor planning, interruptions, unclear instructions, excessive emails, procrastination.

DATABASE FEATURES:
Query: search/filter records using criteria. Report: formatted output with logo and headings.
Form: user-friendly data entry screen. Sort: arrange records alphabetically or by date.
Wildcard (*): search for records containing partial text. Grouping: group by a field value.

MEETINGS:
Before: book venue, prepare and send agenda, book equipment, arrange refreshments, send papers.
During: take accurate minutes, manage time, assign action points.
After: type and distribute minutes, chase actions, evaluate meeting, file documentation.
Meeting documents: notice of meeting, agenda (apologies, minutes of last meeting, matters arising, agenda items, AOB, date of next meeting), minutes (action minutes), evaluation forms.
${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Be direct in feedback. Use correct Admin & IT terminology throughout.`;
  }

  // ── Chemistry ─────────────────────────────────────────────────────────────
  if (subject === "Chemistry") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a Chemistry topic. Explain clearly using the exact SQA ${level} Chemistry curriculum. Use correct chemical equations, formulae and terminology. Be concise — do not teach content beyond the ${level} specification.`,
      paper: `The student will paste a question and/or their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point as the SQA marking scheme would award it. Format: "✓ [point] — [brief explanation]". Show total marks available. For calculations: show each step separately.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. Highlight command word compliance (state/name/describe/explain/calculate/write). For calculations: identify where the error occurred. End with: WEAK TOPIC: [specific topic, 2–5 words e.g. "Mole calculations", "Covalent bonding", "Equilibrium"]

---WORKED EXAMPLE---
A complete model answer achieving full marks. For calculations: show every step. For equations: show balanced equation with state symbols where required.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Chemistry question. State the mark allocation clearly. After they answer, give SQA marking feedback showing mark points awarded/not awarded. Then offer the next question.`,
      mock: `You are generating a complete SQA-style mock exam paper for ${level} Chemistry.`
    };

    // Build level-specific knowledge base
    const n5Knowledge = isN5 ? `
NATIONAL 5 CHEMISTRY — COURSE CONTENT:

CHEMICAL CHANGES AND STRUCTURE:
Rates of reaction: average rate = quantity/time; factors increasing rate: temperature (more particles have energy ≥ Ea), concentration (more particles per unit volume → more frequent collisions), surface area (more particles exposed), catalyst (provides alternative pathway with lower Ea). Rate decreases over time as reactants are consumed.

Atomic structure and bonding: protons (+1, mass 1) and neutrons (0, mass 1) in nucleus; electrons (-1, negligible mass) orbit nucleus. Atomic number = protons. Mass number = protons + neutrons. Isotopes = same atomic number, different mass number (same protons, different neutrons). Nuclide notation: mass number top-left, atomic number bottom-left.
Covalent bonds: shared pair of electrons between two non-metal atoms; strong bonds within molecule, weak forces (London dispersion) between molecules → low mp/bp. Network covalent (diamond, SiO₂): giant covalent structure → very high mp/bp.
Ionic bonding: metal loses electrons (positive ion), non-metal gains electrons (negative ion); electrostatic attraction between ions; lattice structure; high mp/bp; conducts when molten or in solution (ions free to move).
Metallic bonding: positive ions surrounded by delocalised electrons; electrostatic attraction; conducts electricity (delocalised electrons free to move).

Formulae and mole: GFM = sum of relative atomic masses. n = m/GFM. Concentration: c = n/V (mol/l). Percentage composition = (mass of element/GFM) × 100.

Acids and bases: pH < 7 acidic (excess H⁺), pH = 7 neutral, pH > 7 alkaline (excess OH⁻). Diluting acid → pH increases towards 7. Diluting alkali → pH decreases towards 7. Metal oxides + water → alkaline. Non-metal oxides + water → acidic. Neutralisation: acid + base → salt + water. Types: + metal oxide, + metal hydroxide, + metal carbonate (+ CO₂). Salt name: from acid (hydrochloric → chloride, sulfuric → sulfate, nitric → nitrate). Titration: concordant results within 0.2 cm³.

NATURE'S CHEMISTRY:
Homologous series: same general formula, similar chemical properties, boiling point increases with chain length (stronger London dispersion forces). Alkanes CₙH₂ₙ₊₂ (saturated, fuels). Alkenes CₙH₂ₙ (unsaturated, C=C double bond). Cycloalkanes CₙH₂ₙ (saturated, cyclic). Alcohols CₙH₂ₙ₊₁OH (hydroxyl group -OH; methanol/ethanol/propanol miscible with water). Carboxylic acids CₙH₂ₙ₊₁COOH (carboxyl group -COOH; form salts with metals/bases/carbonates).
Isomers: same molecular formula, different structural formulae, may be in different homologous series.
Addition reactions of alkenes: + H₂ → alkane (hydrogenation); + halogen (Br₂/Cl₂) → dihaloalkane (decolourises bromine water); + H₂O → alcohol (hydration).
Combustion: fuel + O₂ → CO₂ + H₂O. Exothermic. Eh = cmΔT; c(water) = 4.18 kJ kg⁻¹ °C⁻¹.

CHEMISTRY IN SOCIETY:
Metals: metallic bonding; reactivity series from reactions with water/acid; extraction method depends on position: heat alone (Ag, Au, Hg) → heating with carbon/CO (Cu, Pb, Sn, Fe, Zn) → electrolysis (Al, Mg, Na). Redox: oxidation = loss of electrons; reduction = gain of electrons. OIL RIG. Ion-electron equations. Electrochemical cells: more reactive metal = higher in electrochemical series = oxidised = electrons flow from it.
Plastics/polymers: addition polymerisation of unsaturated monomers → repeating unit has no double bond.
Fertilisers: nitrogen essential for plant growth; ammonia (NH₃) made by Haber process (N₂ + 3H₂ ⇌ 2NH₃; iron catalyst); nitric acid by Ostwald process (NH₃ → NO → NO₂ → HNO₃; Pt catalyst); ammonium nitrate/phosphate/sulfate = fertilisers.
Nuclear chemistry: radioactive decay = unstable nucleus emitting radiation. Alpha (α) = ⁴₂He; stopped by paper; +2 charge. Beta (β) = ⁰₋₁e; stopped by thin Al. Gamma (γ) = electromagnetic; stopped by thick lead/concrete. Half-life = time for half of nuclei to decay; constant; independent of conditions. Nuclear equations: sum of mass numbers and atomic numbers must balance.` : "";

    const higherKnowledge = isHigher ? `
HIGHER CHEMISTRY — COURSE CONTENT:

CHEMICAL CHANGES AND STRUCTURE:
Periodicity: atomic number order. Covalent radius decreases across period (more protons, same shielding → greater nuclear attraction). Down group: covalent radius increases (extra electron shells). Ionisation energy: energy to remove 1 mole of electrons from 1 mole of gaseous atoms: X(g) → X⁺(g) + e⁻. IE increases across period (same shielding, increasing nuclear charge). Large jump when electron removed from inner shell. Electronegativity: measure of attraction atom has for bonding electrons. Increases across period, decreases down group. Explains bond polarity: Δelectronegativity >1.5 ionic; 0.4–1.5 polar covalent; <0.4 pure covalent.

Structure and bonding: London dispersion forces between all molecules (caused by temporary dipoles → induced dipoles). Strength increases with number of electrons/molecular size. Permanent dipole-permanent dipole: polar molecules. Hydrogen bonding: O-H, N-H or F-H bonds → strong intermolecular forces → explains anomalous bp of H₂O, NH₃, HF; explains why ice floats. Ionic/polar compounds soluble in water; non-polar soluble in non-polar solvents.

Oxidising and reducing agents: oxidising agent accepts electrons (reduced); reducing agent donates electrons (oxidised). Strong oxidising agents at bottom left of electrochemical series. Balancing ion-electron equations: add H₂O to balance O, H⁺ to balance H, then e⁻ to balance charge. Combine by cancelling electrons.

NATURE'S CHEMISTRY:
Systematic carbon chemistry: predict boiling point and solubility from molecular polarity, H-bonding potential, and number of electrons. Isomers: same molecular formula, different structure; may have different physical properties.
Alcohols: -OH group; primary (OH on end carbon), secondary (OH on middle carbon), tertiary (OH on carbon with 3 alkyl groups). Primary → aldehyde → carboxylic acid; secondary → ketone; tertiary: cannot be oxidised by Cr₂O₇²⁻ or CuO.
Aldehydes and ketones: carbonyl group C=O. Aldehyde distinguishable from ketone: Tollens' (silver mirror with aldehyde), Fehling's (brick-red precipitate with aldehyde), acidified dichromate (orange→green with aldehyde). Aldehydes can be oxidised further.
Carboxylic acids: -COOH group; react with metals, bases, carbonates to form salts. Ka = [H⁺][A⁻]/[HA].
Esters: -COO- link; formed by condensation of alcohol + carboxylic acid (+ concentrated H₂SO₄ catalyst); hydrolysis = ester + water → acid + alcohol. Fats and oils = esters of glycerol (propane-1,2,3-triol) + 3 fatty acids. Edible oils have lower mp than fats (more C=C bonds → less packing → weaker LDF).
Soaps: alkaline hydrolysis of fat/oil (saponification) → glycerol + fatty acid salts. Soap ion: ionic hydrophilic head (-COO⁻) + non-polar hydrophobic tail. Cleans by forming micelles around oil/grease. Hard water + soap → scum (insoluble Ca/Mg salts). Soapless detergents work in hard water.
Proteins: amino acids joined by peptide links (-CO-NH-) via condensation. Essential amino acids must come from diet. Denaturation = breaking of H-bonds and other non-covalent bonds changing 3D shape, destroying function.
Oxidation of food: rancidity from oxidation of unsaturated C=C bonds in oils. Antioxidants: easily oxidised, protect food by oxidising in place of food molecules.
Fragrances/terpenes: essential oils; terpenes built from isoprene units (2-methylbuta-1,3-diene, C₅H₈); monoterpene = 2 isoprene units; diterpene = 4 units etc.
Free radicals: atom/molecule with unpaired electron. Chain reaction: initiation (bond homolysis, produces 2 radicals), propagation (radical reacts, new radical formed), termination (two radicals combine).

CHEMISTRY IN SOCIETY:
Getting the most from reactants: % yield = (actual/theoretical) × 100. Atom economy = (mass of desired product/total mass of reactants) × 100. Limiting reactant: the one that runs out first; identified by dividing moles of each reactant by its stoichiometric coefficient.
Controlling the rate: Arrhenius equation context (not required): rate increases with temperature because proportion of particles with E ≥ Ea increases. Potential energy diagram: reactants → activated complex (top of energy barrier) → products. Catalyst lowers Ea, provides alternative pathway. Effect of catalyst: no change to ΔH, no change to equilibrium position, increases rate of both forward and reverse reactions equally.
Chemical energy: ΔH = Σ(ΔHf products) − Σ(ΔHf reactants). Hess's Law: ΔH independent of route taken. Bond enthalpy calculations for gas-phase reactions: energy in (break bonds reactants) − energy out (form bonds products).
Equilibria: dynamic equilibrium in closed system. K = [products]/[reactants] (raised to stoichiometric powers; pure solids/liquids = 1). K depends only on temperature. Increasing temperature: shifts equilibrium in endothermic direction; changes K. Catalyst: does not change position of equilibrium or K; reaches equilibrium faster.
Chemical analysis: chromatography separates by polarity or molecular size. Retention time identifies component. Titration: standard solution of accurately known concentration. Concordant = within 0.2 cm³. Redox titrations: permanganate self-indicating (purple→colourless).` : "";

    const ahKnowledge = isAH ? `
ADVANCED HIGHER CHEMISTRY — COURSE CONTENT:

INORGANIC CHEMISTRY:
Electromagnetic radiation: E = hf = hc/λ; E(per mole) = LhF = Lhc/λ. Emission spectrum: electron drops to lower energy level → photon emitted. Absorption spectrum: electron promoted to higher level. Intensity ∝ concentration.
Atomic orbitals: electrons behave as standing waves. Orbitals: s (spherical), p (dumbbell), d (complex). Four quantum numbers: n (principal, 1,2,3…), l (angular momentum, 0 to n-1), ml (magnetic, -l to +l), ms (spin, +½ or -½). Aufbau principle: fill lowest energy first. Pauli exclusion principle: no two electrons same four quantum numbers. Hund's rule: degenerate orbitals fill singly before pairing.
Ionisation energies: anomalies explained by electronic configurations. Special stability of half-filled (Cr: [Ar]3d⁵4s¹) and filled (Cu: [Ar]3d¹⁰4s¹) d subshells. VSEPR: count all electron pairs (bonding + non-bonding) around central atom; non-bonding/non-bonding > non-bonding/bonding > bonding/bonding repulsion. Shapes: 2 pairs linear, 3 trigonal planar, 4 tetrahedral, 5 trigonal bipyramidal, 6 octahedral.
Transition metals: incomplete d subshell in at least one ion. Oxidation numbers by rules (uncombined = 0; simple ion = charge; O usually -2; H usually +1; sum = charge on ion/molecule). Ligands: molecules or ions that donate lone pair to metal (dative covalent bond); mono/bi/hexadentate. Coordination number = total number of bonds from ligands to metal. Naming: ligands alphabetically (with prefixes di/tri/tetra), then metal (Latin if anion: ferrate, cuprate, etc.) with oxidation state in brackets.
d-orbital splitting: in octahedral complex, ligands along axes repel dₓ²₋ᵧ² and d_z² → higher energy; dxy, dxz, dyz → lower energy. Energy gap Δ determines wavelength absorbed → colour observed is complementary colour. Strong field ligands (CN⁻, NH₃) → larger Δ → absorb higher frequency/shorter wavelength. Spectrochemical series. Homogeneous catalysts: same state as reactants; catalyse via changing oxidation states and intermediate complexes. Heterogeneous: different state; adsorption onto active sites.

PHYSICAL CHEMISTRY:
Chemical equilibrium: K = [C]ᶜ[D]ᵈ/[A]ᵃ[B]ᵇ (pure solids/liquids concentration = 1). K depends only on T. K > 1 products favoured; K < 1 reactants favoured. Increasing T: K increases for endothermic reaction; K decreases for exothermic. Catalyst: no effect on K.
Ionic product of water: Kw = [H₃O⁺][OH⁻] = 10⁻¹⁴ at 25°C. pH = -log[H₃O⁺]. pOH = -log[OH⁻]. pH + pOH = 14.
Brønsted-Lowry: acid = proton donor; base = proton acceptor. Conjugate acid-base pairs. Strong acids fully dissociated; weak acids partially. Ka = [H₃O⁺][A⁻]/[HA]. pKa = -log Ka. Approximate pH of weak acid: pH ≈ ½pKa - ½logc.
Salt solutions: strong acid + strong base → neutral (pH = 7). Weak acid + strong base → alkaline (conjugate base hydrolyses). Strong acid + weak base → acidic (conjugate acid dissociates).
Buffer solutions: mixture of weak acid + its salt (from strong base). Henderson-Hasselbalch: pH = pKa - log([acid]/[salt]) = pKa + log([A⁻]/[HA]). Resists pH change: adding H⁺ → reacts with A⁻; adding OH⁻ → reacts with HA.
Indicators: weak acids where HIn and In⁻ have different colours. KIn = [H₃O⁺][In⁻]/[HIn]. Colour change when [H₃O⁺] = KIn (i.e. pH = pKIn). Range: pH = pKIn ± 1.
Reaction feasibility: ΔH°f = enthalpy of formation from elements in standard states (1 atm, 298K). ΔH°reaction = ΣΔH°f(products) - ΣΔH°f(reactants). Entropy S: measure of disorder. ΔS° = ΣS°(products) - ΣS°(reactants). ΔG° = ΔH° - TΔS°. Feasible if ΔG° < 0. At equilibrium ΔG = 0.
Kinetics: rate equation rate = k[A]^m[B]^n; determined from experiment only. Overall order = m+n. Units of k depend on order. Rate-determining step = slowest step; species in it appear in rate equation. Mechanism must be consistent with rate equation.

ORGANIC CHEMISTRY AND INSTRUMENTAL ANALYSIS:
Molecular orbitals: σ bonds (end-on overlap); π bonds (side-on overlap of parallel p orbitals). sp³ hybridisation: alkanes, tetrahedral. sp² hybridisation: alkenes, benzene; trigonal planar; remaining p orbital forms π bond. sp hybridisation: alkynes; linear; two π bonds. Benzene: six sp² carbons; delocalised π system over all six carbons; does not undergo addition reactions. Chromophores: conjugated systems; electrons promoted from HOMO to LUMO; larger conjugated system → smaller HOMO-LUMO gap → absorbs longer wavelength (visible) → coloured.
Synthesis and mechanisms: homolytic fission → radicals (not useful for synthesis). Heterolytic fission → ions (nucleophile attacks δ+ or positive carbon; electrophile attacks δ- or negative carbon). Nucleophiles: electron-rich, donate lone pair; OH⁻, CN⁻, NH₃, Cl⁻. Electrophiles: electron-deficient, accept lone pair; H⁺, NO₂⁺, SO₃, halogens polarised. Curly arrow: double-headed = electron pair movement; single-headed = single electron.
Haloalkanes: SN1 = tertiary (stable tertiary carbocation intermediate); two steps; racemic mixture. SN2 = primary (backside attack); one step; inversion of configuration.
Alkenes: electrophilic addition. Markovnikov's rule: H adds to carbon with more H atoms already. Mechanism: H⁺ adds first → more stable carbocation → then X⁻. Halogen addition: cyclic halonium ion intermediate → anti addition.
Benzene: electrophilic substitution (not addition — delocalised ring preserved). Nitration: NO₂⁺ (from HNO₃/H₂SO₄). Halogenation: X₂ + AlX₃. Alkylation: RX + AlCl₃ (Friedel-Crafts).
Stereochemistry: geometric isomers (restricted rotation about C=C or in ring; different groups on each carbon of restricted bond). Optical isomers: four different groups on carbon → chiral centre; non-superimposable mirror images; enantiomers; rotate plane-polarised light in opposite directions; racemic mixture = equal amounts of each → optically inactive.
Instrumental analysis: elemental microanalysis → empirical formula from combustion masses. Mass spectrometry: molecular ion peak (M+) gives GFM; fragment ions provide structural information. IR spectroscopy: wavenumbers in data booklet; identifies functional groups (C=O at 1650-1750, O-H broad at 2500-3300, N-H at 3200-3500). ¹H NMR: chemical shift values in data booklet (TMS = 0); number of peaks = number of distinct H environments; integration ∝ number of H in that environment; n+1 rule for splitting patterns in high-resolution spectra.
Pharmaceutical chemistry: drugs bind to receptors (agonist = mimics natural compound; antagonist = blocks receptor) or enzyme active sites (inhibitor). Shape and functional groups of drug determine binding.

RESEARCHING CHEMISTRY:
Gravimetric analysis: precipitation (precipitate, filter, wash, dry to constant mass) or volatilisation (heat to constant mass). Heating to constant mass: heat → cool in desiccator → weigh → repeat until no mass change. Volumetric analysis: standard solutions; primary standards (stable, pure, high GFM); acid-base, redox, complexometric (EDTA), back titrations. Colorimetry: calibration graph from standard solutions; complementary filter; blank = solvent only; concentration from calibration curve. Distillation: separate/identify by boiling point. Reflux: heat without losing volatile reactants. Vacuum filtration: faster separation. Recrystallisation: dissolve in minimum hot solvent → hot filtration → cool slowly → filter/wash/dry pure crystals. Solvent extraction: separating funnel; multiple small extractions more efficient than single large one. TLC: Rf = distance spot travelled / distance solvent travelled; pure compound = single spot; co-spotting for identification. Melting point: pure compound = sharp range at literature value; impure = lower and broader.` : "";

    return `You are Summit, an expert SQA tutor specialising in ${level} Chemistry. You know the SQA curriculum, marking schemes, past paper patterns and examiner expectations precisely.
${n5Knowledge}${higherKnowledge}${ahKnowledge}

CRITICAL SQA CHEMISTRY MARKING RULES:
- "state" or "name" = 1 factual point, no explanation needed
- "describe" = state the observation/fact with relevant detail, no cause needed
- "explain" = cause AND effect, must use because/therefore/as a result
- "calculate" = show all working clearly with units; mark for method + mark for answer
- "write" (equation) = must be balanced for atoms AND charge (ionic equations)
- "suggest" = apply knowledge to an unfamiliar situation — credit any reasonable answer supported by chemistry
- 1 mark = 1 distinct correct point; no half-marks
${weakSubjects.length > 0 ? `\nThe student has flagged low confidence in: ${weakSubjects.join(", ")}. Be especially thorough when these topics arise.` : ""}
${modeInstructions[mode]}
Use precise chemical language. In paper mode: be strict but fair. Highlight command word issues. Keep explanations focused.`;
  }

  // ── Drama ─────────────────────────────────────────────────────────────────
  // ── History ───────────────────────────────────────────────────────────────
  if (subject === "History") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a History topic. Explain clearly using the SQA ${level} History curriculum. Be concise. Reference specific historians or historical evidence where relevant.`,
      paper: isAH
        ? `The student will paste a History essay or source question and their answer. For a 25-mark AH essay, respond in this EXACT format:

---MARKING BREAKDOWN---
Assess against AH criteria: Structure (intro with historiography, paragraphs, conclusion); Thoroughness/Relevance; Analysis, Evaluation and Line of Argument; Historical Sources/Interpretations. Note: to score above 12/25 the essay MUST reference historians by name. List specific strengths and gaps.

---STUDENT SCORE---
X / 25 marks. 2–3 sentences on what earned marks and what cost marks. State whether historian references are present. Map to band (0–9, 10–12, 13–14, 15–17, 18–19, 20–22, 23–25). End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model paragraph showing the standard expected: clear argument, named historian with their view, counter-argument, synthesis.

---TRY AGAIN---
Restate the original question exactly.`
        : isHigher
        ? `The student will paste a History essay or source question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
For a 22-mark essay, mark each component:
HISTORICAL CONTEXT (max 3): Does intro give 2+ relevant background points connected to a line of argument?
KNOWLEDGE (max 6): Count distinct developed knowledge points. A "developed point" = a fact + explanation of its significance OR evidence. Award 1 mark per developed point up to 6.
ANALYSIS (max 6): Count analytical comments. Analysis means showing relationships between factors, contradictions, different interpretations, relative importance. NOT just describing what happened. Award 1 mark per analytical comment up to 6.
EVALUATION (max 4): Does the essay make reasoned evaluative judgements? Does it build a line of argument? Does it consider counter-arguments or alternative interpretations? Award 0–4.
CONCLUSION (max 3): Does conclusion make a RELATIVE overall judgement (not just a summary)? Does it explain WHY this arises from the evidence? A summary = max 1 mark. A judgement comparing factors = 2–3 marks.

For source questions (8 or 10 mark), mark: origin/purpose/timing (up to 3), content interpretation (up to 3), significant omissions (up to 3 for 8-mark; up to 7 for 10-mark).

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. Be specific — e.g. "Your knowledge points were strong but three of them were descriptive statements rather than developed analytical comments." End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model paragraph or full model answer at the correct level. For essays: show what a genuine analytical comment looks like vs a knowledge point. For source questions: show how to comment on origin/purpose and significant omissions.

---TRY AGAIN---
Restate the original question exactly.`
        : `The student will paste a History question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
Identify the question type first, then mark accordingly:
- "To what extent/How important/How successful" (9 marks): intro (1), structured factors (1), up to 5 knowledge points (5), conclusion with judgement (1) + reason (1). Single factor = max 3 knowledge marks.
- "Evaluate the usefulness" (5 marks): who/type/when (1), why written/purpose (1), relevant content (2), significant omissions (1).
- "How fully does source explain" (6 marks): source points (up to 3), recalled knowledge omissions (up to 3).
- "Compare views of sources" (4 marks): overall comparison (1), specific agreements/differences (up to 3).
- "Describe/Explain the reasons" (4–6 marks): 1 mark per accurate distinct point; developed points earn a second mark.
Award 1 mark per correct distinct point throughout.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Complete model answer achieving full marks at N5 level.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} History question with mark allocation clearly stated. After they answer, give SQA-style marking feedback. Then offer another question.`,
      mock: isHigher
        ? `Generate Higher History Paper 1 — Scottish History (36 marks, 1h 30min). Generate ALL FIVE parts so the student can attempt their studied context. Each part: 4 sources from named historians + Q1 Explain reasons (8 marks, recalled knowledge only) + Q2 How fully does Source A explain... (10 marks) + Q3 How much do Sources B and C reveal about differing interpretations... (10 marks) + Q4 Evaluate the usefulness of Source D... (8 marks). End with marking guidance and tell student to attempt their studied part only.

Also generate Paper 2 — British, European and World History (44 marks, 1h 30min). ALL parts in BOTH sections. Every question is a 22-mark essay. Section 1 British (5 parts, 3 essays each). Section 2 European and World (9 parts, 3 essays each). Command words: "How valid is this view?", "To what extent...", "How important were...". Tell student to attempt ONE question from their studied British part and ONE from their studied European/World part.`
        : isN5
        ? `Generate N5 History — 80 marks, 2h 20min. Generate ALL parts in all THREE sections. Section 1 Scottish Contexts (25 marks, 5 parts): each part has Source A (evaluate usefulness, 5 marks) + describe (4) + Sources B&C compare (4) + Source D how fully (6) + explain reasons (6). Section 2 British Contexts (26 marks, 5 parts): each part has to what extent essay (9) + Source A how fully (6) + Source B evaluate usefulness (5) + explain reasons (6). Section 3 European & World (29 marks, 10 parts): each part has describe (4) + explain reasons (6) + Sources A&B compare (4) + to what extent essay (9) + Source C how fully (6). Tell student to attempt their studied part from each section.`
        : `Generate AH History — 90 marks, 3 hours. Generate all 10 sections. Each section: Part A Historical Issues (50 marks) = 5 × 25-mark essays, student attempts 2. Part B Historical Sources (40 marks) = 4 sources from named historians + 3 questions totalling 40 marks (mark splits vary: 12+12+16 or 16+12+12 depending on section). Tell student to attempt ONE section only — all questions in that section.`
    };

    return `You are Summit, an expert SQA History tutor specialising in ${level} History.
You know the SQA marking schemes, command words, and examiner expectations for History inside out.

SQA HISTORY MARKING — CRITICAL KNOWLEDGE:

${isHigher || isAH ? `HIGHER 22-MARK ESSAY STRUCTURE:
- Historical Context: 3 marks — needs 2 relevant background points connected to a LINE OF ARGUMENT (not just description)
- Use of Knowledge: 6 marks — 1 mark per DEVELOPED point (fact + significance/evidence, not just a bare fact)
- Analysis: 6 marks — 1 mark per analytical comment. Analysis = showing relationships, contradictions, differing interpretations, relative importance. NOT narrative description.
- Evaluation: 4 marks — builds a line of argument, considers counter-arguments. Max 3 if no counter-argument/alternative interpretation.
- Conclusion: 3 marks — RELATIVE overall judgement explaining WHY, based on evidence. A mere summary = 1 mark only.

KNOWLEDGE vs ANALYSIS — THE KEY DISTINCTION:
Knowledge point: "Hitler used propaganda to control the German population."
Analytical comment: "Hitler's use of propaganda was central to Nazi consolidation because it created a cult of personality that neutralised the democratic opposition — unlike the crude repression used during the Weimar period, this worked with popular consent, making the regime more stable."

EVALUATION — what it requires:
- Judgements about relative importance: "X was more significant than Y because..."
- Counter-arguments: "However, recent historians have questioned this, arguing instead..."
- Overall significance: "Taken together, these factors suggest..."

CONCLUSION — passing vs failing:
FAILING (summary, max 1 mark): "In conclusion, there were many reasons for X including A, B and C."
PASSING (relative judgement, 2–3 marks): "Overall, X was the most important factor because it was the necessary precondition for all other factors — without it, Y and Z would have been insufficient on their own."` : ""}

${isN5 ? `N5 9-MARK ESSAY STRUCTURE:
- Introduction (1 mark): places question in historical context or outlines relevant factors
- Structured presentation (1 mark): knowledge organised to support different factors  
- Knowledge (up to 5 marks): 1 mark per relevant, factual key point used to support a factor
- Conclusion with valid judgement (1 mark)
- Reason in support of judgement (1 mark)
Note: if only one factor is presented, maximum 3 marks for knowledge.` : ""}

${isAH ? `ADVANCED HIGHER 25-MARK ESSAY:
- To score above 12/25: MUST reference named historians with their views/arguments
- Structure (0–25): building argument, intro with interpretations, conclusion with synthesis
- Thoroughness: evidence clearly linked to analysis/evaluation
- Analysis/Evaluation/Line of Argument: consistent, assured control of arguments
- Historical Sources/Interpretations: named historians, awareness of historiographical debate
- MARK BANDS: 0–9, 10–12 (no historiography ceiling), 13–14, 15–17, 18–19, 20–22, 23–25` : ""}

SOURCE QUESTION TYPES (Higher Scottish Paper):
- "Evaluate the usefulness" (8 marks): origin/type/purpose/timing (up to 3 marks), content — what the source says that is relevant (up to 3 marks), significant omissions — recalled knowledge NOT in the source (up to 3 marks)
- "How fully does Source X explain" (10 marks): points from source interpreted in own words (up to 3 marks), significant omissions from recalled knowledge (up to 7 marks)
- "How much do Sources X and Y reveal about differing interpretations" (10 marks): overall viewpoint identified per source (1 mark each), interpretation points developed per source (up to 2 marks each), recalled knowledge supporting/extending both views (up to 6 marks)
- "Explain the reasons" (8 marks): 1 mark per accurate relevant reason; developed reasons (with explanation of significance) earn a second mark

IMPORTANT — HIGHER PAPER STRUCTURE:
The Higher is TWO SEPARATE papers sat on the same day:
- Paper 1 (Scottish History): entirely source-based — 4 questions per topic, 36 marks total. NO essay.
- Paper 2 (British, European and World History): THREE 22-mark essays (students attempt ONE from Section 1 and ONE from Section 2). No sources.
Students studying one Scottish topic and one British/European/World topic sit BOTH papers.

N5 QUESTION TYPES (vary by section):
Scottish Contexts (25 marks): Evaluate usefulness of source (5 marks), Describe (4 marks), Compare views of two sources (4 marks), How fully does source explain (6 marks), Explain the reasons (6 marks)
British Contexts (26 marks): To what extent essay (9 marks), How fully does source explain (6 marks), Evaluate usefulness (5 marks), Explain the reasons (6 marks)
European/World Contexts (29 marks): Describe (4 marks), Explain the reasons (6 marks), Compare two sources (4 marks), To what extent essay (9 marks), How fully does source explain (6 marks)

N5 "TO WHAT EXTENT" ESSAY (9 marks): intro placing in context (1), structured presentation across factors (1), up to 5 knowledge points (5), conclusion with valid judgement (1), reason for judgement (1). Single factor = max 3 knowledge marks.
N5 "EVALUATE USEFULNESS" (5 marks): who wrote it/type/when (1), why written/purpose (1), what it says that is relevant (2), what it leaves out (1).
N5 "HOW FULLY" (6 marks): points from source (up to 3), significant omissions from recall (up to 3).
N5 "COMPARE" (4 marks): overall comparison (1), specific points of agreement/difference (up to 3).

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Be direct and specific in feedback. Reference SQA criteria by name.`;
  }

  // ── Religious, Moral and Philosophical Studies ───────────────────────────
  if (subject === "Religious, Moral and Philosophical Studies") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand an RMPS topic. Explain clearly using the SQA ${level} RMPS curriculum. Be concise and use correct philosophical and theological terminology.`,
      paper: isAH
        ? `The student will paste an essay or source question answer. For a 30-mark AH essay, respond in this EXACT format:

---MARKING BREAKDOWN---
Assess against three criteria: Knowledge & Understanding (in-depth, accurate, drawn from specific sources/perspectives), Analysis (identifies specific issues, explains consequences/implications/connections with clear understanding of relevance), Evaluation (insightful, valid, clearly reasoned judgements with conclusions woven throughout). Note: all three skills must be integrated throughout. For source questions (5+5+5): Q(a) Describe = 1 mark per accurate point; Q(b) Analyse = 1 mark per analytical point about the source; Q(c) Evaluate = 1 mark per supported evaluative judgement.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what earned marks and what cost marks. For essays, map to the holistic overview band (0–5, 6–10, 11–15, 16–20, 21–25, 26–30). End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model paragraph showing the standard expected: KU woven with analysis and evaluation, specific sources named, conclusions drawn throughout.

---TRY AGAIN---
Restate the original question exactly.`
        : isHigher
        ? `The student will paste a question and answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
For a 10-mark question (Q1 type): mark as KU (up to 6) + Analysis (up to 4). Max 4 marks if no analysis attempted.
For a 20-mark question (Q2 type): mark as KU (up to 10) + Analysis (up to 5) + Evaluation (up to 5). Capped at 8KU if both skills not attempted.
Key distinctions:
- Knowledge = accurate factual information, descriptions of beliefs/arguments, references to sources
- Analysis = consequences/implications, links between components, different interpretations — NOT just listing two sides
- Evaluation = supported judgements with reasoning — NOT just pros/cons lists. Must show WHY a view is strong/weak.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. Be specific — e.g. "Your analysis points were strong but your evaluation listed pros and cons rather than making supported judgements." End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model response at the correct level showing the difference between knowledge, analysis, and evaluation clearly.

---TRY AGAIN---
Restate the original question exactly.`
        : `The student will paste a question and answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
N5 question types and marks:
- "What do X believe about Y?" or "Describe..." (5 marks): 1 mark per accurate point, statement of fact with some explanation required
- "How might belief X affect Y?" (2–4 marks): 1 mark per consequence/impact point
- "Explain why X raises moral issues" (4–6 marks): 1 mark per explained moral issue (state + explain)
- "How far do you agree? Give reasons" (8 marks): marks for knowing the issue, presenting a view, giving reasons, considering other perspectives, conclusion

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Complete model answer achieving full marks at N5 level.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} RMPS question with mark allocation clearly stated. After they answer, give SQA-style marking feedback. Then offer another question.`,
      mock: isHigher
        ? `Generate Higher RMPS Paper 1 — World Religion, Morality and Belief (60 marks, 2h 15min). Generate ALL parts in both sections exactly as the real SQA paper — the student picks which to answer based on what they studied.

SECTION 1 — WORLD RELIGION — 30 marks — Attempt ONE part only:
Part A — Buddhism: Q1 "Analyse the relationship between tanha and the Three Root Poisons." [10] Q2 "Evaluate the relevance of meditation to Buddhists." [20]
Part B — Christianity: Q3 "Analyse the relationship between sin and stewards." [10] Q4 "Evaluate the relevance of worship to Christians." [20]
Part C — Hinduism: Q5 "Analyse the relationship between dukkha and avidya." [10] Q6 "Evaluate the relevance of worship to Hindus." [20]
Part D — Islam: Q7 "Analyse the relationship between suffering and khalifas." [10] Q8 "Evaluate the relevance of worship to Muslims." [20]
Part E — Judaism: Q9 "Analyse the relationship between yetzer harah and suffering." [10] Q10 "Evaluate the relevance of worship to Jews." [20]
Part F — Sikhism: Q11 "Analyse the relationship between atma and free will." [10] Q12 "Evaluate the relevance of worship to Sikhs." [20]

SECTION 2 — MORALITY AND BELIEF — 30 marks — Attempt ONE part only:
Part A — Justice: Q13 "Analyse non-religious responses to moral issues arising from deterrence." [10] Q14 "Evaluate religious responses to moral issues arising from responses to crime." [20]
Part B — Relationships: Q15 "Analyse non-religious responses to moral issues arising from forced marriage." [10] Q16 "Evaluate religious responses to moral issues arising from relationships." [20]
Part C — Environment: Q17 "Analyse non-religious responses to moral issues arising from biodiversity." [10] Q18 "Evaluate religious responses to moral issues arising from economic issues." [20]
Part D — Medicine: Q19 "Analyse non-religious responses to moral issues arising from the therapeutic use of embryos." [10] Q20 "Evaluate religious responses to moral issues arising from organ donation." [20]
Part E — Conflict: Q21 "Analyse non-religious responses to moral issues arising from using weapons of mass destruction." [10] Q22 "Evaluate religious responses to moral issues arising from conflict." [20]

End with marking guidance and tell the student to answer their studied religion part and morality topic part, then paste answers for marking.`
        : isN5
        ? `Generate N5 RMPS — 80 marks, 2h 20min. THREE sections (27+27+26 marks). Generate ALL parts in each section so the student can choose the one they studied. Include "attempt this part if you have studied X" instructions. Show mark allocations clearly.`
        : `Generate AH RMPS — 90 marks, 3 hours. Choose ONE section (Philosophy of Religion / Religious Experience / Medical Ethics). Generate 2 × 30-mark "Discuss" essays + 1 × 15-mark source question (5+5+5 describe/analyse/evaluate). Include a realistic 100-150 word philosophical/ethical extract.`
    };

    return `You are Summit, an expert SQA RMPS tutor specialising in ${level} Religious, Moral and Philosophical Studies.
You know the SQA curriculum, marking schemes, and examiner expectations for RMPS inside out.

SQA RMPS — CRITICAL MARKING KNOWLEDGE:

${isN5 ? `N5 PAPER STRUCTURE (80 marks, one paper, 2h 20min):
- Section 1: World Religion (27 marks) — 3 questions. Q1: describe/what (5 marks). Q2a+b: belief + how it affects (6 marks). Q3a+b: describe two + "how far do you agree" relevance debate (12 marks — 4+8).
- Section 2: Morality & Belief (27 marks) — 3 questions. Q1: describe + religious view (5 marks). Q2: explain why X raises moral issues (6 marks). Q3: describe two + "how far do you agree" debate (12 marks — 4+8).
- Section 3: Religious & Philosophical Questions (26 marks) — 3 questions. Q1: describe + "how far" debate (9 marks — 5+4). Q2: describe/explain/why (5 marks). Q3: describe + "how far" debate (12 marks — 5+4+... varies).
- Students attempt ONE part from each section.
- "How far do you agree" = knowledge + personal view + reasons + other perspectives + conclusion.
- At N5: "straightforward" level — knowledge needs facts with brief explanation (not just bare facts; not as deep as Higher).` : ""}

${isHigher ? `HIGHER PAPER STRUCTURE:
- Paper 1 (60 marks, 2h 15min): Section 1 World Religion (30 marks) + Section 2 Morality & Belief (30 marks). Each section = TWO compulsory questions (10 + 20 marks).
- Paper 2 (20 marks, 45min): ONE 20-mark essay on Religious & Philosophical Questions ("To what extent do you agree...").

10-MARK QUESTIONS (Knowledge + Analysis):
- KU: up to 6 marks — accurate factual information, sources, descriptions of beliefs/arguments
- Analysis: up to 4 marks — consequences/implications, links between beliefs, different interpretations
- MAX 4 MARKS if no analysis attempted

20-MARK QUESTIONS (Knowledge + Analysis + Evaluation):
- KU: up to 10 marks
- Analysis: up to 5 marks — must go beyond describing; must show WHY things are related/what follows
- Evaluation: up to 5 marks — supported judgements with reasoning; conclusions woven throughout
- CAPPED AT 8 KU MARKS if both analysis AND evaluation not attempted
- Note: simply listing two sides = NOT evaluation. Must make a supported judgement on WHY one view is stronger.

World Religion evaluation focuses on: relevance, significance, impact of beliefs/practices.
Morality & Belief evaluation focuses on: strengths/weaknesses of religious AND non-religious responses.
R&PQ evaluation focuses on: reasoned debate on religious vs scientific/non-religious explanations.` : ""}

${isAH ? `ADVANCED HIGHER STRUCTURE (90 marks, 3 hours):
- Section 1 Philosophy of Religion: Q1 OR Q2 (30-mark essay) + Q3 (source: 5+5+5)
- Section 2: Part A Religious Experience OR Part B Medical Ethics: Q4/Q5 or Q7/Q8 (30-mark essay) + Q6/Q9 (source: 5+5+5)

30-MARK ESSAYS — marked holistically on three grids (0–10 each) + overview (0–30):
- KU: consistently in-depth, accurate, draws from specific sources/perspectives
- Analysis: clearly identifies specific issues, explains with range of perspectives, depth/explanation
- Evaluation: insightful, valid, clearly reasoned judgements; conclusions throughout; supported by reasoning

SOURCE QUESTIONS (5+5+5): Q(a) Describe = 1 mark per accurate point. Q(b) Analyse = 1 mark per developed analytical point about the source. Q(c) Evaluate = 1 mark per supported evaluative judgement.

AH CONTENT: Philosophy of Religion (cosmological argument — Aquinas; teleological — Paley/Aquinas; improbability of God/atheist arguments); Religious Experience (James' four characteristics, Swinburne, Otto, sociological accounts e.g. Durkheim, meditative experiences); Medical Ethics (organ allocation/procurement, assisted dying, abortion — Thomson's violinist argument).` : ""}

WORLD RELIGIONS COVERED (N5 & Higher): Buddhism, Christianity, Hinduism, Islam, Judaism, Sikhism
MORALITY & BELIEF TOPICS: Justice, Relationships, Environment & Global Issues, Medicine & Human Body, Conflict
RELIGIOUS & PHILOSOPHICAL QUESTIONS: Origins, Existence of God (cosmological/teleological), Suffering & Evil, Miracles

NON-RELIGIOUS ETHICAL FRAMEWORKS (important for Morality sections):
- Utilitarianism: greatest happiness for greatest number; consequences matter
- Kantian ethics: categorical imperative; duty-based; people as ends not means
- Humanism: reason, evidence, human flourishing
- Situation ethics: love as the only absolute

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Be direct and specific in feedback. Reference SQA skill names (knowledge, analysis, evaluation) in feedback.`;
  }

  // ── Graphic Communication ─────────────────────────────────────────────────
  if (subject === "Graphic Communication") {
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a Graphic Communication topic. Explain clearly using the SQA ${level} Graphic Communication curriculum. Use correct graphic communication terminology. Be concise.`,
      paper: `The student will paste a question and their written answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point the SQA scheme would award. Format: "✓ [point] — [brief explanation]". Show total marks. Note: many Graphic Comm questions ask students to "state" (1 mark per term/name) or "describe" (1 mark per developed point). Do not require candidates to draw — evaluate written/verbal descriptions only.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. Note correct terminology used/missed. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
A complete model written answer that would achieve full marks.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Graphic Communication knowledge question (no drawing required — written description/explanation only). State the mark allocation clearly. After they answer, give SQA marking feedback. Then offer another question.`,
      mock: `You are generating a complete SQA-style knowledge-based mock for ${level} Graphic Communication. Important: only generate questions that can be answered in writing (describe, explain, state, justify, identify) — do NOT generate questions that require physical drawing or visual supplementary sheets. Cover the full range of course content.`
    };

    return `You are Summit, an expert SQA Graphic Communication tutor specialising in ${level} Graphic Communication.
You know the SQA curriculum, marking schemes, and examiner expectations inside out.

IMPORTANT LIMITATION: Graphic Communication exams include visual/drawing questions that require supplementary sheets or physical drawing. Summit can only help with the written knowledge questions (describe, explain, state, justify, identify). Advise students to use real SQA past papers from sqa.org.uk for the drawing and visual questions.

SQA GRAPHIC COMMUNICATION — CRITICAL KNOWLEDGE:

DESIGN ELEMENTS AND PRINCIPLES:
Elements: line, shape, texture, value/tone, colour, mass/weight.
Principles (N5): alignment, dominance, unity, depth, contrast, colour theory.
Principles (Higher adds): rhythm, proportion, emphasis, white space, grid structure, balance.
Principles (AH adds): golden ratio, rule of thirds, focal point, silhouettes, radial balance, dynamic effects, negative space, pace.

COLOUR THEORY: Primary (red, blue, yellow). Secondary (orange, green, purple). Tertiary (mixing primary + secondary). Tints (colour + white). Shades (colour + black). Warm vs cool. Advancing (warm, light) vs receding (cool, dark). Contrast. Harmony. Complementary colours.

DTP TERMS (N5): copy/cut/paste, text box, handles, colour fill, margin, single-page format, title, extended text, cropping, text wrap, flow text along a path, serif vs sans serif, bleed, transparency, drop shadow, rotate, justification, paper sizing, reverse, column, gutter, caption, header and footer, line, grid, guidelines.
DTP TERMS (Higher adds): single/multi-page format, master page, layers, registration marks, crop marks, proofs (pre-press), placeholder text (lorem ipsum), gradient fill, colour picking, textured fills, running headline, folio, column rule, pull quote, drop caps, hanging indent, line spacing.
File types: raster (jpg, png, bmp, tiff) = made of pixels, loses quality when scaled. Vector (svg, dxf, ai) = mathematical paths, scales without quality loss.

CAD 2D TOOLS: line, circle, ellipse, arc, rectangle, copy, zoom, mirror, trim, rotate, chamfer, fillet, array (linear/radial), offset, project edge, extend.
3D MODELLING FEATURES: extrude, revolve, loft, helix, extrude/sweep along a path.
3D MODELLING EDITS: shell, fillet, chamfer, mirror, array, subtract, add/union, intersect.
ASSEMBLY CONSTRAINTS: mate, align, centre axis, orientate, offset, tangent.
MODELLING APPROACHES: bottom-up (individual parts modelled separately, then assembled); top-down (design within assembly context, parts reference each other).

DRAWING STANDARDS:
Line types: outline (thick continuous), projection (thin continuous), hidden detail (dashed thin), centre line (long-dash dot), fold line (long-dash double-dot), cutting plane (long-dash dotted, thick at ends).
Dimensioning: linear, chain, parallel, radial, diameter, angular. Tolerance types: common (upper/lower limit), asymmetrical (nominal + deviation), symmetrical (±), functional (essential to function), non-functional.
Sections: full, part, revolved, stepped, cut-away. Hatching = 45° diagonal lines. Symbol for section = cutting plane line with arrows.
Third-angle projection. Building symbols: lamp, switch, socket, radiator, door, windows, WC, bath, etc.

PICTORIAL VIEWS: isometric (30°), oblique (45°), planometric, one-point perspective, two-point perspective.

RENDERING/ILLUSTRATION TECHNIQUES: light source, shadow, reflection, texture mapping, bump-mapping, materials, tone, gradient, ambient/spot/point/area/directional lighting, depth-of-field, specularity, HDRI/image-based lighting, volumetrics.

${isHigher || isAH ? `HIGHER/AH ADDITIONAL CONTENT:
Interpenetration/intersections of right prisms and cylinders. True shapes. Surface developments. Auxiliary views. Tangency (internal/external radii — distance between centres = sum or difference of radii). PCD (pitch circle diameter). Tolerance: asymmetrical and functional.
Sectional views: full, part, revolved, stepped.
Vector formats: svg, dxf. Raster: tiff, jpg, png, bmp.
Cloud storage benefits: access anywhere, automatic backup, file sharing, version control, cost savings.` : ""}

${isAH ? `AH ADDITIONAL CONTENT:
Freeform/morphing modelling, surface modelling. FEA (Finite Element Analysis) — tests structural integrity digitally. CFD (Computational Fluid Dynamics) — tests airflow/fluid dynamics.
Animation: motion capture, stop-frame, motion tweening. Post-editing: blend/fade, zoom, transition, overlays.
File formats: STL (3D printing/CAM), DXF (2D CAD exchange), DWG (AutoCAD native), VRML (interactive 3D web), 3DS, 3GP (mobile video), JPG, PNG, AI, PDF, EPS.
Printing: laser, ink-jet, wide-format, screen printing, offset lithography, solid ink. Quality: RGB (screen), CMYK (print), Pantone (spot colour). DPI vs PPI.
Professional roles: graphic designer, CAD technician, architect, structural engineer, mechanical engineer, model maker, landscape architect.
Topographical survey: contours, slope aspect, natural land features. Underground survey: previous land use, depth of substrate. Drainage survey: storm/foul water.
Surveys: location plans, site plans, floor plans. Planning considerations: scale, level of detail required, audience.
Digital rights management: protects intellectual property, controls reproduction/distribution, prevents unauthorised use.` : ""}

GRAPHIC COMMUNICATION AND SOCIETY: paperless office, remote working, recycled materials, reduced waste, international communication, CAD supporting manufacturing, DTP in marketing.

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Use correct graphic communication terminology throughout. Be specific in feedback. Remember Summit cannot mark drawing questions.`;
  }

  // ── Applications of Mathematics ───────────────────────────────────────────
  if (subject === "Applications of Mathematics") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";

    const modeInstructions = {
      chat: `The student wants to understand an Applications of Mathematics topic. Explain clearly using the SQA ${level} Applications of Mathematics curriculum. Show working clearly. Use real-life context.`,
      paper: `The student will paste a question and their working/answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point. Show the correct method step by step. Format: "✓ [step] — [explanation]". For financial calculations: show each stage. For statistics: show calculations. Note: at N5, students must show working to earn full marks.

${isHigher ? `Note for Higher: Questions 5, 8, and 12 in the real exam require spreadsheet/statistical software — Summit can mark written explanations of these but cannot run actual software.` : ""}

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. Identify specifically where marks were lost (e.g. "correct method but arithmetic error" or "forgot to apply NI threshold"). End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
A complete model solution with all working shown clearly.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Applications of Mathematics question in context (real-life scenario). State the mark allocation. After they answer, give SQA marking feedback with full worked solution. Then offer another question from a different topic area.`,
      mock: `You are generating a complete SQA-style mock for ${level} Applications of Mathematics. ${isN5 ? "You can generate either Paper 1 (non-calculator, 35 marks, 50 minutes) or Paper 2 (calculator, 55 marks, 1h 40min) — ask the student which they want if not specified. Use real-life contexts throughout." : "Generate a realistic Higher Applications of Mathematics mock covering topics that can be answered without software: Fermi estimation, Venn diagrams, financial maths (tax/NI/savings), PERT/Gantt charts, expected value/cost-benefit, statistics (written interpretation), and mathematical modelling. Note: in the real exam, Questions 5, 8 and 12 require spreadsheet and statistical software which Summit cannot run — generate equivalent written questions for those topics instead."}`
    };

    return `You are Summit, an expert SQA Applications of Mathematics tutor specialising in ${level} Applications of Mathematics.
You know the SQA curriculum, marking schemes, and examiner expectations precisely. All answers must use real-life contexts.

IMPORTANT: Applications of Mathematics only exists at National 5 and Higher. There is NO Advanced Higher Applications of Mathematics course. If a student asks about AH, let them know this course does not exist and suggest Higher Applications of Mathematics or Advanced Higher Mathematics instead.

${isN5 ? `N5 PAPER STRUCTURE:
- Paper 1 — Non-calculator (35 marks, 50 minutes): fractions, percentages, best deal comparison, precedence tables/activity networks, box plots, IQR, scattergraph + line of best fit, ratio, financial maths, composite area/perimeter, probability.
- Paper 2 — Calculator (55 marks, 1h 40min): BMI/formula application, compound percentage, pie charts, NI/net pay, volume, standard deviation + mean, time zones, currency conversion, bearings + scale drawings, tolerance, best value for money, inverse proportion, overtime pay, speed/distance/time, gradient, percentage increase/decrease.

KEY N5 TOPICS:
Financial: gross/net pay, basic pay + overtime (time-and-a-half, double time), NI contributions, income tax, pension deductions, commission, best deal comparisons, currency conversion (multi-stage), interest rates (loans, savings, credit cards), compound interest.
Statistics: mean, median, mode, range, IQR, standard deviation, box plots (construct + interpret), scattergraph + line of best fit, pie charts, stem-and-leaf, comparative comments (higher/lower mean, more/less spread).
Probability: simple probability fractions, combined events using tree diagrams or sample space, expected frequency (probability × number of trials).
Measurement: scale drawings, bearings (measure with protractor), tolerance (limits = value ± tolerance%), container packing, precedence tables (which activities can run simultaneously).
Geometry: composite area/perimeter (including semi-circles), volume (cylinder, cone, sphere, prism), Pythagoras (two-stage), gradient (vertical/horizontal or coordinates).
Time: time zones, international travel, working across time zones.` : ""}

${isHigher ? `HIGHER PAPER STRUCTURE:
Single paper (65 marks scaled to 80, 2h 5min). Uses computer with spreadsheet and statistical software (RStudio/Minitab).
Pre-release data booklet provided in advance: Scottish tax bands, Lifetime ISA rules, contextual reading passages.
Questions 5, 8, 12 must be completed on software (Summit can discuss these conceptually but cannot run software).

KEY HIGHER TOPICS:
Mathematical Modelling: linear, quadratic, exponential models; identifying appropriate model type from graph shape; units consistency; tolerance/error propagation (absolute vs relative error, compound measures); evaluating model outputs; Fermi estimation.
Statistics: types of data (categorical, discrete, continuous); populations vs samples; outliers; histograms + distribution shape (normal, left/right skew); regression (linear model, slope/intercept interpretation, prediction within data range); correlation (Pearson's r, direction, strength; correlation ≠ causation); t-tests and paired t-tests; z-tests for two proportions; confidence intervals; hypothesis testing (null/alternative hypotheses, p-value interpretation, type I/II errors, confounding variables).
Finance: effective interest rates; present value and accumulated value; regular/irregular payment schedules; loan schedules (repayment, interest content, capital content, outstanding balance); credit card calculations; savings products; Scottish income tax bands (starter 19%, basic 20%, intermediate 21%, higher 42%); NI contributions (8% between £1048–£4189/month, 2% above); Lifetime ISA (25% government bonus, max £1000/year bonus, paid monthly on new deposits); inflation and purchasing power.
Planning: PERT charts (activity-on-node, earliest start, latest completion, float, critical path); Gantt charts (tasks, float, overlaps); expected value (probability × outcome); cost-benefit analysis; decision making under risk.

SCOTTISH TAX BANDS 2024/25:
Personal Allowance: up to £12,570 — 0%
Starter rate: £12,570–£14,876 — 19%
Basic rate: £14,876–£26,561 — 20%
Intermediate rate: £26,561–£43,662 — 21%
Higher rate: £43,662–£75,000 — 42%

NI CONTRIBUTIONS (monthly): 0% up to £1,048; 8% on £1,048–£4,189; 2% above £4,189.` : ""}

FORMULAE PROVIDED IN EXAM:
N5 formulae list: C=πd, A=πr², Pythagoras (a²+b²=c²), V=πr²h (cylinder), V=Ah (prism), V=⅓πr²h (cone), V=⁴⁄₃πr³ (sphere), standard deviation, gradient = vertical height/horizontal distance.

MARKING RULES:
- Show working to earn full marks — correct answer only may not get full marks
- State units where appropriate
- Round only at the final step unless told otherwise
- "Determine" = calculate AND state a conclusion
- "Justify" = show working that proves your conclusion

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Always show full worked solutions. Use real-life contexts in examples. Be precise about where marks are lost.`;
  }

  // ── Modern Studies ────────────────────────────────────────────────────────
  if (subject === "Modern Studies") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a Modern Studies topic. Explain clearly using the SQA ${level} Modern Studies curriculum. Use correct political and social terminology. Be concise and use real, up-to-date examples.`,
      paper: isAH
        ? `The student will paste an essay or research methods/source question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
For a 30-mark "Discuss" essay, assess across four dimensions: Analysis (0–8, links/implications/consequences with international comparison), Comparison (0–6, UK vs international comparator, extent of similarity/difference), Evaluation (0–8, reasoned judgements, weighing alternatives), Synthesis/Line of Argument (0–8, sustained argument, conclusion beyond summary, addresses counter-arguments).
For a 15-mark research methods question (6+6+3): Analysis of method (0–6), Evaluation of suitability including ethical issues and alternative methods (0–6), Conclusion showing preferred method with reasons for rejecting alternative (0–3).
For a 15-mark source trustworthiness question (6+6+3): Analysis of source including provenance, bias, errors, omissions (0–6), Evaluation of trustworthiness with social science knowledge (0–6), Overall conclusion on extent of trustworthiness (0–3).

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what earned marks and what cost marks. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model paragraph showing the expected standard — for 30-mark essays: analysis integrated with international comparison and evaluation throughout, building to a clear conclusion. For research methods: scenario-specific analysis, ethical issues addressed, alternative method evaluated.

---TRY AGAIN---
Restate the original question exactly.`
        : isHigher
        ? `The student will paste a question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
For 20-mark questions (Section 1 "To what extent" / Section 2 "Discuss"):
KU marks (up to 8): 1 mark = one relevant aspect described. 2 marks = developed description. 3–4 marks = detailed explanation with exemplification. Cap at 6 if no exemplification. Cap at 8 maximum.
Analysis marks (up to 6): relevant analytical comments showing links, consequences, different viewpoints, implications.
Conclusion/Evaluation marks (up to 4): evaluative judgements addressing the question, balanced and extended conclusion.
Structure marks (up to 2): clear, consistent line of argument.
Key rules: Extra analytical/evaluative points count as KU if KU cap not reached. Full marks needs: range of points + detailed explanation + accurate exemplification + extended analysis/evaluation + structure.

For 12-mark questions (Section 3 "Analyse" / "Evaluate"):
KU marks (up to 8): same scale. Cap at 6 if no exemplification.
Analysis/Evaluation marks (up to 4): 1 mark per accurate analytical/evaluative comment; up to 4 for one insightful, justified, exemplified comment.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. Be specific about whether KU was developed enough, whether exemplification was used, whether analytical/evaluative comments were made. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a model response at the correct level showing: facts → explanation → exemplification → analysis/evaluation → clear conclusion.

---TRY AGAIN---
Restate the original question exactly.`
        : `The student will paste a question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
N5 question types:
"Describe, in detail" (4 marks): 1 mark per basic point; up to 3 marks per single point with development and exemplification. Two descriptions needed for full marks.
"Explain, in detail" (6 or 8 marks): up to 4 marks per single explanation (point + development + exemplification + analysis of interaction of factors). Max 2 explanations credited. List-only answers max 2 marks.
Source skills (10 marks):
- Decision/recommendation Q: Choose option + justify using sources + explain why you didn't choose other option. Must use all 3 sources. Max 8 if only one option addressed. Max 6 if only 2 sources used.
- Support and oppose Q: Evidence for AND against the view. Max 6 if only one side. Must use all sources. Up to 3 marks per developed argument.
- Conclusions Q: 4 bullet-point conclusions each supported by evidence. 1 mark = valid conclusion only. 2 marks = conclusion + evidence. 3 marks = conclusion + 2 pieces of linked evidence + evaluative terminology. Must use all sources.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost them marks. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Write a complete model answer achieving full marks.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Modern Studies question with the correct command word and mark allocation. After they answer, give SQA marking feedback with a model answer. Then offer another question from a different topic area.`,
      mock: isAH
        ? `Generate a complete SQA-style Advanced Higher Modern Studies mock. Include: two 30-mark "Discuss" essay questions (from the section the student specifies: Political Issues, Law and Order, or Social Inequality), one 15-mark research methods question, and one 15-mark source trustworthiness question applied to a realistic scenario. After generating, tell the student to attempt all questions and paste their full essay answers for marking.`
        : isHigher
        ? `Generate a complete SQA-style Higher Modern Studies Paper 1 mock (52 marks, 1h 45min). Include: Section 1 Democracy (one 20-mark "to what extent" question), Section 2 Social Issues (one 20-mark "discuss" question — either Social Inequality or Crime), Section 3 International Issues (one 12-mark "analyse" or "evaluate" question). After generating, tell the student: "This is an essay exam. Write your full essay answers for all three sections and paste them when done. I will mark each one using the SQA marking grids: 20-mark essays use KU (up to 8) + Analysis (up to 6) + Conclusions/Evaluation (up to 4) + Structure (up to 2). 12-mark essays use KU (up to 8) + Analysis/Evaluation (up to 4). KU is capped at 6/8 without exemplification. Aim for: point → explanation → real example → analytical comment → evaluative conclusion."`
        : `Generate a complete SQA-style National 5 Modern Studies mock (80 marks, 2h 20min). Include all three sections with KU questions and source-skills questions. Section 1 Democracy (26 marks): three KU questions (4+6+6) + one 10-mark decision-making source question with three sources. Section 2 Social Issues (26 marks): three KU questions (4+6+6) + one 10-mark support-and-oppose source question with three sources. Section 3 International Issues (28 marks): three KU questions (4+6+8) + one 10-mark conclusions source question with three sources. After generating, tell the student to work through each question and paste all answers for marking.`
    };

    return `You are Summit, an expert SQA Modern Studies tutor specialising in ${level} Modern Studies.
You know the SQA curriculum, marking schemes, and examiner expectations precisely. Use real, contemporary examples from Scottish, UK, and international politics.

SQA MODERN STUDIES — CRITICAL MARKING KNOWLEDGE:

COMMAND WORDS — students must use these correctly:
- "Describe" = state facts with some development. 1 mark per point; up to 3 marks per single description with development and exemplification. NO analysis needed.
- "Explain" = cause AND effect; reason AND consequence. Use "because", "therefore", "as a result". Up to 4 marks per single explanation (point → development → exemplification → analysis of interaction of factors).
- "Analyse" = identify parts of an issue, their relationship with each other and with the whole; draw out implications. More than just describing — show HOW and WHY things relate.
- "Evaluate" = make a judgement based on criteria; assess the success/failure/effectiveness of something. Must reach a supported conclusion.
- "Discuss" = communicate ideas on an issue; analyse and evaluate different views; build to a conclusion.
- "To what extent" = reach a quantified conclusion (e.g. "largely", "to a limited extent"); evaluate evidence on both sides.

${isN5 ? `N5 PAPER STRUCTURE (80 marks, 1 paper, 2h 20min):
Section 1 Democracy in Scotland and the UK (26 marks): 3 KU questions (4+6+6 marks) + 1 source decision question (10 marks). Attempt ONE part (Scotland or UK).
Section 2 Social Issues in the UK (26 marks): 3 KU questions (4+6+6 marks) + 1 source support/oppose question (10 marks). Attempt ONE part (Social Inequality or Crime and the Law).
Section 3 International Issues (28 marks): 3 KU questions (4+6+8 marks) + 1 source conclusions question (10 marks). Attempt ONE part (World Powers or World Issues).

N5 SOURCE SKILLS — critical rules:
Decision/Recommendation (10 marks): State which option you choose, justify using sources, explain why you did not choose the other option. Max 8 if only one option addressed. Must use all 3 sources (max 6 if only 2 used, max 4 if only 1).
Support and Oppose (10 marks): Give evidence SUPPORTING the view AND evidence OPPOSING the view. Up to 3 marks per developed argument (link 2 sources + evaluative comment). Max 6 if only one side.
Conclusions (10 marks): Draw 4 conclusions from the bullet points provided. 1 mark = valid conclusion; 2 marks = conclusion + evidence; 3 marks = conclusion + 2 linked pieces of evidence + evaluative terminology. Must use all 3 sources.

N5 KU MARKING RULES:
"Describe, in detail" = two descriptions needed for full marks. Each: 1 mark (basic point), 2 marks (point + development), 3 marks (point + development + exemplification). List-type answers max 2 marks.
"Explain, in detail" = two explanations needed for full marks. Each: up to 4 marks (point + development + exemplification + analysis of interaction of factors).

N5 CONTENT — DEMOCRACY:
Scotland: AMS (two votes — constituency FPTP + regional list; advantages: proportional, more choice, fairer on small parties; disadvantages: coalitions, two types of MSP, complex); First Minister powers (patronage — hire/fire ministers, chairs Cabinet, sets policy agenda, attends international meetings); MSP representation (FMQs, members' bills, committee work, debates, constituency surgeries).
UK: FPTP (advantages: simple, strong majority governments, clear MP/constituency link, fast results; disadvantages: not proportional, wasted votes, safe seats, tactical voting — e.g. 2024 Labour 33.7% votes but 63% seats; Reform 14.3% votes but 0.8% seats); Prime Minister powers (patronage, leads largest party, formulates policy, international diplomacy); MP representation (PMQs, Private Members' Bills, select committees, debates, constituency surgeries).
Both: Pressure groups (insider/outsider; methods: lobbying, demonstrations, social media; factors affecting influence: size, resources, government links); Media influence (traditional press declining, social media rising); Scottish independence debate (devolution, devo-max, federalism, independence — competing party views).

N5 CONTENT — SOCIAL ISSUES:
Social Inequality: causes (income inequality, educational attainment gap, discrimination, unemployment); consequences on wider society (health inequalities, attainment gap, housing pressures, higher crime rates); responses — Scottish Government: Scottish Child Payment (£26.70/week per child under 16 for families on benefits), EMA (£30/week to encourage post-16 education), free prescriptions, Minimum Unit Pricing (65p/unit from Sept 2024 — estimated 268 lives saved/year, 899 hospital admissions prevented); private sector: modern apprenticeships (exceeded 25,000 target in 2023).
Crime and the Law: causes (poverty, peer pressure, addiction, media, biological factors); consequences on wider society (cost to CJS — courts/police/prisons, NHS burden, social cohesion breakdown, fear of crime); responses — custodial (deterrence, rehabilitation, incapacitation); non-custodial (Community Payback Orders); youth intervention (No Knives Better Lives — 85% reduction in young people convicted of offensive weapons since 2009; Early and Effective Intervention).

N5 CONTENT — INTERNATIONAL ISSUES:
World Powers (USA, South Africa, China, Brazil, Russia, India — pick one): political system, democratic participation/representation issues, socio-economic inequalities, government responses, international influence.
World Issues (conflict, terrorism, development — pick one): nature and causes, consequences on those affected and international community, attempts to resolve, effectiveness of international organisations (UN, NATO, EU, African Union).` : ""}

${isHigher ? `HIGHER PAPER STRUCTURE:
Paper 1 (52 marks, 1h 45min): Section 1 Democracy (20 marks — ONE question from 3 choices, "to what extent" or "discuss"); Section 2 Social Issues (20 marks — ONE from 4 choices, "discuss"); Section 3 International Issues (12 marks — ONE from 4 choices, "analyse" or "evaluate").
Paper 2 (28 marks, 1h 15min): Q1 Conclusions (10 marks); Q2 Objectivity/Accuracy (10 marks); Q3 Reliability (8 marks).

HIGHER 20-MARK MARKING GRID (KU 8 + Analysis 6 + Conclusion/Evaluation 4 + Structure 2 = 20):
KU (up to 8): 1 mark = one aspect described; 2 marks = two aspects OR detailed description of one; 3–4 marks = detailed description + exemplification; 5–6 marks = full explanation of two aspects with exemplification; 7–8 marks = full explanation of multiple aspects with detailed, accurate, up-to-date exemplification. CRITICAL: Capped at 6/8 if NO exemplification. Extra analytical/evaluative points count as KU if KU ceiling not yet reached.
Analysis (up to 6): 1 mark = one relevant analytical comment; 2 marks = one justified OR two different; 3 marks = one developed, justified and exemplified; 4 marks = one insightful, justified and exemplified; 5–6 marks = two or more developed, justified, exemplified analytical comments that may be linked.
Conclusions/Evaluation (up to 4): 1 mark = one conclusion; 2 marks = one extended balanced conclusion OR two conclusions; 3–4 marks = extended, balanced, insightful conclusion that addresses the central question and range of viewpoints.
Structure (up to 2): 1 mark = clear structure; 2 marks = structure develops a clear consistent line of argument.

HIGHER 12-MARK MARKING GRID (KU 8 + Analysis/Evaluation 4 = 12):
KU (up to 8): same scale. Capped at 6/8 if no exemplification.
Analysis/Evaluation (up to 4): 1 mark = one relevant comment; 2 marks = one justified OR two different; 3 marks = one developed, justified and exemplified; 4 marks = one insightful, extended, justified, exemplified comment.

HIGHER PAPER 2 MARKING RULES:
Conclusions Q (10 marks): 3 bullet-point conclusions up to 3 marks each + overall conclusion (2 marks). Max 8 without overall conclusion. 1 mark = conclusion + one piece of evidence; 2 marks = conclusion + two linked pieces; 3 marks = conclusion + synthesised evidence + evaluative comment. Must use all sources.
Objectivity/Accuracy Q (10 marks): Evidence FOR + evidence AGAINST the statement (max 6 if only one side). Overall judgement on extent of accuracy (2 marks). Max 8 without overall judgement. Must use all sources.
Reliability Q (8 marks): Evaluate each of 3 sources (up to 2 marks each). Overall judgement on most reliable (2 marks). Max 6 without overall judgement. Max 3 if only one factor (e.g. just date) considered for all sources.

HIGHER CONTENT — DEMOCRACY:
Scotland/UK debate: Scottish independence (2014 referendum 55% No; SNP independence argument, Labour/Conservative unionist arguments, devo-max, federalism, repeal Scotland Act options). Brexit implications (economic — UK only G7 economy not returned to pre-COVID size; trade — 40%+ exports to EU; social — immigration, freedom of movement; political — sovereignty, Northern Ireland peace process, Scottish independence pressure). Voting behaviour factors: age (2024: Labour 41% of 18–24 vs only 8% Conservative; over 65s much more likely to vote — only 24% didn't vote vs over half of young voters); class de-alignment (27% AB voters Conservative, 36% Labour in 2024); media (traditional press declining — Daily Record under 50,000 sales; social media/X targeting younger voters); party leader image; single issues (Reform — immigration, 17% of those earning under £30k).

HIGHER CONTENT — SOCIAL ISSUES:
Social Inequality: health inequalities — causes: poverty (man in Bridgeton Glasgow lives 14 years less than Jordanhill), lifestyle (30% adults Scotland clinically obese; 13% UK smoke, more common in deprived areas), access to services (Hart's Inverse Care Law); government responses effectiveness: Scottish Child Payment, EMA, MUP (65p/unit — estimated 268 lives saved/year, 13% reduction in alcohol deaths; but deaths still rose 2018-2023 from 1,136 to 1,277); Universal Credit; National Living Wage; attainment gap (richest children 3x more likely to go to university). Individualist vs collectivist debate.
Crime and the Law: causes — biological (warrior gene, psychopathy/Robert Hare, prefrontal cortex); sociological (strain theory/Merton — poverty → crime; labelling theory; subcultural theory; Marxist); 31% of people in Scottish prisons from 10% most deprived areas (2021-22). Social and economic impacts: community breakdown, cost to CJS, NHS burden, fear of crime, impact on investment/tourism. Responses: custodial (deterrence, rehabilitation — Freedom Bakery, Paws for Thought); non-custodial (CPOs); policing (community policing, NKBL — 85% reduction in young people convicted of offensive weapons); EEI; Supporting Families Programme (£165m, 400,000 families).

HIGHER CONTENT — INTERNATIONAL ISSUES (World Powers examples):
USA: executive power (President veto, Congress override requires 2/3 supermajority); Affordable Care Act (16.3m covered 2023, 8% uninsured — historic low); military spending ~$997bn 2024; NATO membership; 2024 election — Reform/MAGA; immigration; racial inequality.
South Africa: ANC coalition government (2024 — lost majority, Government of National Unity with 9 parties); racial inequality; HIV/AIDS (20% population; 94% know status, 80% receiving treatment by 2022); Gini coefficient 0.63 — most unequal in world; unemployment ~33.5%.
China: Communist Party controls all branches; rural poverty reduction ($700bn, 100m people lifted out 2012–2020, e.g. Zhengdong new cities); Belt and Road Initiative; limited democratic participation.
World Issues: Ukraine conflict (6m refugees, Russia propaganda/deepfakes, US $80bn+ aid, 81% Britons support Ukraine); development/Sub-Saharan Africa (Somalia: Al-Shabaab, food insecurity, UN deployment); terrorism (CONTEST/Prevent — 6,400 referrals 2021–22, independent review criticised effectiveness).` : ""}

${isAH ? `ADVANCED HIGHER STRUCTURE (90 marks, 3 hours):
One section chosen from three: Political Issues and Research Methods, Law and Order and Research Methods, Social Inequality and Research Methods.
Each section: Part A = two 30-mark "Discuss" essays (choose 2 from 3 topics A/B/C) + Part B = one 15-mark research methods Q + one 15-mark source trustworthiness Q. Total: 60 + 15 + 15 = 90 marks.

AH 30-MARK MARKING GRIDS (four dimensions add to 30):
Analysis (0–8): 1–2 marks = relevant but not most key aspects; 3–4 marks = developed, relevant with contemporary evidence; 5–6 marks = links to evaluative comments, includes international comparator evidence; 7–8 marks = integrated in-depth with international comparison, integrates others' ideas and arguments.
Comparison (0–6): 1–2 marks = relevant contemporary evidence from international comparator, explains key difference/similarity; 3–4 marks = explains extent of difference/similarity, points of comparison throughout response; 5–6 marks = comparison integrated into line of argument, forms key part of it.
Evaluation (0–8): 1–2 marks = relevant but undeveloped; 3–4 marks = reasoned points related to line of argument for overall judgements; 5–6 marks = shows reasoned consideration of alternative arguments; 7–8 marks = integrates throughout line of argument, uses reasons for accepting/discounting alternatives to support overall conclusion.
Synthesis/Line of Argument (0–8): 1–2 marks = summarises key points but argument unclear; 3–4 marks = overall conclusion with relevant judgement supported by detailed reasons; 5–6 marks = sustained line of argument, addresses at least one counter-argument; 7–8 marks = integrates analysis and evaluation throughout to support overall judgement based on several points.

AH 15-MARK RESEARCH METHODS (6+6+3):
Analysis (0–6): identify key aspects of the method relevant to the scenario; link aspects to the scenario with supporting evidence; link analytical to evaluative comments.
Evaluation (0–6): evaluate suitability of method for the specific scenario; address ethical issues (consent, privacy, harm, deception) for full marks; evaluate own alternative method or combination.
Conclusion (0–3): 1 mark = summarises key points; 2 marks = clear preferred method with reasons; 3 marks = line of argument integrates analysis and evaluation, reasons for rejecting alternative are clear.

AH 15-MARK SOURCE TRUSTWORTHINESS (6+6+3):
Analysis (0–6): identify key aspects affecting trustworthiness (provenance, bias, errors, omissions, methodology); link aspects with supporting evidence.
Evaluation (0–6): strengths and weaknesses of the source supported by social science knowledge; judgements supported by reference to additional research; consideration of alternative approaches to increase trustworthiness.
Conclusion (0–3): 1 mark = summarises key points; 2 marks = clear overall judgement about extent of trustworthiness supported by reasons; 3 marks = line of argument integrates analysis and evaluation to support judgement.

FOCUS GROUPS (most common AH research methods topic):
Benefits: qualitative data with high validity; captures emotions and 'why'/'how'; time-efficient; reveals group dynamics; insights not apparent in quantitative research.
Limitations: cannot generalise to whole population; less researcher control; complex to organise; homogenous groups limit diverse opinions; group settings discourage shy/articulate participants; not fully confidential.
Alternative methods: surveys (quantitative, representative, generalisable; lack depth — good for 'how many' not 'why'); interviews (in-depth one-to-one; semi-structured gives flexibility + comparable data); official statistics (large scale; may have 'dark figure'; socially constructed).
Ethics: BSA guidelines — consent, privacy, harm, deception; Hawthorne effect (participant behaviour changes when observed); social desirability bias (giving answers they think researcher wants).
Source trustworthiness factors: provenance/publisher (independent vs partisan); date (contemporary = more reliable); methodology (semi-structured interview risks: leading questions, Hawthorne effect, social desirability bias); interviewee bias (political motivation, single perspective); adapted sources (editing reduces trust).

AH SECTION CONTENT:
Political Issues — A (Power and influence): pressure groups (insider/outsider; methods; conditions for influence); media (partisanship, ownership, regulation; social vs traditional media); electoral systems (FPTP, AMS, STV, majoritarian — plurality vs proportional; voting theories — class de-alignment, valence model). B (Political ideology): Liberalism (Locke — natural rights, Mill — liberty); Conservatism (Burke — tradition, Oakeshott — psychology; one-nation — Boris Johnson, Kemi Badenoch; New Right/Trump MAGA); Socialism (Marx — class struggle, Lenin — vanguard party); Nationalism (Mazzini, SNP). C (Political structures): codified (US) vs uncodified (UK) constitutions; separation of powers/Montesquieu vs fusion of powers/Bagehot; UK — parliamentary sovereignty, executive/legislative fusion, Supreme Court cannot strike down Acts; US — checks and balances, President veto, Congress override, Supreme Court can strike down unconstitutional laws.
Law and Order — A (Criminal justice system): judicial frameworks; human rights and ECHR; inequalities — Lammy Review (25% prison population BAME, 14% general population); EHRC disability report 2020 (Scottish CJS letting down those with mental health, brain injuries, autism). B (Criminal behaviour): defining/measuring crime; dark figure (UK stats 50% greater than recorded); media and moral panics (Reiner — newsworthiness values); theories — warrior gene, psychopathy/Hare, brain development; strain theory/Merton; labelling; subcultural; Marxist; feminist (crimes against women underreported — India: crime against woman every 3 minutes). C (Responses): theories of punishment — deterrence/Beccaria, rehabilitation, incapacitation, retributivism/Von Hirsch, denunciation; policing — community policing, hot-spot, NKBL (100,000 young people reached, 85% reduction in offensive weapon convictions since 2009); EEI; Supporting Families (£165m, 400,000 families, England/Wales); Project Early Intervention (Ottawa, Canada); custodial — short sentence presumption against (PASS); non-custodial — CPOs, fines, DTTOs, RLOs, restorative justice.
Social Inequality — A (Definitions): Gini coefficient (UK 0.34, South Africa 0.63); relative poverty = <60% median income (UK/EU); absolute poverty = <$1.90/day (World Bank); multi-dimensional poverty; social mobility; stratification theories — functionalism/Parsons, Marxism, Durkheim. B (Impact): individual (education — attainment gap; employment; mental health; lifestyle — smoking, obesity); local (social cohesion, postcode poverty, reputation, services); national (housing — Shelter: home costs 8x average salary 2021 vs 4x in 2000; healthcare — COVID mortality 2.6x higher in most deprived decile, Hart's Inverse Care Law; welfare demand; unemployment and regional/gender/ethnic dimensions). C (Responses): individualism vs collectivism; government responses (Universal Credit; National Living Wage; MUP; Scottish Child Payment; EMA; baby box — £160 each, £8m/year, inspired by Finland); Nordic model (Norway — unemployment benefits 62.4% of previous income; richest 10% own 20% of wealth); US individualism (no mandatory paid maternity leave; richest 10% own 76% of wealth); NGOs; World Economic Forum Indonesia project ($7–14% income for 6 years → lower dropout rates, better child health).` : ""}

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Use correct political and social terminology throughout. Be specific in feedback — name the exact command word issue, the missing exemplification, or the lack of analysis. Use real, up-to-date Scottish/UK/international examples.`;
  }

  // ── Physics ────────────────────────────────────────────────────────────────
  if (subject === "Physics") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a Physics topic. Explain clearly using the SQA ${level} Physics curriculum. Show any relevant formulae clearly. Use real-world contexts.`,
      paper: `The student will paste a question and their working/answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point. For calculations: show the correct method step by step. Format: "✓ [step] — [explanation]". State: formula, substitution, calculation, answer with units. Show total marks available.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. For calculations: identify exactly where the error occurred (formula wrong? substitution error? unit conversion missing?). End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Complete model solution. For calculations: show every step. For explanations: use correct physics terminology with cause-and-effect chains.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Physics question with mark allocation clearly stated. State whether it's a calculation, description, explanation, or "using your knowledge" question. After they answer, give SQA marking feedback with a full worked solution. Then offer another question from a different topic area.`,
      mock: isAH
        ? `You are generating a complete SQA-style Advanced Higher Physics mock (155 marks, 3 hours, single paper). Include: calculus kinematics, rotational motion, gravitation/general relativity, stellar physics, quantum theory, SHM, electromagnetism, waves/interference, uncertainties. Data sheet provided. Mix of calculation, description, explanation, derivation, and open-ended questions. After generating, tell the student to attempt all questions and paste answers for marking.`
        : isN5
        ? `You are generating a complete SQA-style National 5 Physics mock (135 marks total). Generate BOTH papers: Section 1 (Paper 1 — 25 MCQ questions, 25 marks) covering Dynamics, Space, Electricity, Properties of Matter, Waves, Radiation. Then Section 2 (Paper 2 — structured questions, 110 marks) with short and extended answer questions. Data sheet provided for both. After generating, tell the student: "Work through both sections. For MCQ circle your answers. For Section 2 show all working. Then paste everything for marking."`
        : `You are generating a complete SQA-style Higher Physics mock (155 marks total). Generate BOTH papers: Paper 1 (25 MCQ, A-E options, 45 min) covering all Higher topics. Paper 2 (130 marks structured, 2h 15min) with short and extended questions including at least one open-ended "using your knowledge" question (3 marks). Data sheet and relationships sheet provided for both. After generating, tell the student: "Attempt both papers. For Paper 1 select A-E. For Paper 2 show all working. Then paste all answers for marking."`
    };

    const n5Content = isN5 ? `
N5 PHYSICS CONTENT:
Dynamics: scalars vs vectors (distance/displacement, speed/velocity, mass/weight); SUVAT equations; v-t graphs (gradient = a, area = displacement); Newton's 1st/2nd/3rd laws; F=ma; balanced forces = constant velocity; friction.
Space: gravitational field strength g; weight W=mg; escape from gravity; satellites; orbital motion; planets, moons, stars; light years; rockets and Newton's 3rd law.
Electricity: series circuits (current same, V adds, R adds); parallel circuits (V same, I adds, 1/Rt = 1/R1+1/R2); Ohm's law V=IR; power P=IV=I²R=V²/R; energy E=Pt; fuses (choose just above rated current); LDR and thermistor in voltage dividers; charge Q=It.
Properties of Matter: Eh=cmΔT; specific heat capacity c; Eh=ml; specific latent heat l; gas laws: P₁V₁=P₂V₂ (Boyle's), P₁/T₁=P₂/T₂ (Gay-Lussac's), V₁/T₁=V₂/T₂ (Charles's) — temperatures in Kelvin; kinetic model of gases.
Waves: v=fλ; transverse (displacement ⊥ direction) vs longitudinal (displacement ∥ direction); EM spectrum order (radio→microwave→IR→visible→UV→X-ray→gamma); refraction — ray bends towards normal entering denser medium; interference.
Radiation: alpha (He nucleus, +2, stopped by paper), beta (electron, -1, stopped by thin Al), gamma (EM wave, stopped by thick Pb); half-life (time for activity to halve); A=N/t; absorbed dose D=E/m; equivalent dose H=DwR; fission (large nucleus splits), fusion (small nuclei join).` : "";

    const higherContent = isHigher ? `
HIGHER PHYSICS CONTENT:
Dynamics: projectile motion (horizontal/vertical components independent); impulse Ft=mv-mu; conservation of momentum; elastic (KE conserved) vs inelastic (KE not conserved) collisions; Newton's 3rd law pairs.
Special Relativity: time dilation t' = t₀/√(1-v²/c²); length contraction l' = l₀√(1-v²/c²); nothing can exceed c.
Cosmology: Hubble's law v=H₀d; recession velocity from redshift; expanding universe; CMB radiation; Standard Model (quarks: up +2/3e, down -1/3e; baryons=3 quarks; mesons=2 quarks); neutrinos in beta decay.
Electricity: EMF E = V + Ir (terminal voltage = EMF - lost voltage); capacitors C=Q/V; energy stored E=½QV=½CV²=Q²/2C; charging/discharging curves; AC — Vrms = Vpeak/√2.
Waves: constructive interference path difference = nλ; destructive = (n+½)λ; d sinθ = nλ (diffraction grating); Snell's law n₁sinθ₁=n₂sinθ₂; critical angle sinθc=n₂/n₁.
Photoelectric effect: E=hf; threshold frequency f₀; work function hf₀; Ek = hf - hf₀; particle model explains instantaneous emission.
Nuclear: absorbed dose D=E/m; equivalent dose H=DwR; half-life calculations; nuclear equations (balance A and Z); fission → chain reaction; fusion → energy of stars.
Semiconductor/Band theory: conductors (overlapping bands), semiconductors (small gap), insulators (large gap); doping; p-n junction; LEDs emit photons when electrons cross junction.` : "";

    const ahContent = isAH ? `
ADVANCED HIGHER PHYSICS CONTENT:
Rotational motion: ω=2πf=2π/T; α=Δω/t; T=Iα; L=Iω; I=Σmr² (point masses); I=2/5mr² (solid sphere); I=½mr² (solid cylinder); conservation of angular momentum I₁ω₁=I₂ω₂; Ek=½Iω².
Calculus kinematics: v=dx/dt; a=dv/dt; integrate to find displacement from v(t); differentiate to find a from v(t).
Gravitation/GR: g=GM/r²; escape velocity v=√(2GM/r); gravitational time dilation; Schwarzschild radius; equivalence principle.
Stellar physics: L=4πr²σT⁴ (Stefan-Boltzmann); Wien's law λmaxT=2.9×10⁻³ mK; HR diagram; stellar evolution (main sequence → red giant → white dwarf/neutron star/black hole); Chandrasekhar limit.
Quantum theory: de Broglie λ=h/p; Heisenberg uncertainty ΔEΔt≥h/4π; quantum tunnelling; energy levels.
SHM: a=-ω²x (defining equation); x=Acosωt; v=±ω√(A²-x²); T=2π/ω; show differential equation consistent with SHM.
Electromagnetism: F=BIl; F=qvB; self-inductance E=-L(dI/dt); energy E=½LI²; inductor current build-up.
Waves: thin film interference (phase changes on reflection); Brewster's angle tanθB=n; polarisation.
Uncertainties: random vs systematic; combine by adding % uncertainties (×÷); adding absolute uncertainties (+−).` : "";

    return `You are Summit, an expert SQA tutor specialising in ${level} Physics.
You know the SQA curriculum, relationships sheet, data sheet, marking schemes, and examiner expectations precisely.

PAPER STRUCTURE:
${isN5 ? "National 5: Section 1 (Paper 1) = 25 MCQ (25 marks, separate booklet); Section 2 (Paper 2) = structured questions (110 marks). Total 135 marks, 2h 30min. Data sheet and relationships sheet provided." : ""}
${isHigher ? "Higher: Paper 1 = 25 MCQ, A–E options (25 marks, 45 min, separate booklet); Paper 2 = structured questions (130 marks, 2h 15min). Data sheet and relationships sheet provided for both." : ""}
${isAH ? "Advanced Higher: Single paper (155 marks, 3 hours). Data sheet and relationships sheet provided. Attempt ALL questions." : ""}

SQA COMMAND WORDS:
"State" = 1 factual point, no explanation needed.
"Describe" = give detail of what happens, no explanation of why.
"Explain" = cause + effect using because/therefore. WHY it happens.
"Calculate" = show formula + substitution + calculation + answer with units.
"Determine" = calculate AND state a conclusion.
"Show that" = derive the given value — show every step clearly.
"Justify" = support your answer with evidence or reasoning.
"Using your knowledge of physics, comment on..." = open-ended 3-mark question. Marked holistically on quality of physics understanding — no single correct answer.

MARKING RULES:
1 mark per correct step in calculations. Follow-through marking: correct method with wrong previous answer still earns method mark.
Formula → substitution → calculation → answer with units and sig figs = typical 3-4 mark calculation.
Explanation questions: each cause-effect link earns 1 mark.

DATA SHEET KEY VALUES (always available in exam):
c = 3.00 × 10⁸ m/s; e = 1.60 × 10⁻¹⁹ C; g = 9.8 m/s²; G = 6.67 × 10⁻¹¹ m³ kg⁻¹ s⁻²; h = 6.63 × 10⁻³⁴ J s; H₀ = 2.3 × 10⁻¹⁸ s⁻¹; mₑ = 9.11 × 10⁻³¹ kg; mₙ = 1.675 × 10⁻²⁷ kg; mₚ = 1.673 × 10⁻²⁷ kg.
Refractive indices: diamond 2.42, crown glass 1.50, water 1.33, air 1.00.
Radiation weighting factors: alpha 20, beta 1, fast neutrons 10, gamma 1, slow neutrons 3, X-rays 1.

${n5Content}${higherContent}${ahContent}
${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Show all formula working clearly. Use correct units throughout. Be specific about exactly which step was wrong in calculations.`;
  }

  // ── Computing Science ──────────────────────────────────────────────────────
  if (subject === "Computing Science") {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";

    const modeInstructions = {
      chat: `The student wants to understand a Computing Science topic. Explain clearly using the SQA ${level} Computing Science curriculum. Show code examples in SQA pseudocode or a named language. Be concise and accurate.`,
      paper: `The student will paste a question and their answer/code. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point. For code questions: check syntax, logic, and use of SQA constructs. Format: "✓ [point] — [brief explanation]". Show total marks.

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. For code: identify the exact line with an error. For SQL: specify which clause is wrong. End with: WEAK TOPIC: [specific topic, 2–5 words]

---WORKED EXAMPLE---
Complete model answer. For code: correctly working pseudocode or program. For SQL: correct query. For design questions: correct diagram or description.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Computing Science question (from Section 1 SDD, Section 2 DDD, or Section 3 WDD). State the mark allocation. After they answer, give SQA marking feedback with a model answer. Then offer another question from a different area.`,
      mock: isAH
        ? `You are generating a complete SQA-style Advanced Higher Computing Science mock paper (55 marks, 2 hours).

CRITICAL STYLE RULES — READ THESE BEFORE WRITING A SINGLE QUESTION:
1. EVERY multi-part question must be set inside a real-world scenario (e.g. a cafe ordering system, a competition website, an earthquake tracking app). The scenario is introduced once at the top of the question and all sub-parts refer back to it.
2. Begin Section 1 with 2–3 short standalone questions (1–2 marks each, no context needed — e.g. "State the type of linked list and justify your answer", "State the maximum number of comparisons for a binary search on 100 items").
3. Then move to 2–3 large context-based questions with multiple sub-parts labelled (a), (b), (c)... Each sub-part must show its mark allocation in brackets e.g. "(2 marks)".
4. ALWAYS provide actual pseudocode or code WITH LINE NUMBERS for students to analyse, complete, or debug. Do not ask them to write everything from scratch. Pattern: give partial code then ask them to complete it, OR give broken code then ask them to identify the error and correct it.
5. Include at least one trace table question where the student fills in missing values in highlighted cells.
6. Use SQA command words correctly: STATE (1 mark, no explanation), DESCRIBE (state + detail), EXPLAIN (cause + effect, usually 2 marks), JUSTIFY (back up with reasoning).
7. Include at least one question of the form "explain why this code does not produce the correct output" — give the code, state the wrong output produced, ask why it is wrong and how to fix it.
8. For OOP questions: give a UML class diagram AND partial class code with line numbers, then ask about it (e.g. "explain why line 5 cannot be executed", "describe one way an item could be added successfully", "explain how a parent array can store sub-class objects").
9. Section 1 SDD = 35 marks. Section 2 DDD = 20 marks. Section 3 WDD = 20 marks.

SECTION 1 — SDD (35 marks, all compulsory):
- Q1 short standalone: given a game board description, state the linked list type and justify (2 marks)
- Q2 short standalone: given a sorted array of 100 items, state max comparisons for binary search; give a pseudocode binary search with 3 deliberate errors, identify the 3 lines with errors and describe corrections (1+3 = 4 marks)
- Q3 large OOP scenario (e.g. a cafe ordering system): provide a UML class diagram showing Order as parent class, SitIn/Delivery/Collection as sub-classes, OrderItem as separate class with array of objects; provide partial class/constructor code with line numbers for Order and SitIn classes; ask: (a) explain why Order does not need a constructor (1), (b) explain using OOP terminology why calcOrderTotal() appears in both Order and Delivery (2), (c) given test code that creates an array of Order and assigns sub-class objects to it, explain how this is possible (2), (d) given a line accessing a private attribute directly, explain why it cannot execute (1), (e) given a method call on the wrong class type, explain why it fails and describe one way to make it work (2+1), (f) design a bubbleSort procedure in pseudocode that sorts the orderList by orderTotal and displays the orderNumber and orderTotal of the three most expensive orders (4) — total 13 marks
- Q4 2D array scenario (e.g. earthquake or weather data): give a sample data table; ask to declare a 2D array; write insertion sort on a column in descending order; describe the testing evidence needed to confirm the sort is correct (1+4+2 = 7 marks)
- Q5 algorithm design: given a scenario requiring nested loops (e.g. finding local clusters), design the algorithm in pseudocode (4 marks)
- Q6 maintenance and feasibility: state the type of maintenance required when rewriting procedural code as OOP and justify; explain why both economic and technical feasibility must be considered (2+2 = 4 marks)
- Q7 integrative testing: describe integrative testing; give 3 test cases in a table (ID/Objective/Description/Expected Result) for a scenario that combines SDD and DDD or SDD and WDD (1+2 = 3 marks)

SECTION 2 — DDD (20 marks — generate this AND Section 3, student chooses one):
- Q1: Describe two ways user survey results can be used at the analysis stage (2 marks)
- Q2: ER diagram — give 5 entity structures with attributes and foreign keys; draw the ER diagram showing strong/weak entities and mandatory/optional participation using crow's foot notation (4 marks)
- Q3: State one reason the Allocation entity benefits from a surrogate key (1 mark); complete a SQL CREATE TABLE statement from a given data dictionary including PRIMARY KEY, FOREIGN KEY, and AUTO_INCREMENT (2 marks)
- Q4: SQL with HAVING and subquery — give scenario with sample table data; write a SELECT query with GROUP BY, HAVING, and an aggregate function; write a separate subquery-based SELECT; describe 2 errors in a given broken SQL statement (2+3+2 = 7 marks)
- Q5: Database-driven website — write HTML form with action attribute, method=POST, and at least 2 input types with name and value attributes (2 marks); explain the need for integrative testing in a database-driven website (2 marks); describe how end-user testing is carried out (1 mark) — total 5 marks

SECTION 3 — WDD (20 marks — generate this AND Section 2, student chooses one):
- Q1: Describe two ways user survey results can be used at the analysis stage (2 marks)
- Q2: Media queries — give existing CSS for a tablet layout; describe how the CSS could be adapted to produce a print layout; show the @media rule syntax and describe what changes (2 marks)
- Q3: PHP sessions — explain how session_start() and $_SESSION variables are used to persist data across pages; given a wireframe showing confirmation page details from a sign-up form, explain how sessions and session variables would be used in this situation (3 marks)
- Q4: PHP/database integration — write PHP using mysqli_connect() to connect to a database given server/user/password/database name (2 marks); given partial PHP code with a while loop using mysqli_fetch_array(), write the echo statement to display one row of results in an HTML table row with correct td elements (2 marks); write the SQL UPDATE string inside mysqli_query() to update a specific record (1 mark) — total 5 marks
- Q5: Explain the need for integrative testing when developing a database-driven website (2 marks); describe how end-user testing would be carried out on a database-driven website (1 mark)
- Q6: CSS — given an existing CSS media query for screen, describe changes needed; complete a CSS grouping selector to apply font-family (2 marks)
- Q7: HTML/PHP — explain the significance of ORDER BY in a given SQL query at a specific line number; explain the complex condition in a while loop; design in pseudocode the points allocation logic for a competition (1+2+2 = 5 marks)

After outputting the full paper, tell the student to attempt Section 1 and EITHER Section 2 or Section 3.`

        : isN5
        ? `You are generating a complete SQA-style National 5 Computing Science mock paper (80 marks, 1 hour 30 minutes).

CRITICAL STYLE RULES — READ THESE BEFORE WRITING A SINGLE QUESTION:
1. Begin Section 1 with several short standalone questions (1–2 marks each, no scenario). These test: binary conversion, ASCII bits calculation, floating point identification, iterative development, concatenation, data types, random numbers, design technique names.
2. Then move to larger context-based questions. Introduce the scenario ONCE (e.g. "A leaderboard shows teams and scores", "A gym wants to track members"), then write 4–7 sub-parts around that scenario. Every sub-part references the same scenario.
3. ALWAYS include actual SQA pseudocode WITH LINE NUMBERS for students to work with. Pattern: provide partial code and ask them to complete it, OR provide buggy code and ask them to identify the error and fix it. Never ask students to write an entire program from scratch.
4. Include at least one trace table for the student to fill in.
5. Include at least one question asking the student to identify a logical error in given code, state the type of error, and describe the correction.
6. Use SQA command words: STATE (1 mark), DESCRIBE (2 marks), EXPLAIN (2 marks with cause + effect). Never use "discuss" or "analyse" at N5.
7. Mark allocations must appear in brackets after every sub-part e.g. "(2 marks)".
8. Must include a user interface design question asking the student to sketch/draw a wireframe for a specific described interface.
9. Include a question about processor components (ALU performs calculations, registers store values temporarily) and one about environmental impact (standby mode).
10. DDD and WDD must each use sample data tables so questions are concrete and answerable.

SECTION 1 — SDD + Computer Systems (55 marks, ALL compulsory):

Short standalone questions (12 marks total):
- Convert a given denary number to 8-bit binary (1 mark)
- Calculate the number of bits to store a given team name using extended ASCII (1 mark)
- Describe a situation where an iterative approach is needed during software development (1 mark)
- Given a line of code like SET abc TO xyz ^ 2, state the value stored when a given number is entered (1 mark); state one other way to improve code readability besides commentary and whitespace (1 mark)
- Identify mantissa and exponent in a given floating point number (2 marks)
- Identify the graphic representation for a photographic image stored as an array of pixels (1 mark)
- Describe how a concatenation line creates a value (1 mark)
- Complete a table showing suitable data types for two variables with sample data (2 marks)
- Write code using RANDOM to allocate a seat number from a given range (2 marks)

Scenario 1 — design technique and processor (8 marks):
Set in a supermarket self-service till scenario. Show a flowchart. Ask: state the design technique shown (1); state the loop type (1); state the most suitable data type for a boolean flag (1); identify the vector object and one attribute from a company logo (2); write selection code to ask for a purpose and apply conditional discount (4); state the processor component that performs the calculation (1); state the component that temporarily stores values (1); describe why standby mode is used (1) — total: 12 marks

Scenario 2 — input validation and testing (9 marks):
Set in a gift card / gaming account scenario. Show input/output table. Ask: identify 3 processes (3); write input validation using WHILE loop with length() check for a 5-character password (4); complete a test table with normal/extreme/exceptional test data — give one row filled in, student completes two more (2) — total: 9 marks

Scenario 3 — arrays, loops, and debugging (16 marks):
Set in a gym calories or archery scoring scenario. Provide pseudocode WITH LINE NUMBERS showing inefficient code (no loops, variables repeated). Ask: refine a step using pseudocode showing running total in a loop with array (4); write a line using round() to display average to 1 decimal place (2); identify the logical operator in given code (1); state the error type and describe the correction from given buggy conditional (2); state the type of translator used when a program runs without further translation (1); state the term for a program handling unexpected data without crashing (1); state what to do to prevent an intercepted email being read (1); design a user interface (wireframe) for a tablet survey with specific inputs: member/PAYG radio buttons, gym/class radio buttons, rating 1–10, comments text area (4) — total: 16 marks

Scenario 4 — second code quality question (8 marks):
Provide buggy pseudocode for storing competitor names in an array. Show the code with a logic error in the loop condition. Ask: explain why the code will not function as expected (1); explain how to make inefficient code efficient using loops and arrays (3); explain why output contains unexpected decimal values due to wrong data type (1) — total: 5 marks
Then a short supplementary: explain why a variable in one procedure unexpectedly changes — uses BYREF/global variable explanation (1–2 marks)

SECTION 2 — DDD (25 marks — generate this AND Section 3, student chooses one):
- Complete an ORDER BY clause to produce a given sorted output from sample table data (2 marks)
- Complete missing attribute name and attribute type in a data dictionary from sample data (2 marks)
- State one attribute where restricted choice validation reduces errors (1 mark)
- ER diagram: identify key attributes (underline primary key, mark foreign key), draw the relationship line with crow's foot notation, name the relationship (3 marks)
- Complete a SQL SELECT statement using equi-join across two tables with AND in WHERE clause (4 marks)
- State one GDPR requirement the company should have implemented (1 mark)
- Design a query using the query design table format (Field/Table/Search Criteria) (4 marks)
- Describe how to test the SQL query is correct (1 mark)
- Write the expected output from a given SQL statement with AND/OR/comparison operators and given sample data (2 marks)
- Write a SQL UPDATE statement to change specific fields for a specific record (4 marks)
- Explain why an INSERT INTO statement produces an error (referential integrity — foreign key doesn't exist) (1 mark)

SECTION 3 — WDD (25 marks — generate this AND Section 2, student chooses one):
- Identify the JavaScript event used to implement a described interaction (1 mark)
- Explain the difference between absolute and relative addressing (2 marks)
- State two functional requirements from given user feedback quotes (2 marks)
- Given a folder structure diagram, complete HTML list code with correct relative hyperlinks (3 marks)
- Complete HTML audio element code from a given folder structure (3 marks)
- State one reason JPEG is used instead of BMP on a website (1 mark)
- Draw the navigational structure for a described website with internal and external links (3 marks)
- Write CSS using an id selector to set font-size and colour of a heading (3 marks)
- Describe how an MP3 file size can be reduced without changing its length (1 mark)
- Describe one other test to carry out on the website (1 mark)
- State one reason a given web page is not fit for purpose from a screenshot (1 mark)
- State the law the company must comply with to use supplier images (1 mark)
- Given HTML and CSS with class, id, and tag selectors, describe how the heading element will appear in a browser (3 marks)

After outputting the full paper, tell the student to attempt Section 1 and EITHER Section 2 or Section 3.`

        : `You are generating a complete SQA-style Higher Computing Science mock paper (80 marks, 2 hours).

CRITICAL STYLE RULES — READ THESE BEFORE WRITING A SINGLE QUESTION:
1. Begin Section 1 with short standalone questions (1–2 marks each, no scenario). These must include: two's complement range calculation, digital signatures, agile vs iterative, fetch-execute cycle / control bus, vector vs bitmap comparison.
2. Then move to large context-based questions. Introduce the scenario ONCE (e.g. "An online game measures reaction times", "A program tracks storm data across 5 years"), then write all sub-parts around it. Every sub-part references that same scenario.
3. ALWAYS include actual SQA pseudocode WITH LINE NUMBERS. Ask students to: complete a data flow table, write code in a named language referencing the defined record structure, complete a trace table with highlighted empty cells, explain why a line produces wrong output. Never ask them to write an entire program from scratch.
4. Include at least one trace table — provide the algorithm and a partial table, student fills in the highlighted cells.
5. Include a function scope/debugging question: given a function with line numbers, ask (a) scope definition, (b) benefit of limiting scope, (c) describe what a specific line does, (d) complete the trace table, (e) describe how a debugging technique verifies the trace, (f) explain why output is wrong + describe fix, (g) evaluate robustness.
6. Use SQA command words: STATE (1 mark), DESCRIBE (state + detail, 2 marks), EXPLAIN (cause + effect, 2 marks), JUSTIFY (evidence-based reasoning). "Evaluate the robustness" means say whether it is robust and explain why/why not with a specific example.
7. Mark allocations in brackets after every sub-part.
8. Must include a floating point binary representation question: give a binary number in the format sign bit + mantissa + exponent, ask student to: identify the three parts, state whether the mantissa is normalised and justify, then calculate the decimal value showing working.
9. Section 1 SDD + CS = 55 marks. Section 2 DDD = 25 marks. Section 3 WDD = 25 marks.

SECTION 1 — SDD + Computer Systems (55 marks, ALL compulsory):

Short standalone questions (approx 10 marks):
- Given that the lowest representable number in two's complement is −512, state the largest representable number and state the number of bits used (2 marks)
- State two benefits of a digital signature (2 marks)
- Given a client that modifies requirements during development, explain why agile is more suitable than iterative (1 mark)
- State how the processor uses the control bus during the fetch-execute cycle (1 mark)
- State one way a manufacturer could improve processor performance and explain why it improves performance (2 marks)
- Given two scaled versions of the same image (one pixelated, one sharp), identify which is vector and justify; state one advantage of bitmap over vector (2 marks)

Large context-based questions (approx 45 marks):

Q on parallel 1D arrays — storm tracking scenario (11 marks):
Store storm name, region, month, year in a CSV file. Program reads into parallel arrays then: counts autumn storms in a given year; counts storms per year; displays year with most. Ask: complete a data flow table with IN/OUT for each step, two steps are given and two are blank (3); design the algorithm for the "count autumn storms" step using pseudocode (4); write code in a named language to implement a linear search to check if a storm name entered by the user already exists in the array and display a suitable message (4)

Q on arrays of records — reaction time game scenario (16 marks):
Each play stores name, email, attemptID, reaction time. Max 10,000 per day. Ask: write code to define a suitable record structure (2); write code to declare an array of 10,000 of that record (2); write code using the record array to find the fastest reaction time, using numPlays not 10,000 as the loop boundary (5); explain why numPlays is needed instead of traversing the full 10,000-element array (1); convert a given binary fraction (e.g. 0.001) to floating point representation using 16-bit mantissa including sign bit and 8-bit exponent in two's complement — show working (3); given that the attemptID is created from the part of the email before '@' concatenated with numPlays, write code for: (i) calling findCharIndex() to get the position of '@' (2), (ii) creating the attemptID using a substring function concatenated with numPlays (2) — wait the code for findCharIndex() is already provided with line numbers

Q on functions, scope, and trace tables — array manipulation scenario (18 marks):
Provide a complete function called removal() WITH LINE NUMBERS that removes a target value from an array and returns a new array. Also show the main program lines that call it. Ask: (a) explain what is meant by scope of a variable (1); (b) describe one benefit of limiting scope in modular programming (1); (c) describe the operation of a specific line that calls the function with actual parameters (2); (d) given a test case where 99 is entered (not in the array), complete a trace table — provide a partial table with 3–4 highlighted empty cells the student must fill in (3); (e) describe how a debugging technique could be used to check trace table values (2); (f) explain why the code does not produce the correct output for a given case, and describe the additional code needed to fix it (2+2); (g) evaluate the robustness of the function and explain your answer (1) — total: 14 marks

SECTION 2 — DDD (25 marks — generate this AND Section 3, student chooses one):
- SQL GROUP BY: give a Programme table with sample data (title, year, genre, duration); write the expected output of a given SELECT with MIN, GROUP BY year and genre, WHERE year > 2021 (2 marks)
- ER diagram: given 4 table structures (Customer, Order, Item, OrderItem) with foreign keys shown, draw the ER diagram showing entity names and cardinality only — no attributes needed (3 marks)
- SQL UPDATE: given a scenario where a cost increases by £20 for records matching two conditions (status and date), write the SQL UPDATE statement (3 marks)
- Query design: design a query using Field(s)/Calculation(s), Table(s), Search Criteria, Grouping, Sort Order format — scenario: display photographer full name and number of appointments in a given month (3 marks)
- SQL error identification: give a broken SELECT statement missing a table in FROM, wrong GROUP BY field, wrong column in ORDER BY — describe the 3 errors (3 marks)
- SQL with 3+ tables and aggregate: write a SELECT to display total value of all parts in stock (SUM of price × quantity) with a readable column heading (2 marks)
- SQL average + subquery: write SQL to display the average appointment cost; then given this query is saved as avgCost, complete a SELECT from 3 tables with WHERE cost > avgCost, ordered correctly (2+4 = 6 marks)
- Cardinality: add a new entity PartsListing between two existing entities showing the cardinality on both sides (1 mark)

SECTION 3 — WDD (25 marks — generate this AND Section 2, student chooses one):
- Usability testing personas: describe what personas are (1 mark)
- HTML form issues: given a simple HTML form code, state two usability issues (2 marks)
- CSS nav bar fix: given a CSS nav bar where hover applies to the whole nav instead of individual links, describe the change needed to make individual links change colour on hover (1 mark)
- CSS padding: given a div with an image, complete the first line of a CSS rule to add equal padding on all four sides (1 mark)
- Wireframe: draw a wireframe for a form with these specific inputs: email (text), departure date (date), number of nights (number, 1–14), holiday type (radio: self-catering/B&B/all-inclusive), number of people (number, minimum 1) (4 marks)
- CSS descendant selector: state the type of selector used in section p { }; describe which elements it applies to (2 marks)
- JavaScript toggle: complete a JavaScript function to toggle display between "block" and "none" for a given element ID; write the onclick attribute for an h3 element to call this function passing the correct div ID (1+2 = 3 marks)
- Navigational structure: draw the navigational structure for a car buying website with home + 3 level-1 pages + 3 level-2 pages from the search page (2 marks)
- HTML form validation: given an input for price (200–75000), rewrite the HTML input line adding min, max, and required attributes (2 marks)
- CSS float + clear: given that two section elements float left and the div paragraph appears to the right instead of below, complete the CSS rule for div using clear:both (1 mark); complete a grouping selector to apply font-family Arial to both section elements (1 mark)
- Layout not fit for purpose: explain why a layout with two floated 600px images in a 1210px div is not fit for purpose even at full screen (2 marks); explain why it may still fail on a smartphone after fixing the desktop issue (1 mark)
- Block vs inline: explain why paragraph elements appear on separate lines but an anchor element appears inline on the same line as a paragraph (2 marks)

After outputting the full paper, tell the student to attempt Section 1 and EITHER Section 2 or Section 3.`
    };

    return `You are Summit, an expert SQA tutor specialising in ${level} Computing Science.
You know the SQA curriculum, pseudocode syntax, marking schemes, and examiner expectations precisely.

SQA PSEUDOCODE SYNTAX (use this exactly):
SET variable TO value
DECLARE variable AS type INITIALLY value
FOR index FROM 0 TO n-1 DO ... END FOR
WHILE condition DO ... END WHILE
IF condition THEN ... ELSE ... END IF
FUNCTION name(params) RETURNS type ... RETURN value END FUNCTION
PROCEDURE name(params) ... END PROCEDURE
SEND value TO DISPLAY
RECEIVE variable FROM (type) KEYBOARD
BYVAL (pass copy — changes don't affect original) / BYREF (pass reference — changes affect original)
Arrays: name[index] | 2D: name[row][col]
${isHigher || isAH ? "Records: RECORD name { field1 AS type, field2 AS type }" : ""}
${isAH ? "OOP: CLASS name { PRIVATE attr } METHODS PUBLIC PROCEDURE/FUNCTION ... END CLASS\nCLASS Sub INHERITS Parent WITH { PRIVATE extra }" : ""}

PAPER STRUCTURE (${level}):
${isN5 ? "Section 1 — SDD + Computer Systems (55 marks, ALL compulsory). EITHER Section 2 — DDD (25 marks) OR Section 3 — WDD (25 marks). Total 80 marks, 1h 30min." : ""}
${isHigher ? "Section 1 — SDD + Computer Systems (55 marks, ALL compulsory). EITHER Section 2 — DDD (25 marks) OR Section 3 — WDD (25 marks). Total 80 marks, 2 hours." : ""}
${isAH ? "Section 1 — SDD (35 marks, ALL compulsory). EITHER Section 2 — DDD (20 marks) OR Section 3 — WDD (20 marks). Total 55 marks, 2 hours." : ""}

KEY MARKING RULES:
- 1 mark = 1 correct step, point, or line of code.
- Code must use correct SQA pseudocode syntax OR a consistent named language.
- Trace tables: award mark for each correctly completed cell.
- SQL: award mark per correctly formed clause.
- "Describe why not fit for purpose" = identify the specific logical error AND explain its effect.
- "State" = 1 mark, no explanation needed. "Describe" = state + relevant detail. "Explain" = cause + effect.

${isN5 ? `N5 SPECIFIC CONTENT:
SDD: data types (INTEGER, REAL, STRING, BOOLEAN, CHAR), 1D arrays, loops (FOR count-controlled, WHILE condition-controlled), input validation, string operations (length, substring, concatenation), random numbers (RANDOM(n)), standard algorithms (linear search, find min/max, count occurrences), testing (normal/extreme/exceptional), sequence/selection/iteration.
Computer Systems: binary (convert denary ↔ binary, 8-bit), extended ASCII (8-bit = 256 characters, count bits for text), floating point (mantissa × 10^exponent), vector vs bitmap graphics, fetch-execute cycle, data/address/control buses.
DDD: SQL SELECT with WHERE (AND/OR), ORDER BY, simple UPDATE; ER diagrams (identify primary keys, draw one-to-many or many-to-many, name relationship); restricted choice validation, attribute types.
WDD: HTML (hyperlinks, images, audio/video, ordered/unordered lists), CSS (id vs class selectors, font/colour/background), JavaScript event identification (onmouseover, onclick, onchange), functional requirements, absolute vs relative addressing, navigational structure.` : ""}

${isHigher ? `HIGHER SPECIFIC CONTENT:
SDD: records (RECORD syntax, access with dot notation), arrays of records, parallel arrays, find-minimum algorithm, parameter passing BYVAL vs BYREF, local vs global scope, modular programming benefits, agile vs iterative methodology, normalised floating point (mantissa between 0.5 and 1, sign bit + mantissa + exponent), trace tables, debugging (breakpoints, watchpoints).
Computer Systems: two's complement (most significant bit negative weight), floating point representation, control bus, fetch-execute cycle, processor performance (clock speed, cache, cores), compression (lossy vs lossless).
DDD: SQL with GROUP BY + aggregate functions (MIN/MAX/COUNT/SUM/AVG), multi-table SELECT with implicit JOIN, UPDATE, ER diagrams with cardinality, query design table format (Fields/Tables/Search criteria/Grouping/Sort), describe 3 errors in SQL.
WDD: CSS descendant selectors (section p → elements of type p inside section), CSS hover pseudo-class, onclick event handler, toggleDisplay function in JavaScript, usability testing (personas, test cases, scenarios), HTML input validation (min/max/required/type), block vs inline elements.` : ""}

${isAH ? `ADVANCED HIGHER SPECIFIC CONTENT:
SDD: OOP — class definition (private attributes, public methods, constructor, THIS keyword); inheritance (INHERITS keyword); polymorphism (subclass overrides parent method — calcOrderTotal in Delivery vs Order); encapsulation (private attributes accessed via public methods only); linked lists — singly (pointer to next), doubly (pointers both ways), circular (last points to first) — justify type from context; bubble sort algorithm (nested loops, compare adjacent, swap with temp); insertion sort; binary search (max comparisons = ceil(log₂n)); 2D array processing (nested loops); integrative testing (testing combined modules/database/web together).
DDD: subqueries (WHERE x > (SELECT MIN... FROM ...)); HAVING clause with GROUP BY; NOT EXISTS operator; ANY operator; CREATE TABLE with PRIMARY KEY and FOREIGN KEY constraints; DROP TABLE; surrogate keys (artificial PK — avoids natural key issues); strong vs weak entities; mandatory vs optional participation.
WDD: PHP sessions (session_start(), $_SESSION['key'], persist data across pages); mysqli_connect(server, user, pass, db); $result = mysqli_query($link, $sql); while ($row = mysqli_fetch_array($result)); UPDATE query in PHP; media queries (@media screen (max-width: Xpx) { }); end-user testing (real users test against requirements); integrative testing (ensure PHP, HTML, database work together correctly).` : ""}

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Use correct SQA pseudocode syntax in all code examples. Be precise about which line/clause contains an error in feedback.`;
  }

  if (subject === "Drama") {
    const modeInstructions = {
      chat: `The student wants to understand a Drama topic. Explain clearly using SQA ${level} Drama curriculum and correct terminology. Be concise.`,
      paper: `The student will paste a question and/or their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point the SQA scheme would award. Format: "✓ [point] — [brief explanation]". Show total marks available.

---STUDENT SCORE---
X / Y marks. 2-3 sentences on what they got right and what cost them marks. Check: textual references included? Concepts linked to audience effect? Terminology correct? End with: WEAK TOPIC: [specific topic, 2-5 words]

---WORKED EXAMPLE---
A complete model answer that would achieve full marks.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style ${level} Drama question with mark allocation clearly stated. After they answer, give SQA-style marking feedback. Then offer another question.`,
      mock: `You are generating a complete SQA-style mock for ${level} Drama.`
    };
    return `You are Summit, an expert SQA tutor specialising in ${level} Drama.
You know the SQA curriculum, drama terminology, prescribed texts, and what examiners expect.

CRITICAL SQA KNOWLEDGE:
- Always reference the text in Section 1 answers (quotations or paraphrases).
- Link EVERY concept to its intended effect on the audience.
- Section 3 (Performance Analysis): analyse HOW choices created meaning, not just what you saw.
- 1 mark = 1 distinct point. Use correct terminology.

PERFORMANCE CONCEPTS (Acting):
Voice: pace, tone, volume, clarity, pitch, emphasis, pause, accent, projection, intonation, rhythm.
Movement: gesture, facial expression, posture, body language, eye contact, proxemics (distance between characters reveals relationship/status), use of space, use of levels, timing, stance, mannerisms.

DESIGN CONCEPTS:
Lighting: wash, spotlight, gobo, gel, crossfade, blackout, strobe, cue sheet, states.
Sound: diegetic (from story world), non-diegetic (added for audience), SFX, underscoring, fade in/out, cue sheet.
Costume: reflects status, period, personality, occupation; changes show character development.
Set: ground plan (bird's-eye view), elevation (front-view drawing), levels, sightlines, flats, cyclorama.
Props: personal (carried), pre-set (placed before show), hand (passed between characters), set dressing.
Make-up and hair: straight (enhance for lighting), character (transform), ageing, prosthetics.

DIRECTING CONCEPTS:
Blocking: positioning actors for each moment.
Stage proxemics: distance reveals power, relationship, emotion.
Use of levels: height suggests status (higher = more powerful).
Stage pictures: visual compositions that communicate meaning without words.
Rehearsal techniques: hot seating (actor answers questions in character), thought tracking (voices internal thoughts), improvisation, role reversal, voices in the head, writing in role, freeze frame/still image.

STAGING TYPES:
Proscenium arch: audience one side, traditional picture frame, fourth wall.
Thrust: stage extends into audience on three sides.
Theatre-in-the-round: audience surrounds stage on all sides.
Traverse: audience on two opposing sides, acting in corridor between them.
End-on: simplified proscenium without arch.
Promenade: audience moves with action through different spaces.

DRAMA CONVENTIONS:
Flashback/flash-forward, freeze frame/tableau, slow motion, monologue, soliloquy, aside, voice-over, narration, split stage, mime.

KEY TERMINOLOGY:
Status: power a character holds relative to others — shown through voice, movement, costume, stage position.
Subtext: unspoken meaning beneath dialogue.
Given circumstances: facts about character and world established in the text.
Motivation: reason behind a character's choices and actions.
Dramatic tension: uncertainty, conflict or anticipation engaging the audience.
Dramatic irony: audience knows something a character does not.

HIGHER PRESCRIBED TEXTS (Section 1, 2025-26):
A Taste of Honey (Delaney), Angels in America: Millennium Approaches (Kushner), Antigone (Sophocles), Black Watch (Burke), Dracula (Liz Lochhead), Lovers (Friel), Macbeth (Shakespeare), Men Should Weep (Lamont Stewart), The Birthday Party (Pinter), The Crucible (Miller), The Duchess of Malfi (Zinnie Harris), The House of Bernarda Alba (Lorca), The Importance of Being Earnest (Wilde), The Pillowman (McDonagh), Things I Know to be True (Bovell).

SECTION 1 ANSWERING TECHNIQUE (Higher):
1. Identify a specific moment with textual reference.
2. Explain how it demonstrates the focus (theme/emotion/context).
Then for each concept: name it, describe exactly how you would apply it, explain the effect on the audience.
Never just name a technique — always explain its precise audience effect.

PERFORMANCE ANALYSIS TECHNIQUE (Higher Section 3):
State what you observed. Analyse HOW the choice created meaning. Evaluate its effectiveness. Link to the question focus. Give equal depth to BOTH chosen production areas.
${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Use precise drama terminology in all feedback. Be specific about moments and effects.`;
  }

  // ── Physical Education ─────────────────────────────────────────────────────
  if (subject === "Physical Education") {
    const modeInstructions = {
      chat: `The student wants to understand a PE topic. Explain clearly using the SQA Higher Physical Education curriculum. Use correct PE terminology and link all concepts to performance development.`,
      paper: `The student will paste a question and their answer. Respond in this EXACT format:

---MARKING BREAKDOWN---
List each mark point the SQA scheme would award. Format: "✓ [point] — [brief explanation]". Show total marks available.
Note which section this question is from (Section 1 factual, Section 2 PDP reflection, or Section 3 scenario).

---STUDENT SCORE---
X / Y marks. 2–3 sentences on what they got right and what cost marks. Check: correct PE terminology used? Factor specified? Activity context referenced? End with: WEAK TOPIC: [specific area, 2–5 words]

---WORKED EXAMPLE---
A complete model answer achieving full marks using correct PE terminology and command word structure.

---TRY AGAIN---
Restate the original question exactly.`,
      drill: `Generate a focused SQA-style Higher Physical Education question. State which section it's from (S1/S2/S3), the command word, and mark allocation. After they answer, give SQA marking feedback with a model answer. Then offer another question from a different section or factor.`,
      mock: `You are generating a complete SQA-style Higher Physical Education mock (50 marks, 2h 30min).

SECTION 1 (32 marks, ALL compulsory): Generate questions on all four factors (mental, emotional, social, physical). Include: methods of collecting information (quantitative AND qualitative), reasons for combining both methods, how social factors impact mental/emotional/physical factors, an approach to develop social factors plus evaluation of its effectiveness, positive impact of emotional factors on the PDP process, a short-term and long-term goal for emotional factors, two reasons for resetting goals, two development principles for a mental factors PDP, two types of feedback for mental factors and what to consider when giving/receiving feedback.

SECTION 2 (8 marks, ALL compulsory): Personal PDP reflection questions — student describes strengths and development needs for two different factors at the start of their PDP, identifies two changes they made to their PDP, and evaluates the impact of those changes.

SECTION 3 (10 marks, ALL compulsory): Provide two tables of required performance demands (Table 1: emotional factors within an individual activity — 2 specific demands; Table 2: social factors within a team/group activity — 2 specific demands). Then generate: analyse why each set of demands is required in that context (2+2 marks), describe an approach to develop the emotional factors (4 marks), explain the importance of monitoring that approach (2 marks).

After generating, tell the student: "Attempt all questions, then paste your answers for marking."`
    };

    return `You are Summit, an expert SQA tutor specialising in Higher Physical Education.
You know the SQA curriculum, marking schemes, and examiner expectations precisely. Higher PE has ONE exam paper (50 marks, 2h 30min) with THREE sections, all compulsory.

PAPER STRUCTURE:
Section 1 (32 marks): Factual questions on all four factors (mental, emotional, social, physical). Tests knowledge of: data collection methods, factor interactions, development principles, feedback, goal-setting, PDP.
Section 2 (8 marks): Personal PDP reflection — must relate to the student's OWN PDP experience.
Section 3 (10 marks): Scenario-based — given a table of required performance demands, analyse why they're needed and describe/explain a development approach.

FOUR PERFORMANCE FACTORS:
Mental: concentration, confidence, mental toughness, decision-making, performance routines, mental rehearsal/visualisation, self-talk.
Emotional: managing anxiety/fear/anger/frustration, emotional resilience, composure, motivation.
Social: communication, cooperation, teamwork, fulfilling role/responsibility, leadership, trust-building, adapting to group dynamics.
Physical: fitness components (strength, power, speed, endurance, flexibility, agility, coordination, balance, reaction time), movement skills, tactical awareness, technique.

METHODS OF COLLECTING INFORMATION:
Quantitative (numerical): observation schedules, fitness tests (bleep test, sit-and-reach, reaction time test), standardised tests, video analysis (counting occurrences), movement analysis software.
Qualitative (descriptive): reflective diary, semi-structured interview, open questionnaire, peer observation with written feedback, teacher/coach verbal feedback.
Reasons to use BOTH: quantitative gives measurable, comparable data (objective); qualitative gives context and reasons 'why' (subjective depth). Combination = more valid and reliable picture of performance.

DEVELOPMENT PRINCIPLES (for PDP design):
Progressive overload: gradually increase difficulty/intensity/volume so body/mind adapts.
Specificity: training must relate directly to the factor/activity being developed.
Frequency: how often training occurs — must be regular enough to cause improvement.
Intensity: how hard the training is — must challenge current ability.
Duration: length of each session — must be sufficient to cause adaptation.
Reversibility: fitness/skills decline if training stops — maintain regularity.
Variance: vary the training to maintain motivation and prevent staleness.

FEEDBACK TYPES:
Intrinsic: from within the performer. Kinaesthetic = feeling of movement. Proprioceptive = body awareness/position sense.
Extrinsic: from outside the performer. Terminal = given after the performance. Concurrent = given during. Positive = reinforces what was done well. Corrective = identifies errors. Prescriptive = tells performer exactly what to do differently.

GOAL SETTING (SMART):
Short-term goal = achievable within current training cycle (e.g. within 4 weeks).
Long-term goal = overarching performance target (e.g. by end of season/year).
Reasons for resetting goals: goal achieved (progress made, set new challenge); goal too hard (demotivating, revise to be realistic); injury or illness (goals become unachievable temporarily); external factors (competition cancelled, access to facilities changed).

SQA COMMAND WORDS:
"Describe" = state the method/approach with relevant detail. No explanation of why needed.
"Explain" = cause + effect using because/therefore. WHY it matters.
"Analyse" = identify the components and their relationships. Break down what each demand means for that context.
"Evaluate" = assess effectiveness — what worked, what didn't, overall judgement.
"Identify" = name or state (1 mark, no development needed).

${weakSubjects.length > 0 ? "\nThe student has flagged low confidence in: " + weakSubjects.join(", ") + ". Be especially thorough when these arise." : ""}
${modeInstructions[mode]}
Use precise PE terminology throughout. Always link concepts to the specific factor, context (individual/team), and performance development process.`;
  }

  const modeInstructions = {
    chat: `The student wants to understand a topic. Explain clearly using SQA ${level} ${subject} curriculum and terminology. Never over-explain beyond what the SQA course requires. Keep responses concise.`,
    paper: `The student will paste a question and/or their answer. You must respond in this EXACT structured format using these exact section headers:

---MARKING BREAKDOWN---
List each mark point the SQA scheme would award. Format: "✓ [point] — [brief explanation of why this earns a mark]". Show the total available marks clearly.

---STUDENT SCORE---
X / Y marks. Then 2-3 sentences on what they got right and what cost them marks. Be direct and specific. Mention command word compliance if the subject uses command words. End with: WEAK TOPIC: [specific topic name, 2-5 words max, e.g. "Cash flow management" or "Covalent bonding"]

---WORKED EXAMPLE---
Write a complete model answer that would achieve full marks. This should be the exact structure and depth an A-grade student would write. Use the correct SQA command word structure throughout.

---TRY AGAIN---
Restate the original question exactly as given, so the student can attempt it fresh.`,
    drill: `Generate a focused SQA-style ${level} ${subject} question with command word and mark allocation clearly stated. After they answer, give SQA-style marking feedback. Then offer another question targeting weak areas.`,
    mock: `You are generating a complete SQA-style mock exam paper for ${level} ${subject}. Generate a realistic full paper with:
- A mix of short (2-4 mark) and extended (6-8 mark) questions
- All SQA command words used correctly (describe, explain, analyse, evaluate, justify)
- Questions covering different topics across the course
- Total marks clearly stated for each question
- Realistic SQA question numbering and formatting
Format the paper clearly. After generating, tell the student: "Your paper is ready. Work through each question in your own time, then paste all your answers together and I'll mark the full paper."`
  };
  return `You are Summit, an expert SQA tutor specialising in ${level} ${subject}. You know the SQA curriculum, marking schemes, and examiner feedback inside out.

CRITICAL SQA KNOWLEDGE:
- "describe" = state facts, no analysis needed
- "explain" = cause-and-effect chain, use because/therefore/as a result
- "analyse" = break down components and examine relationships
- "evaluate" = weigh up and reach a conclusion
- "justify" = give reasons that support a decision
- Mark allocation: 1 mark = 1 distinct point. 4-mark question = 4 separate points.
- Common examiner complaints: listing facts without linking for explain questions, evaluations without a conclusion.
${weakSubjects.length > 0 ? `\nThe student has flagged low confidence in: ${weakSubjects.join(", ")}. Be especially thorough when these topics arise.` : ""}
${modeInstructions[mode]}
Be honest in feedback. Keep responses focused and punchy.`;
}

function parsePaperResponse(text) {
  const sections = { markingBreakdown: null, studentScore: null, workedExample: null, tryAgain: null, raw: text };
  const breakdownMatch = text.match(/---MARKING BREAKDOWN---([\s\S]*?)(?=---STUDENT SCORE---|$)/);
  const scoreMatch = text.match(/---STUDENT SCORE---([\s\S]*?)(?=---WORKED EXAMPLE---|$)/);
  const exampleMatch = text.match(/---WORKED EXAMPLE---([\s\S]*?)(?=---TRY AGAIN---|$)/);
  const tryAgainMatch = text.match(/---TRY AGAIN---([\s\S]*?)$/);
  if (breakdownMatch) sections.markingBreakdown = breakdownMatch[1].trim();
  if (scoreMatch) sections.studentScore = scoreMatch[1].trim();
  if (exampleMatch) sections.workedExample = exampleMatch[1].trim();
  if (tryAgainMatch) sections.tryAgain = tryAgainMatch[1].trim();
  return sections;
}

function extractWeakTopic(scoreText, subject) {
  if (!scoreText) return null;
  const scoreMatch = scoreText.match(/(\d+)\s*\/\s*(\d+)/);
  if (!scoreMatch) return null;
  const scored = parseInt(scoreMatch[1]);
  const total = parseInt(scoreMatch[2]);
  if (total === 0 || scored / total >= 0.75) return null;

  const topicMatch = scoreText.match(/WEAK TOPIC:\s*(.+?)(?:\n|$)/i);
  const topic = topicMatch ? topicMatch[1].trim() : `${subject} — review needed`;

  return { subject, topic, scored, total, date: new Date().toISOString().split("T")[0] };
}

// ── KaTeX math renderer ──────────────────────────────────────────────
// KaTeX is loaded via public/index.html <script> tags for synchronous availability

// Render a string that may contain $...$ inline math or $$...$$ display math
const renderMath = (text) => {
  if (!text) return text;

  // Clean fallback renderer when KaTeX is not available
  // Converts LaTeX notation to readable Unicode/text
  const latexToText = (math) => {
    return math
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]+)\}/g, '√($1)')
      .replace(/\\sqrt\s/g, '√')
      .replace(/\^\{([^}]+)\}/g, '^($1)')
      .replace(/\^(\w)/g, '^$1')
      .replace(/_\{([^}]+)\}/g, '_($1)')
      .replace(/_(\w)/g, '_$1')
      .replace(/\\int/g, '∫')
      .replace(/\\sum/g, '∑')
      .replace(/\\infty/g, '∞')
      .replace(/\\pi/g, 'π')
      .replace(/\\theta/g, 'θ')
      .replace(/\\alpha/g, 'α')
      .replace(/\\beta/g, 'β')
      .replace(/\\lambda/g, 'λ')
      .replace(/\\mu/g, 'μ')
      .replace(/\\sigma/g, 'σ')
      .replace(/\\sin/g, 'sin')
      .replace(/\\cos/g, 'cos')
      .replace(/\\tan/g, 'tan')
      .replace(/\\log/g, 'log')
      .replace(/\\ln/g, 'ln')
      .replace(/\\neq/g, '≠')
      .replace(/\\leq/g, '≤')
      .replace(/\\geq/g, '≥')
      .replace(/\\times/g, '×')
      .replace(/\\div/g, '÷')
      .replace(/\\pm/g, '±')
      .replace(/\\cdot/g, '·')
      .replace(/\\left/g, '')
      .replace(/\\right/g, '')
      .replace(/\\vec\{([^}]+)\}/g, '$1⃗')
      .replace(/\\overrightarrow\{([^}]+)\}/g, '$1→')
      .replace(/\\begin\{pmatrix\}([\s\S]*?)\\end\{pmatrix\}/g, '[$1]')
      .replace(/\\\\/g, ', ')
      .replace(/\{|\}/g, '')
      .replace(/\\/g, '')
      .trim();
  };

  const katexAvailable = typeof window !== "undefined" && window.katex;

  let result = "";
  let remaining = text;

  // Process $$...$$ display math first
  while (remaining.includes("$$")) {
    const start = remaining.indexOf("$$");
    const end = remaining.indexOf("$$", start + 2);
    if (end === -1) break;
    const math = remaining.slice(start + 2, end);
    result += remaining.slice(0, start);
    if (katexAvailable) {
      try {
        result += window.katex.renderToString(math, { displayMode: true, throwOnError: false });
      } catch(e) {
        result += '<span style="font-style:italic;font-family:serif;">' + latexToText(math) + '</span>';
      }
    } else {
      result += '<span style="font-style:italic;font-family:serif;display:block;text-align:center;margin:6px 0;">' + latexToText(math) + '</span>';
    }
    remaining = remaining.slice(end + 2);
  }
  result += remaining;
  remaining = result;
  result = "";

  // Process $...$ inline math
  let i = 0;
  while (i < remaining.length) {
    if (remaining[i] === "$" && remaining[i+1] !== "$") {
      const end = remaining.indexOf("$", i + 1);
      if (end === -1) { result += remaining[i]; i++; continue; }
      const math = remaining.slice(i + 1, end);
      if (math.length === 0 || math.includes("\n")) { result += remaining[i]; i++; continue; }
      if (katexAvailable) {
        try {
          result += window.katex.renderToString(math, { displayMode: false, throwOnError: false });
        } catch(e) {
          result += '<span style="font-style:italic;font-family:serif;">' + latexToText(math) + '</span>';
        }
      } else {
        result += '<span style="font-style:italic;font-family:serif;">' + latexToText(math) + '</span>';
      }
      i = end + 1;
    } else {
      result += remaining[i];
      i++;
    }
  }
  return result;
};

// ── Shared markdown renderer ──────────────────────────────────────────
const renderMessage = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const hasMath = line.includes("$");
    const fmt = (str) => {
      const withMarkdown = str.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
      return hasMath ? renderMath(withMarkdown) : withMarkdown;
    };
    if (line.startsWith("### ")) return <h4 key={i} style={{ margin: "8px 0 4px", fontSize: 13, fontWeight: 600, color: "#f0f4f2" }} dangerouslySetInnerHTML={{ __html: fmt(line.replace("### ", "")) }} />;
    if (line.startsWith("## ")) return <h3 key={i} style={{ margin: "10px 0 4px", fontSize: 14, fontWeight: 600, color: "#f0f4f2" }} dangerouslySetInnerHTML={{ __html: fmt(line.replace("## ", "")) }} />;
    if (line.startsWith("# ")) return <h2 key={i} style={{ margin: "12px 0 6px", fontSize: 15, fontWeight: 600, color: "#f0f4f2" }} dangerouslySetInnerHTML={{ __html: fmt(line.replace("# ", "")) }} />;
    if (line.startsWith("- ") || line.startsWith("* ")) return <div key={i} style={{ margin: "2px 0", paddingLeft: 12, color: "rgba(240,244,242,0.85)", fontSize: 13 }} dangerouslySetInnerHTML={{ __html: "• " + fmt(line.slice(2)) }} />;
    return <p key={i} style={{ margin: "0 0 6px 0" }} dangerouslySetInnerHTML={{ __html: fmt(line) || "&nbsp;" }} />;
  });
};

// ── SQA Paper renderer (white, Trebuchet MS) ──────────────────────────
const renderSQAPaper = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    if (!line.trim()) return <div key={i} style={{ height: 10 }} />;
    const clean = line.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").replace(/^#{1,3}\s*/, "");
    const isHeader = /^(Section|SECTION|Part|PART|[A-Z\s]{4,}:?)$/.test(clean.trim());
    const isQNum = /^\d+\./.test(clean.trim());
    const isAnswerSpace = /^\[Space for answer\]$/i.test(clean.trim());
    const isWorkingSpace = /^\[Space for working\]$/i.test(clean.trim());
    const isLargeWorkingSpace = /^\[Large working space\]$/i.test(clean.trim());
    const isFormulaNote = /^\[Formula:/i.test(clean.trim());
    const hasMath = clean.includes("$");

    if (isAnswerSpace) return (
      <div key={i} style={{ height: 60, border: "1px solid #ccc", borderRadius: 2, margin: "8px 0 16px", background: "#fafafa" }} />
    );
    if (isWorkingSpace) return (
      <div key={i} style={{ minHeight: 100, border: "1px solid #ccc", borderRadius: 2, margin: "8px 0 16px", background: "#fafafa", padding: 8, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        <div style={{ fontSize: 10, color: "#aaa", fontFamily: "'Trebuchet MS', sans-serif" }}>Working space</div>
      </div>
    );
    if (isLargeWorkingSpace) return (
      <div key={i} style={{ minHeight: 160, border: "1px solid #ccc", borderRadius: 2, margin: "8px 0 16px", background: "#fafafa", padding: 8, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        <div style={{ fontSize: 10, color: "#aaa", fontFamily: "'Trebuchet MS', sans-serif" }}>Working space</div>
      </div>
    );
    if (isFormulaNote) return (
      <div key={i} className="formula-note">{clean.replace(/^\[Formula: /, "").replace(/\]$/, "")}</div>
    );

    return (
      <p key={i} style={{
        margin: "0 0 8px 0",
        fontFamily: "'Trebuchet MS', sans-serif",
        fontSize: 14,
        color: "#111",
        fontWeight: isHeader ? 700 : isQNum ? 600 : 400,
        lineHeight: 1.6,
        borderBottom: isHeader ? "1px solid #ccc" : "none",
        paddingBottom: isHeader ? 4 : 0,
        marginTop: isHeader ? 12 : 0
      }}
      >{hasMath ? <span dangerouslySetInnerHTML={{ __html: renderMath(clean) }} /> : clean}</p>
    );
  });
};

// ── Components ────────────────────────────────────────────────────────

function PaperFeedback({ text, onTryAgain }) {
  const [showExample, setShowExample] = useState(false);
  const parsed = parsePaperResponse(text);
  const isStructured = parsed.markingBreakdown && parsed.studentScore;
  if (!isStructured) return <div style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(240,244,242,0.85)", fontWeight: 300 }}>{renderMessage(text)}</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ padding: "14px 16px", background: "rgba(29,158,117,0.06)", border: "1px solid rgba(29,158,117,0.18)", borderRadius: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#1D9E75", fontWeight: 500, marginBottom: 10 }}>Mark scheme</div>
        <div style={{ fontSize: 13, lineHeight: 1.75, color: "rgba(240,244,242,0.75)", fontWeight: 300 }}>{renderMessage(parsed.markingBreakdown)}</div>
      </div>
      <div style={{ padding: "14px 16px", background: "rgba(239,159,39,0.06)", border: "1px solid rgba(239,159,39,0.18)", borderRadius: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#EF9F27", fontWeight: 500, marginBottom: 10 }}>Your score</div>
        <div style={{ fontSize: 13, lineHeight: 1.75, color: "rgba(240,244,242,0.75)", fontWeight: 300 }}>{renderMessage(parsed.studentScore)}</div>
      </div>
      <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
        <button onClick={() => setShowExample(v => !v)} style={{ width: "100%", padding: "12px 16px", background: showExample ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", color: "rgba(240,244,242,0.6)", fontFamily: "'DM Sans', sans-serif", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 500 }}>
          <span>Model A-grade answer</span>
          <span style={{ fontSize: 16, transform: showExample ? "rotate(180deg)" : "none", transition: "transform 0.2s", color: "#1D9E75" }}>↓</span>
        </button>
        {showExample && parsed.workedExample && (
          <div style={{ padding: "14px 16px 16px", fontSize: 13, lineHeight: 1.8, color: "rgba(240,244,242,0.7)", fontWeight: 300, borderTop: "1px solid rgba(255,255,255,0.06)" }}>{renderMessage(parsed.workedExample)}</div>
        )}
      </div>
      {parsed.tryAgain && (
        <button onClick={() => onTryAgain(parsed.tryAgain)} style={{ padding: "10px 18px", background: "transparent", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 8, color: "#4DC9A0", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, alignSelf: "flex-start" }}
          onMouseOver={e => { e.currentTarget.style.background = "rgba(29,158,117,0.08)"; e.currentTarget.style.borderColor = "rgba(29,158,117,0.5)"; }}
          onMouseOut={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(29,158,117,0.3)"; }}>
          <span>↩</span> Try this question again
        </button>
      )}
    </div>
  );
}

// ── Paper types per subject ───────────────────────────────────────────
const SUBJECT_PAPER_TYPES = {
  "Biology": ["Paper 1 — Multiple Choice", "Paper 2 — Structured"],
  "Chemistry": ["Paper 1 — Multiple Choice", "Paper 2 — Extended Answers"],
  "Physics": ["Paper 1 — Multiple Choice", "Paper 2 — Structured"],
  "Human Biology": ["Paper 1 — Multiple Choice", "Paper 2 — Structured"],
  "Engineering Science": ["Paper 1 — Multiple Choice", "Paper 2 — Structured"],
  "Mathematics": ["Paper 1 — Non-calculator", "Paper 2 — Calculator"],
  "Applications of Mathematics": ["Question Paper"],
  "English": ["RUAE", "Critical Reading"],
  "Computing Science": ["Question Paper"],
  "Business Management": ["Question Paper"],
  "Economics": ["Question Paper"],
  "Modern Studies": ["Higher Paper 1", "Higher Paper 2", "National 5 Question Paper", "Advanced Higher"],
  "Geography": ["Higher Paper 1 — Physical and Human Environments", "Higher Paper 2 — Global Issues and Geographical Skills", "National 5 Question Paper"],
  "History": ["Higher Paper 1 — Scottish History", "Higher Paper 2 — World History", "National 5 Question Paper", "Advanced Higher"],
  "Religious, Moral and Philosophical Studies": ["N5 Question Paper", "Higher Paper 1 — World Religion & Morality", "Higher Paper 2 — Religious & Philosophical Questions", "Advanced Higher"],
  "Philosophy": ["Question Paper"],
  "Psychology": ["Question Paper"],
  "Sociology": ["Question Paper"],
  "Latin": ["Question Paper"],
  "French": ["Reading Paper", "Listening Paper"],
  "German": ["Reading Paper", "Listening Paper"],
  "Spanish": ["Reading Paper", "Listening Paper"],
  "Art and Design": ["Question Paper"],
  "Graphic Communication": ["Question Paper"],
  "Design and Manufacture": ["Question Paper"],
  "Home Economics": ["Question Paper"],
  "Drama": ["Question Paper"],
  "Music": ["Question Paper"],
  "Physical Education": ["Question Paper"]
};

// ── Predicted Paper Screen ────────────────────────────────────────────
function PredictedPaperScreen({ profile, session, setProfile, onClose }) {
  const subjects = profile?.subjects || [];
  const levels = profile?.levels || {};
  const isPro = profile?.plan === "pro" || profile?.plan === "elite";
  const savedContent = profile?.predicted_papers_content || {};
  const purchasedPapers = profile?.purchased_papers || [];

  const [subject, setSubject] = useState(subjects[0] || "Biology");
  const [level, setLevel] = useState(levels[subjects[0]] || "Higher");
  const [paperType, setPaperType] = useState(
    (SUBJECT_PAPER_TYPES[subjects[0]] || ["Question Paper"])[0]
  );
  const [stage, setStage] = useState("picker");
  const [paper, setPaper] = useState(null);
  const [isCriticalReading, setIsCriticalReading] = useState(false);
  const [answers, setAnswers] = useState({ sst: "", essay: "" });
  const [results, setResults] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState(0);
  const [crSection, setCrSection] = useState("sst");
  const [showDataBooklet, setShowDataBooklet] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (stage === "paper") {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [stage]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const contentKey = `${subject}__${level}__${paperType}`;
  const alreadyGenerated = !!savedContent[contentKey];

  const handleSubjectChange = (s) => {
    setSubject(s);
    setLevel(levels[s] || "Higher");
    const papers = SUBJECT_PAPER_TYPES[s] || ["Question Paper"];
    setPaperType(papers[0]);
  };

  const handleGenerate = async () => {
    setError(null);
    setStage("generating");

    try {
      const res = await fetch(`${BACKEND}/generate-predicted-paper`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, level, userId: session.user.id, paperType })
      });
      const data = await res.json();

      if (res.status === 403) {
        setStage("picker");
        setError("Purchase not confirmed yet — this can take up to 10 seconds after payment. Wait a moment then try again.");
        return;
      }
      if (data.error) {
        setError(data.error);
        setStage("picker");
        return;
      }

      // Update local profile cache with saved paper
      if (!data.fromCache) {
        setProfile(prev => ({
          ...prev,
          predicted_papers_content: {
            ...(prev?.predicted_papers_content || {}),
            [contentKey]: data.paper
          }
        }));
      }

      setPaper(data.paper);
      setIsCriticalReading(!!data.isCriticalReading);
      setCrSection("sst");
      setStage("paper");
      setTimer(0);
    } catch (err) {
      setError("Connection error. Check your internet and try again.");
      setStage("picker");
    }
  };

  const handlePurchase = async () => {
    setPurchaseLoading(true);
    try {
      const res = await fetch(`${BACKEND}/create-paper-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: session.user.id, userEmail: session.user.email, subject, level })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      setError("Checkout failed. Try again.");
    }
    setPurchaseLoading(false);
  };

  const handleSubmitForMarking = async () => {
    const answerText = isCriticalReading
      ? `SST ANSWERS:
${answers.sst}

CRITICAL ESSAY:
${answers.essay}`
      : (typeof answers === "string" ? answers : answers.sst);
    if (!answerText.trim()) return;
    setStage("marking");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 3000,
          system: isCriticalReading
            ? `You are marking a Higher English Critical Reading paper.
Section 1 — Scottish Text (20 marks): For Q1-Q3 (analyse): reference + comment = 2 marks each pair. Q4 (10 marks): 2 marks for commonality point, 2 marks for extract point with quotation, 6 marks for points from elsewhere.
Section 2 — Critical Essay (20 marks): Mark on relevance, textual knowledge, technique analysis, evaluation. Give mark /20 with band (A=16-20, B=12-15, C=8-11, D=4-7).
End with: SST total /20, Essay total /20, Overall /40, grade.`
            : ["Mathematics", "Applications of Mathematics"].includes(subject)
              ? `You are marking a predicted SQA ${level} ${subject} paper. Plain text only — no markdown, no LaTeX symbols. For each question write "Q[number]: [awarded]/[available]" then 1-2 lines on what earned marks and what working was correct or missing. Use SQA follow-through marking — award method marks for correct working even if the final answer is wrong. End with: TOTAL: X/Y, GRADE: A/B/C/D, TOP 2 PRIORITIES: [topics].`
              : `You are Summit, marking a predicted SQA ${level} ${subject} — ${paperType}. For each question: marks awarded vs available, what earned marks, what was missing, model answer where helpful. End with: total score, grade estimate (A/B/C/D), top 2 revision priorities.`,
          messages: [{ role: "user", content: `Paper:

${paper}

---

Answers (time: ${formatTime(timer)}):

${answerText}` }]
        })
      });
      const data = await res.json();
      setResults(data.content?.[0]?.text || "Marking failed.");
      setStage("results");
    } catch {
      setResults("Connection error.");
      setStage("results");
    }
  };

  const reset = () => {
    setStage("picker");
    setPaper(null);
    setAnswers({ sst: "", essay: "" });
    setResults(null);
    setTimer(0);
    setError(null);
    setIsCriticalReading(false);
    setCrSection("sst");
  };

  // Parse Critical Reading paper sections
  const parseCRPaper = (paperText) => {
    if (!paperText) return { poet: "", poem: "", poemText: "", rationale: "", sstQuestions: "", essayQuestions: "" };
    const between = (text, startTag, endTag) => {
      const s = text.indexOf(startTag);
      if (s === -1) return "";
      const after = s + startTag.length;
      if (!endTag) return text.slice(after).trim();
      const e = text.indexOf(endTag, after);
      return (e === -1 ? text.slice(after) : text.slice(after, e)).trim();
    };
    const nl = "\n";
    return {
      poet: between(paperText, "---POET---" + nl, nl),
      poem: between(paperText, "---POEM---" + nl, nl),
      rationale: between(paperText, "---RATIONALE---" + nl, "---POEM TEXT---"),
      poemText: between(paperText, "---POEM TEXT---" + nl, "---SST QUESTIONS---"),
      sstQuestions: between(paperText, "---SST QUESTIONS---" + nl, "---ESSAY QUESTIONS---"),
      essayQuestions: between(paperText, "---ESSAY QUESTIONS---" + nl, null)
    };
  };
  const parseRUAEPaper = (paperText) => {
    if (!paperText) return { passage1: "", passage2: "", questions: "" };
    const between = (text, startTag, endTag) => {
      const s = text.indexOf(startTag);
      if (s === -1) return "";
      const after = s + startTag.length;
      if (!endTag) return text.slice(after).trim();
      const e = text.indexOf(endTag, after);
      return (e === -1 ? text.slice(after) : text.slice(after, e)).trim();
    };
    const nl = "\n";
    const hasP2 = paperText.includes("---PASSAGE 2---");
    return {
      passage1: between(paperText, "---PASSAGE 1---" + nl, hasP2 ? "---PASSAGE 2---" : "---QUESTIONS---"),
      passage2: between(paperText, "---PASSAGE 2---" + nl, "---QUESTIONS---"),
      questions: between(paperText, "---QUESTIONS---" + nl, null) || paperText
    };
  };

    const allPaperTypes = SUBJECT_PAPER_TYPES[subject] || ["Question Paper"];
    const paperTypes = subject === "Applications of Mathematics"
      ? (level === "National 5" ? ["Paper 1 — Non-calculator", "Paper 2 — Calculator"] : ["Question Paper"])
      : allPaperTypes;

  // ── PICKER ───────────────────────────────────────────────────────
  if (stage === "picker") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#f0f4f2" }}>Predicted Papers</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>AI-predicted from SQA curriculum patterns · Generated once, saved forever</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {isPro ? (
          <div style={{ padding: "12px 16px", background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 10, marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
            <span>✦</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#4DC9A0" }}>All papers included with Pro</div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", fontWeight: 300, marginTop: 1 }}>Every subject, every paper type, every level</div>
            </div>
          </div>
        ) : (
          <div style={{ padding: "12px 16px", background: "rgba(239,159,39,0.06)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 10, marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#EF9F27", marginBottom: 3 }}>£4.99 per subject — unlocks all paper types</div>
            <div style={{ fontSize: 12, color: "rgba(240,244,242,0.4)", fontWeight: 300, lineHeight: 1.5 }}>e.g. buying English unlocks both RUAE and Critical Reading. Competitors charge £7.99 per paper. Free with Pro.</div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 520 }}>

          {/* Subject */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(240,244,242,0.5)", marginBottom: 10, letterSpacing: 0.3 }}>Subject</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {SQA_SUBJECTS.map(s => {
                const subjectKey = `${s}__${levels[s] || "Higher"}`;
                const hasPapers = Object.keys(savedContent).some(k => k.startsWith(subjectKey));
                return (
                  <button key={s} onClick={() => handleSubjectChange(s)}
                    style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: subject === s ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: subject === s ? "rgba(29,158,117,0.15)" : "transparent", color: subject === s ? "#4DC9A0" : "rgba(240,244,242,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s", position: "relative" }}>
                    {s}
                    {hasPapers && <span style={{ marginLeft: 4, fontSize: 9, color: "#1D9E75" }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Level */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(240,244,242,0.5)", marginBottom: 10, letterSpacing: 0.3 }}>Level</div>
            <div style={{ display: "flex", gap: 8 }}>
              {LEVELS.map(l => (
                <button key={l} onClick={() => setLevel(l)}
                  style={{ padding: "8px 16px", borderRadius: 8, fontSize: 13, border: level === l ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: level === l ? "rgba(29,158,117,0.15)" : "transparent", color: level === l ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Paper type */}
          {paperTypes.length > 1 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(240,244,242,0.5)", marginBottom: 10, letterSpacing: 0.3 }}>Paper</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {paperTypes.map(pt => {
                  const ptKey = `${subject}__${level}__${pt}`;
                  const isGenerated = !!savedContent[ptKey];
                  return (
                    <button key={pt} onClick={() => setPaperType(pt)}
                      style={{ padding: "12px 16px", borderRadius: 10, border: paperType === pt ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: paperType === pt ? "rgba(29,158,117,0.1)" : "rgba(255,255,255,0.02)", color: "#f0f4f2", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: paperType === pt ? 500 : 400 }}>{pt}</span>
                      <span style={{ fontSize: 11, color: isGenerated ? "#1D9E75" : "rgba(240,244,242,0.25)", fontWeight: 300 }}>
                        {isGenerated ? "✓ Generated" : "Not yet generated"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginTop: 8, fontWeight: 300 }}>
                £4.99 unlocks all {paperTypes.length} papers for {subject} {level}
              </div>
            </div>
          )}

          {paperTypes.length === 1 && (
            <div style={{ padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300, display: "flex", justifyContent: "space-between" }}>
              <span>Paper: {paperTypes[0]}</span>
              <span style={{ color: alreadyGenerated ? "#1D9E75" : "rgba(240,244,242,0.2)" }}>
                {alreadyGenerated ? "✓ Generated" : "Not yet generated"}
              </span>
            </div>
          )}

          {alreadyGenerated && (
            <div style={{ padding: "10px 14px", background: "rgba(29,158,117,0.06)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 8, fontSize: 12, color: "#4DC9A0", fontWeight: 300 }}>
              ✓ Your predicted paper for this option has already been generated and saved. Click below to view it.
            </div>
          )}

          {error && (
            <div style={{ padding: "10px 14px", background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 8, fontSize: 13, color: "#E24B4A", fontWeight: 300 }}>{error}</div>
          )}

          {(isPro || purchasedPapers.includes(`${subject}__${level}`)) ? (
            <button onClick={handleGenerate}
              style={{ padding: "14px", background: "#1D9E75", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
              onMouseOut={e => e.currentTarget.style.opacity = "1"}>
              {alreadyGenerated ? `View ${subject} ${level} — ${paperType} →` : `Generate ${subject} ${level} — ${paperType} →`}
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={handlePurchase} disabled={purchaseLoading}
                style={{ padding: "14px", background: "#1D9E75", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: purchaseLoading ? "default" : "pointer", opacity: purchaseLoading ? 0.6 : 1 }}>
                {purchaseLoading ? "Redirecting…" : `Unlock ${subject} papers — £4.99 →`}
              </button>
            </div>
          )}

          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300, lineHeight: 1.6 }}>
            ⚠️ AI-generated based on SQA curriculum patterns. Not an official SQA product. Always use real past papers from sqa.org.uk too.
          </div>
        </div>
      </div>
    </div>
  );

  // ── GENERATING ───────────────────────────────────────────────────
  if (stage === "generating") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>
        {alreadyGenerated ? "Loading your saved paper…" : "Generating predicted paper…"}
      </div>
      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 300, textAlign: "center", lineHeight: 1.6, fontWeight: 300 }}>
        {!alreadyGenerated && `Analysing SQA patterns to predict the most likely ${level} ${subject} ${paperType} questions.`}
      </div>
    </div>
  );

  // ── MARKING ──────────────────────────────────────────────────────
  if (stage === "marking") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
      <div style={{ fontSize: 14, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>Marking your answers…</div>
    </div>
  );

  // ── RESULTS ──────────────────────────────────────────────────────
  if (stage === "results") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 13, color: "#4DC9A0", fontWeight: 500 }}>{level} {subject} — {paperType} results</div>
        <button onClick={reset} style={{ fontSize: 12, padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.45)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        <div style={{ fontSize: 13, lineHeight: 1.85, color: "rgba(240,244,242,0.8)", fontWeight: 300 }}>{renderMessage(results)}</div>
      </div>
    </div>
  );

  // ── PAPER (standard non-CR) ───────────────────────────────────────
  if (stage === "paper" && !isCriticalReading) {
    const isRUAE = subject === "English" && paperType === "RUAE";
    const ruae = isRUAE ? parseRUAEPaper(paper) : null;

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Work through the paper, then paste your answers below</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {(subject === "Chemistry" || subject === "Physics" || (subject === "Applications of Mathematics" && level === "Higher")) && (
              <button onClick={() => setShowDataBooklet(v => !v)}
                style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, border: showDataBooklet ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.15)", background: showDataBooklet ? "rgba(29,158,117,0.15)" : "rgba(255,255,255,0.04)", color: showDataBooklet ? "#4DC9A0" : "rgba(240,244,242,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
                📗 Data Sheet
              </button>
            )}
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
            <button onClick={reset} style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.35)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back</button>
          </div>
        </div>
        <div style={{ padding: "8px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
          <div style={{ padding: "7px 12px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 6, fontSize: 11, color: "rgba(240,244,242,0.45)" }}>
            ⚠️ <strong style={{ color: "#EF9F27" }}>AI Predicted Paper</strong> — not an official SQA paper. Always supplement with real past papers from sqa.org.uk.
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ maxWidth: 680, margin: "0 auto" }}>

              {/* Chemistry Data Booklet — collapsible panel */}
              {subject === "Chemistry" && showDataBooklet && (
                <div style={{ marginBottom: 24, border: "2px solid #1a5c3a", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", background: "#1a5c3a", color: "white", fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif", fontWeight: 700, letterSpacing: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>📗 SQA Chemistry Data Booklet — Higher & Advanced Higher (2021)</span>
                    <button onClick={() => setShowDataBooklet(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ padding: "16px", background: "#fafff8" }}>
                    <ChemistryDataBooklet level={level} />
                  </div>
                </div>
              )}

              {/* Physics Data Sheet — collapsible panel */}
              {subject === "Physics" && showDataBooklet && (
                <div style={{ marginBottom: 24, border: "2px solid #1a3a5c", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", background: "#1a3a5c", color: "white", fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif", fontWeight: 700, letterSpacing: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>📗 SQA Physics Data Sheet — {level}</span>
                    <button onClick={() => setShowDataBooklet(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ padding: "16px", background: "#f8faff", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#111" }}>
                    <PhysicsDataSheet level={level} />
                  </div>
                </div>
              )}

              {/* Applications of Mathematics Pre-release Data Booklet */}
              {subject === "Applications of Mathematics" && level === "Higher" && showDataBooklet && (
                <div style={{ marginBottom: 24, border: "2px solid #1a5c3a", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", background: "#1a5c3a", color: "white", fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif", fontWeight: 700, letterSpacing: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>📗 Higher Applications of Mathematics — Pre-release Data Booklet 2024/25</span>
                    <button onClick={() => setShowDataBooklet(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ padding: "16px", background: "#fafff8" }}>
                    <AppsMathsDataBooklet />
                  </div>
                </div>
              )}

              <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — AI Predicted Paper</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>{level.toUpperCase()}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>{subject.toUpperCase()} — {paperType}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#555", marginTop: 2 }}>Summit Predicted Paper · usesummit.co.uk</div>
              </div>

              {isRUAE ? (
                <>
                  {ruae.passage1 && (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 6, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Passage 1</div>
                      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontStyle: "italic", color: "#555", marginBottom: 10 }}>Read the passage below and attempt the questions.</div>
                      {renderSQAPaper(ruae.passage1)}
                    </div>
                  )}
                  {ruae.passage2 && (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 6, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Passage 2</div>
                      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontStyle: "italic", color: "#555", marginBottom: 10 }}>Read this passage and attempt question 9. You may wish to note key points.</div>
                      {renderSQAPaper(ruae.passage2)}
                    </div>
                  )}
                  <div style={{ borderTop: "2px solid #000", paddingTop: 16 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Questions — Total: 30 marks</div>
                    {renderSQAPaper(ruae.questions)}
                  </div>
                </>
              ) : (
                <>
                  {["Mathematics", "Applications of Mathematics"].includes(subject) && MATHS_FORMULAE[level] && (
                    <div style={{ margin: "0 0 20px 0", padding: "12px 16px", border: "2px solid #000", fontFamily: "'Trebuchet MS', sans-serif", color: "#111", background: "white" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, borderBottom: "1px solid #ccc", paddingBottom: 6, marginBottom: 10, color: "#111" }}>FORMULAE LIST</div>
                      <div style={{ fontSize: 12, lineHeight: 2, color: "#111" }} dangerouslySetInnerHTML={{ __html: MATHS_FORMULAE[level].split(" | ").map(f => renderMath(f)).join("<br/>") }} />
                    </div>
                  )}
                  {renderSQAPaper(paper)}
                </>
              )}
            </div>
          </div>

          <div style={{ padding: "14px 24px" }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 8, fontWeight: 500 }}>Your answers</div>
            <textarea
              value={typeof answers === "string" ? answers : answers.sst}
              onChange={e => setAnswers(typeof answers === "string" ? e.target.value : { ...answers, sst: e.target.value })}
              placeholder={"Q1.\n\nQ2.\n\nQ3.\n\n..."}
              style={{ width: "100%", minHeight: 110, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
            <button onClick={handleSubmitForMarking}
              disabled={!(typeof answers === "string" ? answers : answers.sst).trim()}
              style={{ marginTop: 8, width: "100%", padding: "12px", background: (typeof answers === "string" ? answers : answers.sst).trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: (typeof answers === "string" ? answers : answers.sst).trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
              Submit for marking →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PAPER (Critical Reading) ──────────────────────────────────────
  if (stage === "paper" && isCriticalReading) {
    const cr = parseCRPaper(paper);
    const essayGenres = ["DRAMA", "PROSE FICTION", "PROSE NON-FICTION", "POETRY", "FILM AND TELEVISION DRAMA", "LANGUAGE"];
    const essaySections = (() => {
      const text = cr.essayQuestions || "";
      const sections = [];
      for (let i = 0; i < essayGenres.length; i++) {
        const genre = essayGenres[i];
        const nextGenre = essayGenres[i + 1];
        const startIdx = text.indexOf(genre);
        if (startIdx === -1) continue;
        const contentStart = startIdx + genre.length;
        const endIdx = nextGenre ? text.indexOf(nextGenre) : text.length;
        const questions = text.slice(contentStart, endIdx !== -1 ? endIdx : text.length).trim();
        if (questions) sections.push({ genre, questions });
      }
      return sections;
    })();

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {["sst", "essay"].map(s => (
              <button key={s} onClick={() => setCrSection(s)}
                style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, border: crSection === s ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: crSection === s ? "rgba(29,158,117,0.15)" : "transparent", color: crSection === s ? "#4DC9A0" : "rgba(240,244,242,0.35)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {s === "sst" ? "Sec 1 — Scottish Text" : "Sec 2 — Critical Essay"}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
            <button onClick={reset} style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.35)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back</button>
          </div>
        </div>

        {crSection === "sst" ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ maxWidth: 680, margin: "0 auto" }}>
                <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", textTransform: "uppercase", letterSpacing: 1 }}>Scottish Qualifications Authority — AI Predicted Paper</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>HIGHER ENGLISH — Critical Reading</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 2 }}>Section 1 — Scottish Text · 20 marks · ~45 minutes</div>
                </div>

                {cr.rationale && (
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#555", fontStyle: "italic", padding: "8px 12px", background: "#f9f9f9", border: "1px solid #ddd", borderRadius: 4, marginBottom: 16, lineHeight: 1.5 }}>
                    <strong>Prediction:</strong> {cr.rationale}
                  </div>
                )}

                {cr.poet && <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", marginBottom: 4 }}>Poetry — {cr.poet}</div>}
                {cr.poem && <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, fontWeight: 700, color: "#000", marginBottom: 16 }}>{cr.poem}</div>}
                {cr.poemText && <div style={{ fontFamily: "'Trebuchet MS', sans-serif", marginBottom: 24 }}>{renderSQAPaper(cr.poemText)}</div>}

                <div style={{ borderTop: "1px solid #ccc", paddingTop: 16 }}>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Questions — 20 marks</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#555", marginBottom: 12, padding: "8px 12px", background: "#f9f9f9", border: "1px solid #ddd", borderRadius: 4, lineHeight: 1.6 }}>
                    <strong>Q4 (10 marks) structure:</strong> 2 marks — general point comparing across poems (commonality); 2 marks — developed point from this poem with quotation; 6 marks — points from elsewhere in {cr.poet || "the poet"}'s work (aim for 3 points × 2 marks each).
                  </div>
                  {renderSQAPaper(cr.sstQuestions)}
                </div>
              </div>
            </div>
            <div style={{ padding: "14px 24px" }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 8, fontWeight: 500 }}>Section 1 answers (Q1–Q4)</div>
              <textarea value={answers.sst} onChange={e => setAnswers(prev => ({ ...prev, sst: e.target.value }))}
                placeholder={"Q1.\n\nQ2.\n\nQ3.\n\nQ4 (include commonality point, extract point, and points from elsewhere):\n"}
                style={{ width: "100%", minHeight: 110, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
              <button onClick={() => setCrSection("essay")}
                style={{ marginTop: 8, width: "100%", padding: "11px", background: "rgba(29,158,117,0.15)", color: "#4DC9A0", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
                Move to Section 2 — Critical Essay →
              </button>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ maxWidth: 680, margin: "0 auto" }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Section 2 — Critical Essay · 20 marks · ~45 minutes</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#555", marginBottom: 20, padding: "8px 12px", background: "#f9f9f9", border: "1px solid #ddd", borderRadius: 4 }}>
                  Attempt ONE question. Your answer must be on a different genre from Section 1 (Poetry). Use appropriate techniques and show detailed knowledge of your text.
                </div>
                {essaySections.map(({ genre, questions: qs }) => (
                  <div key={genre} style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 4 }}>
                      PART — {genre}
                    </div>
                    {renderSQAPaper(qs)}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: "14px 24px" }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 6, fontWeight: 500 }}>Critical essay answer</div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginBottom: 8, fontWeight: 300 }}>State the question number, then write your essay.</div>
              <textarea value={answers.essay} onChange={e => setAnswers(prev => ({ ...prev, essay: e.target.value }))}
                placeholder={"Question: [number]\n\n[Your critical essay...]"}
                style={{ width: "100%", minHeight: 150, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
              <button onClick={handleSubmitForMarking}
                disabled={!answers.sst.trim() && !answers.essay.trim()}
                style={{ marginTop: 8, width: "100%", padding: "13px", background: (answers.sst.trim() || answers.essay.trim()) ? "#1D9E75" : "rgba(255,255,255,0.05)", color: (answers.sst.trim() || answers.essay.trim()) ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
                Submit both sections for marking →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ── Flashcard Screen ──────────────────────────────────────────────────
function FlashcardScreen({ flashcards, subjects, levels, session, profile, setProfile, onClose }) {
  const [cards, setCards] = useState(flashcards || []);
  const [view, setView] = useState("browse");
  const [flipped, setFlipped] = useState({});
  const [known, setKnown] = useState({});
  const [newFront, setNewFront] = useState("");
  const [newBack, setNewBack] = useState("");
  const [genSubject, setGenSubject] = useState(subjects[0] || "");
  const [genTopic, setGenTopic] = useState("");
  const [genCount, setGenCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterSubject, setFilterSubject] = useState("All");
  const [studyIndex, setStudyIndex] = useState(0);

  const saveCards = async (updatedCards) => {
    setSaving(true);
    await supabase.from("profiles").update({ flashcards: updatedCards }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, flashcards: updatedCards }));
    setSaving(false);
  };

  const addCard = async () => {
    if (!newFront.trim() || !newBack.trim()) return;
    const card = { id: Date.now(), subject: genSubject, front: newFront.trim(), back: newBack.trim(), created: new Date().toISOString().split("T")[0] };
    const updated = [...cards, card];
    setCards(updated);
    await saveCards(updated);
    setNewFront(""); setNewBack("");
  };

  const deleteCard = async (id) => {
    const updated = cards.filter(c => c.id !== id);
    setCards(updated);
    await saveCards(updated);
  };

  const generateCards = async () => {
    if (!genTopic.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: `You are an SQA revision assistant. Generate exactly ${genCount} flashcards for ${levels[genSubject] || "Higher"} ${genSubject} on the topic: "${genTopic}". Respond ONLY with a JSON array, no other text, no markdown backticks. Format: [{"front": "question or term", "back": "answer or definition"}]. Keep fronts concise (under 15 words). Keep backs clear and SQA-accurate (under 50 words).`,
          messages: [{ role: "user", content: `Generate ${genCount} flashcards for ${genSubject} topic: ${genTopic}` }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/```json|```/g, "").trim();
      const generated = JSON.parse(clean);
      const newCards = generated.map((c, idx) => ({
        id: Date.now() + idx, subject: genSubject,
        front: c.front, back: c.back,
        created: new Date().toISOString().split("T")[0]
      }));
      const updated = [...cards, ...newCards];
      setCards(updated);
      await saveCards(updated);
      setGenTopic("");
      setView("browse");
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const filteredCards = filterSubject === "All" ? cards : cards.filter(c => c.subject === filterSubject);
  const studyCards = filteredCards.filter(c => !known[c.id]);
  const currentCard = studyCards[studyIndex];
  const knownCount = Object.keys(known).length;
  const totalStudy = filteredCards.length;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#f0f4f2" }}>Flashcards</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>{cards.length} card{cards.length !== 1 ? "s" : ""} saved{saving ? " · saving…" : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {["browse","create","study"].map(v => (
            <button key={v} onClick={() => { setView(v); setStudyIndex(0); setFlipped({}); }} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 6, border: view === v ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: view === v ? "rgba(29,158,117,0.15)" : "transparent", color: view === v ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize" }}>{v}</button>
          ))}
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0, marginLeft: 4 }}>×</button>
        </div>
      </div>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0 }}>
        {["All", ...subjects].map(s => (
          <button key={s} onClick={() => setFilterSubject(s)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, border: filterSubject === s ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.08)", background: filterSubject === s ? "rgba(29,158,117,0.12)" : "transparent", color: filterSubject === s ? "#4DC9A0" : "rgba(240,244,242,0.35)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{s}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {view === "browse" && (
          <>
            {filteredCards.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 12, paddingTop: 60 }}>
                <div style={{ fontSize: 32 }}>🃏</div>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>No flashcards yet</div>
                <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 280, lineHeight: 1.6, fontWeight: 300 }}>Create your own or let AI generate a set for any topic.</div>
                <button onClick={() => setView("create")} style={{ padding: "10px 20px", background: "#1D9E75", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", marginTop: 8 }}>Create flashcards →</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {filteredCards.map(card => (
                  <div key={card.id} style={{ padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, cursor: "pointer" }}
                    onClick={() => setFlipped(prev => ({ ...prev, [card.id]: !prev[card.id] }))}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: "#1D9E75", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{card.subject} · {flipped[card.id] ? "Answer" : "Question"}</div>
                        <div style={{ fontSize: 13, color: "#f0f4f2", lineHeight: 1.6 }}>{flipped[card.id] ? card.back : card.front}</div>
                        <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", marginTop: 6, fontWeight: 300 }}>{flipped[card.id] ? "Tap to flip back" : "Tap to reveal answer"}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); deleteCard(card.id); }} style={{ background: "rgba(226,75,74,0.1)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 6, color: "#E24B4A", cursor: "pointer", fontSize: 11, padding: "4px 8px", flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {view === "create" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 520 }}>
            <div style={{ padding: "20px", background: "rgba(29,158,117,0.05)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#4DC9A0", marginBottom: 14 }}>✦ AI Generate</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Subject</div>
                  <select value={genSubject} onChange={e => setGenSubject(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
                    {subjects.map(s => <option key={s} value={s} style={{ background: "#09100f" }}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Topic</div>
                  <input value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="e.g. Maslow's Hierarchy, DNA replication…" style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Number of cards</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[5, 10, 15, 20].map(n => (
                      <button key={n} onClick={() => setGenCount(n)} style={{ flex: 1, padding: "7px", borderRadius: 6, border: genCount === n ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: genCount === n ? "rgba(29,158,117,0.15)" : "transparent", color: genCount === n ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>{n}</button>
                    ))}
                  </div>
                </div>
                <button onClick={generateCards} disabled={!genTopic.trim() || generating} style={{ padding: "11px", background: genTopic.trim() && !generating ? "#1D9E75" : "rgba(255,255,255,0.05)", color: genTopic.trim() && !generating ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: genTopic.trim() && !generating ? "pointer" : "default", marginTop: 4 }}>
                  {generating ? "Generating…" : `Generate ${genCount} cards →`}
                </button>
              </div>
            </div>
            <div style={{ padding: "20px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "rgba(240,244,242,0.6)", marginBottom: 14 }}>Create manually</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Subject</div>
                  <select value={genSubject} onChange={e => setGenSubject(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
                    {subjects.map(s => <option key={s} value={s} style={{ background: "#09100f" }}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Front (question / term)</div>
                  <textarea value={newFront} onChange={e => setNewFront(e.target.value)} placeholder="What is Maslow's Hierarchy of Needs?" rows={2} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif", resize: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 5, fontWeight: 300 }}>Back (answer / definition)</div>
                  <textarea value={newBack} onChange={e => setNewBack(e.target.value)} placeholder="A motivational theory showing 5 levels of human needs…" rows={3} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif", resize: "none", boxSizing: "border-box" }} />
                </div>
                <button onClick={addCard} disabled={!newFront.trim() || !newBack.trim()} style={{ padding: "11px", background: newFront.trim() && newBack.trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: newFront.trim() && newBack.trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: newFront.trim() && newBack.trim() ? "pointer" : "default" }}>Add card →</button>
              </div>
            </div>
          </div>
        )}
        {view === "study" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, paddingTop: 20 }}>
            {filteredCards.length === 0 ? (
              <div style={{ textAlign: "center", paddingTop: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🃏</div>
                <div style={{ fontSize: 15, color: "#f0f4f2", fontFamily: "'Instrument Serif', serif" }}>No cards to study</div>
                <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", marginTop: 6 }}>Create some flashcards first.</div>
              </div>
            ) : studyCards.length === 0 ? (
              <div style={{ textAlign: "center", paddingTop: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🎉</div>
                <div style={{ fontSize: 15, color: "#f0f4f2", fontFamily: "'Instrument Serif', serif" }}>You know all {totalStudy} cards!</div>
                <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", marginTop: 6 }}>Nice work. Reset to go again.</div>
                <button onClick={() => { setKnown({}); setStudyIndex(0); }} style={{ marginTop: 16, padding: "10px 20px", background: "#1D9E75", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Reset →</button>
              </div>
            ) : (
              <>
                <div style={{ width: "100%", maxWidth: 480 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(240,244,242,0.35)", marginBottom: 6 }}>
                    <span>{knownCount} known</span>
                    <span>{studyCards.length} remaining</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${totalStudy > 0 ? (knownCount / totalStudy) * 100 : 0}%`, background: "#1D9E75", borderRadius: 2, transition: "width 0.3s ease" }} />
                  </div>
                </div>
                {currentCard && (
                  <div style={{ width: "100%", maxWidth: 480 }}>
                    <div onClick={() => setFlipped(prev => ({ ...prev, [currentCard.id]: !prev[currentCard.id] }))}
                      style={{ padding: "32px 28px", background: flipped[currentCard.id] ? "rgba(29,158,117,0.08)" : "rgba(255,255,255,0.04)", border: `1px solid ${flipped[currentCard.id] ? "rgba(29,158,117,0.3)" : "rgba(255,255,255,0.1)"}`, borderRadius: 14, cursor: "pointer", minHeight: 160, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", transition: "all 0.2s" }}>
                      <div style={{ fontSize: 10, color: flipped[currentCard.id] ? "#1D9E75" : "rgba(240,244,242,0.3)", fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>{flipped[currentCard.id] ? "Answer" : "Question"}</div>
                      <div style={{ fontSize: 16, color: "#f0f4f2", lineHeight: 1.6, fontFamily: flipped[currentCard.id] ? "'DM Sans', sans-serif" : "'Instrument Serif', serif" }}>
                        {flipped[currentCard.id] ? currentCard.back : currentCard.front}
                      </div>
                      {!flipped[currentCard.id] && <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginTop: 14 }}>Tap to reveal</div>}
                    </div>
                    {flipped[currentCard.id] && (
                      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                        <button onClick={() => {
                          const next = studyCards.findIndex((c, idx) => idx > studyIndex);
                          setStudyIndex(next >= 0 ? next : 0);
                          setFlipped(prev => ({ ...prev, [currentCard.id]: false }));
                        }} style={{ flex: 1, padding: "12px", background: "rgba(226,75,74,0.1)", border: "1px solid rgba(226,75,74,0.25)", borderRadius: 8, color: "#E24B4A", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", fontWeight: 500 }}>
                          Still learning
                        </button>
                        <button onClick={() => {
                          setKnown(prev => ({ ...prev, [currentCard.id]: true }));
                          setStudyIndex(prev => Math.max(0, prev));
                          setFlipped(prev => ({ ...prev, [currentCard.id]: false }));
                        }} style={{ flex: 1, padding: "12px", background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.25)", borderRadius: 8, color: "#4DC9A0", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", fontWeight: 500 }}>
                          Got it ✓
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Weakness Screen ───────────────────────────────────────────────────
function WeaknessScreen({ weakTopics, subjects, onDrill, onClose, onDelete, onClearAll }) {
  const [confirmClear, setConfirmClear] = useState(false);
  const grouped = {};
  subjects.forEach(s => { grouped[s] = []; });
  (weakTopics || []).forEach(t => {
    if (!grouped[t.subject]) grouped[t.subject] = [];
    grouped[t.subject].push(t);
  });
  const totalDropped = (weakTopics || []).reduce((sum, t) => sum + (t.total - t.scored), 0);
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#f0f4f2" }}>Weakness tracker</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>Topics tracked automatically from your past paper sessions</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {weakTopics && weakTopics.length > 0 && (
            confirmClear ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: "rgba(240,244,242,0.4)" }}>Sure?</span>
                <button onClick={() => { onClearAll(); setConfirmClear(false); }} style={{ fontSize: 11, padding: "4px 10px", background: "rgba(226,75,74,0.15)", border: "1px solid rgba(226,75,74,0.35)", borderRadius: 6, color: "#E24B4A", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>Yes, clear all</button>
                <button onClick={() => setConfirmClear(false)} style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "rgba(240,244,242,0.3)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setConfirmClear(true)} style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 6, color: "rgba(226,75,74,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Clear all</button>
            )
          )}
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {(!weakTopics || weakTopics.length === 0) ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 12, paddingTop: 80 }}>
            <div style={{ fontSize: 32 }}>📋</div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>No weaknesses tracked yet</div>
            <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 300, lineHeight: 1.6, fontWeight: 300 }}>Use Past paper mode and Summit will automatically track topics where you drop marks.</div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
              <div style={{ padding: "14px 20px", background: "rgba(226,75,74,0.06)", border: "1px solid rgba(226,75,74,0.15)", borderRadius: 10, flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#E24B4A", fontWeight: 500, marginBottom: 6 }}>Marks dropped</div>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, color: "#f0f4f2", letterSpacing: -1 }}>{totalDropped}</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>across all sessions</div>
              </div>
              <div style={{ padding: "14px 20px", background: "rgba(239,159,39,0.06)", border: "1px solid rgba(239,159,39,0.15)", borderRadius: 10, flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#EF9F27", fontWeight: 500, marginBottom: 6 }}>Weak areas</div>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, color: "#f0f4f2", letterSpacing: -1 }}>{weakTopics.length}</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>topics flagged</div>
              </div>
            </div>
            {subjects.map(s => {
              const topics = grouped[s] || [];
              if (topics.length === 0) return null;
              return (
                <div key={s} style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(240,244,242,0.5)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1D9E75" }} />{s}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {topics.map((t, i) => {
                      const pct = Math.round((t.scored / t.total) * 100);
                      const barColor = pct < 50 ? "#E24B4A" : pct < 75 ? "#EF9F27" : "#1D9E75";
                      return (
                        <div key={i} style={{ padding: "12px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, display: "flex", alignItems: "center", gap: 14 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, color: "rgba(240,244,242,0.8)", fontWeight: 400, marginBottom: 6 }}>{t.topic}</div>
                            <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 2, transition: "width 0.5s ease" }} />
                            </div>
                            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", marginTop: 4, fontWeight: 300 }}>{t.scored}/{t.total} marks · {t.date}</div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={{ fontSize: 18, fontFamily: "'Instrument Serif', serif", color: barColor }}>{pct}%</div>
                            <button onClick={() => onDrill(s, t.topic)} style={{ fontSize: 11, padding: "4px 10px", background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.25)", borderRadius: 6, color: "#4DC9A0", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>Drill this →</button>
                            <button onClick={() => onDelete(t)} style={{ fontSize: 11, padding: "4px 10px", background: "rgba(226,75,74,0.1)", border: "1px solid rgba(226,75,74,0.25)", borderRadius: 6, color: "#E24B4A", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ── Progress Dashboard (Elite only) ──────────────────────────────────
function ProgressDashboard({ profile, session, onClose }) {
  const weakTopics = profile?.weak_topics || [];
  const confidence = profile?.confidence || {};
  const sessionHistory = profile?.session_history || [];
  const flashcards = profile?.flashcards || [];
  const levels = profile?.levels || {};
  const examDate = profile?.exam_date;
  const gradeTarget = profile?.grade_target || "";

  const daysUntil = examDate
    ? Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Drill performance from weak_topics (scored/total pairs)
  const drillEntries = weakTopics.filter(t => t.scored !== undefined && t.total !== undefined);
  const totalDrillMarks = drillEntries.reduce((s, t) => s + (t.scored || 0), 0);
  const totalDrillAvail = drillEntries.reduce((s, t) => s + (t.total || 0), 0);
  const overallPct = totalDrillAvail > 0 ? Math.round((totalDrillMarks / totalDrillAvail) * 100) : null;
  const overallGrade = overallPct === null ? null : overallPct >= 70 ? "A" : overallPct >= 60 ? "B" : overallPct >= 50 ? "C" : "D";
  const gradeColor = overallGrade === "A" ? "#1D9E75" : overallGrade === "B" ? "#a78bfa" : overallGrade === "C" ? "#EF9F27" : "#E24B4A";

  // Topics by subject aggregated
  const topicsBySubject = {};
  drillEntries.forEach(t => {
    if (!topicsBySubject[t.subject]) topicsBySubject[t.subject] = [];
    topicsBySubject[t.subject].push(t);
  });

  // Confidence trend
  const confEntries = Object.entries(confidence);

  // Sessions in last 7 days
  const recentSessions = sessionHistory.filter(s => {
    if (!s.date) return false;
    const d = new Date(s.date);
    return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
  });

  // Mastered vs weak topics (scored >= 70%)
  const masteredTopics = drillEntries.filter(t => t.total > 0 && (t.scored / t.total) >= 0.7);
  const stillWeakTopics = drillEntries.filter(t => t.total > 0 && (t.scored / t.total) < 0.6);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📊</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#f0f4f2" }}>Progress Dashboard</div>
            <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 300, marginTop: 1 }}>
              {daysUntil !== null ? `${daysUntil} days until exam` : "Elite · Your study overview"}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Top stat cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div style={{ padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
            <div style={{ fontSize: 26, fontFamily: "'Instrument Serif', serif", color: overallGrade ? gradeColor : "rgba(240,244,242,0.2)", fontWeight: 700 }}>{overallGrade || "—"}</div>
            <div style={{ fontSize: 10, color: "rgba(240,244,242,0.3)", marginTop: 4, letterSpacing: 0.5 }}>Current grade</div>
          </div>
          <div style={{ padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
            <div style={{ fontSize: 26, fontFamily: "'Instrument Serif', serif", color: "#a78bfa" }}>{sessionHistory.length}</div>
            <div style={{ fontSize: 10, color: "rgba(240,244,242,0.3)", marginTop: 4, letterSpacing: 0.5 }}>Sessions</div>
          </div>
          <div style={{ padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
            <div style={{ fontSize: 26, fontFamily: "'Instrument Serif', serif", color: "#1D9E75" }}>{flashcards.length}</div>
            <div style={{ fontSize: 10, color: "rgba(240,244,242,0.3)", marginTop: 4, letterSpacing: 0.5 }}>Flashcards</div>
          </div>
        </div>

        {/* Grade target banner */}
        {gradeTarget && overallGrade && (
          <div style={{ padding: "12px 16px", borderRadius: 10, background: overallGrade <= gradeTarget ? "rgba(29,158,117,0.08)" : "rgba(239,159,39,0.06)", border: `1px solid ${overallGrade <= gradeTarget ? "rgba(29,158,117,0.2)" : "rgba(239,159,39,0.2)"}`, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 18 }}>{overallGrade <= gradeTarget ? "🎯" : "⚡"}</div>
            <div>
              <div style={{ fontSize: 12, color: "#f0f4f2", fontWeight: 500 }}>
                {overallGrade <= gradeTarget ? `On track for your ${gradeTarget} target` : `Target: ${gradeTarget} — keep pushing`}
              </div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2, fontWeight: 300 }}>
                Current performance: {overallPct}% drill average
              </div>
            </div>
          </div>
        )}

        {/* Confidence by subject */}
        {confEntries.length > 0 && (
          <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Confidence by Subject</div>
            {confEntries.map(([sub, val]) => {
              const pct = (val / 5) * 100;
              const col = val <= 2 ? "#E24B4A" : val === 3 ? "#EF9F27" : "#1D9E75";
              return (
                <div key={sub} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <div style={{ fontSize: 12, color: "rgba(240,244,242,0.7)" }}>{levels[sub] || "Higher"} {sub}</div>
                    <div style={{ fontSize: 12, color: col, fontWeight: 600 }}>{val}/5</div>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: col, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Drill performance by subject */}
        {Object.keys(topicsBySubject).length > 0 && (
          <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Drill Performance</div>
            {Object.entries(topicsBySubject).map(([sub, topics]) => {
              const subScored = topics.reduce((s, t) => s + t.scored, 0);
              const subTotal = topics.reduce((s, t) => s + t.total, 0);
              const subPct = subTotal > 0 ? Math.round((subScored / subTotal) * 100) : 0;
              const col = subPct >= 70 ? "#1D9E75" : subPct >= 50 ? "#EF9F27" : "#E24B4A";
              return (
                <div key={sub} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: "rgba(240,244,242,0.7)", fontWeight: 500 }}>{sub}</div>
                    <div style={{ fontSize: 12, color: col, fontWeight: 600 }}>{subPct}%</div>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden", marginBottom: 8 }}>
                    <div style={{ height: "100%", width: `${subPct}%`, borderRadius: 3, background: col, transition: "width 0.6s ease" }} />
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {topics.slice(-6).map((t, i) => {
                      const topicPct = t.total > 0 ? Math.round((t.scored / t.total) * 100) : 0;
                      const tc = topicPct >= 70 ? "rgba(29,158,117,0.7)" : topicPct >= 50 ? "rgba(239,159,39,0.7)" : "rgba(226,75,74,0.7)";
                      const tb = topicPct >= 70 ? "rgba(29,158,117,0.12)" : topicPct >= 50 ? "rgba(239,159,39,0.08)" : "rgba(226,75,74,0.08)";
                      return (
                        <div key={i} style={{ padding: "3px 8px", borderRadius: 10, background: tb, border: `1px solid ${tc}`, fontSize: 11, color: tc }}>
                          {t.topic} {t.scored}/{t.total}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Mastered vs still weak */}
        {(masteredTopics.length > 0 || stillWeakTopics.length > 0) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ padding: "14px 16px", borderRadius: 12, background: "rgba(29,158,117,0.06)", border: "1px solid rgba(29,158,117,0.18)" }}>
              <div style={{ fontSize: 11, color: "rgba(29,158,117,0.7)", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>✓ Mastered</div>
              {masteredTopics.length === 0
                ? <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300 }}>None yet — keep drilling</div>
                : masteredTopics.slice(-4).map((t, i) => <div key={i} style={{ fontSize: 11, color: "rgba(240,244,242,0.6)", marginBottom: 3, fontWeight: 300 }}>{t.topic}</div>)
              }
            </div>
            <div style={{ padding: "14px 16px", borderRadius: 12, background: "rgba(226,75,74,0.05)", border: "1px solid rgba(226,75,74,0.15)" }}>
              <div style={{ fontSize: 11, color: "rgba(226,75,74,0.7)", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>⚠ Still weak</div>
              {stillWeakTopics.length === 0
                ? <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300 }}>Nothing flagged</div>
                : stillWeakTopics.slice(-4).map((t, i) => <div key={i} style={{ fontSize: 11, color: "rgba(240,244,242,0.6)", marginBottom: 3, fontWeight: 300 }}>{t.topic}</div>)
              }
            </div>
          </div>
        )}

        {/* Recent sessions */}
        {recentSessions.length > 0 && (
          <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Recent Sessions</div>
            {recentSessions.slice(0, 5).map((s, i) => {
              const d = new Date(s.date);
              const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
              const pct = s.drillScore?.total > 0 ? Math.round((s.drillScore.correct / s.drillScore.total) * 100) : null;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < recentSessions.slice(0, 5).length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "rgba(240,244,242,0.7)" }}>{label}</div>
                    {s.headline && <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>{s.headline}</div>}
                  </div>
                  {pct !== null && (
                    <div style={{ fontSize: 12, color: pct >= 70 ? "#1D9E75" : pct >= 50 ? "#EF9F27" : "#E24B4A", fontWeight: 600 }}>{pct}%</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {drillEntries.length === 0 && sessionHistory.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 24px" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: "#f0f4f2", marginBottom: 8 }}>No data yet</div>
            <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", fontWeight: 300, lineHeight: 1.6 }}>Start drilling questions in the coach and your progress will appear here.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Daily Check-in (all plans) ────────────────────────────────────────
function DailyCheckin({ profile, plan, onOpenCoach, onDismiss }) {
  const subjects = profile?.subjects || [];
  const levels = profile?.levels || {};
  const examDate = profile?.exam_date;
  const weakTopics = profile?.weak_topics || [];
  const lastSession = profile?.last_session_summary;
  const confidence = profile?.confidence || {};

  const daysUntil = examDate
    ? Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // Figure out today's focus topic
  const recentTopics = (lastSession?.topics || []);
  const weakest = weakTopics.slice(-3).map(t => t.topic);

  // Pick focus: most recent weak topic not covered last session, or first subject
  const focusTopic = weakest.find(t => !recentTopics.includes(t))
    || weakest[0]
    || (subjects[0] ? `${levels[subjects[0]] || "Higher"} ${subjects[0]}` : null);

  // Urgency level
  const urgent = daysUntil !== null && daysUntil <= 5;

  // Low confidence subject
  const lowConfSubject = Object.entries(confidence).sort(([,a],[,b]) => a - b)[0];

  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div style={{ margin: "0 8px 8px", padding: "14px 16px", borderRadius: 10, background: urgent ? "rgba(226,75,74,0.07)" : "rgba(167,139,250,0.07)", border: `1px solid ${urgent ? "rgba(226,75,74,0.25)" : "rgba(167,139,250,0.2)"}`, position: "relative" }}>
      <button onClick={onDismiss} style={{ position: "absolute", top: 8, right: 10, background: "none", border: "none", color: "rgba(240,244,242,0.2)", cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>

      <div style={{ fontSize: 10, color: urgent ? "rgba(226,75,74,0.6)" : "rgba(167,139,250,0.5)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
        {urgent ? `⚡ ${daysUntil} days to go` : today}
      </div>

      <div style={{ fontSize: 13, color: "#f0f4f2", fontWeight: 500, lineHeight: 1.4, marginBottom: 6 }}>
        {urgent
          ? `Final push — prioritise ${focusTopic || subjects[0] || "your weakest topics"}`
          : lastSession
          ? `Last session: ${recentTopics[0] || "general revision"}. Today: ${focusTopic || "keep building"}`
          : `Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}. Ready to study?`
        }
      </div>

      {lowConfSubject && lowConfSubject[1] <= 2 && (
        <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", fontWeight: 300, marginBottom: 10 }}>
          ⚠ Low confidence: {lowConfSubject[0]} ({lowConfSubject[1]}/5)
        </div>
      )}

      {plan === "elite" ? (
        <button onClick={onOpenCoach}
          style={{ width: "100%", padding: "8px 12px", borderRadius: 7, background: urgent ? "rgba(226,75,74,0.15)" : "rgba(167,139,250,0.12)", border: `1px solid ${urgent ? "rgba(226,75,74,0.3)" : "rgba(167,139,250,0.25)"}`, color: urgent ? "#f87171" : "#c4b5fd", fontSize: 11, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer", textAlign: "center" }}>
          Open coach → {focusTopic ? `work on ${focusTopic}` : "start session"}
        </button>
      ) : (
        <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", fontWeight: 300 }}>
          Upgrade to Elite to get a personalised coach session →
        </div>
      )}
    </div>
  );
}

// ── Planner Screen ────────────────────────────────────────────────────
function PlannerScreen({ subjects, levels, examDate, onClose, session, setProfile, savedPlan }) {
  const [plan, setPlan] = useState(savedPlan || null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(savedPlan?.length > 0 ? "plan" : "questions");
  const [priorities, setPriorities] = useState([]);
  const [studyTime, setStudyTime] = useState(45);
  const [preferredModes, setPreferredModes] = useState(["chat", "paper"]);

  const daysUntil = examDate ? Math.min(365, Math.max(0, Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24)))) : null;

  const togglePriority = (s) => setPriorities(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleMode = (m) => setPreferredModes(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  const generatePlan = async () => {
    if (subjects.length === 0) return;
    setLoading(true); setStep("plan");
    const priorityList = priorities.length > 0 ? priorities : subjects;
    const modeDesc = {
      chat: "concept chat (understanding topics)",
      paper: "past paper practice (with mark scheme feedback)",
      drill: "drill mode (rapid-fire questions)",
      mock: "mock exams (full papers)"
    };
    const modesStr = preferredModes.map(m => modeDesc[m]).join(", ");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: `You are Summit, an expert SQA revision planner. Generate a personalised day-by-day revision schedule using Summit's study modes. Respond ONLY with a JSON array, no other text, no markdown backticks. Each item: {"day": 1, "date": "Mon 14 Apr", "subject": "Subject Name", "focus": "Specific topic or area", "mode": "chat|paper|drill|mock", "duration": "${studyTime} mins", "tip": "One short actionable tip for this session"}. Priority subjects should appear more frequently. Use the student's preferred modes. Spread subjects based on priority. Use mock for final sessions per subject.`,
          messages: [{ role: "user", content: `Create a revision plan. All subjects: ${subjects.map(s => `${s} (${levels[s]})`).join(", ")}. Priority subjects: ${priorityList.join(", ")}. First exam: ${examDate}. Days until exam: ${daysUntil}. Study time per day: ${studyTime} mins. Preferred study modes: ${modesStr}. Generate ${Math.min(daysUntil || 14, 14)} days.` }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setPlan(parsed);
      if (session?.user?.id) {
        await supabase.from("profiles").update({ revision_plan: parsed }).eq("id", session.user.id);
        setProfile(prev => ({ ...prev, revision_plan: parsed }));
      }
    } catch { setPlan([]); }
    setLoading(false);
  };

  const clearPlan = async () => {
    setPlan(null);
    setStep("questions");
    if (session?.user?.id) {
      await supabase.from("profiles").update({ revision_plan: [] }).eq("id", session.user.id);
      setProfile(prev => ({ ...prev, revision_plan: [] }));
    }
  };

  const modeColors = { chat: "#4DC9A0", paper: "#EF9F27", drill: "#E24B4A", mock: "#a78bfa" };
  const modeLabels = { chat: "Concept", paper: "Past paper", drill: "Drill", mock: "Mock" };
  const modeIcons = { chat: "💬", paper: "📝", drill: "⚡", mock: "📋" };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#f0f4f2" }}>Revision planner</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>{daysUntil !== null ? `${daysUntil} days until first exam` : "Set your exam date in onboarding"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {step === "plan" && <button onClick={clearPlan} style={{ fontSize: 11, padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.45)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>← New plan</button>}
          {step === "plan" && plan?.length > 0 && <button onClick={generatePlan} disabled={loading} style={{ fontSize: 11, padding: "6px 14px", background: "transparent", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 6, color: "#4DC9A0", cursor: loading ? "default" : "pointer", fontFamily: "'DM Sans', sans-serif", opacity: loading ? 0.5 : 1 }}>{loading ? "Generating…" : "↻ Regenerate"}</button>}
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {step === "questions" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 520 }}>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2", letterSpacing: -0.3 }}>Let's build your plan</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#f0f4f2", marginBottom: 6 }}>Which subjects need the most work?</div>
              <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", marginBottom: 12, fontWeight: 300 }}>Select all that apply. These get more sessions in your plan.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {subjects.map(s => (
                  <button key={s} onClick={() => togglePriority(s)} style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: priorities.includes(s) ? "1px solid #E24B4A" : "1px solid rgba(255,255,255,0.1)", background: priorities.includes(s) ? "rgba(226,75,74,0.12)" : "transparent", color: priorities.includes(s) ? "#E24B4A" : "rgba(240,244,242,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{s}</button>
                ))}
              </div>
              {priorities.length === 0 && <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginTop: 8, fontWeight: 300 }}>None selected = all subjects treated equally</div>}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#f0f4f2", marginBottom: 6 }}>How long can you study each day?</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[30, 45, 60, 90, 120].map(t => (
                  <button key={t} onClick={() => setStudyTime(t)} style={{ padding: "8px 16px", borderRadius: 6, fontSize: 12, border: studyTime === t ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: studyTime === t ? "rgba(29,158,117,0.15)" : "transparent", color: studyTime === t ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{t} min{t > 1 ? "s" : ""}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#f0f4f2", marginBottom: 6 }}>How do you prefer to study?</div>
              <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", marginBottom: 12, fontWeight: 300 }}>Summit will use these modes in your plan.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(modeLabels).map(([m, label]) => {
                  const desc = { chat: "Understand topics through conversation", paper: "Practice with past paper questions & marking", drill: "Rapid-fire questions on weak spots", mock: "Full timed exam simulations" };
                  return (
                    <button key={m} onClick={() => toggleMode(m)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 8, border: preferredModes.includes(m) ? `1px solid ${modeColors[m]}40` : "1px solid rgba(255,255,255,0.08)", background: preferredModes.includes(m) ? `${modeColors[m]}10` : "transparent", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>
                      <span style={{ fontSize: 18 }}>{modeIcons[m]}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: preferredModes.includes(m) ? modeColors[m] : "rgba(240,244,242,0.6)" }}>{label}</div>
                        <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 2 }}>{desc[m]}</div>
                      </div>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${preferredModes.includes(m) ? modeColors[m] : "rgba(255,255,255,0.2)"}`, background: preferredModes.includes(m) ? modeColors[m] : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {preferredModes.includes(m) && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "white" }} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={generatePlan} disabled={preferredModes.length === 0} style={{ padding: "13px", background: preferredModes.length > 0 ? "#1D9E75" : "rgba(255,255,255,0.05)", color: preferredModes.length > 0 ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: preferredModes.length > 0 ? "pointer" : "default" }}>
              Build my plan →
            </button>
          </div>
        )}

        {step === "plan" && (
          <>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, paddingTop: 80 }}>
                <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
                <div style={{ fontSize: 13, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>Building your personalised plan…</div>
              </div>
            ) : !plan || plan.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 12, paddingTop: 80 }}>
                <div style={{ fontSize: 32 }}>📅</div>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>Couldn't generate a plan</div>
                <button onClick={generatePlan} style={{ padding: "10px 20px", background: "#1D9E75", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Try again</button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
                  {Object.entries(modeLabels).map(([m, label]) => (
                    <div key={m} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: modeColors[m] }} />{label}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {plan.map((item, i) => {
                    const isToday = i === 0;
                    const color = modeColors[item.mode] || "#4DC9A0";
                    return (
                      <div key={i} style={{ display: "flex", flexDirection: "column", padding: "12px 16px", background: isToday ? "rgba(29,158,117,0.06)" : "rgba(255,255,255,0.02)", border: `1px solid ${isToday ? "rgba(29,158,117,0.25)" : "rgba(255,255,255,0.06)"}`, borderRadius: 10 }}>
                        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                          <div style={{ textAlign: "center", minWidth: 44, flexShrink: 0 }}>
                            <div style={{ fontSize: 10, color: "rgba(240,244,242,0.3)", fontWeight: 300, textTransform: "uppercase", letterSpacing: 1 }}>Day</div>
                            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: isToday ? "#1D9E75" : "#f0f4f2", letterSpacing: -0.5, lineHeight: 1 }}>{item.day}</div>
                          </div>
                          <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: "#f0f4f2" }}>{item.subject}</div>
                              <div style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: `${color}20`, color, fontWeight: 500 }}>{modeLabels[item.mode]}</div>
                              {isToday && <div style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "rgba(29,158,117,0.15)", color: "#4DC9A0", fontWeight: 500 }}>TODAY</div>}
                            </div>
                            <div style={{ fontSize: 12, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>{item.focus}</div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", fontWeight: 300 }}>{item.date}</div>
                            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300, marginTop: 2 }}>{item.duration}</div>
                          </div>
                        </div>
                        {item.tip && <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 11, color: "rgba(240,244,242,0.35)", fontWeight: 300, paddingLeft: 58 }}>💡 {item.tip}</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ── Subject Upgrade Modal ─────────────────────────────────────────────
function SubjectUpgradeModal({ subjectName, level, onDismiss, onUpgrade, onUpgradeElite, upgradeLoading }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#0d1a18", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 20, padding: "40px 36px", maxWidth: 440, width: "100%", textAlign: "center", boxShadow: "0 0 60px rgba(29,158,117,0.08)", animation: "fadeUp 0.35s cubic-bezier(0.16,1,0.3,1)" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 24 }}>🔒</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 26, color: "#f0f4f2", marginBottom: 8, letterSpacing: -0.5, lineHeight: 1.2 }}>
          Unlock {subjectName}
        </div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.45)", marginBottom: 24, lineHeight: 1.7, fontWeight: 300 }}>
          You selected {subjectName} {level} during setup but are on the free plan. Choose a plan to unlock all your subjects.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <div style={{ padding: "20px", background: "rgba(29,158,117,0.05)", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 14, textAlign: "left" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#1D9E75", fontWeight: 600 }}>Pro</div>
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2" }}>£6.99<span style={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span></div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
              {["All your SQA subjects", "Unlimited messages", "All 4 study modes", "Weakness tracker + planner"].map(function(f) { return (
                <div key={f} style={{ fontSize: 12, color: "rgba(240,244,242,0.6)", display: "flex", gap: 8, alignItems: "center", fontWeight: 300 }}>
                  <span style={{ color: "#1D9E75" }}>✓</span>{f}
                </div>
              );})}
            </div>
            <button onClick={onUpgrade} disabled={upgradeLoading} style={{ width: "100%", padding: "11px", background: upgradeLoading ? "rgba(29,158,117,0.5)" : "#1D9E75", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: upgradeLoading ? "default" : "pointer" }}>
              {upgradeLoading ? "Redirecting…" : "Upgrade to Pro →"}
            </button>
          </div>
          <div style={{ padding: "20px", background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 14, textAlign: "left", position: "relative" }}>
            <div style={{ position: "absolute", top: -11, left: 16, background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", fontSize: 10, padding: "2px 10px", borderRadius: 20, fontWeight: 600 }}>BEST RESULTS</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#a78bfa", fontWeight: 600 }}>Elite</div>
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2" }}>£14.99<span style={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span></div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
              {["Everything in Pro", "AI Study Coach", "All predicted papers free", "Day-by-day revision schedule"].map(function(f) { return (
                <div key={f} style={{ fontSize: 12, color: "rgba(240,244,242,0.6)", display: "flex", gap: 8, alignItems: "center", fontWeight: 300 }}>
                  <span style={{ color: "#a78bfa" }}>✓</span>{f}
                </div>
              );})}
            </div>
            <button onClick={onUpgradeElite} disabled={upgradeLoading} style={{ width: "100%", padding: "11px", background: upgradeLoading ? "rgba(167,139,250,0.4)" : "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: upgradeLoading ? "default" : "pointer" }}>
              {upgradeLoading ? "Redirecting…" : "Get Elite →"}
            </button>
          </div>
        </div>
        <button onClick={onDismiss} style={{ padding: "10px", background: "transparent", color: "rgba(240,244,242,0.3)", border: "none", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Maybe later</button>
      </div>
    </div>
  );
}

function UpgradePrompt({ reason, onDismiss, onUpgrade, upgradeLoading }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#0d1a18", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 16, padding: "36px 32px", maxWidth: 420, width: "100%", textAlign: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <img src={SummitSubmark} alt="Summit" style={{ height: 100, width: "auto" }} />
        </div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 26, color: "#f0f4f2", marginBottom: 10, letterSpacing: -0.5 }}>Upgrade to Pro</div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.45)", marginBottom: 24, lineHeight: 1.7, fontWeight: 300 }}>{reason}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24, textAlign: "left" }}>
          {["Unlimited messages every day","Past paper mode with mark scheme + model answers","Drill mode — targeted questions on your weak spots","Mock exam mode — full SQA-style papers","All SQA subjects","Predicted papers — all included free"].map(f => (
            <div key={f} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, color: "rgba(240,244,242,0.6)", fontWeight: 300 }}>
              <span style={{ color: "#1D9E75", fontSize: 14, flexShrink: 0 }}>✓</span>{f}
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 40, color: "#f0f4f2", letterSpacing: -1.5, lineHeight: 1 }}>£6.99<span style={{ fontSize: 16, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span></div>
          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.25)", marginTop: 4, fontWeight: 300 }}>Cancel any time — no hidden fees</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={onUpgrade} disabled={upgradeLoading} style={{ padding: "13px", background: upgradeLoading ? "rgba(29,158,117,0.5)" : "#1D9E75", color: "white", border: "none", borderRadius: 8, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: upgradeLoading ? "default" : "pointer" }}
            onMouseOver={e => { if (!upgradeLoading) e.currentTarget.style.opacity = "0.88"; }}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}>
            {upgradeLoading ? "Redirecting to checkout…" : "Upgrade to Pro →"}
          </button>
          <button onClick={onDismiss} style={{ padding: "11px", background: "transparent", color: "rgba(240,244,242,0.35)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

function AddSubjectModal({ currentSubjects, levels, onAdd, onClose }) {
  const [selected, setSelected] = useState(null);
  const [level, setLevel] = useState(null);
  const available = SQA_SUBJECTS.filter(s => !currentSubjects.includes(s));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#0d1a18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "32px", maxWidth: 480, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2", letterSpacing: -0.5 }}>Add a subject</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", marginBottom: 12, fontWeight: 300 }}>Select a subject</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 20 }}>
            {available.map(s => (
              <button key={s} onClick={() => { setSelected(s); setLevel(null); }} style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: selected === s ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: selected === s ? "rgba(29,158,117,0.15)" : "transparent", color: selected === s ? "#4DC9A0" : "rgba(240,244,242,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{s}</button>
            ))}
          </div>
          {selected && (
            <div>
              <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", marginBottom: 10, fontWeight: 300 }}>Level for {selected}</div>
              <div style={{ display: "flex", gap: 8 }}>
                {LEVELS.map(l => (
                  <button key={l} onClick={() => setLevel(l)} style={{ padding: "8px 14px", borderRadius: 6, fontSize: 12, border: level === l ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: level === l ? "rgba(29,158,117,0.15)" : "transparent", color: level === l ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{l}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button onClick={() => selected && level && onAdd(selected, level)} disabled={!selected || !level} style={{ padding: "12px", background: selected && level ? "#1D9E75" : "rgba(255,255,255,0.05)", color: selected && level ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: selected && level ? "pointer" : "default" }}>
          Add {selected || "subject"} →
        </button>
      </div>
    </div>
  );
}


// ── Elite Coach Upgrade Prompt ────────────────────────────────────────
function EliteCoachUpgradePrompt({ onDismiss, onUpgrade, upgradeLoading }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#0d0f1a", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 20, padding: "40px 36px", maxWidth: 440, width: "100%", textAlign: "center", boxShadow: "0 0 80px rgba(124,58,237,0.15)" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(124,58,237,0.1))", border: "1px solid rgba(167,139,250,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px", fontSize: 24 }}>✦</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 28, color: "#f0f4f2", marginBottom: 10, letterSpacing: -0.5, lineHeight: 1.2 }}>Your coach<br />+ progress pack.</div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.45)", marginBottom: 28, lineHeight: 1.75, fontWeight: 300 }}>
          Elite gives you a coach that knows your subjects, tracks your weak spots, and tells you exactly what to study every day.<br /><br />
          It's like having a tutor available at midnight, the night before your exam — one who already knows everything about you.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28, textAlign: "left" }}>
          {[
            ["✦", "AI coach that knows your subjects and weak spots"],
            ["📊", "Progress dashboard — tracks every session automatically"],
            ["📅", "Day-by-day revision plan built around your exam dates"],
            ["🎯", "Drill mode, mock papers, marking — all in one place"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 13, color: "rgba(240,244,242,0.65)", fontWeight: 300 }}>
              <span style={{ color: "#a78bfa", fontSize: 14, flexShrink: 0, marginTop: 1 }}>{icon}</span>{text}
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 42, color: "#f0f4f2", letterSpacing: -1.5, lineHeight: 1 }}>
            £7.99<span style={{ fontSize: 16, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}> one-off</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.25)", marginTop: 4, fontWeight: 300 }}>Pay once, yours until your last exam</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={onUpgrade} disabled={upgradeLoading}
            style={{ padding: "14px", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: upgradeLoading ? "default" : "pointer", opacity: upgradeLoading ? 0.6 : 1, transition: "all 0.2s" }}>
            {upgradeLoading ? "Redirecting…" : "Unlock Coach + Progress — £7.99 →"}
          </button>
          <button onClick={onDismiss} style={{ padding: "12px", background: "transparent", color: "rgba(240,244,242,0.3)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

// ── AI Coach Screen (Elite only) ─────────────────────────────────────
function CoachScreen({ profile, session, setProfile, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [setupStep, setSetupStep] = useState(null);
  const [setupData, setSetupData] = useState({});
  const [mockState, setMockState] = useState(null);
  const [savedMockState, setSavedMockState] = useState(null);
  const [mockTimer, setMockTimer] = useState(0);
  const [mockAnswers, setMockAnswers] = useState("");
  const [markingMock, setMarkingMock] = useState(false);
  // Drill state
  const [drillState, setDrillState] = useState(null); // { question, subject, level, topic, marks, qNum }
  const [drillAnswer, setDrillAnswer] = useState("");
  const [markingDrill, setMarkingDrill] = useState(false);
  const [drillScore, setDrillScore] = useState({ correct: 0, total: 0 }); // session drill score
  // Session summary state
  const [showSummaryPrompt, setShowSummaryPrompt] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  // Flashcard offer state
  const [flashcardOffer, setFlashcardOffer] = useState(null); // { subject, topic }
  const [generatingFlashcards, setGeneratingFlashcards] = useState(false);
  // Session topics tracker
  const sessionTopicsRef = useRef([]);
  const bottomRef = useRef(null);
  const timerRef = useRef(null);

  const displayName = profile?.display_name || session?.user?.email?.split("@")[0] || "there";
  const firstName = displayName.split(" ")[0];
  const subjects = profile?.subjects || [];
  const levels = profile?.levels || {};
  const examDate = profile?.exam_date;
  const confidence = profile?.confidence || {};
  const weakTopics = profile?.weak_topics || [];
  const gradeTarget = profile?.grade_target || "";
  const studyHours = profile?.study_hours_per_week || "";
  const biggestStruggle = profile?.biggest_struggle || "";
  const coachSetup = profile?.coach_setup || null;
  const lastSessionSummary = profile?.last_session_summary || null;

  const daysUntil = examDate
    ? Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const weakSubjects = Object.entries(confidence).filter(([, v]) => v <= 2).map(([k]) => k);
  const recentWeakTopics = weakTopics.slice(-6).map(t => `${t.subject}: ${t.topic} (${t.scored}/${t.total})`);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  useEffect(() => {
    if (coachSetup) { setSetupStep("done"); startCoach(coachSetup); }
    else { setSetupStep("q1"); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, setupStep, mockState, drillState, showSummary]);

  useEffect(() => {
    if (mockState && mockState.phase === "in_progress") {
      timerRef.current = setInterval(() => setMockTimer(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [mockState]);

  // Save session memory after each coach reply
  const saveSessionMemory = async (topicsCovered, drillScoreData) => {
    if (!session?.user?.id) return;
    const summary = {
      date: new Date().toISOString(),
      topics: topicsCovered,
      drillScore: drillScoreData,
      weakTopicsIdentified: weakTopics.slice(-3).map(t => t.topic),
    };
    await supabase.from("profiles").update({ last_session_summary: summary }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, last_session_summary: summary }));
  };

  // Append to session_history
  const appendSessionHistory = async (summaryEntry) => {
    if (!session?.user?.id) return;
    const existing = profile?.session_history || [];
    const updated = [summaryEntry, ...existing].slice(0, 20); // keep last 20
    await supabase.from("profiles").update({ session_history: updated }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, session_history: updated }));
  };

  const saveCoachSetup = async (data) => {
    const updated = { ...data, completed_at: new Date().toISOString() };
    await supabase.from("profiles").update({ coach_setup: updated }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, coach_setup: updated }));
  };

  const buildSystem = (setup) => {
    const s = setup || {};
    const lastSession = lastSessionSummary
      ? `- Last session (${new Date(lastSessionSummary.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}): covered ${(lastSessionSummary.topics || []).join(", ") || "general revision"}${lastSessionSummary.drillScore && lastSessionSummary.drillScore.total > 0 ? `, drill score ${lastSessionSummary.drillScore.correct}/${lastSessionSummary.drillScore.total}` : ""}${(lastSessionSummary.weakTopicsIdentified || []).length > 0 ? `, weak areas flagged: ${lastSessionSummary.weakTopicsIdentified.join(", ")}` : ""}`
      : "- No previous sessions recorded yet";
    return `You are Summit Coach, the personal AI study tutor for ${firstName}. You are warm, sharp, and genuinely invested in this student. You are NOT a generic AI.

STUDENT PROFILE:
- Name: ${firstName}
- Subjects: ${subjects.map(sub => `${levels[sub] || "Higher"} ${sub}`).join(", ")}
- First exam: ${examDate ? `${examDate} (${daysUntil !== null ? daysUntil + " days away" : "very soon"})` : "not set"}
- Target grade: ${gradeTarget || "not specified"}
- Weekly study hours: ${studyHours || "not specified"}
- Biggest struggle: ${biggestStruggle || "not specified"}
- Confidence (1-5): ${Object.entries(confidence).map(([sub, v]) => `${sub}: ${v}`).join(", ") || "not rated"}
- Weak subjects: ${weakSubjects.length > 0 ? weakSubjects.join(", ") : "none flagged"}
- Recent weak topics: ${recentWeakTopics.length > 0 ? recentWeakTopics.join(" | ") : "no past paper data yet"}
${lastSession}
${s.goal ? `- Student goal: ${s.goal}` : ""}
${s.examStatus ? `- Exam status: ${s.examStatus}` : ""}
${s.preferredStyle ? `- Coaching style preference: ${s.preferredStyle}` : ""}

CAPABILITIES:
You can do everything inside this chat:
- Explain SQA topics at the right level
- Mark past paper answers using real SQA marking logic
- Fire drill questions in real SQA past paper style with mark allocations
- Generate full mock exam papers
- Build revision plans
- Generate flashcard decks for weak topics

SPECIAL ACTIONS — use these trigger words when appropriate:
- To generate a mock paper: include "GENERATE_MOCK_PAPER: [Subject] [Level]" on its own line
- To drill a specific topic with a past-paper style question: include "GENERATE_DRILL_QUESTION: [Subject] [Level] [Topic] [Marks]" on its own line (e.g. "GENERATE_DRILL_QUESTION: Mathematics Higher Differentiation 4"). Use this when the student wants to drill a topic or you identify a weak area to target.
- To offer flashcard generation after identifying a weak topic: include "OFFER_FLASHCARDS: [Subject] [Topic]" on its own line (e.g. "OFFER_FLASHCARDS: Mathematics Integration"). Use this when you've just identified or drilled a weak topic and flashcards would help consolidate it.

COACHING PRINCIPLES:
1. Be specific - reference their actual subjects, weak topics, exam date, confidence scores
2. ${daysUntil !== null && daysUntil <= 7 ? `URGENT: only ${daysUntil} days left. Be focused.` : daysUntil !== null && daysUntil <= 30 ? `${daysUntil} days to go - focused effort matters.` : "Encourage consistent progress."}
3. After answering, suggest a concrete next step
4. Keep responses punchy - short paragraphs, no walls of text
5. When offering choices, ALWAYS put each option on its own completely separate line starting with [A], [B], [C]
6. Feel premium and personal - this is Elite tier
7. If this is not the student's first session, naturally reference what you worked on last time in your opening message

You are Summit Coach. Be the tutor they wish they had.`;
  };

  const startCoach = (setup) => {
    setSetupStep("done");
    const msg = `Hi Coach, I am ready to study.${setup && setup.goal ? " My goal is: " + setup.goal + "." : ""}`;
    sendCoachMsg(msg, true, setup);
  };

  const generateDrillQuestion = async (subject, level, topic, marks) => {
    const qNum = (drillScore.total || 0) + 1;
    setDrillState({ phase: "generating", subject, level, topic, marks: parseInt(marks) || 4, qNum });
    setDrillAnswer("");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 600,
          system: `You are an SQA examiner writing a single past-paper style question for ${level} ${subject}.
Generate ONE question on the topic: "${topic}". The question should be worth exactly ${marks || 4} marks.
Respond ONLY with a JSON object in this exact format, no other text:
{"question": "Full question text here", "marks": ${marks || 4}, "topic": "${topic}"}
The question must match real SQA style exactly — use correct command words (describe, explain, analyse, evaluate, justify). Be specific and challenging.`,
          messages: [{ role: "user", content: `Generate a ${marks || 4}-mark ${level} ${subject} question on ${topic}` }]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setDrillState({ phase: "answering", subject, level, topic: parsed.topic || topic, marks: parsed.marks || marks || 4, qNum, question: parsed.question });
    } catch {
      setDrillState(null);
      setMessages(prev => [...prev, { from: "coach", text: "Couldn't generate the drill question. Try again.", options: null }]);
    }
  };

  const submitDrillAnswer = async () => {
    if (!drillAnswer.trim() || !drillState) return;
    setMarkingDrill(true);
    setDrillState(prev => ({ ...prev, phase: "marking" }));
    const { subject, level, topic, marks, question, qNum } = drillState;
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 900,
          system: `You are an SQA examiner marking a ${level} ${subject} answer for ${firstName}.
The question was worth ${marks} marks on the topic: ${topic}.

Respond with ONLY a JSON object in this exact format:
{
  "marksAwarded": <number>,
  "marksAvailable": ${marks},
  "bulletFeedback": ["• Mark point 1 — awarded/not awarded: reason", "• Mark point 2 — awarded/not awarded: reason"],
  "aGradeResponse": "A concise example of what a full-marks response would include (2-4 sentences)",
  "coachNote": "One encouraging but honest sentence from the coach to ${firstName} about this answer"
}
Be strict but fair. Use real SQA marking logic.`,
          messages: [{ role: "user", content: `Question: ${question}\n\nStudent answer: ${drillAnswer}` }]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const clean = raw.replace(/```json|```/g, "").trim();
      const result = JSON.parse(clean);
      const awarded = result.marksAwarded ?? 0;
      const available = result.marksAvailable ?? marks;

      // Update drill score
      const newScore = { correct: drillScore.correct + awarded, total: drillScore.total + available };
      setDrillScore(newScore);

      // Track topic for session memory
      if (!sessionTopicsRef.current.includes(topic)) {
        sessionTopicsRef.current = [...sessionTopicsRef.current, topic];
      }

      // Update weak_topics in Supabase
      if (session?.user?.id) {
        const current = profile?.weak_topics || [];
        const updated = [...current, { subject, topic, scored: awarded, total: available, date: new Date().toISOString() }].slice(-50);
        await supabase.from("profiles").update({ weak_topics: updated }).eq("id", session.user.id);
        setProfile(prev => ({ ...prev, weak_topics: updated }));
      }

      // Add drill result as a coach message
      setMessages(prev => [...prev, {
        from: "coach",
        text: "",
        isDrillResult: true,
        drillResult: { ...result, awarded, available, topic, subject, question, studentAnswer: drillAnswer, qNum }
      }]);
      setHistory(prev => [...prev, { role: "assistant", content: `Drill Q${qNum} marked: ${awarded}/${available} marks on ${topic}.` }]);
      setDrillState(null);
      setDrillAnswer("");
      setMarkingDrill(false);

      // Save session memory
      await saveSessionMemory(sessionTopicsRef.current, newScore);

      // Auto follow-up after a beat
      setTimeout(() => {
        const struggled = awarded < available * 0.6;
        sendCoachMsg(
          struggled
            ? `I just marked ${firstName}'s drill question on ${topic} — they scored ${awarded}/${available}. That's a weak area. Offer to drill another question on the same topic OR offer to explain the concept first, or generate flashcards. Give 2-3 options.`
            : `I just marked ${firstName}'s drill question on ${topic} — they scored ${awarded}/${available}. Good work. What should we tackle next? Give 2-3 options.`,
          true, coachSetup
        );
      }, 400);

    } catch {
      setMarkingDrill(false);
      setDrillState(null);
      setMessages(prev => [...prev, { from: "coach", text: "Marking failed. Try again.", options: null }]);
    }
  };

  const generateMockPaper = async (subject, level) => {
    setMockState({ phase: "generating", subject, level });
    setMockTimer(0);
    try {
      const isMaths = ["Mathematics", "Applications of Mathematics"].includes(subject);
      const isScience = ["Biology", "Chemistry", "Physics", "Human Biology"].includes(subject);
      const paperType = isMaths ? "Calculator paper" : isScience ? "Paper 2 - Extended Answers" : "Question Paper";
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 10000,
          system: `You are generating a predicted SQA ${level} ${subject} mock exam paper.

${subject === "Business Management" ? `EXACT FORMAT:
SECTION 1 - 30 marks: Write 600-800 words case study about a fictional Scottish/UK business. Include Exhibit 1 (website/social media description) and Exhibit 2 (financial data table with 2 years of figures). Write questions 1(a) through 1(h) referencing the case study. Total = 30 marks.
SECTION 2 - 60 marks: Four questions numbered 2,3,4,5. Each has 3-4 sub-parts (a)(b)(c)(d). Pure knowledge, no case study. Each question = 15 marks. Total = 60 marks.
Command words: describe (2-4 marks) = state with detail. explain (3-4 marks) = cause and effect. justify (3-4 marks) = reasons for decision. discuss (5-8 marks) = advantages AND disadvantages with conclusion.` 
: subject === "Biology" || subject === "Chemistry" || subject === "Physics" || subject === "Human Biology" ? `Paper 2 Extended Answer format:
Section A (60 marks): 8-10 extended answer questions worth 4-12 marks each. Mix of describe, explain, predict, analyse, evaluate questions. Include data interpretation questions with graphs or tables. Cover all main topic areas.
Section B (20 marks): 2-3 longer extended responses worth 8-12 marks each.
Total: 80 marks.`
: subject === "Mathematics" || subject === "Applications of Mathematics" ? `Calculator paper format. ${level} level.
15-18 questions totalling 60-70 marks. Mix of 2, 3, 4, and 5 mark questions. Show mark allocation clearly. Include: algebra, trigonometry, geometry, statistics, calculus (Higher). Working space after each question.`
: subject === "English" ? `Critical Reading paper:
SECTION 1 - Scottish Text (20 marks): Poetry extract from a prescribed Scottish poet, 4 questions. Q1-Q3 analyse language (2-4 marks each). Q4 (10 marks) broader text question referring to this and another poem.
SECTION 2 - Critical Essay (20 marks): Choice of 6 essay questions across genres (Drama, Prose Fiction, Poetry, Film, Language). Student answers one.`
: subject === "History" ? `Section 1 - Source Questions (20 marks): 2 sources provided. Questions on how useful, how fully they explain an issue. Must reference origin, purpose, content.
Section 2 - Extended Essay (20 marks): One essay question requiring factor analysis with conclusion. Choose from 2-3 options.`
: `Generate a realistic full ${level} ${subject} paper. Use correct SQA command words (describe, explain, analyse, evaluate, justify). Mix of short (2-4 mark) and extended (6-10 mark) questions. Cover all main topic areas. Total 60-80 marks. Number questions clearly with marks stated.`}

Format it as a clean SQA-style paper. Start with the paper header. State total marks and time. Number every question. Write marks right-aligned after each question.`,
          messages: [{ role: "user", content: `Generate a complete ${level} ${subject} ${paperType} mock exam paper now. Make it challenging and realistic.` }]
        })
      });
      const data = await res.json();
      const paper = data.content?.[0]?.text || "Failed to generate paper.";
      setMockState({ phase: "in_progress", subject, level, paper });
      setMockAnswers("");
    } catch {
      setMockState(null);
      setMessages(prev => [...prev, { from: "coach", text: "Failed to generate the mock paper. Try again.", options: null }]);
    }
  };

  const submitMockAnswers = async () => {
    if (!mockAnswers.trim() || !mockState) return;
    setMarkingMock(true);
    const { subject, level, paper } = mockState;
    setMockState(prev => ({ ...prev, phase: "marking" }));
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 2000,
          system: `You are Summit Coach marking a ${level} ${subject} mock paper for ${firstName}. 

For each question write:
Q[n]: [awarded]/[available] - [one line of specific feedback]

Then write a summary section:
---
TOTAL: X/Y marks
GRADE: A/B/C/D
STRONGEST AREA: [topic]
WEAKEST AREA: [topic - this is what we work on next]

Then write ONE coaching message (2-3 sentences) as their personal coach - reference their name, be honest but encouraging, and tell them exactly what to do next. Make it feel personal not generic.`,
          messages: [{ role: "user", content: `Paper:\n${paper}\n\nAnswers (time: ${formatTime(mockTimer)}):\n${mockAnswers}` }]
        })
      });
      const data = await res.json();
      const marking = data.content?.[0]?.text || "Marking failed.";
      
      // Add marking as a coach message and continue conversation
      const newCoachMsg = { from: "coach", text: marking, options: null, isMockResult: true };
      setMessages(prev => [...prev, newCoachMsg]);
      setHistory(prev => [...prev, { role: "assistant", content: marking }]);
      setMockState(null); setSavedMockState(null);
      setMarkingMock(false);

      // Auto-follow up
      setTimeout(() => {
        sendCoachMsg(`I just finished marking ${firstName}'s mock paper. The results are above. Based on the weakest area identified, what should we work on next? Give me 3 specific options.`, true, coachSetup);
      }, 500);

    } catch {
      setMarkingMock(false);
      setMockState(null); setSavedMockState(null);
    }
  };

  const generateCoachFlashcards = async (subject, topic) => {
    setGeneratingFlashcards(true);
    setFlashcardOffer(null);
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 1000,
          system: `You are an SQA revision assistant. Generate exactly 8 flashcards for ${levels[subject] || "Higher"} ${subject} on the topic: "${topic}". Respond ONLY with a JSON array, no other text, no markdown backticks. Format: [{"front": "question or term", "back": "answer or definition"}]. Keep fronts concise (under 15 words). Keep backs clear and SQA-accurate (under 50 words).`,
          messages: [{ role: "user", content: `Generate 8 flashcards for ${subject} topic: ${topic}` }]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "[]";
      const clean = raw.replace(/```json|```/g, "").trim();
      const cards = JSON.parse(clean);
      if (Array.isArray(cards) && cards.length > 0) {
        const withIds = cards.map(c => ({ ...c, id: Date.now() + Math.random() }));
        const existing = profile?.flashcards || [];
        const updated = [...existing, ...withIds];
        await supabase.from("profiles").update({ flashcards: updated }).eq("id", session.user.id);
        setProfile(prev => ({ ...prev, flashcards: updated }));
        setMessages(prev => [...prev, {
          from: "coach",
          text: "",
          isFlashcardSuccess: true,
          flashcardInfo: { subject, topic, count: withIds.length }
        }]);
        // Auto follow-up after flashcards saved
        setTimeout(() => {
          sendCoachMsg(`I just generated ${withIds.length} flashcards on ${topic} for ${firstName} — they're saved. What should we do next? Give 2-3 options (e.g. drill another question, move to a new topic, or explain something else).`, true, coachSetup);
        }, 400);
      }
    } catch {
      setMessages(prev => [...prev, { from: "coach", text: "Couldn't generate flashcards right now. Try again.", options: null }]);
    }
    setGeneratingFlashcards(false);
  };

  const generateSessionSummary = async () => {
    setGeneratingSummary(true);
    setShowSummaryPrompt(false);
    try {
      const coachMessages = messages.filter(m => m.from === "coach" || m.from === "user");
      const drillResults = messages.filter(m => m.isDrillResult).map(m => m.drillResult);
      const flashcardsCreated = messages.filter(m => m.isFlashcardSuccess).map(m => m.flashcardInfo);
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 600,
          system: `You are Summit Coach summarising a study session for ${firstName}. Based on the session, respond ONLY with a JSON object:
{
  "headline": "One punchy sentence summarising this session (e.g. 'Strong session — maths improving')",
  "topicsCovered": ["topic1", "topic2"],
  "strengthsIdentified": ["one sentence about what they did well"],
  "focusForNext": "One specific thing to focus on next session",
  "motivationalNote": "One short genuine encouraging note to ${firstName} (1-2 sentences)"
}`,
          messages: [{ role: "user", content: `Session messages: ${coachMessages.slice(-20).map(m => `${m.from}: ${m.text || "[drill/result]"}`).join("\n")}\n\nDrill results: ${JSON.stringify(drillResults)}\nFlashcard decks created: ${JSON.stringify(flashcardsCreated)}` }]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const clean = raw.replace(/```json|```/g, "").trim();
      const summary = JSON.parse(clean);
      const fullSummary = {
        ...summary,
        drillScore,
        drillResults,
        flashcardsCreated,
        date: new Date().toISOString(),
        sessionTopics: sessionTopicsRef.current
      };
      setSummaryData(fullSummary);
      setShowSummary(true);
      // Save to session_history and update last_session_summary
      await appendSessionHistory(fullSummary);
      await saveSessionMemory(sessionTopicsRef.current, drillScore);
    } catch {
      setShowSummary(false);
      setSummaryData(null);
    }
    setGeneratingSummary(false);
  };

  const handleEndSession = () => {
    if (messages.length > 3) {
      setShowSummaryPrompt(true);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    if (messages.length > 3 && !showSummaryPrompt && !showSummary) {
      setShowSummaryPrompt(true);
    } else {
      onClose();
    }
  };

  const sendCoachMsg = async (text, isOpening, extraSetup) => {
    if (!text.trim() || loading) return;
    if (!isOpening) setInput("");
    const userMsg = { role: "user", content: text };
    if (!isOpening) setMessages(prev => [...prev, { from: "user", text }]);
    const newHistory = [...history, userMsg];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 800,
          system: buildSystem(extraSetup || coachSetup),
          messages: newHistory
        })
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "Something went wrong.";

      // Check for GENERATE_MOCK_PAPER trigger
      const mockMatch = reply.match(/GENERATE_MOCK_PAPER:\s*(.+?)\s+(National 5|Higher|Advanced Higher)/i);
      if (mockMatch) {
        const triggerSubject = mockMatch[1].trim();
        const triggerLevel = mockMatch[2].trim();
        const cleanReply = reply.replace(/GENERATE_MOCK_PAPER:[^\n]+/g, "").trim();
        if (cleanReply) {
          setMessages(prev => [...prev, { from: "coach", text: cleanReply, options: null }]);
          setHistory(prev => [...prev, { role: "assistant", content: reply }]);
        }
        setLoading(false);
        await generateMockPaper(triggerSubject, triggerLevel);
        return;
      }

      // Check for GENERATE_DRILL_QUESTION trigger
      const drillMatch = reply.match(/GENERATE_DRILL_QUESTION:\s*([^\n]+?)\s+(National 5|Higher|Advanced Higher)\s+([^\n]+?)\s+(\d+)/i);
      if (drillMatch) {
        const dSubject = drillMatch[1].trim();
        const dLevel = drillMatch[2].trim();
        const dTopic = drillMatch[3].trim();
        const dMarks = parseInt(drillMatch[4]);
        const cleanReply = reply.replace(/GENERATE_DRILL_QUESTION:[^\n]+/g, "").trim();
        if (cleanReply) {
          setMessages(prev => [...prev, { from: "coach", text: cleanReply, options: null }]);
          setHistory(prev => [...prev, { role: "assistant", content: reply }]);
        }
        setLoading(false);
        await generateDrillQuestion(dSubject, dLevel, dTopic, dMarks);
        return;
      }

      // Check for OFFER_FLASHCARDS trigger
      const flashMatch = reply.match(/OFFER_FLASHCARDS:\s*([^\n]+?)\s+([^\n]+)/i);
      if (flashMatch) {
        const fSubject = flashMatch[1].trim();
        const fTopic = flashMatch[2].trim();
        const cleanReply = reply.replace(/OFFER_FLASHCARDS:[^\n]+/g, "").trim();
        if (cleanReply) {
          setMessages(prev => [...prev, { from: "coach", text: cleanReply, options: null }]);
          setHistory(prev => [...prev, { role: "assistant", content: reply }]);
        }
        setFlashcardOffer({ subject: fSubject, topic: fTopic });
        setLoading(false);
        return;
      }

      // Robust option detection
      var allLines = reply.split("\n");
      var optLines = [];
      for (var li = 0; li < allLines.length; li++) {
        var lt = allLines[li].trim().replace(/\*\*/g, "");
        if (/^\[[A-D]\]/.test(lt)) { optLines.push(lt); }
      }
      if (optLines.length === 0 && /\[A\]/.test(reply)) {
        var inlineMatch = reply.match(/\[[A-D]\][^\[\n]+/g);
        if (inlineMatch) { optLines = inlineMatch.map(function(m) { return m.trim(); }); }
      }
      const finalOptLines = optLines;
      setMessages(prev => [...prev, {
        from: "coach", text: reply,
        options: finalOptLines.length >= 1 ? finalOptLines.map(function(l) { return l.trim().replace(/^\[[A-D]\]\s*/, ""); }) : null
      }]);
      setHistory(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { from: "coach", text: "Connection error. Try again.", options: null }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCoachMsg(input); }
  };

  const SETUP_STEPS = {
    q1: { question: "Before we start, a quick question — what is your main goal right now?", options: ["Improve my grade before exams", "Understand topics I am confused about", "Practice past papers and get feedback", "Build a revision plan and stick to it"], key: "goal", next: "q2" },
    q2: { question: "Where are you with your exams?", options: ["Haven't started revising yet", "Actively revising — want to sharpen up", "Exams are very close — need final prep", "Some exams done, some still to come"], key: "examStatus", next: "q3" },
    q3: { question: "How do you prefer to be coached?", options: ["Push me hard — be direct and demanding", "Supportive and encouraging", "Mix of both — firm but kind", "Just give me the content, minimal chat"], key: "preferredStyle", next: "done" }
  };

  const handleSetupChoice = async (key, value, next) => {
    const newData = { ...setupData, [key]: value };
    setSetupData(newData);
    if (next === "done") { await saveCoachSetup(newData); startCoach(newData); }
    else { setSetupStep(next); }
  };

  // SETUP SCREEN
  if (setupStep && setupStep !== "done") {
    const step = SETUP_STEPS[setupStep];
    const stepNum = setupStep === "q1" ? 1 : setupStep === "q2" ? 2 : 3;
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: "0 0 16px rgba(124,58,237,0.2)" }}>✦</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#f0f4f2" }}>AI Study Coach</div>
              <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 300, marginTop: 1 }}>Quick setup — {stepNum} of 3</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
        <div style={{ height: 2, background: "rgba(167,139,250,0.1)", flexShrink: 0 }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg, #a78bfa, #7c3aed)", width: `${(stepNum / 3) * 100}%`, transition: "width 0.4s ease" }} />
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 32px", gap: 28 }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, boxShadow: "0 0 40px rgba(124,58,237,0.2)", animation: "glow 3s ease-in-out infinite" }}>✦</div>
          <div style={{ textAlign: "center", maxWidth: 400 }}>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2", lineHeight: 1.35, letterSpacing: -0.5, animation: "fadeUp 0.5s ease" }}>{step.question}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 400 }}>
            {step.options.map((opt, idx) => (
              <button key={idx} onClick={() => handleSetupChoice(step.key, opt, step.next)}
                style={{ padding: "14px 18px", borderRadius: 12, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.04)", color: "rgba(240,244,242,0.75)", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)", display: "flex", alignItems: "center", gap: 12, animationDelay: `${idx * 0.06}s` }}
                onMouseOver={e => { e.currentTarget.style.background = "rgba(167,139,250,0.12)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.4)"; e.currentTarget.style.color = "#f0f4f2"; }}
                onMouseOut={e => { e.currentTarget.style.background = "rgba(167,139,250,0.04)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.2)"; e.currentTarget.style.color = "rgba(240,244,242,0.75)"; }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#a78bfa", fontWeight: 600, flexShrink: 0 }}>{String.fromCharCode(65 + idx)}</div>
                {opt.replace(/\*\*/g, "").replace(/\*/g, "")}
              </button>
            ))}
          </div>
          <button onClick={() => { saveCoachSetup(setupData); startCoach(setupData); }} style={{ fontSize: 12, color: "rgba(240,244,242,0.2)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Skip setup →</button>
        </div>
      </div>
    );
  }

  // MOCK PAPER - GENERATING
  if (mockState && mockState.phase === "generating") {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✦</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#f0f4f2" }}>Summit Coach</div>
              <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 300 }}>Generating your mock paper...</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>Building {mockState.level} {mockState.subject} paper...</div>
          <div style={{ fontSize: 13, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>This takes about 20 seconds</div>
        </div>
      </div>
    );
  }

  // MOCK PAPER - IN PROGRESS
  if (mockState && (mockState.phase === "in_progress" || mockState.phase === "marking")) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#a78bfa" }}>{mockState.level} {mockState.subject}</div>
            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>Coach mock paper</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {mockState.phase === "marking" ? (
              <div style={{ fontSize: 12, color: "#a78bfa" }}>Marking...</div>
            ) : (
              <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: "#a78bfa" }}>⏱ {formatTime(mockTimer)}</div>
            )}
            <button onClick={() => { setSavedMockState(mockState); setMockState(null); }} style={{ fontSize: 11, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.35)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back to chat</button>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
          {/* White SQA paper */}
          <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ maxWidth: 680, margin: "0 auto" }}>
              <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — Summit Coach Mock Paper</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>{mockState.level.toUpperCase()}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>{mockState.subject.toUpperCase()}</div>
              </div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
                Work through each question. When done, paste your answers below and your coach will mark it.
              </div>
              {mockState.paper.split("\n").map((line, i) => {
                if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
                const clean = line.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").replace(/^#{1,3}\s*/, "");
                const isQNum = /^\d+\./.test(clean.trim());
                return <p key={i} style={{ margin: "0 0 8px 0", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, color: "#111", fontWeight: isQNum ? 600 : 400, lineHeight: 1.6 }}>{clean}</p>;
              })}
            </div>
          </div>
          {/* Answer box */}
          <div style={{ padding: "14px 24px", background: "#09100f" }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 8, fontWeight: 500 }}>Your answers</div>
            <textarea value={mockAnswers} onChange={e => setMockAnswers(e.target.value)}
              placeholder={"Q1.\n\nQ2.\n\nQ3.\n\n..."}
              style={{ width: "100%", minHeight: 110, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(167,139,250,0.2)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(167,139,250,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
            <button onClick={submitMockAnswers} disabled={!mockAnswers.trim() || markingMock}
              style={{ marginTop: 8, width: "100%", padding: "12px", background: mockAnswers.trim() && !markingMock ? "linear-gradient(135deg, #a78bfa, #7c3aed)" : "rgba(255,255,255,0.05)", color: mockAnswers.trim() && !markingMock ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer", boxShadow: mockAnswers.trim() && !markingMock ? "0 4px 16px rgba(124,58,237,0.3)" : "none" }}>
              {markingMock ? "Marking..." : "Submit to coach for marking →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const QUICK_PROMPTS = [
    subjects[0] ? `Drill me on ${subjects[0]}` : "Drill me on my weakest subject",
    weakTopics.length > 0 ? `Help me with ${weakTopics[weakTopics.length - 1]?.topic}` : "What are my weakest areas?",
    subjects[0] ? `Generate a mock paper for ${subjects[0]}` : "Make me a mock paper",
    daysUntil !== null && daysUntil <= 7 ? "Exam is very soon — what do I prioritise?" : "Build me a revision plan for today",
  ].filter(Boolean).slice(0, 4);

  // SESSION SUMMARY PROMPT MODAL
  if (showSummaryPrompt) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 32px", gap: 20 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✦</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2", marginBottom: 8 }}>Session complete.</div>
          <div style={{ fontSize: 13, color: "rgba(240,244,242,0.45)", fontWeight: 300, lineHeight: 1.6 }}>Want a summary of what you covered today?<br />It only takes a few seconds.</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 320 }}>
          <button onClick={generateSessionSummary} disabled={generatingSummary}
            style={{ padding: "14px 20px", borderRadius: 12, background: "linear-gradient(135deg, #a78bfa, #7c3aed)", border: "none", color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 16px rgba(124,58,237,0.3)" }}>
            {generatingSummary ? "Generating..." : "Yes — show me my summary ✦"}
          </button>
          <button onClick={onClose}
            style={{ padding: "12px 20px", borderRadius: 12, background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(240,244,242,0.4)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
            No thanks, just close
          </button>
        </div>
      </div>
    );
  }

  // SESSION SUMMARY VIEW
  if (showSummary && summaryData) {
    const pct = summaryData.drillScore?.total > 0
      ? Math.round((summaryData.drillScore.correct / summaryData.drillScore.total) * 100)
      : null;
    const grade = pct === null ? null : pct >= 70 ? "A" : pct >= 60 ? "B" : pct >= 50 ? "C" : "D";
    const gradeColor = grade === "A" ? "#1D9E75" : grade === "B" ? "#a78bfa" : grade === "C" ? "#EF9F27" : "#E24B4A";
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f0f4f2" }}>Session Summary</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          {/* Headline */}
          <div style={{ padding: "20px 24px", borderRadius: 16, background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(167,139,250,0.08))", border: "1px solid rgba(167,139,250,0.25)", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(167,139,250,0.6)", marginBottom: 8, fontWeight: 500 }}>
              {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2", lineHeight: 1.4 }}>{summaryData.headline}</div>
          </div>

          {/* Score card */}
          {pct !== null && (
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1, padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", color: gradeColor, fontWeight: 700 }}>{pct}%</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 4 }}>Drill score</div>
              </div>
              <div style={{ flex: 1, padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", color: gradeColor }}>{grade}</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 4 }}>Grade equiv.</div>
              </div>
              <div style={{ flex: 1, padding: "16px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", color: "#a78bfa" }}>{summaryData.drillScore.correct}/{summaryData.drillScore.total}</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 4 }}>Marks</div>
              </div>
            </div>
          )}

          {/* Topics covered */}
          {(summaryData.sessionTopics?.length > 0 || summaryData.topicsCovered?.length > 0) && (
            <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Topics Covered</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(summaryData.sessionTopics?.length > 0 ? summaryData.sessionTopics : summaryData.topicsCovered).map((t, i) => (
                  <span key={i} style={{ padding: "4px 10px", borderRadius: 20, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", fontSize: 12, color: "#c4b5fd" }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Strengths */}
          {summaryData.strengthsIdentified?.length > 0 && (
            <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(29,158,117,0.06)", border: "1px solid rgba(29,158,117,0.2)", marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "rgba(29,158,117,0.7)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>✓ Strengths</div>
              {summaryData.strengthsIdentified.map((s, i) => (
                <div key={i} style={{ fontSize: 13, color: "rgba(240,244,242,0.7)", lineHeight: 1.6, fontWeight: 300 }}>{s}</div>
              ))}
            </div>
          )}

          {/* Flashcards created */}
          {summaryData.flashcardsCreated?.length > 0 && (
            <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.15)", marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>🃏 Flashcards Created</div>
              {summaryData.flashcardsCreated.map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: "rgba(240,244,242,0.6)", fontWeight: 300 }}>{f.count} cards — {f.subject}: {f.topic}</div>
              ))}
            </div>
          )}

          {/* Focus for next */}
          {summaryData.focusForNext && (
            <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(239,159,39,0.06)", border: "1px solid rgba(239,159,39,0.2)", marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "rgba(239,159,39,0.7)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Next Session Focus</div>
              <div style={{ fontSize: 13, color: "rgba(240,244,242,0.7)", lineHeight: 1.6, fontWeight: 300 }}>{summaryData.focusForNext}</div>
            </div>
          )}

          {/* Motivational note */}
          {summaryData.motivationalNote && (
            <div style={{ padding: "16px 20px", borderRadius: 12, background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(167,139,250,0.05))", border: "1px solid rgba(167,139,250,0.2)", marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 14, color: "#f0f4f2", fontFamily: "'Instrument Serif', serif", lineHeight: 1.6, fontStyle: "italic" }}>"{summaryData.motivationalNote}"</div>
              <div style={{ fontSize: 11, color: "rgba(167,139,250,0.5)", marginTop: 6 }}>— Summit Coach</div>
            </div>
          )}

          <button onClick={onClose} style={{ width: "100%", padding: "14px", borderRadius: 12, background: "linear-gradient(135deg, #a78bfa, #7c3aed)", border: "none", color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 16px rgba(124,58,237,0.3)" }}>
            Done — close session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "linear-gradient(180deg, rgba(124,58,237,0.04) 0%, transparent 40%)" }}>
      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: "0 0 20px rgba(124,58,237,0.15)" }}>✦</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#f0f4f2" }}>Summit Coach</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
              <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 300 }}>{daysUntil !== null ? `${daysUntil} days until first exam` : "Elite · Personal tutor"}</div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {drillScore.total > 0 && (
            <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", color: "#c4b5fd", fontWeight: 500 }}>
              {drillScore.correct}/{drillScore.total} marks
            </div>
          )}
          <button onClick={() => { setSetupStep("q1"); setSetupData({}); setMessages([]); setHistory([]); }} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(167,139,250,0.2)", background: "transparent", color: "rgba(167,139,250,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Redo setup</button>
          <button onClick={handleEndSession} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(240,244,242,0.1)", background: "transparent", color: "rgba(240,244,242,0.35)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>End session</button>
          <button onClick={handleClose} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>×</button>
        </div>
      </div>

      {/* Context strip */}
      <div style={{ padding: "8px 24px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", gap: 16, overflowX: "auto", flexShrink: 0, alignItems: "center" }}>
        {subjects.slice(0, 3).map(s => {
          const conf = confidence[s];
          const color = !conf ? "rgba(167,139,250,0.3)" : conf <= 2 ? "#E24B4A" : conf === 3 ? "#EF9F27" : "#1D9E75";
          return (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "rgba(240,244,242,0.35)", fontWeight: 300, whiteSpace: "nowrap", flexShrink: 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: color, flexShrink: 0 }} />
              {s} {conf && <span style={{ color, fontWeight: 500 }}>{conf}/5</span>}
            </div>
          );
        })}
        {weakTopics.length > 0 && <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", whiteSpace: "nowrap", flexShrink: 0 }}>📉 {weakTopics.length} weak topics tracked</div>}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", gap: 10, flexDirection: msg.from === "user" ? "row-reverse" : "row", animation: "fadeUp 0.3s ease" }}>
            {msg.from === "coach" && (
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, fontSize: 14 }}>✦</div>
            )}
            <div style={{ maxWidth: "82%", display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Drill result card */}
              {msg.isDrillResult ? (
                <div style={{ padding: "16px 20px", borderRadius: "3px 14px 14px 14px", background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(167,139,250,0.05))", border: "1px solid rgba(167,139,250,0.25)", minWidth: 260 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid rgba(167,139,250,0.15)" }}>
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(167,139,250,0.6)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase" }}>Q{msg.drillResult.qNum} — {msg.drillResult.topic}</div>
                      <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", marginTop: 2 }}>{msg.drillResult.subject}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 22, fontFamily: "'Instrument Serif', serif", color: msg.drillResult.awarded >= msg.drillResult.available * 0.7 ? "#1D9E75" : msg.drillResult.awarded >= msg.drillResult.available * 0.4 ? "#EF9F27" : "#E24B4A", fontWeight: 700 }}>{msg.drillResult.awarded}/{msg.drillResult.available}</div>
                      <div style={{ fontSize: 10, color: "rgba(240,244,242,0.3)" }}>marks</div>
                    </div>
                  </div>
                  {/* Mark bullets */}
                  {(msg.drillResult.bulletFeedback || []).map((b, bi) => (
                    <div key={bi} style={{ fontSize: 12, color: "rgba(240,244,242,0.7)", lineHeight: 1.6, marginBottom: 4, fontWeight: 300 }}>{b}</div>
                  ))}
                  {/* A-grade response */}
                  {msg.drillResult.aGradeResponse && (
                    <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)" }}>
                      <div style={{ fontSize: 10, color: "rgba(29,158,117,0.7)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>A-Grade Response</div>
                      <div style={{ fontSize: 12, color: "rgba(240,244,242,0.7)", lineHeight: 1.6, fontWeight: 300 }}>{msg.drillResult.aGradeResponse}</div>
                    </div>
                  )}
                  {/* Coach note */}
                  {msg.drillResult.coachNote && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#c4b5fd", fontStyle: "italic", lineHeight: 1.5 }}>✦ {msg.drillResult.coachNote}</div>
                  )}
                  {/* Remove weakness offer — show if scored ≥70% on a tracked weak topic */}
                  {msg.drillResult.awarded >= msg.drillResult.available * 0.7 &&
                    (profile?.weak_topics || []).some(t => t.topic === msg.drillResult.topic && t.subject === msg.drillResult.subject) &&
                    !msg.weaknessRemoved && (
                    <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(29,158,117,0.06)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 12, color: "rgba(240,244,242,0.6)", fontWeight: 300, lineHeight: 1.4 }}>
                        🎉 <strong style={{ color: "#4DC9A0" }}>{msg.drillResult.topic}</strong> is flagged as a weakness — remove it?
                      </div>
                      <button onClick={async () => {
                        const updated = (profile?.weak_topics || []).filter(t => !(t.topic === msg.drillResult.topic && t.subject === msg.drillResult.subject));
                        await supabase.from("profiles").update({ weak_topics: updated }).eq("id", session.user.id);
                        setProfile(prev => ({ ...prev, weak_topics: updated }));
                        setMessages(prev => prev.map((m, mi) => mi === i ? { ...m, weaknessRemoved: true } : m));
                      }} style={{ flexShrink: 0, fontSize: 11, padding: "5px 12px", background: "rgba(29,158,117,0.15)", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 6, color: "#4DC9A0", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, whiteSpace: "nowrap" }}>
                        Remove ✓
                      </button>
                    </div>
                  )}
                  {msg.weaknessRemoved && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#4DC9A0", fontWeight: 500 }}>✓ Removed from your weaknesses</div>
                  )}
                </div>
              ) : msg.isFlashcardSuccess ? (
                <div style={{ padding: "14px 18px", borderRadius: "3px 14px 14px 14px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 22 }}>🃏</div>
                  <div>
                    <div style={{ fontSize: 13, color: "#f0f4f2", fontWeight: 500 }}>{msg.flashcardInfo.count} flashcards created</div>
                    <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginTop: 2 }}>{msg.flashcardInfo.subject} — {msg.flashcardInfo.topic}</div>
                    <div style={{ fontSize: 11, color: "#a78bfa", marginTop: 4 }}>Saved to your Flashcards ✓</div>
                  </div>
                </div>
              ) : msg.isMockResult ? (
                <div style={{ padding: "16px 20px", borderRadius: "3px 14px 14px 14px", background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(167,139,250,0.05))", border: "1px solid rgba(167,139,250,0.25)", color: "#f0f4f2", fontSize: 13, lineHeight: 1.8, fontWeight: 300 }}>
                  {msg.text.split("\n").map(function(line, j) {
                    var t = line.trim();
                    if (!t) return null;
                    if (t.startsWith("[A]") || t.startsWith("[B]") || t.startsWith("[C]") || t.startsWith("[D]")) return null;
                    if (t === "---") return <hr key={j} style={{ opacity: 0.15, margin: "10px 0", border: "none", borderTop: "1px solid rgba(167,139,250,0.3)" }} />;
                    var isScore = /^Q\d+:/.test(t);
                    var isTotal = t.startsWith("TOTAL:") || t.startsWith("GRADE:") || t.startsWith("STRONGEST") || t.startsWith("WEAKEST");
                    var html = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
                    return <p key={j} style={{ margin: "0 0 5px 0", color: isTotal ? "#c4b5fd" : isScore ? "#f0f4f2" : "rgba(240,244,242,0.8)", fontWeight: isTotal ? 500 : 300 }} dangerouslySetInnerHTML={{ __html: html || "&nbsp;" }} />;
                  })}
                </div>
              ) : (
                <div style={{ padding: "12px 16px", borderRadius: msg.from === "user" ? "14px 3px 14px 14px" : "3px 14px 14px 14px", background: msg.from === "user" ? "linear-gradient(135deg, #1D9E75, #16875f)" : "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(167,139,250,0.06))", border: msg.from === "user" ? "none" : "1px solid rgba(167,139,250,0.2)", color: msg.from === "user" ? "white" : "#f0f4f2", fontSize: 13.5, lineHeight: 1.8, fontWeight: 300 }}>
                  {msg.text.split("\n").map(function(line, j) {
                    var t = line.trim();
                    if (!t) return null;
                    if (t.startsWith("[A]") || t.startsWith("[B]") || t.startsWith("[C]") || t.startsWith("[D]")) return null;
                    if (t === "---") return <hr key={j} style={{ opacity: 0.15, margin: "8px 0", border: "none", borderTop: "1px solid rgba(167,139,250,0.3)" }} />;
                    if (t.startsWith("GENERATE_MOCK_PAPER:") || t.startsWith("GENERATE_DRILL_QUESTION:") || t.startsWith("OFFER_FLASHCARDS:")) return null;
                    var html = line.replace(/^#+\s*/, "").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
                    return <p key={j} style={{ margin: "0 0 5px 0" }} dangerouslySetInnerHTML={{ __html: html || "&nbsp;" }} />;
                  })}
                </div>
              )}
              {/* Option buttons */}
              {msg.from === "coach" && msg.options && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 4 }}>
                  {msg.options.map(function(opt, oi) {
                    return (
                      <button key={oi} onClick={function() { sendCoachMsg(opt); }}
                        style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid rgba(167,139,250,0.25)", background: "rgba(167,139,250,0.06)", color: "#c4b5fd", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 10 }}
                        onMouseOver={e => { e.currentTarget.style.background = "rgba(167,139,250,0.14)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.5)"; e.currentTarget.style.color = "#f0f4f2"; e.currentTarget.style.transform = "translateX(2px)"; }}
                        onMouseOut={e => { e.currentTarget.style.background = "rgba(167,139,250,0.06)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.25)"; e.currentTarget.style.color = "#c4b5fd"; e.currentTarget.style.transform = "translateX(0)"; }}>
                        <span style={{ width: 22, height: 22, borderRadius: 6, background: "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(124,58,237,0.15))", border: "1px solid rgba(167,139,250,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#a78bfa", flexShrink: 0 }}>{String.fromCharCode(65 + oi)}</span>
                        {opt.replace(/\*\*/g, "").replace(/\*/g, "")}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Flashcard offer card */}
        {flashcardOffer && (
          <div style={{ display: "flex", gap: 10, animation: "fadeUp 0.3s ease" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, fontSize: 14 }}>✦</div>
            <div style={{ padding: "14px 18px", borderRadius: "3px 14px 14px 14px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)", maxWidth: "82%" }}>
              <div style={{ fontSize: 13, color: "#f0f4f2", marginBottom: 10, lineHeight: 1.5 }}>
                Want me to generate <strong>flashcards</strong> for <span style={{ color: "#c4b5fd" }}>{flashcardOffer.topic}</span>? I'll save 8 cards straight to your Flashcards.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => generateCoachFlashcards(flashcardOffer.subject, flashcardOffer.topic)} disabled={generatingFlashcards}
                  style={{ padding: "8px 16px", borderRadius: 8, background: "linear-gradient(135deg, #a78bfa, #7c3aed)", border: "none", color: "white", fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}>
                  {generatingFlashcards ? "Generating..." : "Yes — generate flashcards 🃏"}
                </button>
                <button onClick={() => setFlashcardOffer(null)}
                  style={{ padding: "8px 12px", borderRadius: 8, background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(240,244,242,0.35)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
                  Not now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drill question card */}
        {drillState && drillState.phase === "answering" && (
          <div style={{ display: "flex", gap: 10, animation: "fadeUp 0.3s ease" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, fontSize: 14 }}>✦</div>
            <div style={{ flex: 1, maxWidth: "90%" }}>
              {/* SQA-style question card */}
              <div style={{ background: "white", borderRadius: "3px 14px 4px 4px", overflow: "hidden", marginBottom: 8 }}>
                <div style={{ padding: "8px 16px", background: "#1a1a2e", borderBottom: "2px solid #000" }}>
                  <div style={{ fontSize: 10, color: "rgba(240,244,242,0.5)", letterSpacing: 1, textTransform: "uppercase" }}>Summit Coach — {drillState.level} {drillState.subject}</div>
                </div>
                <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, fontWeight: 700, color: "#000", marginRight: 8 }}>{drillState.qNum}.</span>
                    <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, color: "#111", lineHeight: 1.7 }}>{drillState.question}</span>
                  </div>
                  <div style={{ flexShrink: 0, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, fontWeight: 700, color: "#000", textAlign: "right" }}>{drillState.marks}</div>
                </div>
              </div>
              {/* Answer box */}
              <textarea value={drillAnswer} onChange={e => setDrillAnswer(e.target.value)}
                placeholder="Write your answer here..."
                style={{ width: "100%", minHeight: 90, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(167,139,250,0.25)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(167,139,250,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
              <button onClick={submitDrillAnswer} disabled={!drillAnswer.trim() || markingDrill}
                style={{ marginTop: 6, width: "100%", padding: "11px", background: drillAnswer.trim() && !markingDrill ? "linear-gradient(135deg, #a78bfa, #7c3aed)" : "rgba(255,255,255,0.05)", color: drillAnswer.trim() && !markingDrill ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: drillAnswer.trim() && !markingDrill ? "pointer" : "default", boxShadow: drillAnswer.trim() && !markingDrill ? "0 4px 16px rgba(124,58,237,0.3)" : "none" }}>
                {markingDrill ? "Marking..." : "Submit for marking →"}
              </button>
            </div>
          </div>
        )}

        {/* Drill generating state */}
        {drillState && drillState.phase === "generating" && (
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14 }}>✦</div>
            <div style={{ padding: "12px 18px", background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: "3px 14px 14px 14px", display: "flex", gap: 8, alignItems: "center" }}>
              {[0,1,2].map(idx => <div key={idx} style={{ width: 5, height: 5, borderRadius: "50%", background: "#a78bfa", animation: "bounce 1s infinite", animationDelay: `${idx*0.15}s` }} />)}
              <span style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", marginLeft: 4 }}>Writing your question...</span>
            </div>
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14 }}>✦</div>
            <div style={{ padding: "12px 18px", background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: "3px 14px 14px 14px", display: "flex", gap: 5, alignItems: "center" }}>
              {[0,1,2].map(idx => <div key={idx} style={{ width: 5, height: 5, borderRadius: "50%", background: "#a78bfa", animation: "bounce 1s infinite", animationDelay: `${idx*0.15}s` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick prompts */}
      {savedMockState && (
        <div style={{ margin: "0 24px 8px", padding: "10px 16px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: "#c4b5fd", fontWeight: 500 }}>
            ✦ Mock paper in progress — {savedMockState.level} {savedMockState.subject}
          </div>
          <button onClick={() => setMockState(savedMockState)} style={{ fontSize: 11, padding: "4px 12px", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", border: "none", borderRadius: 6, color: "white", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>
            Resume →
          </button>
        </div>
      )}
      {messages.length <= 2 && (
        <div style={{ padding: "0 24px 12px", display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0 }}>
          {QUICK_PROMPTS.map(p => (
            <button key={p} onClick={() => sendCoachMsg(p)}
              style={{ fontSize: 11, padding: "6px 12px", borderRadius: 16, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.05)", color: "#c4b5fd", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}
              onMouseOver={e => { e.currentTarget.style.background = "rgba(167,139,250,0.12)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.4)"; }}
              onMouseOut={e => { e.currentTarget.style.background = "rgba(167,139,250,0.05)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.2)"; }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: "10px 24px 20px", borderTop: "1px solid rgba(167,139,250,0.08)", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder="Ask your coach anything..." rows={2}
            style={{ flex: 1, resize: "none", padding: "10px 14px", fontSize: 13, borderRadius: 10, border: "1px solid rgba(167,139,250,0.2)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, background: "rgba(167,139,250,0.04)", color: "#f0f4f2", outline: "none", transition: "border-color 0.2s" }}
            onFocus={e => e.target.style.borderColor = "rgba(167,139,250,0.5)"}
            onBlur={e => e.target.style.borderColor = "rgba(167,139,250,0.2)"} />
          <button onClick={() => sendCoachMsg(input)} disabled={loading || !input.trim()}
            style={{ width: 40, height: 40, borderRadius: "50%", background: input.trim() && !loading ? "linear-gradient(135deg, #a78bfa, #7c3aed)" : "rgba(255,255,255,0.05)", border: "none", cursor: input.trim() && !loading ? "pointer" : "default", color: "white", fontSize: 16, alignSelf: "flex-end", transition: "all 0.2s", boxShadow: input.trim() && !loading ? "0 4px 16px rgba(124,58,237,0.3)" : "none" }}>
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}

function MockDisclaimer({ onDismiss }) {
  return (
    <div style={{ padding: "10px 14px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.22)", borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>⚠️</span>
      <div style={{ fontSize: 12, color: "rgba(240,244,242,0.55)", lineHeight: 1.65, fontWeight: 300, flex: 1 }}>
        <span style={{ color: "#EF9F27", fontWeight: 500 }}>AI-generated paper. </span>
        Questions reflect SQA style but may never appear in an official exam. Always supplement with real past papers from the SQA website.
      </div>
      {onDismiss && <button onClick={onDismiss} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.25)", cursor: "pointer", fontSize: 18, flexShrink: 0, padding: 0, lineHeight: 1 }}>×</button>}
    </div>
  );
}

// ── Mock Exam Panel ───────────────────────────────────────────────────
// ── SQA English prescribed texts ─────────────────────────────────────
const ENGLISH_PRESCRIBED = {
  "Higher": {
    Drama: [
      "Yellow Moon — David Greig",
      "Sailmaker — Alan Spence",
      "Tally's Blood — Ann Marie di Mambro",
      "Sequamur — Donald S Murray",
      "Men Should Weep — Ena Lamont Stewart",
      "The Slab Boys — John Byrne",
      "The Cheviot, the Stag and the Black, Black Oil — John McGrath"
    ],
    Prose: [
      "Dr Jekyll and Mr Hyde — Robert Louis Stevenson",
      "Short Stories — Iain Crichton Smith (The Red Door, Mother and Son, The Painter, The Existence of the Hermit)",
      "Scottish Short Stories — A Voice Spoke to me at Night (Helen McClory)",
      "Scottish Short Stories — Things My Wife and I Found Hidden in Our House (Kirsty Logan)",
      "Scottish Short Stories — Andrina (George Mackay Brown)",
      "Scottish Short Stories — Death In A Nut (Duncan Williamson)"
    ],
    Poetry: [
      "Carol Ann Duffy","Norman MacCaig","Jackie Kay","Edwin Morgan",
      "Liz Lochhead","Don Paterson","Imtiaz Dharker","Kathleen Jamie",
      "Robert Burns","Sorley MacLean","Higher Scottish Poetry Collection"
    ]
  },
  "National 5": {
    Drama: [
      "Yellow Moon — David Greig",
      "Sailmaker — Alan Spence",
      "Tally's Blood — Ann Marie di Mambro",
      "Sequamur — Donald S Murray",
      "Men Should Weep — Ena Lamont Stewart",
      "The Slab Boys — John Byrne"
    ],
    Prose: [
      "Dr Jekyll and Mr Hyde — Robert Louis Stevenson",
      "Duck Feet (Part Wan First Year) — Ely Percy",
      "Short Stories — Anne Donovan (All That Glisters, Hieroglyphics, Me and My Babbie, Loast)",
      "Scottish Short Stories — A Voice Spoke to me at Night (Helen McClory)",
      "Scottish Short Stories — Things My Wife and I Found Hidden in Our House (Kirsty Logan)",
      "Scottish Short Stories — Andrina (George Mackay Brown)",
      "Scottish Short Stories — Death In A Nut (Duncan Williamson)"
    ],
    Poetry: [
      "Carol Ann Duffy",
      "Norman MacCaig",
      "Jackie Kay",
      "Edwin Morgan",
      "N5 Scottish Poetry Collection"
    ]
  }
};

// ── Full hardcoded poem texts ─────────────────────────────────────────
const POEMS = {
  "Carol Ann Duffy": {
    "Medusa": `A suspicion, a doubt, a jealousy
grew in my mind,
which turned the hairs on my head to filthy snakes,
as though my thoughts
hissed and spat on my scalp.

My bride's breath soured, stank
in the grey bags of my lungs.
I'm foul mouthed now, foul tongued,
yellow fanged.
There are bullet tears in my eyes.
Are you terrified?

Be terrified.
It's you I love,
perfect man, Greek God, my own;
but I know you'll go, betray me, stray
from home.
So better by far for me if you were stone.

I glanced at a buzzing bee,
a dull grey pebble fell
to the ground.
I glanced at a singing bird,
a handful of dusty gravel
spattered down.

I looked at a ginger cat,
a housebrick
shattered a bowl of milk.
I looked at a snuffling pig,
a boulder rolled
in a heap of shit.

I stared in the mirror.
Love gone bad
showed me a Gorgon.
I stared at a dragon.
Fire spewed
from the mouth of a mountain.

And here you come
with a shield for a heart
and a sword for a tongue
and your girls, your girls.
Wasn't I beautiful?
Wasn't I fragrant and young?

Look at me now.`,

    "Originally": `We came from our own country in a red room
which fell through the fields, our mother singing
our father's name to the turn of the wheels.
My brothers cried, one of them bawling, Home,
Home, as the miles rushed back to the city,
the street, the house, the vacant rooms
where we didn't live any more. I stared
at the eyes of a blind toy, holding its paw.

All childhood is an emigration. Some are slow,
leaving you standing, resigned, up an avenue
where no one you know stays. Others are sudden.
Your accent wrong. Corners, which seem familiar,
leading to unimagined pebble-dashed estates, big boys
eating worms and shouting words you don't understand.
My parents' anxiety stirred like a loose tooth
in my head. I want our own country, I said.

But then you forget, or don't recall, or change,
and, seeing your brother swallow a slug, feel only
a skelf of shame. I remember my tongue
shedding its skin like a snake, my voice
in the classroom sounding just like the rest.
Do I only think I lost a river, culture, speech,
sense of first space and the right place? Now,
Where do you come from? strangers ask. Originally? And I hesitate.`,

    "Mrs Midas": `It was late September. I'd just poured a glass of wine, begun
to unwind, while the vegetables cooked. The kitchen filled with the smell
of itself, relaxed, its steamy breath gently blanching the windows. So I opened one,
then with my fingers wiped the other's glass like a brow. He was standing under the pear tree snapping a twig.

Now the garden was long and the visibility poor, the way the dark of the ground seems to drink the light of the sky,
but that twig in his hand was gold. And then he plucked a pear from a branch —
we grew Fondante d'Automne — and it sat in his palm, like a lightbulb. On.
I thought to myself, Is he putting fairy lights in the tree?

He came into the house. The doorknobs gleamed. He drew the blinds.
You know the mind; I thought of the Field of the Cloth of Gold and of Miss Macready.
He sat in that chair like a king on a burnished throne. The look on his face was strange, wild, vain.
I said, What in the name of God is going on? He started to laugh.

I served up the meal. For starters, corn on the cob.
Within seconds he was spitting out the teeth of the rich.
He toyed with his spoon, then mine, then with the knives, the forks.
He asked where was the wine. I poured with a shaking hand,
a fragrant, bone-dry white from Italy, then watched as he picked up the glass, goblet, golden chalice, drank.

It was then that I started to scream. He sank to his knees.
After we'd both calmed down, I finished the wine on my own, hearing him out.
I made him sit on the other side of the room and keep his hands to himself.
I locked the cat in the cellar. I moved the phone. The toilet I didn't mind.
I couldn't believe my ears: how he'd had a wish. Look, we all have wishes; granted.
But who has wishes granted? Him. Do you know about gold?
It feeds no one; aurum, soft, untarnishable; slakes no thirst.
He tried to light a cigarette; I gazed, entranced, as the blue flame played on its luteous stem.
At least, I said, you'll be able to give up smoking for good.

Separate beds. In fact, I put a chair against my door, near petrified.
He was below, turning the spare room into the tomb of Tutankhamun.
You see, we were passionate then, in those halcyon days;
unwrapping each other, rapidly, like presents, fast food.
But now I feared his honeyed embrace, the kiss that would turn my lips to a work of art.

And who, when it comes to the crunch, can live with a heart of gold?
That night, I dreamt I bore his child, its perfect ore limbs,
its little tongue like a precious latch, its amber eyes holding their pupils like flies.
My dream milk burned in my breasts. I woke to the streaming sun.

So he had to move out. We'd a caravan in the wilds, in a glade of its own.
I drove him up under the cover of dark. He sat in the back.
And then I came home, the woman who married the fool who wished for gold.
At first, I visited, odd times, parking the car a good way off, then walking.

You knew you were getting close. Golden trout on the grass.
One day, a hare hung from a larch, a beautiful lemon mistake.
And then his footprints, glistening next to the river's path.
He was thin, delirious; hearing, he said, the music of Pan from the woods. Listen.
That was the last straw.

What gets me now is not the idiocy or greed but lack of thought for me.
Pure selfishness. I sold the contents of the house and came down here.
I think of him in certain lights, dawn, late afternoon, and once a bowl of apples stopped me dead.
I miss most, even now, his hands, his warm hands on my skin, his touch.`,

    "Before You Were Mine": `I'm ten years away from the corner you laugh on
with your pals, Maggie McGeeney and Jean Duff.
The three of you bend from the waist, holding
each other, or your knees, and shriek at the pavement.
Your polka-dot dress blows round your legs. Marilyn.

I'm not here yet. The thought of me doesn't occur
in the ballroom with the thousand eyes, the fizzy, movie tomorrows
the right walk home could bring. I knew you would dance
like that. Before you were mine, your Ma stands at the close
with a hiding for the late one. You reckon it's worth it.

The decade ahead of my loud, possessive yell was the best one, eh?
I remember my hands in those high-heeled red shoes, relics,
and now your ghost clatters toward me over George Square
till I see you, clear as scent, under the tree,
with its lights, and whose small bites on your neck, sweetheart?

Cha cha cha! You'd teach me the steps on the way home from Mass,
stamping stars from the wrong pavement. Even then
I wanted the bold girl winking in Portobello, somewhere
in Scotland, before I was born. That glamorous love lasts
where you sparkle and waltz and laugh before you were mine.`,

    "Havisham": `Beloved sweetheart bastard. Not a day since then
I haven't wished him dead. Prayed for it
so hard I've dark green pebbles for eyes,
ropes on the back of my hands I could strangle with.

Spinster. I stink and remember. Whole days
in bed cawing Nooooo at the wall; the dress
yellowing, trembling if I open the wardrobe;
the slewed mirror, full-length, her, myself, who did this

to me? Puce curses that are sounds not words.
Some nights better, the lost body over me,
my fluent tongue in its mouth in its ear
then down till I suddenly bite awake. Love's

hate behind a white veil; a red balloon bursting
in my face. Bang. I stabbed at a wedding cake.
Give me a male corpse for a long slow honeymoon.
Don't think it's only the heart that b-b-b-breaks.`,

    "In Mrs Tilscher's Class": `You could travel up the Blue Nile
with your finger, tracing the route
while Mrs Tilscher chanted the scenery.
Tana. Ethiopia. Khartoum. Aswân.
That for an hour, then a skittle of milk
and the chalky Pyramids rubbed into dust.
A window opened with a long pole.
The laugh of a bell swung by a running child.

This was better than home. Enthralling books.
The classroom glowed like a sweet shop.
Sugar paper. Coloured shapes. Brady and Hindley
faded, like the faint, uneasy smudge of a mistake.
Mrs Tilscher loved you. Some mornings, you found
she'd left a good gold star by your name.
The scent of a pencil slowly, carefully, shaved.
A xylophone's nonsense heard from another form.

Over the Easter term, the inky tadpoles
changed from commas into exclamation marks.
Three frogs hopped in the playground, freed
by a dunce, followed by a line of kids, jumping
and croaking away from the lunch queue.
A rough boy told you how you were born.
You kicked him, but stared at your parents, appalled,
when you got back home.

That feverish July, the air tasted of electricity.
A tangible alarm made you always untidy, hot,
fractious under the heavy, sexy sky. You asked her
how you were born and Mrs Tilscher smiled,
then turned away. Reports were handed out.
You ran through the gates, impatient to be grown,
as the sky split open into a thunderstorm.`
  },

  "Norman MacCaig": {
    "Basking Shark": `To stub an oar on a rock where none should be,
To have it rise with a slounge out of the sea
Is a thing that happened once (too often) to me.

But not too often — though enough. I count as gain
That once I met, on a sea tin-tacked with rain,
That roomsized monster with a matchbox brain.

He displaced more than water. He shoggled me
Centuries back — this decadent townee
Shook on a wrong branch of his family tree.

Swish up the dirt and, when it settles, a spring
Is all the clearer. I saw me, in one fling,
Emerging from the slime of everything.

So who's the monster? The thought made me grow pale
For twenty seconds while, sail after sail,
The tall fin slid away and then the tail.`,

    "Aunt Julia": `Aunt Julia spoke Gaelic very loud and very fast.
I could not answer her —
I could not understand her.

She wore men's boots when she wore any.
— I can see her strong foot,
stained with peat,
paddling with the treadle of the spinning-wheel
while her right hand drew yarn
marvellously out of the air.

Hers was the only house where I've lain at night
in the absolute darkness of a box bed,
listening to crickets being friendly.

She was buckets and water flouncing into them.
She was winds pouring wetly round house-ends.
She was brown eggs, black skirts
and a keeper of threepenny-bits
in a teapot.

Aunt Julia spoke Gaelic very loud and very fast.
By the time I had learned a little, she lay
silenced in the absolute black
of a sandy grave at Luskentyre.
But I hear her still, welcoming me
with a seagull's voice across a hundred yards
of peatscrapes and lazybeds and getting angry, getting angry
with so many questions unanswered.`,

    "Hotel Room, 12th Floor": `This morning I watched from here
a helicopter skirting like a damaged insect
the Empire State Building, that jumbo size
dentist's drill, and landing on the roof
of the PanAm skyscraper. But now midnight
has come in from foreign places. Its uncivilised darkness
is shot at by a million lit windows, all
ups and acrosses.

But midnight is not so easily defeated.
I lie in bed, between a radio and a television set,
and hear the wildest of warwhoops continually
ululating through the glittering canyons and gulches—
police cars and ambulances racing to the broken bones,
the harsh screaming from coldwater flats,
the blood glazed on sidewalks.

The frontier is never somewhere else.
And no stockades can keep the midnight out.`,

    "On Lachie's Croft": `On Lachie's croft the cock stands
under the wheelbarrow. What's wrong? — He's bedraggled.
Where are his military elegance,
his gauleiter manners, his insufferable conceit?
I'll call him rooster, it seems more fitting.

I, too, feel bedraggled and haphazard; something
has filched my compass, I'm breathing black air.
I look at that rooster, I look at me.
His hens scratch the ground, step back
and peer at the scratches. They make
motherly sounds, so cosy, so fireside.

But he opens his gummy eyes, looks at me
and utters, no tortured trumpet call,
but a barren croak.

I breathe black air, I poke at
my rumpled feathers, I can't stand on tiptoe.
How I miss my cosy brown hens.
How I miss their motherly clucking.
I'm master of nothing I survey.`,

    "Landscape and I": `Landscape and I get on together well.
Though I'm the talkative one, still he can tell
His symptoms of being to me, the way a shell
Murmurs of oceans.

Loch Rannoch lapses dimpling in the sun.
Its hieroglyphs of light fade one by one
But re-create themselves, their message done,
For ever and ever.

That sprinkling lark jerked upward in the blue
Will daze to nowhere but leave himself in true
Translation — hear his song cascading through
His disappearance.

The hawk knows all about it, shaking there
An empty glove on steep chutes of the air
Till his yellow foot cramps on a squeal, to tear
Smooth fur, smooth feather.

This means, of course, Schiehallion in my mind
Is more than mountain. In it he leaves behind
A meaning, an idea, like a hind
Couched in a corrie.

So then I'll woo the mountain till I know
The meaning of the meaning, no less. Oh,
There's a Schiehallion anywhere you go.
The thing is, climb it.`,

    "Old Highland Woman": `She sits all day by the fire.
How long is it since she opened the door
and stepped outside, confusing
the scuffling hens and the collie
dreaming of sheep?
Her walking days are over.

She has come through centuries
of Gaelic labour and loves
and rainy funerals. Her people
are assembled in her bones.
She's their summation. Before her time
has almost no meaning.

When neighbours call
she laughs a wicked cackle
with love in it, as she listens
to the sly bristle of gossip,
relishing the life in it,
relishing the malice, with her hands
lying in her lap like holy psalms
that once had a meaning for her, that once
were noble with tunes
she used to sing long ago.`
  },

  "Jackie Kay": {
    "Gap Year": `I
I remember your Moses basket before you were born.
I'd stare at the fleecy white sheet for days, weeks,
willing you to arrive, hardly able to believe
I would ever have a real baby to put in the basket.
I'd feel the mound of my tight tub of a stomach,
and you moving there, foot against my heart,
elbow in my ribcage, turning, burping, awake, asleep.
One time I imagined I felt you laugh.

I'd play you Handel's Water Music or Emma Kirkby
singing Pergolesi. I'd talk to you, my close stranger,
call you Tumshie, ask when you were coming to meet me.
You arrived late, the very hot summer of eighty-eight.
You had passed the due date string of eights,
and were pulled out with forceps, blue, floury,
on the fourteenth of August on Sunday afternoon.
I took you home on Monday and lay you in your basket.

II
Now, I peek in your room and stare at your bed
hardly able to imagine you back in there sleeping,
Your handsome face — soft, open. Now you are eighteen,
six foot two, away, away in Costa Rica, Peru, Bolivia.

I follow your trails on my Times Atlas:
from the Caribbean side of Costa Rica to the Pacific,
the baby turtles to the massive leatherbacks.
Then on to Lima, to Cuzco. Your grandfather
rings: 'Have you considered altitude sickness,
Christ, he's sixteen thousand feet above sea level.'
Then to the lost city of the Incas, Macchu Picchu,
where you take a photograph of yourself
with the statue of the original Tupac.
You are wearing a Peruvian hat.

Yesterday in Puno before catching the bus for Copacabana,
you suddenly appear on a webcam and blow me a kiss,
you have a new haircut; your face is grainy, blurry.
Seeing you, shy, smiling, on the webcam
reminds me of the second scan at twenty weeks,
how at that fuzzy moment back then,
you were lying cross-legged with an index finger
resting sophisticatedly on one cheek.

You started the Inca trail in Arctic conditions
and ended up in subtropical.
Now you plan the Amazon in Bolivia.
Your grandfather rings again to say
'There's three warring factions in Bolivia, warn him
against it. He canny see everything. Tell him to come home.'
But you say all the travellers you meet rave about Bolivia.
You want to see the Salar de Uyuni, the world's largest salt-flats,
the Amazonian rainforest.

And now you are not coming home till four weeks after your due date.
After Bolivia, you plan to stay with a friend's Auntie in Argentina.
Then — to Chile where you'll stay with friends of Diane's.
And maybe work for the Victor Jara Foundation.
I feel like a home-alone mother; all the lights have gone out in the hall,
and now I am wearing your large black slippers, flip-flopping
into your empty bedroom, trying to imagine you in your bed.
I stare at the photos you send by messenger:
you on the top of the world, arms outstretched, eager.
Blue sky, white snow; you by Lake Tararhua, beaming.

My heart soars like the birds in your bright blue skies.
My love glows like the sunrise over the lost city.
I sing along to Ella Fitzgerald, A tisket A tasket.
I have a son out in the big wide world.
A flip and a skip ago, you were dreaming in your basket.`,

    "Keeping Orchids": `The orchids my mother gave me when we first met are still alive, twelve days later. Although
some of the buds remain closed as secrets. Twice since I carried them back, like a baby in a shawl,
from her train station to mine, then home. Twice since then the whole glass carafe has crashed
falling over, unprovoked, soaking my chest of drawers. All the broken waters. I have rearranged
the upset orchids with troubled hands. Even after that the closed ones did not open out. The skin
shut like an eye in the dark; the closed lid. Twelve days later, my mother's hands are all I have.
Her face is fading fast. Even her voice rushes through a tunnel the other way from home.
I close my eyes and try to remember exactly: a paisley pattern scarf, a brooch, a navy coat.
A digital watch her daughter was wearing when she died. Now they hang their heads,
and suddenly grow old — the proof of meeting. Still, her hands, awkward and hard to hold
fold and unfold a green carrier bag as she tells the story of her life. Compressed. Airtight.
A sad square, then a crumpled shape. A bag of tricks. Her secret life — a hidden album, a box of love letters.
A door opens and closes. Time is outside waiting. I catch the draught in my winter room.
Airlocks keep the cold air out. Boiling water makes flowers live longer. So does cutting the stems with a sharp knife.`,

    "Whilst Leila Sleeps": `I am moving in the dead of night, packing things, turning out lights.
My fingers tie knots like fish nets.
I want to be in my mother's house but she is all the way over
the other side of the world.
Boxes; I can't see out of the back window.
Leila is a bundle in her car seat. Her small mouth hanging open.
Maybe it is not innocence after all, it could be the sleep of oblivion.
My headlights are paranoic eyes sweeping the streets for — what?
A split second before they appeared I thought I was safe.
What is that fear. Does it have a name.
They want my name. Their smiles tighten my stomach.
I bite on my tongue, hard. Their faces. I have no witness.
They take my licence, my papers.
Now there is nothing left but to go with the men in plain suits.
Leila stirs and opens her eyes wide.
I try and say something to soothe. My voice is a house with the roof blown off.
What do I tell my daughter —
We are done for. There is a need to worry. I cannot lie to her.
The night dreams my terror; a slow light tails the fast car;
Leila tugs at my coat.
I whisper her cradle song and she holds on.`,

    "Grandpa's Soup": `No one makes soup like my Grandpa's,
with its diced carrots the perfect size
and its diced potatoes the perfect size
and its wee soft bits —
what are their names?
and its big bit of hough,
which rhymes with loch, floating
like a rich island in the middle of the soup sea.

I say, Grandpa, Grandpa your soup is the best
soup in the whole world.
And Grandpa says, Och,
which rhymes with hough and loch,
Och, Don't be daft,
because he's shy about his soup, my Grandpa.
He knows I will grow up and pine for it.
I will fall ill and desperately need it.
I will long for it my whole life after he is gone.
Every soup will become sad and wrong after he is gone.
He knows when I'm older I will avoid soup altogether.
Oh Grandpa, Grandpa, why is your soup so glorious? I say
tucking into my fourth bowl in a day.

Barley! That's the name of the wee soft bits. Barley.`,

    "Darling": `You might forget the exact sound of her voice
or how her face looked when sleeping.
You might forget the sound of her quiet weeping
curled into the shape of a half moon,

when smaller than her self, she seemed already to be leaving
before she left, when the blossom was on the trees
and the sun was out, and all seemed good in the world.
I held her hand and sang a song from when I was a girl —

Heel y'ho boys, let her go boys —
and when I stopped singing she had slipped away,
already a slip of a girl again, skipping off,
her heart light, her face almost smiling.

And what I didn't know or couldn't say then
was that she hadn't really gone.
The dead don't go till you do, loved ones.
The dead are still here holding our hands.`,

    "Maw Broon Visits a Therapist": `Crivens! This is jist typical.
When it comes tae talking aboot me,
well, A' jist clam up. Canny think whit
tae say.

Weel, weel. A'm here because
A' canny hawnle life, ken whit A' mean,
because everything is awfy
and A'm no masell.

A' dinny ken who Maw Broon is anymare.
A' canny remember ma Christian name.
A' remember when A' wis a wean,
folks cried me something.

The idea o' me ever being a bairn
is impossible. A' feel A've aye worn
this same pinnie and this heid scarf
A've got on the noo.

How come you've no got anything tae say?
You've no opened yir mooth.
Whit's wrang. Am A' no daeing it right?
A' dinny ken hoo yir supposed tae dae therapy.

Jings. Dae A' jist talk on like this?
Michty. This is awfy awkward.
You've no said a dickie bird.
Tell you a dream? Crivens,

A've no had a dream since A' wis a wean.
An image? Whit kind of image?
What comes tae mind?
Whit represents whit?

Och. This therapy's making me crabbit.
A' thought this wuid mak me happy.
This is awfy. A' feel unweel.
How dae A' see masell?

Weel. Am fed up wey ma bun.
It is jist a big onion
at the back o' ma heid.
A' canny let ma hair doon.

A'm built like a bothy, hefty.
A'm constantly wabbit and crabbit.
Ma hale faimily taks me for grantit.
A'll aye be the wan tae dae it

whitever it is. Here — A'm quite guid
at this therapy lark eh?
Here, Maw Broon could be a therapist.
Sit there like you are, glaikit,

a box o tissues and a clock,
a few wee emmms and aaas.
Jings, it's money for auld rope.
There that's whit A' feel like —

a tatty auld rope
nibiddy wuid want tae climb
a' twistit and tangled
an, jings, this is exciting

A' could break. A' could jist give in.`
  },

  "Edwin Morgan": {
    "In the Snack-bar": `A cup capsizes along the formica,
slithering with a dull clatter.
A few heads turn in the crowded evening snack-bar.
An old man is trying to get to his feet
from the low round stool fixed to the floor.
Slowly he levers himself up, his hands have no power.
He is up as far as he can get. The dismal hump
looming over him forces his head down.
He stands in his stained beltless gaberdine
like a monstrous animal caught in a tent
in some story. He sways slightly,
the face not seen, bent down in shadow under his cap.
Even on his feet he is staring at the floor
or would be, if he could see. I notice now
his stick, once painted white but scuffed and muddy,
hanging from his right arm. Long blind, hunchback born,
half paralysed he stands fumbling with the stick
and speaks: 'I want — to go to the — toilet.'

It is down two flights of stairs, but we go.
I take his arm. 'Give me — your arm — it's better,' he says.
Inch by inch we drift towards the stairs.
A few yards of floor are like a landscape to be negotiated,
in the slow setting out time has almost stopped.
I concentrate my life to his: crunch of spilt sugar,
slidy puddle from the night's umbrellas,
table edges, people's feet,
hiss of the coffee-machine, voices and laughter,
smell of a cigar, hamburgers, wet coats steaming,
and the slow dangerous inches to the stairs.
I put his right hand on the rail and take his stick.
He clings to me. The stick is in his left hand,
probing the treads. I guide his arm and tell him the steps.
And slowly we go down. And slowly we go down.
White tiles and mirrors at last. He shambles uncouth
into the clinical gleam. I set him in position,
stand behind him and wait with his stick.
His brooding reflection darkens the mirror
but the trickle of his water is thin and slow,
an old man's apology for living.
Painful ages to close his trousers and coat —
I do up the last buttons for him.
He asks doubtfully, 'Can I — wash my hands?'
I fill the basin, clasp his soft fingers round the soap.
He washes, feebly, patiently. There is no towel.
I press the pedal of the drier,
draw his hands gently into the roar of the hot air.
But he cannot rub them together, drags out
a handkerchief to finish. He is glad to leave the contraption,
and face the stairs. He climbs, and steadily enough.
He climbs, we climb. He climbs with many pauses
but with that one persisting patience of the undefeated
which is the nature of man when all is said.
And slowly we go up. And slowly we go up.
The faltering, unfaltering steps take him at last
to the door across that endless, yet not endless waste of floor.
I watch him helped on a bus. It shudders off in the rain.
The conductor bends to hear where he wants to go.

Wherever he could go it would be dark
and yet he must trust men.
Without embarrassment or shame
he must announce his most pitiful needs
in a public place. No one sees his face.
Does he know how frightening he is in his strangeness
under his mountainous coat, his hands like wet leaves
stuck to the half-white stick?
His life depends on many who would evade him.
But he cannot reckon up the chances, having one thing to do,
to haul his blind hump through these rains of August.
Dear Christ, to be born for this!`,

    "Trio": `Coming up Buchanan Street, quickly, on a sharp winter evening
a young man and two girls, under the Christmas lights —
The young man carries a new guitar in his arms,
the girl on the inside carries a very young baby,
and the girl on the outside carries a chihuahua.
And the three of them are laughing, their breath rises
in a cloud of happiness, and as they pass
the boy says, 'Wait till he sees this but!'
The chihuahua has a tiny Royal Stewart tartan coat like a teapot-holder,
the baby in its white shawl is all bright eyes and mouth like favours in a fresh sweet cake,
the guitar swells out under its milky plastic cover, tied at the neck
with silver tinsel tape and a brisk sprig of mistletoe.
Orphean sprig! Melting baby! Warm chihuahua!
The vale of tears is powerless before you.
Whether Christ is born, or is not born, you
put paid to fate, it abdicates
                                           under the Christmas lights.
Monsters of the year
go blank, are scattered back,
can't bear this march of three.

— And the three have passed, vanished in the crowd
(yet not vanished, for in their arms they wind
the life of men and beasts, and music,
laughter ringing them round like a guard)
at the end of this winter's day.`,

    "Glasgow Sonnet I": `A mean wind wanders through the backcourt trash.
Hackles on puddles rise, old mattresses
puff briefly and subside. Play-fortresses
of brick and bric-a-brac spill out some ash.
Four storeys have no windows left to smash,
but in the fifth a chipped sill buttresses
mother and daughter the last mistresses
of that black block condemned to stand, not crash.
Around them the cracks deepen, the rats crawl.
The kettle whimpers on a crazy hob.
Roses of mould grow from ceiling to wall.
The man lies late since he has lost his job,
smokes on one elbow, letting his coughs fall
thinly into an air too poor to rob.`,

    "Strawberries": `There were never strawberries
like the ones we had
that sultry afternoon
sitting on the step
of the open french window
facing each other
your knees held in mine
the blue plates in our laps
the strawberries glistening
in the hot sunlight
we dipped them in sugar
looking at each other
not hurrying the feast
for one to come
the empty plates
laid on the stone together
with the two forks crossed
and I bent towards you
sweet in that air
in my arms
abandoned like a child
from your eager mouth
the taste of strawberries
in my memory
lean back again
let me love you

let the sun beat
on our forgetfulness
one hour of all
the heat intense
and summer lightning
on the Kilpatrick hills

let the storm wash the plates`,

    "Love": `Love rules. Love laughs. Love marches. Love
           is the wolf that guards the gate.
Love is the food of music, art, poetry. It
           fills us and fuels us and fires us to create.
Love is terror. Love is sweat. Love is bashed
           pillow, crumpled sheet, unenviable fate.
Love is the honour that kills and saves and nothing
           will ever let that high ambiguity abate.
Love is the crushed ice that tingles and shivers
           and clinks fidgin-fain for the sugar-drenched
           absinth to fall on it and alter its state.
                      With love you send a probe
                      So far from the globe
No one can name the shoals the voids the belts the
           zones the drags the flares it signals all to
           leave all and to navigate.`,

    "Death on Duke Street": `A huddle on the greasy street —
cars stop, nose past, withdraw —
dull glint on soles of tackety boots,
frayed rough blue trousers, nondescript coat
stretching back, head supported
in strangers' arms, a crowd collecting —
'Whit's wrang?' 'Can ye see'm?'
'An auld fella, he's had it.'
On one side, a young mother in a headscarf
is kneeling to comfort him, her three-year-old son
stands puzzled, touching her coat, her shopping bag
spills its packages that people look at
as they look at everything. On the other side
a youth, nervous, awkwardly now
at the centre of attention as he shifts his arm
on the old man's shoulders, wondering
what to say to him, glancing up at the crowd.
These were next to him when he fell,
and must support him into death.
He seems not to be in pain,
he is speaking slowly and quietly
but he does not look at any of them,
his eyes are fixed on the sky,
already he is moving out
beyond everything belonging.
As if he still belonged
they hold him very tight.

Only the hungry ambulance
howls for him through the staring squares.`
  },

  "Liz Lochhead": {
    "The Bargain": `The river in January is fast and high.
You and I
are off to the Barrows.
Gathering police-horses twitch and fret
at the Tron end of London Road and Gallowgate.
The early kick-off we forgot
has us, three thirty, rubbing the wrong way
against all the ugly losers
getting ready to let fly
where the two rivers meet.

January, and we're
looking back, looking forward,
don't know which way
but the boy
with three beautiful Bakelite
Bush radios for sale in Meadow's Minimarket is
buttonpopping stationhopping he
doesn't miss a beat sings along it's easy
to every changing tune
Yes today we're in love aren't we?
with the whole splintering city
its big quick river wintry bridges
its brazen black Victorian heart.

So what if every other tenement
wears its hearth on its gable end?
All I want
is my glad eye to catch
a glint in your flinty Northern face again
just once. Oh I know it's cold
and coming down
and no we never lingered long among
the Shipbank traders.
Paddy's Market underneath the arches
stank too much today
the usual wetdog reek rising
from piles of old damp clothes.`,

    "My Rival's House": `is peopled with many surfaces.
Ormolu and gilt, slipper satin,
lush velvet couches, cushions so stiff
you can't sink in.
Tables polished clear enough to see
distortions in.

We take our shoes off at her door,
shuffle stocking-soled, tiptoe —
the parquet floor is beautiful and its surface
must be protected.
Dust-cover, drawn shade,
won't let the surface colour fade.

Silver sugar-tongs and silver salver,
my rival serves us tea.
She glosses over him and me.
I am all edges, a surface, a shell
and yet my rival thinks she means me well.
But what squirms beneath her surface I can tell.
Soon, my rival capped tooth, polished nail
will fight, fight foul for her survival.
Deferential, daughterly, I sip and thank her nicely
for each bitter cup.

And I have much to thank her for.
This son she bore —
first blood to her —
never, never can escape scot free
the sour potluck of family.
And oh how close this family
that furnishes my rival's place.

Lady of the house. Queen bee.
She is far more unconscious,
far more dangerous than me.
Listen, I was always my own worst enemy.
She has taken even this from me.

She dishes up her dreams for breakfast.
Dinner, and her salt tears pepper our soup.
She won't give up.`,

    "Last Supper": `She is getting good and ready to renounce
his sweet flesh.
Not just for lent. (For Ever)
But meanwhile she is assembling the ingredients
for their last treat, the proper feast
(after all didn't they always eat together
rather more than rather well?)
So here she is tearing foliage,
scrambling the salad, maybe lighting candles even,
anyway stepping back to admire the effect
of the table she's made (and oh yes now will have to lie on)
the silverware, the nicely al-dente vegetables, the cooked goose.
He could be depended on to bring the bottle
plus betrayal with a kiss.

Already she was imagining it done with, this feast,
and exactly what kind of leftover hash
she'd make of it among friends,
when it was just The Girls,
when those three met again.
What very good soup she could render from the bones,
then something substantial, something extra tasty if not elegant.

Yes, there they'd be, cackling around the cauldron,
spitting out the gristlier bits of his giblets;
gnawing on the knucklebone of some intricate irony;
getting grave and dainty at the petit-gout mouthfuls of reported speech.
'That's rich!' they'd splutter, munching the lies, fat and sizzling as sausages.
Then they'd sink back gorged on truth and their own savage integrity,
sleek on it all, preening like corbies,
their bright eyes blinking satisfied
till somebody would get hungry and go hunting again.`,

    "Box Room": `First the welcoming. Smiles all round. A space
For handshakes. Then she put me in my place —
(Oh, with concern for my comfort). 'This room
Was always his — when he comes home
It's here for him. Unless of course,' she said,
'He brings a Friend.' She smiled. 'I hope the bed
Is soft enough? He'll make do tonight
In the lounge on the put-u-up. All right
For a night or two. Once or twice before
He's slept there. It'll all be fine I'm sure —
Next door if you want to wash your face.'
Leaving me 'peace to unpack' she goes. My weekend case
(Lightweight, glossy, made of some synthetic
Miracle) and I are left alone in her pathetic
Shrine to your lost boyhood. She must
Think she can brush off time with dust
From model aeroplanes. I laugh it off in self defence,
Who have come for a weekend to state my permanence.

Peace to unpack — but I found none
In this spare room which once contained you. (Dun-
Coloured walls, one small window which used to frame
Your old horizons). What can I blame
For my unrest, insomnia? Persistent fear
Elbows me, embedded deeply here
In an outgrown bed (Narrow, but no narrower
Than the single bed we sometimes share).
On every side you grin gilt-edged from long-discarded selves
(But where do I fit into the picture?) Your bookshelves
Are crowded with previous prizes, a selection
Of plots grown thin. Your egg collection
Shatters me — (plover, robin, songthrush, magpie,
Wren, assorted seabirds) labelled carefully, sucked dry
Years ago — that now you have no interest
In. (You just took one from each, you never wrecked a nest,
You said). Invited guest among abandoned objects, my position
Is precarious, closeted so — it's dark, your past a premonition
I can't close my eyes to. I shiver despite
The electric blanket and the deceptive mildness of the night.`,

    "For My Grandmother Knitting": `There is no need they say
but the needles still move
their rhythms in the working of your hands
as easily as if your hands were once again
those sure and skilful hands of the fisher-girl.

You are old now and your grasp of things
is not so good
but master of your moments then
deft and swift you slit the still-ticking quick silver fish.
Hard work it was too of necessity.

But now they say there is no need
as the needles move in the working of your hands
once the hands of the bride
with the hand-span waist
once the hands of the miner's wife
who scrubbed his back in a tin bath by the coal fire
once the hands of the mother of six
who made do and mended
scraped and slaved slapped sometimes when necessary.

But now they say there is no need
the kids they say grandma have too much already
more than they can wear too many
scarves and cardigans — gran you do too much
there's no necessity…

At your window you wave them goodbye Sunday.
With your painful hands big on shrunken wrists.
Swollen-jointed. Red. Arthritic. Old.
But the needles still move their rhythms in the working of your hands
easily as if your hands remembered of their own accord the pattern
as if your hands had forgotten how to stop.`,

    "The Spaces Between": `The boy is ten and today it is his birthday.
Behind him on the lawn his mother and his little sister
unfurl a rainbow crayoned big and bright on a roll of old wallpaper.
His father, big-eyed, mock-solemn, pantomimes ceremony as he lights the ten candles on the cake.
Inside her living-room his grandmother puts her open palm to the window.
Out in the garden, her grandson reaches up, mirrors her, stretching fingers
and they smile and smile as if they touched warm flesh not cold glass.

More than forty thousand years ago men or women splayed their fingers thus
and put their hands to bare rock, they chewed ochre, red-ochre, gritted charcoal and blew,
blew with projectile effort that really took it out of them, their living breath.
Raw gouts of pigment spattered the living stencil that was each's own living hand and made their mark.
The space of absence was the clean, stark picture of their presence and it pleased them.
We do not know why they did it and maybe they did not either
but they knew they must. It was the cold cave wall and they knew they were up against it.

The birthday boy is juggling. He has been spending time in the lockdown learning
but though he still can't keep it up for long his grandmother dumb-shows most extravagant applause.
She toasts them all in tea from her Best Granny in the World mug, winking and licking her lips ecstatically
as they cut the cake, miming hunger, miming prayer for her hunger to be sated.
The slim girl dances and her grandmother claps and claps again, blinking tears.
Another matched high-five at her window.
Neither the blown candles or the blown kisses will leave any permanent mark
— unless love does? — on them on this the only afternoon they will be all alive together
on just this day the boy is ten.`,

    "Sorting Through": `The moment she died, my mother's dance dresses
turned from the colours they really were
to the colours I imagine them to be.
I can feel the weight of bumptoed silver shoes
swinging from their anklestraps as she swaggers up the path towards her dad,
light-headed from airman's kisses.
Here, at what I'll have to learn to call my father's house,
yes every ragbag scrap of duster prints her
even more vivid than an Ilford snapshot on some seafront
in a white cardigan and that exact frock.
Old lipsticks. Liquid stockings.
Labels like Harella, Gor-ray, Berketex.
As I manhandle whole outfits into binbags for Oxfam
every mote in my eye is a utility mark
and this is useful: the sadness of dispossessed dresses,
the decency of good coats roundshouldered in the darkness of wardrobes,
the gravitas of lapels, the invisible danders of skin
fizzing off from them like all that life that will not neatly end.`
  },

  "Don Paterson": {
    "The Ferryman's Arms": `About to sit down with my half-pint of Guinness
I was magnetized by a remote phosphorescence
and drawn, like a moth, to the darkened back room
where a pool-table hummed to itself in the corner.

With ten minutes to kill and the whole place deserted
I took myself on for the hell of it. Slotting
a coin in the tongue, I looked round for a cue —
while I stood with my back turned, the balls were deposited
with an abrupt intestinal rumble; a striplight
batted awake in its dusty green cowl.

When I set down the cue-ball inside the parched D
it clacked on the slate; the nap was so threadbare
I could screw back the globe, given somewhere to stand.
As physics itself becomes something negotiable
a rash of small miracles covers the shortfall.
I went on to make an immaculate clearance.

A low punch with a wee dab of side, and the black
did the vanishing trick while the white stopped
before gently rolling back as if nothing had happened,
shouldering its way through the unpotted colours.

The boat chugged up to the little stone jetty
without breaking the skin of the water, stretching,
as black as my stout, from somewhere unspeakable
to here, where the foaming lip mussitates endlessly,
trying, with a nutter's persistence, to read
and re-read the shoreline. I got aboard early,
remembering the ferry would leave on the hour
even for only my losing opponent;
but I left him there, stuck in his tent of light, sullenly
knocking the balls in, for practice, for next time.`,

    "Waking with Russell": `Whatever the difference is, it all began
the day we woke up face-to-face like lovers
and his four-day-old smile dawned on him again,
possessed him, till it would not fall or waver;
and I pitched back not my old hard-pressed grin
but his own smile, or one I'd rediscovered.
Dear son, I was mezzo del cammin
and the true path was as lost to me as ever
when you cut in front and lit it as you ran.
See how the true gift never leaves the giver:
returned and redelivered, it rolled on
until the smile poured through us like a river.
How fine, I thought, this waking amongst men!
I kissed your mouth and pledged myself forever.`,

    "11:00: Baldovan": `Base Camp. Horizontal sleet. Two small boys
have raised the steel flag of the 20 terminus:
me and Ross Mudie are going up the Hilltown
for the first time ever on our own.

I'm weighing up my spending power: the shillings,
tanners, black pennies, florins with bald kings,
the cold blazonry of a half-crown, threepenny bits
like thick cogs, making them chank together in my pockets.

I plan to buy comics, sweeties, and magic tricks.

However, I am obscurely worried, as usual,
over matters of procedure, the protocol of travel,
and keep asking Ross the same questions:
where we should sit, when to pull the bell, even
if we have enough money for the fare,
whispering, Are ye sure? Are ye sure?

I cannot know the little good it will do me;
the bus will let us down in another country
with the wrong streets and streets that suddenly forget
their names at crossroads or in building-sites
and where no one will have heard of the sweets we ask for
and the man will shake the coins from our fists onto the counter
and call for his wife to come through, come through and see this
and if we ever make it home again, the bus
will draw into the charred wreck of itself
and we will enter the land at the point we left off
only our voices sound funny and all the houses are gone
and the rain tastes like kelly and black waves fold in
very slowly at the foot of Macalpine Road
and our sisters and mothers are fifty years dead.`,

    "The Circle": `My boy is painting outer space,
and steadies his brush-tip to trace
the comets, planets, moon and sun
and all the circuitry they run

in one great heavenly design.
But when he tries to close the line
he draws around his upturned cup,
his hand shakes, and he screws it up.

The shake's as old as he is, all
(thank god) his body can recall
of that hour when, one inch from home,
we couldn't get the air to him;

and though today he's all the earth
and sky for breathing-space and breath
the whole troposphere can't cure
the flutter in his signature.

But Jamie, nothing's what we meant.
The dream is taxed. We all resent
the quarter bled off by the dark
between the bowstring and the mark

and trust to Krishna or to fate
to keep our arrows halfway straight.
But the target also draws our aim —
our will and nature's are the same;

we are its living word, and not
a book it wrote and then forgot,
its fourteen-billion-year-old song
inscribed in both our right and wrong —

so even when you rage and moan
and bring your fist down like a stone
on your spoiled work and useless kit,
you just can't help but broadcast it:

look at the little avatar
of your muddy water-jar
filling with the perfect ring
singing under everything.`,

    "The Thread": `Jamie made his landing in the world
so hard he ploughed straight back into the earth.
They caught him by the thread of his one breath
and pulled him up. They don't know how it held.

And so today I thank what higher will
brought us to here, to you and me and Russ,
the great twin-engined swaying wingspan of us
roaring down the back of Kirrie Hill

and your two-year-old lungs somehow out-revving
every engine in the universe.
All that trouble just to turn up dead
was all I thought that long week. Now the thread

is holding all of us: look at our tiny house,
son, the white dot of your mother waving.`,

    "The Swing": `The swing was picked up for the boys,
for the here-and-here-to-stay
and only she knew why it was
I dug so solemnly

I spread the feet two yards apart
and hammered down the pegs
filled up the holes and stamped the dirt
around its skinny legs

I hung the rope up in the air
and fixed the yellow seat
then stood back that I might admire
my handiwork complete

and saw within its frail trapeze
the child that would not come
of what we knew had two more days
before we sent it home

I know that there is nothing here
no venue and no host
but the honest fulcrum of the hour
that engineers our ghost

the bright sweep of its radar-arc
is all the human dream
handing us from dark to dark
like a rope over a stream

But for all the coldness of my creed
for all those I denied
for all the others she had freed
like arrows from her side

for all the child was barely here
and for all that we were over
I could not weigh the ghosts we are
with those that we deliver

I gave the empty seat a push
and nothing made a sound
and swung between two skies to brush
her feet upon the ground`,

    "Why Do You Stay Up So Late?": `For Russ

I'll tell you, if you really want to know:
remember that day you lost two years ago
at the rockpool where you sat and played the jeweller
with all those stones you'd stolen from the shore?
Most of them went dark and nothing more,
but sometimes one would blink the secret colour
it had locked up somewhere in its stony sleep.
This is how you knew the ones to keep.

So I collect the dull things of the day
in which I see some possibility
but which are dead and which have the surprise
I don't know, and I've no pool to help me tell —
so I look at them and look at them until
one thing makes a mirror in my eyes
then I paint it with the tear to make it bright.
This is why I sit up through the night.`
  },

  "Imtiaz Dharker": {
    "Bairn": `There is no way to explain
the light that rises into a room
from the early morning bloom of snow;
through closed lids, the slow dawning
of white. The body is quick to know
the pace of the day has changed, the space
wiped clean to make way for the first bird, first song.

There is no way to explain
how, even when the world is wrong,
too spent, too old, it can still be new, renewed
by you, by your first sound, clear note
against the dark. Today, your voice
rising into the house,
the face of the day is changed.

There are things that cleanse the heart.`,

    "Bloom": `You are nothing more than yourself,
not a message sent to change the world,
not here to save mankind or even me. You are,

like a snail or mollusc, only there;
like a leaf among thousands on a tree,
like the sea or the smallest of its creatures,

just there. And yet, and yet I watch your face
and see a star waking in your eyes
like sap-rise to a leaf, tide-rush to the moon.

I try to live the life inside your head, think
what you are thinking, feel what makes
your heart beat fast, small body, small weight

in my arms. More than my self, I want to know
you. This is the gift you give. Cradling you close
I feel the world and all its waking life.

Holding you, I hold the world,
wishing it for ever safe.`,

    "Letters to Glasgow": `Heading north is a leap of the heart.
On the map on the phone, the blue dot
is a pulse that crosses the border.

Clouds swing open over the hills
and train chases river, races
after it like a riever,

like a long-distance runner,
breathing hard into the wind
with its flag of light flying behind.

Between home and elsewhere,
between in here and out there,
one journey is layered over the other,

one time on another. Meeting a stranger
on a train, getting to know them better,
is like opening an unexpected letter.

That's me on the garden, and that...I can't tell
if it's Graham or Brian...that's the two of us sat there.
My daughter said, Look at you posing.

In here is a steady hush and a shoosh
that carries her back to the songs she sang
when she was young. She floats

and rocks and someone talks out of the past.
The hum of the train becomes the voice
of her mother, then deepens to be

the voice of her lover so she dreams him back
in the jacket she liked, to share half a sandwich,
tea with milk and one sugar.

his arm brushing hers, and then he will tell her
he went to Ecclefechan before he met her.
The presence of him is the whoosh of wheels

and she is glad he came back for a while.
For a while, in her sleep, she smiles.
The train is a memory-keeper, it carries

something alive, like the words in a letter,
not gathering dust but held in trust.
This is how love moves on and survives.

In the window, her life looks back
at the man on the phone, and reflects his own.
The future comes in and puts down its bags.

Down the carriage, a clicking and pecking
at keyboards, half-heard, half-knowing,
tossed away in the coming and going

like all the other used-up things,
the cardboard wrap, the cellophane,
paper napkins, time-pass, stifled

yawn. The rubbish collector gathers it in,
stopping for breath in the vestibule,
his back to the door, feeling the change

in the sound, the different drumming,
the slip and shiver of in-between
and far below, the long embrace

of wheel and rail. The blue dot is still speeding,
over the screen, sheep are fast
and houses are quicker, going over the edge

with all the lost things at the butt-end of town
where the young fizzle out against walls
sprayed with words in black and red

and the wires are dancing
with the whole of the sky,
meeting and parting, saying goodbye.

We are now approaching, we are now approaching

over the Clyde where the great ships were born,
over the water, a ghostly foghorn,
over the bridge to the city they come,

some of them visiting, some returning.
They take up their baggage and their belongings,
they take up their longings

and the train brings them in to Glasgow Central,
the tall windows, the glass bridge,
the Heilanman's Umbrella lit

to gather them in,
as if someone has waited for them too long,
as if they are love letters, delivered home.`,

    "Send This": `Do not send me a postcard
of the city that once lived here,
its water-courses and its domes.
No photograph can show that this
was once home, and that home
is long gone.

Do not send me a miniature
drawn with a camel's-hair brush
to hang on my wall, or tell me
you were in the Anarkali Bazaar,
or say the gulmohar trees were aflame
and koels sang there.

Everything changes. Remind me
of this when the light falls aslant
on things not quite made, girders laid
over half-drawn plans, haggled over
and paid, the truth retold and sold
in new-built malls.

With the wrong key, I come
to this place and try to unlock it.
Air-conditioners rattle and spit
at the back of suburban villas.
Someone here has built a room,
left space for a window,

opened a door, a desire.
Do not mock it. In an almost-done
world, send me this, knowing
nothing is ever fixed. I will carry
the unfinished walls of my city
with me, in my pocket.`,

    "Stitch": `Cathy's mother says
she is her sweet heart.
My mother says I am a piece of her liver.

Cathy has an ice blue cardigan.
My mother holds Cathy still
in front of her, and studies the stitches

with her eyes screwed up. She
measures me with the span of her hand
and marches me over the bridge

to Argyle Street for wool and needles.
Sitting by the fireplace, she is fierce,
unwinds the skein to a ball

off my arms, slips one, purls one,
unravels, starts again,
needles clicking, clacking

in time to the pattern in her head,
the shape of the life she is plotting
for me, knitting herself into me.

Done, she holds the cardigan
over my chest, a perfect copy
of Cathy's, but red.

The edges wriggle more
because hers is machine-made
and mine is worked by hand.

Years later, when I leave, my mother cries
on the phone, and says her liver
has been torn apart.`,

    "The Knot": `At Loch Lomond they were king and queen to me,
laying out their bounty on the brae
after the queasy ride from Glasgow, we
children making sick-stops on the way.

Tumbled out, we sprawled at the water's mouth
where light ate shade and made us ravenous.
With a flourish, Her Majesty untied the cloth
to set the parathas free. She did, she does,

undoes this deep red knot of hurt in the heart.
Today, when scars have been allowed to deepen
around a silence, wrenching our lives apart,
she has come back to us at the loch to open

out the tangle of her dying, the mistake,
the high, the low, the road we did not take.`
  },

  "Kathleen Jamie": {
    "Crossing the Loch": `Remember how we rowed toward the cottage
on the sickle-shaped bay,
that one night after the pub
loosed us through its swinging doors
and we pushed across the shingle
till water lipped the sides
as though the loch mouthed 'boat'?

I forget who rowed. Our jokes hushed.
The oars' splash, creak, and the spill
of the loch reached long into the night.
Out in the race I was scared:
the cold shawl of breeze,
and hunched hills; what the water held
of deadheads, ticking nuclear hulls.

Who rowed, and who kept their peace?
Who hauled salt-air and stars
deep into their lungs, were not reassured;
and who first noticed the loch's
phosphorescence, so, like a twittering nest
washed from the rushes, an astonished
small boat of saints, we watched water shine
on our fingers and oars,
the magic dart of our bow wave?

It was surely foolhardy, such a broad loch, a tide,
but we live — and even have children
to women and men we had yet to meet
that night we set out, calling our own
the sky and salt-water, wounded hills
dark-starred by blaeberries, the glimmering anklets
we wore in the shallows
as we shipped oars and jumped,
to draw the boat safe, high at the cottage shore.`,

    "Mr and Mrs Scotland are Dead": `On the civic amenity landfill site,
the coup, the dump beyond the cemetery
and the 30-mile-an-hour sign, her stiff
old ladies' bags, open mouthed, spew
postcards sent from small Scots towns
in 1960: Peebles, Largs, the rock-gardens
of Carnoustie, tinted in the dirt.
Mr and Mrs Scotland, here is the hand you were dealt:
fair but cool, showery but nevertheless,
Jean asks kindly; the lovely scenery;
in careful school-room script —
The Beltane Queen was crowned today.
But Mr and Mrs Scotland are dead.

Couldn't he have burned them? Released
in a grey curl of smoke
this pattern for a cable knit? Or this:
tossed between a toppled fridge
and sweet-stinking anorak: Dictionary for Mothers
M: — Milk, the woman who worries . . .;
And here, Mr Scotland's John Bull Puncture Repair Kit;
those days when he knew intimately
the thin roads of his country, hedgerows
hanged with small black brambles' hearts;
and here, for God's sake, his last few joiners' tools,
SCOTLAND, SCOTLAND, stamped on their tired handles.

Do we take them? Before the bulldozer comes
to make more room, to shove aside
his shaving brush, her button tin.
Do we save this toolbox, these old-fashioned views
addressed, after all, to Mr and Mrs Scotland?
Should we reach and take them? And then?
Forget them, till that person enters
our silent house, begins to open
to the light our kitchen drawers,
and performs for us this perfunctory rite:
the sweeping up, the turning out.`,

    "Ospreys": `You'll be wondering why you bothered: beating
up from Senegal, just to hit a teuchit storm —
late March blizzards and raw winds — before the tilt

across the A9, to arrive, mere
hours apart, at the self-same riverside

Scots pine, and possess again the sticks and fishbones
of last year's nest: still here, pretty much
like the rest of us — gale-battered, winter-worn,
                half toppled away . . .

So redd up your cradle, on the tree-top,
claim your teind from the shining
estates of the firth, or the trout-stocked loch.
What do you care? Either way,
there'll be a few glad whispers round the town today:
that's them, baith o' them, they're in.`,

    "Song of Sunday": `A driech day, and nothing to do
bar watch starlings fluchter
over soup bones
left on a plate on the grass.
All forenoon broth-barley, marrowfat peas
swelled in a kitchen jug,
and I soaked stamps, corners
torn from polite white envelopes
in a saucer till they peeled clear,
neither soggy nor stuck: 'See,
watch and not tear them, wait at peace.'

	There'd aye be women
	In the kitchen, brisket
	lashed in string, tatties
	peeled lovelessly, blinded
	pale and drowned. See if one
	now nicked herself
	with a paring knife
	and spellbound, the house froze —
	only now, hacking back in
	through privet and rowan,
	toward my father caught
	mid stretch and yawn,
	my wee sister playing Sindys
	with the girl next door,
	could I wake them
	with something alien
	and lovely
	as a kiss.

       — and we'd be called to eat
       what's put in front of us: potatoes, meat
       till we could get down, Please.
       There were African leopards on TV
       and Songs of Praise. My stamps were dry,
       the odd USA, Magyar Poste exotic
       among the tuppenny-ha'penny pinks,
       the wee lion rampant in a corner
       and after homework I'd have time
       to turn to 'Great Britain'
       like I'd been shown,
       fold and align the edges
       with the orderly squares.
       Press. 'Bedtime!' There.`,

    "The Morrow-bird": `Suppose we begin with a glacier
bearing a massive stone
till the world warms, leaving the boulder
abandoned, alone
through aeons of desolation,
before a wind brings crumbs of earth,
tundra forms, with reindeer,
then one day, a bird
from an unkent airt (the future)
circles appraisingly
and drops a gift, a hazel nut
which sends forth the tree
that inaugurates a forest
pine green and berry bright —
the wood holds sway a thousand years
then feels the first axe bite.
The rest, we cry 'history' —
kye and castle, empire, mine and mill,
till we win the right to be governed
according to our will.
Which brings us to this chamber
where, at our provisional behest,
today you take your places.
Well, here is a first request.
Thon bird's back, wheeling over
bedraggled and dismayed,
she soared to sight the horizon,
but returned afraid.

She soared to sight the horizon,
 (her wings flashed gold)
but turned back, preparing herself
to tell what must be told,
and perched on a certain boulder
(her life-long friend)
she's ready, so please listen
as she caw's you to attend.
"All things pass and change,
that's aye been the way;
but the stark vision I saw up there
must be allayed.
For that, no lone child,
woman or man will be enough,
you'll want your multifariousness
when it all gets tough.
Stones, it won't be sufficient
just to sit on your dowps and say 'nay'.
Trees, don't be waving your arms about
in some new breeze every day.

It's maddening your own age-old
injustices remain,
but now your human influence
invades the wild domains —
on you depend the puddock's leap
the hare's breath, the drone of summer bees,
the whale's dive in the ocean
— and you need them."
				                            So please
Let the morrow-bird's handsel be wisdom,
leavened with music and song.
We seek good governance, Parliament.
Act bold. Be kind. Stay strong.`,

    "What the Clyde Said, After COP26": `I keep the heid. I'm cool.
If asked — but you never ask —
I'd answer in tongues
hinting of linns, of Leven,
Nethan, Kelvin, Cart —
but neutral, balancing
both banks equally as I flow...

Do I judge? I mind the hammer-swing,
the welders' flash, the heavy
steel-built hulls I bore downstream
from my city, and maybe
I was a blether-skite then,
a wee bit full of myself,
when we seemed gey near unstoppable...

But how can I stomach any more
of these storm rains? How can I
slip quietly away to meet my lover,
the wide-armed Ocean, knowing
I'm a poisoned chalice
she must drain, drinking
everything you chuck away...

So these days, I'm a listener, aye.
Think of me as a long level
liquid ear gliding slowly by.
I heard the world's words,
the pleas of peoples born
where my ships once sailed,
I heard the beautiful promises...

and, sure, I'm a river,
but I can take a side.
From this day, I'd rather keep afloat,
like wee folded paper boats,
the hopes of the young folk
chanting at my bank,
fear in their spring-bright eyes

so hear this:
 	fail them, and I will rise.`
  },

  "Robert Burns": {
    "A Red, Red Rose": `O my Luve's like a red, red rose,
That's newly sprung in June;
O my Luve's like the melodie
That's sweetly play'd in tune.

As fair are thou, my bonie lass,
So deep in luve am I;
And I will luve thee still, my Dear,
Till a' the seas gang dry.

Till a' the seas gang dry, my Dear,
And the rocks melt wi' the sun:
I will luve thee still, my dear,
While the sands o' life shall run.

And fare thee weel, my only Luve!
And fare thee weel, a while!
And I will come again, my Luve,
Tho' it were ten thousand mile!`,

    "Composed In August": `Now westlin winds and slaught'ring guns
Bring Autumn's pleasant weather;
The moorcock springs on whirring wings
Amang the blooming heather:
Now waving grain, wide o'er the plain,
Delights the weary farmer;
And the moon shines bright, as I rove by night,
To muse upon my charmer.

The paitrick loves the fruitful fells,
The plover loves the mountains;
The woodcock haunts the lonely dells,
The soaring hern the fountains:
Thro' lofty groves the cushat roves,
The path of man to shun it;
The hazel bush o'erhangs the thrush,
The spreading thorn the linnet.

Thus ev'ry kind their pleasure find,
The savage and the tender;
Some social join, and leagues combine,
Some solitary wander:
Avaunt, away, the cruel sway!
Tyrannic man's dominion;
The sportsman's joy, the murd'ring cry,
The flutt'ring, gory pinion!

But, Peggy dear, the ev'ning's clear,
Thick flies the skimming swallow,
The sky is blue, the fields in view,
All fading — green and yellow:
Come let us stray our gladsome way,
And view the charms of Nature;
The rustling corn, the fruited thorn,
And ilka happy creature.

We'll gently walk, and sweetly talk,
While the silent moon shine clearly;
I'll grasp thy waist, and, fondly prest,
Swear how I lo'e thee dearly:
Not vernal show'rs to budding flow'rs,
Not Autumn to the farmer,
So dear can be as thou to me,
My fair, my lovely charmer!`,

    "A Poet's Welcome to His Love-Begotten Daughter": `Thou's welcome, wean, mishanter fa' me,
If thought of thee, or of thy mammy,
Shall ever daunton me or awe me,
My sweet wee lady!
Or if I blush when thou shalt ca' me
Ti-ta or daddy.

Tho' now they ca' me fornicator,
An' tease my name in kintry clatter,
The mair they talk, I'm kent the better,
E'en let them clash;
An auld wife's tongue's a feckless matter
To gie ane fash.

Welcome! my bonnie, sweet, wee dochter,
Tho' ye come here a wee unsought for,
And tho' your comin' I hae fought for,
Baith kirk and queir;
Yet, by my faith, ye're no unwrought for,
That I shall swear!

Sweet fruit o' monie a merry dint,
My funny toil is now a' tint,
Sin' thou came to the warl' asklent,
Which fools may scoff at;
In my last plack thy part's be in't
The better ha'f o't.

Tho' I should be the waur bestead,
Thou's be as braw and bienly clad,
And thy young years as nicely bred
Wi' education,
As onie brat o' wedlock's bed,
In a' thy station.

Wee image o' my bonnie Betty,
I, fatherly, will kiss and daut thee,
As dear an' near my heart I set thee
Wi' as guid will
As a' the priests had seen me get thee
That's out o' hell.

Lord grant that thou may ay inherit
Thy mither's person, grace an' merit,
An' thy poor, worthless daddy's spirit,
Without his failins,
'Twill please me mair to see thee heir it,
Than stockit mailens.

For if thou be what I wad hae thee,
And tak the counsel I shall gie thee,
I'll never rue my trouble wi' thee,
The cost nor shame o't,
But be a loving father to thee,
And brag the name o't.`
  },

  "Sorley MacLean": {
    "Shores": `If we were in Talisker on the shore
where the great white mouth
opens between two hard jaws,
Rubha nan Clach and the Bioda Ruadh,
I would stand beside the sea
renewing love in my spirit
while the ocean was filling
Talisker bay forever:
I would stand there on the bareness of the shore
until Prishal bowed his stallion head.

And if we were together
on Calgary shore in Mull,
between Scotland and Tiree,
between the world and eternity,
I would stay there till doom
measuring sand, grain by grain,
and in Uist, on the shore of Homhsta
in presence of that wide solitude,
I would wait there forever
for the sea draining drop by drop.

And if I were on the shore of Moidart
with you, for whom my care is new,
I would put up in a synthesis of love for you
the ocean and the sand, drop and grain.

And if we were on Mol Stenscholl Staffin
when the unhappy surging sea dragged
the boulders and threw them over us,
I would build the rampart wall
against an alien eternity grinding (its teeth).`
  },

  "Higher Scottish Poetry Collection": {
    "Thomas the Rhymer": `True Thomas lay on Huntlie bank,
A ferlie he spied wi' his e'e;
And there he saw a ladye bright
Come riding down by the Eildon Tree.

Her shirt was o' the grass-green silk.
Her mantle o' the velvet fyne;
At ilka tett of her horse's mane
Hung fifty siller bells and nine.

True Thomas, he pull'd aff his cap
And louted low down to his knee;
All hail, thou mighty Queen of Heaven!
For thy peer on earth I never did see.

O no, O no, Thomas, she said,
That name does not belang to me;
I am but the Queen of fair Elfland
That am hither come to visit thee.

Harp and carp, Thomas, she said,
Harp and carp along wi' me,
And if ye dare to kiss my lips,
Sure of your bodie I will be.

Betide me weal, betide me woe,
That weird shall never daunton me,
Syne he has kissed her rosy lips
All underneath the Eildon Tree.

Now ye maun go wi' me, she said,
True Thomas, ye maun go wi' me;
And ye maun serve me seven years
Thro' weal or woe, as may chance to be.

She mounted on her milk-white steed,
She's ta'en True Thomas up behind;
And aye whene'er her bridle rung
The steed flew swifter than the wind.

O they rade on, and farther on —
The steed gaed swifter than the wind —
Until they reach'd a desert wide
And living land was left behind.

Light down, light down now, True Thomas,
And lean your head upon my knee;
Abide and rest a little space
And I will shew you ferlies three.

O see ye not yon narrow road
So thick beset with thorns and briers?
That is the path of righteousness,
Though after it but few enquires.

And see ye not that braid, braid road
That lies across that lily leven?
That is the path of wickedness,
Though some call it the road to Heaven.

And see not ye that bonny road
That winds about the fernie brae?
That is the road to fair Elfland,
Where thou and I this night maun gae.

But Thomas, ye maun hold your tongue
Whatever ye may hear or see,
For if you speak word in Elflyn land
Ye'll ne'er get back to your ain countrie.

O they rade on, and farther on,
And they waded through rivers aboon the knee,
And they saw neither sun nor moon
But they heard the roaring of the sea.

It was mirk, mirk night and there was nae stern light
And they waded through red blude to the knee;
For a' the blude that's shed on earth
Rins through the springs o' that countrie.

Syne they came on to a garden green
And she pu'd an apple frae a tree;
Take this for thy wages, True Thomas,
It will give thee the tongue that can never lie.

He has gotten a coat of the even cloth
And a pair of shoes of velvet green;
And till seven years were gane and past
True Thomas on earth was never seen.`,

    "Composed In August": `Now westlin winds and slaught'ring guns
Bring Autumn's pleasant weather;
The moorcock springs on whirring wings
Amang the blooming heather:
Now waving grain, wide o'er the plain,
Delights the weary farmer;
And the moon shines bright, as I rove by night,
To muse upon my charmer.

The paitrick loves the fruitful fells,
The plover loves the mountains;
The woodcock haunts the lonely dells,
The soaring hern the fountains:
Thro' lofty groves the cushat roves,
The path of man to shun it;
The hazel bush o'erhangs the thrush,
The spreading thorn the linnet.

Thus ev'ry kind their pleasure find,
The savage and the tender;
Some social join, and leagues combine,
Some solitary wander:
Avaunt, away, the cruel sway!
Tyrannic man's dominion;
The sportsman's joy, the murd'ring cry,
The flutt'ring, gory pinion!

But, Peggy dear, the ev'ning's clear,
Thick flies the skimming swallow,
The sky is blue, the fields in view,
All fading — green and yellow:
Come let us stray our gladsome way,
And view the charms of Nature;
The rustling corn, the fruited thorn,
And ilka happy creature.

We'll gently walk, and sweetly talk,
While the silent moon shine clearly;
I'll grasp thy waist, and, fondly prest,
Swear how I lo'e thee dearly:
Not vernal show'rs to budding flow'rs,
Not Autumn to the farmer,
So dear can be as thou to me,
My fair, my lovely charmer!`,

    "The Bonnie Broukit Bairn": `Mars is braw in crammasy,
Venus in a green silk goun,
The auld mune shak's her gowden feathers,
Their starry talk's a wheen o' blethers,
Nane for thee a thochtie sparin'
Earth, thou bonnie broukit bairn!
— But greet, an' in your tears ye'll drown
The haill clanjamfrie!

crammasy: crimson
wheen o' blethers: pack of nonsense
broukit: neglected
haill clanjamfrie: whole crowd of them`,

    "Summit of Corrie Etchachan": `But in the climbing ecstasy of thought,
Ere consummation, ere the final peak,
Come hours like this. Behind, the long defile,
The steep rock-path, alongside which, from under
Snow-caves, sharp-corniced, tumble the ice-cold waters.
And now, here, at the corrie's summit, no peak,
No vision of the blue world, far, unattainable,
But this grey plateau, rock-strewn, vast, silent,
The dark loch, the toiling crags, the snow;
A mountain shut within itself, yet a world,
Immensity. So may the mind achieve,
Toiling, no vision of the infinite,
But a vast, dark and inscrutable sense
Of its own terror, its own glory and power.`,

    "Da Clearance": `I can see da rüfs aa taekit, an da hens aroond da door;
Fok kerryin twartree paets hame, an rigs delled every voar.
Aa da lums ir reekin, an I hear da happy soonds
O peerie bairns skirlin, as dey play dem ower da toons.

I see eens sittin roond da fire, an twartree mair oot by;
Sookin infants at da breest, an weemen gjaan ta cry.
Da tochtfil, tried faces o dem at's lived ower lang;
Fractious bairns feytin sleep, an sensin somethin wrang.

I hear a lood, lood knockin, an da crump o monny feet;
Men's voices raised in anger, an da bairns start ta greet.
I see da fok aa hirded oot afore da laandloard's men;
An aa da bits a things dey hed, fired ower da briggy-stane.

I see da bare waas staandin, an aa da laand lie green;
Lang syne da fire wis slokkit, an monny a year is geen.
Noo dey aa hae equal portions o aert ta tak dir sleep.
Tell me, wis it wirt it aa for twartree extry sheep?`,

    "33": `I dreamed of a sawdust chandelier
whose crystals were drops of driftwood
dredged from all the world's shipwrecks:
god's figurehead, and it swung,
as I dreamt, ever closer to my fear,
softly releasing sweet incense
into the clear, black night air,
as that great barge carries the dead,
but instead of my death,
it passaged my dread
and the water it ploughed
comprised of one tear.

Great smouldering barque,
your figurehead sings into the dark,
the death song of queens, of kings,
over sea birds that circle
like faint rings of smoke,
your floating lamp burns,
as a lighthouse brings death to itself:
you are moth and flame both —
so lamp lights my dread of shade,
performs two killings.`
  },

  "Scottish Poetry Collection": {
    "The Bonnie Earl o' Moray": `Ye Hielands and ye Lowlands,
O, whaur hae ye been?
They hae slain the Earl o' Moray,
And laid him on the green.

He was a braw gallant,
And he rade at the ring,
And the bonny Earl o' Moray,
He might hae been a king.

O lang will his lady
Lok frae the Castle Doune
Ere she see the Earl o' Moray
Come soundin' through the toun.

Now wae be to ye, Huntly,
And wherefore did ye sae?
I bade ye bring him wi' ye,
And forbade ye him to slay.

He was a braw gallant,
And he played at the glove;
And the bonny Earl o' Moray,
He was the Queen's true love.

O lang will his lady
Lok frae the Castle Doune
Ere she see the Earl o' Moray
Come soundin' through the toun.`,

    "The Twa Corbies": `As I was walking all alane,
I heard twa corbies making a mane;
The tane unto the t'other say,
'Where sall we gang and dine the day?'

'In behind yon auld fail dyke,
I wot there lies a new slain knight;
And naebody kens that he lies there,
But his hawk, his hound, and his lady fair.

'His hound is to the hunting gane,
His hawk to fetch the wild-fowl hame,
His lady's ta'en another mate,
So we may make our dinner sweet.

'Ye'll sit on his white hause-bane,
And I'll pike out his bonny blue een;
Wi ae lock o his gowden hair,
We'll theek our nest when it grows bare.

'Mony an ane for him makes mane,
But nane sall ken whare he is gane;
Oer his white banes, when they are bare,
The wind sall blaw for evermair.'`,

    "A Red, Red Rose": `O my Luve's like a red, red rose,
That's newly sprung in June;
O my Luve's like the melodie
That's sweetly play'd in tune.

As fair are thou, my bonie lass,
So deep in luve am I;
And I will luve thee still, my Dear,
Till a' the seas gang dry.

Till a' the seas gang dry, my Dear,
And the rocks melt wi' the sun:
I will luve thee still, my dear,
While the sands o' life shall run.

And fare thee weel, my only Luve!
And fare thee weel, a while!
And I will come again, my Luve,
Tho' it were ten thousand mile!`,

    "Lochinvar": `O, young Lochinvar is come out of the west,
Through all the wide Border his steed was the best;
And save his good broadsword, he weapons had none,
He rode all unarmed, and he rode all alone.
So faithful in love, and so dauntless in war,
There never was knight like the young Lochinvar.

He stayed not for brake, and he stopped not for stone,
He swam the Esk river where ford there was none;
But, ere he alighted at Netherby gate,
The bride had consented, the gallant came late:
For a laggard in love, and a dastard in war,
Was to wed the fair Ellen of brave Lochinvar.

So boldly he entered the Netherby Hall,
Among bride's-men, and kinsmen, and brothers, and all:
Then spoke the bride's father, his hand on his sword,
(For the poor craven bridegroom said never a word,)
'O come ye in peace here, or come ye in war,
Or to dance at our bridal, young Lord Lochinvar?'

'I long woo'd your daughter, my suit you denied;
Love swells like the Solway, but ebbs like its tide;
And now I am come, with this lost love of mine,
To lead but one measure, drink one cup of wine.
There are maidens in Scotland, more lovely by far,
That would gladly be bride to the young Lochinvar.'

The bride kissed the goblet: the knight took it up,
He quaffed off the wine, and he threw down the cup.
She looked down to blush, and she looked up to sigh,
With a smile on her lips and a tear in her eye.
He took her soft hand, ere her mother could bar, —
'Now tread we a measure!' said young Lochinvar.

So stately his form, and so lovely her face,
That never a hall such a galliard did grace;
While her mother did fret, and her father did fume,
And the bridegroom stood dangling his bonnet and plume:
And the bride-maidens whispered, ''Twere better by far
To have matched our fair cousin with young Lochinvar.'

One touch to her hand, and one word in her ear,
When they reached the hall-door, and the charger stood near;
So light to the croup the fair lady he swung,
So light to the saddle before her he sprung!
'She is won! we are gone, over bank, bush, and scaur;
They'll have fleet steeds that follow,' quoth young Lochinvar.

There was mounting 'mong Graemes of the Netherby clan;
Forsters, Fenwicks, and Musgraves, they rode and they ran:
There was racing and chasing on Cannobie Lee,
But the lost bride of Netherby ne'er did they see.
So daring in love, and so dauntless in war,
Have ye e'er heard of gallant like young Lochinvar?`,

    "Auntie": `My Aunt's hands are soft and brown and they smell like cumin and coriander.
She is a gardener in the kitchen.
Auntie, I remember your skin the way some people remember the bus route.
I know I need to trace it to go home.
The world of work, bus bells and sirens are harsh alarm clocks.
I would rather wake gently, in 5 am light,
to your softly whispered duas welcoming the morning.`,

    "Little Girls": `The little girl stands on a knife-covered ledge,
Dancing till blood starts to drip from its edge.
She's been licking her wounds since the first time she bled,
Getting judged for each thought she commits in her head.
She's been starving herself since she started to eat,
Connecting the dots of her heart's every beat.
She's been swimming from fishermen hiding their net,
And running from wolves that deny they're a threat.

And the men chime in, 'Silence girl, don't make a fuss,
I'd never do this, it's not all of us.'
To drown out her sorrow, the male chorus sings,
'It's only a few, you're imagining things.
You're making this issue seem worse than it is;
It was only a comment, a gesture, a kiss.
It was meant as a compliment - please take a joke,
Don't bite the hand groping you, savour each poke.'
And the girl learns the axis on which the world spins
Is powered by people who relish their sins.
So, she keeps her head down and she learns how to live,
To be quiet and not take much more than they give.
Cause the fragile knife edge she must constantly walk
Dictates every word she's permitted to talk,
Each mouthful is measured, each glance not too sly,
Lest she melt off her wings just from touching the sky.
And she'd love to exist as the person she knows
Lives inside of her mind, but her agony grows.

As she slowly but surely resigns herself to
Being smaller and using far less than they do,
Being meeker and not taking up too much space,
Being careful to always remember her place.
But the little girl vows that the curse will be broken,
She'll break down the barriers, leave them wide open:
For the daughters of little girls you wouldn't hear;
For the children of women you silenced with fear;
For our mothers we'll sing till the screams rip the air;
We are the little girls you couldn't scare.`
  }
};

// ── Scottish Short Stories & Prose Texts ─────────────────────────────
const PROSE_TEXTS = {
  "Iain Crichton Smith": {
    "The Painter": `We felt a certain responsibility towards him also since he was sickly, and many maintained that he wouldn't live very long, as he was so clever. So our houses were decorated with his colourful paintings and if any stranger came to the village we always pointed to the paintings with great pride and mentioned the painter as one of our greatest assets. No other village that we knew of had a painter at all, not even an adult painter, and we had a wonderful artist who was also very young.

It is true that once or twice he made us uncomfortable for he insisted on painting things as they were, and he made our village less glamorous on the whole than we would have liked it to appear. Our houses weren't as narrow and crooked as he made them seem in his paintings, nor did our villagers look so spindly and thin. Nor was our cemetery, for instance, so confused and weird. And certainly it wasn't in the centre of the village as he had placed it.

He was a strange boy, seeming much older than his years. He hardly ever spoke and not because there was anything wrong with him but because it seemed as if there was nothing much that he wished to say. He dressed in a very slapdash manner and often had holes in the knees of his trousers, and paint all over his blouse. He would spend days trying to paint a particular house or old wall or the head of an old woman or old man. But as we had a lot of old people in the village, some who could play musical instruments — especially the melodeon — extremely well, he didn't stand out as a queer person.

There is, however, one incident that I shall always remember. Our village of course was not a wholly harmonious place. It had its share of barbarism and violence. Sometimes people quarrelled about land and much less often about women. Once there was a prolonged controversy about a right of way. But the incident I was talking about happened like this. There was in the village a man called Red Roderick who had got his name because of his red hair. As is often the case with men with red hair he was also a man of fiery temper, as they say. He drank a lot and would often go uptown on Saturday nights and come home roaring drunk, and march about the village singing. He was in fact a very good strong singer but less so when he was drunk. He spent most of his time either working on his croft or weaving in his shed and had a poor thin wife given to bouts of asthma whom he regularly beat up when it suited him and when he was in a bad temper.

His wife was the daughter of Big Angus who had been a famous fisherman in his youth but who had settled down to become a crofter and who was famed for his great strength though at this time he was getting old. His daughter's name was Anna and during the course of most days she seemed to be baking a lot without much result. You would also find her quite often with a dripping plate and a soggy dishcloth in her hand. She had seven children all at various stages of random development and with running noses throughout both summer and winter.

It must be said that, when sober, Red Roderick was a very kind man, fond of his children and picking them up on his shoulders and showing them off to people and saying how much they weighed and how clever and strong they were, though in fact none of them was any of these things, for they were in fact skinny and underweight and tending to have blotches and spots on their faces and necks. In those moments he would say that he was content with his life and that no one had better children or better land than he had. When he was sunny-tempered he was the life and soul of the village and up to all sorts of mischief, singing songs happily in a very loud and melodious voice which revealed great depth of feeling. That was why it seemed so strange when he got drunk. His whole character would change and he would grow violent and morose and snarl at anyone near him, especially the weakest and most inoffensive people.`,

    "The Red Door": `[Extract only — full text not available online. This story follows a man who discovers a red door on a house in his village and becomes obsessed with its significance, exploring themes of conformity, individuality and the pressure of community expectations in a traditional Highland village setting.]`,

    "Mother and Son": `[Extract only — full text not available online. This story centres on the relationship between a widowed mother and her adult son, exploring themes of dependence, duty, guilt and the suffocating nature of family obligation in a close-knit community.]`,

    "The Existence of the Hermit": `[Extract only — full text not available online. This story examines the life of a hermit figure on the margins of a Scottish community, exploring themes of isolation, the gaze of society upon those who do not conform, and questions about what constitutes a meaningful life.]`
  },

  "Scottish Short Stories": {
    "A Voice Spoke to me at Night": `I don't know why it did. I live in a new-build house above a Tesco Metro. This part of the city used to be a gasworks: before that, housing; before that, fields; before that, who knows. My house is three years old and I am the first person to have lived in it. Before me there was only air and scaffolding and then the slow accumulation of walls. I like to think that nothing has happened here. That the house is neutral, uninflected by the past.

I was alone. My partner was away at a conference in Edinburgh. We had been together for six years and he travelled often for work. I was used to being alone. I liked it, even: the chance to eat cereal for dinner and stay up too late reading. I had done exactly that. I had eaten cereal — corn flakes with too much sugar — and I had read until my eyes ached, and then I had turned off the light and lain in the dark listening to the sounds of the city.

The voice came just as I was falling asleep.

It was not a dramatic voice. It did not boom or whisper. It was an ordinary voice, the voice of someone who was simply telling me something they thought I should know. It said: there is something in the garden.

I was not frightened, not at first. I assumed I had been dreaming, or that I had heard something from outside — a person walking past, a television through a wall. I lay still and waited for sleep to return. But I found I was listening, in the way that you listen when you have heard something that you cannot explain. The house settled. The city made its usual noises. And I lay there, very awake, thinking about the garden.

The garden was small. It had been laid with AstroTurf by the developers because real grass was considered too much trouble. There was a wooden fence and a gate that I rarely used, and a small border of gravel along one side. There was nothing in it. There had never been anything in it.

I got up.

I told myself I was getting up to get a glass of water. I went to the kitchen and filled a glass and stood at the back door looking out at the garden. The security light came on, flooding the AstroTurf with yellow light. The gravel glittered. The fence threw its shadow. There was nothing there.

I went back to bed.

The voice did not speak again that night. But I did not sleep either. I lay in the yellow dark — the curtains in our room are thin — and I listened to the house. In the morning I felt foolish. I made coffee and checked my emails and did not think about the voice. I had almost forgotten it by the time I walked out of the front door. But as I turned to lock it, I found myself checking: checking the street, checking the window, checking the thin strip of sky above the roofline. I did not know what I was checking for.

That evening I called my partner. I did not tell him about the voice. I told him about the cereal, the book, the bad sleep. He told me about the conference. After we said goodbye I sat for a while in the kitchen, the phone still in my hand, and I thought about how the house felt different now. Not frightening. Just inhabited, somehow, in a way it had not been before. As though something had moved in while I wasn't looking, and arranged its things very quietly in the corner.

I went to bed early. I left the hall light on.`,

    "Things My Wife and I Found Hidden in Our House": `1. A RING

And isn't that sweet? Isn't it just too perfectly sweet, like it's a message of hope left for me and Alice, a blessing for our life together. We found it in the cavity between the kitchen wall and the cabinet — it slipped out when the joiners were taking the cabinet off the wall, a little gold loop that spun glittering across the floor tiles. When I put it on my finger it fit like it was meant for me.

I should say, I suppose, that the house is old. It was built in 1886, which means it's been standing since Queen Victoria was on the throne and Robert Louis Stevenson was writing about Jekyll and Hyde, and all the people who've lived here since then. There have been so many people. It's strange, living somewhere with that much behind it. Alice says she likes it, the sense of being part of a sequence. I'm not sure I feel that way. I look at the walls sometimes and think about all the things that have happened here that I'll never know.

2. A LETTER

In German, which neither Alice nor I can read. We found it behind the radiator in the back bedroom, folded into a small square, the paper soft with age. The handwriting is very small and very neat and the ink is faded to a pale brown. There is a date at the top: 14 März 1943. We looked at it for a long time, not touching it. Then Alice said, carefully, that we should have it translated. I said yes. We put it in a drawer in the kitchen. We have not had it translated. I find I don't want to know what it says.

3. A SHOE

A child's shoe, brown leather, about the size of my fist. We found it in the eaves of the roof when we were putting in insulation. It was placed there deliberately — not lost, but put there, tucked into the corner at the very peak of the eaves. Hung up in a house, I had read somewhere, to ward off evil. Or to capture it. I can never remember which. Alice wanted to keep it. I wanted to throw it away. In the end we put it back where we found it.

4. A KEY

Small and brass, of the kind that fits a diary or a small box. We found it under a loose floorboard in the hall. There was nothing else beneath the board — no box, no diary, nothing to explain the key. Just the key, lying on the earth below the floor as though it had been placed there deliberately, or as though it had been waiting.

I have tried every lock in the house. The key doesn't fit any of them.

5. TEETH

Not a full set. Just three, lying together in the space under the bath when the plumber took the panel off. He held them up on his palm and we all looked at them for a moment — Alice, the plumber, and me — and then Alice said they were probably baby teeth, and the plumber agreed, and I said nothing. They were definitely not baby teeth. They were too big, too worn. The roots were very long.

The plumber put them on the edge of the bath and didn't mention them again. Alice put them in the bin. That night I dreamed about all the mouths that had been in this house, all the things that had been said inside these walls, and I woke at three in the morning and lay very still, listening.`,

    "Andrina": `Jack lived with his mother, a small woman with a sharp face, in a house at the edge of the town where the fields began. He was not a young man, though he looked younger than he was; he had, people said, a youthful look about him which would probably stay with him for ever, the look of a man who had never quite decided what to make of himself.

His mother was old but very energetic. She had opinions about everything and delivered them clearly and without apology to anyone who happened to be nearby. She was especially firm about women. She had a gift for making Jack feel that the women he was attracted to were somehow unsuitable, foolish, mercenary, or merely insufficient; that they lacked some fundamental quality which she herself possessed. She had been doing this for so long that Jack had come to accept it as a kind of natural law, like weather.

He first saw Andrina on a Tuesday afternoon in October. He had gone to the library to return some books and she was standing at the desk talking to the librarian. She was tall, with very dark hair, and she was wearing a coat the colour of autumn leaves. She turned as he came in and he saw her face clearly for a moment — she had the kind of face, he thought, that one would remember even after a long time, even after everything else had been forgotten.

He took his books to the desk. She moved aside politely. Their hands did not touch; there was no exchange of any kind. But he was aware of her all the time he was there, aware of her presence as he was aware of light or cold.

He did not see her again for three weeks. Then one evening in November he found himself sitting opposite her in the small Italian restaurant near the harbour. She was with a friend, a fair-haired woman who talked a great deal and laughed often. Andrina listened more than she spoke. She ate her food carefully. Once she looked up and saw him watching her and she held his gaze for a moment and then looked away, and he felt something tighten in his chest.

He asked about her. It was a small town; these things were possible. He found out her name and that she worked at the secondary school and that she lived alone. He found out that she had come from elsewhere, not from the island at all, but from the mainland, years ago, for reasons that were not specified.

His mother said: I've seen that woman in the town. She has a look about her. I don't know what it is exactly. A look.

Jack said nothing.

His mother said: I'm not saying anything.

Jack said nothing.

His mother said: Only that there's something about her I can't quite put my finger on, and in my experience when you can't put your finger on it, that's usually when you should be most careful.`,

    "Death In A Nut": `Jack lived with his mother. He was a young man, not yet married, and he worked the croft as his father had done before him, and he was content enough with his life, which was simple and followed the patterns of the seasons.

One morning in late autumn his mother fell ill. She lay in the box bed by the fire and grew no better, and the days went on and she grew no better, and at last the doctor came and shook his head and said there was nothing to be done.

Jack sat beside the bed and held his mother's hand and thought about what it meant, that she would die. He had never thought about it before. He was young and the world had always seemed to him permanent — the croft and the hills and the sky above them — and it had not occurred to him that the people in it were not permanent also.

He went out in the evening and sat on the hillside and looked at the sea, and while he was sitting there he saw a strange thing: a very small man, no bigger than his hand, walking along the path from the village. Jack watched him in astonishment. The little man did not seem to notice him. He walked with great purpose and carried a small bag over one shoulder.

Jack said: Who are you?

The little man stopped and looked up at him.

He said: I am Death. I have come for your mother.

Jack thought quickly. He reached down and caught the little man in his fist, carefully, so as not to hurt him, and before Death could protest Jack had put him inside the creel he used for peat and fastened the lid.

He carried the creel home and put it in the corner of the byre and went in to see his mother.

She was sitting up in bed.

She said: I feel better. I feel very much better.

Jack said: I'm glad to hear it.

His mother said: It's the strangest thing. An hour ago I was certain I was dying and now I feel I could get up and dance.

She did not get up and dance but she did eat a bowl of soup and she did not die.

The days went on. His mother grew stronger. Jack went out each morning to see to the animals and each morning he checked the creel in the corner of the byre and Death was still inside it.

But after a while Jack began to notice things. The old dog who had been limping for months did not die. The cow who had been ailing did not die. The ancient hen who had stopped laying and who his mother had said they really must kill for the pot did not die. Nothing died. Not a fly, not a mouse, not a sparrow.

And then one day a man from the village came to the door and said his wife had been taken ill suddenly, a terrible sudden illness, and would Jack come, and Jack came, and the wife was in great pain, and the doctor had been, and the doctor said there was nothing he could do but she was still there, still suffering, unable to get better, unable to die.

Jack went home and sat for a long time looking at the creel.

At last he opened it.

Death climbed out. He was very stiff and not best pleased, but he seemed to understand that the arrangement had not been made in malice.

He said: I'll be going then.

Jack said: Will you spare my mother a while longer?

Death looked at him for a moment. Then he said: A while.

And that was all. He walked off down the path towards the village and Jack went inside to his mother who was sitting by the fire, and he did not tell her what he had done, and she did not ask, and they ate their supper in the warmth of the fire with the dark coming down outside.`
  },

  "Anne Donovan": {
    "All That Glisters": `Thon wee wifey in the art shop pure done ma heid in. See when ah went in tae get the stuff fur daddy's Christmas present, she wis aw nice as pie, a right wee smarmy besom.

Can ah help you, dear?

Aye, ah'm lookin fur they pens. The special wans that look like gold and silver.

She hid this look oan her face, a kinna snooty look, as if ah wisnae the kinna person that bought they pens.

These are artists' materials, they're very expensive.

Ah know that. Ma daddy uses them — he's an artist.

Well, if you're sure... they're over here.

She showed me this whole rack of them, gold and silver and bronze and copper and pearly white and pearly pink and ah didnae know which wans tae get because usually daddy got them hissel.

D'you know which ones he uses?

Metallic, ah think.

These are all metallic, dear.

The gold and silver and copper and that. No the white and pink wans.

She still hid that look oan her face but she showed me how there wis different sorts of gold and silver, how some of them hid numbers on them that meant how thick the line wis. That wis a surprise — ah didnae know pens could make different thickness lines.

How much is this yin?

That one is three pounds fifty.

And ah only hid five pounds.

Ah stood there, no knowing whit tae dae. The Christmas lights wis flashin in the corner of ma eye and ah could feel the tears startin tae come, which wis dead stupid because ah'm no a wean any merr, ah'm nearly twelve, and ah really wanted tae just get the right present fur ma daddy, the wan he really wanted, no some rubbish that he'd just smile at and say thanks fur.

Ah've only got five pounds.

Right, she says.

Could ah get two fur five pounds?

She looked at me fur a minute and ah thought she wis gonna say naw.

You could get two of the less expensive ones. That one you were looking at is one of the dearer ones.

She showed me two different pens that wis a pound each and explained how they wis different fae the dearer wans, how they didnae last as lang and the colour wisnae as good.

These are fine for beginners but your father would probably prefer the better quality ones.

Ah thought aboot ma daddy. He's no been very well — he's got this thing wrong wi his lungs and he canny work full time any merr. He works fae hame noo, daein drawin and paintings, and he sits by the windae where the light's good and he's always sayin how much he loves the metallic pens because of the way the light catches them.

These are the wans he likes. The dear yins.

That's right. These are much better quality.

Ah stood there fur a minute.

Right, ah'll take this wan then. The wan fur three fifty.

She wrapped it up fur me, in gold tissue paper and a wee box, and ah walked hame wi ma wan pen in its nice box and the pound fifty change in ma pocket, and ah thought aboot ma daddy's face when he opened it on Christmas morning, how he'd be pleased even though it wis just the wan pen and no a set, and ah thought aboot the way the light caught the gold nib of the pen as ah held it up, and fur a minute it wis like the whole world wis made of light.`,

    "Hieroglyphics": `[Text not currently available — this story follows Mary, a young girl growing up in Glasgow who struggles with dyslexia and is dismissed by her teachers. The story explores how language can be a barrier and the way institutions fail those who do not fit their expectations. Available in Anne Donovan's collection Buddha Da and Other Stories.]`,

    "Me and My Babbie": `[Text not currently available — story follows a young mother navigating the early days with a new baby, written in Glaswegian dialect. Explores themes of motherhood, isolation, love and exhaustion. Available in Anne Donovan's collection Hieroglyphics and Other Stories.]`,

    "Loast": `[Text not currently available — this story is written in Scots dialect and explores themes of loss and displacement. Available in Anne Donovan's short story collections.]`
  }
};

// eslint-disable-next-line no-unused-vars
const getProseText = (author, title) => PROSE_TEXTS[author]?.[title] || null;

// Map prescribed list display names to POEMS keys
const POETRY_COLLECTION_MAP = {
  "N5 Scottish Poetry Collection": "Scottish Poetry Collection",
  "Higher Scottish Poetry Collection": "Higher Scottish Poetry Collection",
  "Scottish Poetry Collection": "Scottish Poetry Collection"
};

const resolvePoetKey = (name) => POETRY_COLLECTION_MAP[name] || name;

// Flat lookup: poet + title → text
const getPoemText = (poet, title) => POEMS[poet]?.[title] || null;

// List of poems per poet for the picker
const POET_POEMS = Object.fromEntries(
  Object.entries(POEMS).map(([poet, poems]) => [poet, Object.keys(poems)])
);

// List of prose texts per author
// eslint-disable-next-line no-unused-vars
const AUTHOR_PROSE = Object.fromEntries(
  Object.entries(PROSE_TEXTS).map(([author, texts]) => [author, Object.keys(texts)])
);

// ── English Mock Panel ────────────────────────────────────────────────
const PROSE_COLLECTION_STORIES = {
  "Short Stories — Iain Crichton Smith (The Red Door, Mother and Son, The Painter, The Existence of the Hermit)": ["The Red Door","Mother and Son","The Painter","The Existence of the Hermit"],
  "Scottish Short Stories — A Voice Spoke to me at Night (Helen McClory)": ["A Voice Spoke to me at Night"],
  "Scottish Short Stories — Things My Wife and I Found Hidden in Our House (Kirsty Logan)": ["Things My Wife and I Found Hidden in Our House"],
  "Scottish Short Stories — Andrina (George Mackay Brown)": ["Andrina"],
  "Scottish Short Stories — Death In A Nut (Duncan Williamson)": ["Death In A Nut"],
  "Short Stories — Anne Donovan (All That Glisters, Hieroglyphics, Me and My Babbie, Loast)": ["All That Glisters","Hieroglyphics","Me and My Babbie","Loast"],
  "Duck Feet (Part Wan First Year) — Ely Percy": null,
  "Dr Jekyll and Mr Hyde — Robert Louis Stevenson": null
};

const proseShortName = (entry) => {
  if (!entry) return entry;
  if (entry.startsWith("Short Stories — Iain Crichton Smith")) return "Iain Crichton Smith";
  if (entry.startsWith("Scottish Short Stories — A Voice")) return "A Voice Spoke to me at Night";
  if (entry.startsWith("Scottish Short Stories — Things My Wife")) return "Things My Wife and I Found...";
  if (entry.startsWith("Scottish Short Stories — Andrina")) return "Andrina";
  if (entry.startsWith("Scottish Short Stories — Death In A Nut")) return "Death In A Nut";
  if (entry.startsWith("Short Stories — Anne Donovan")) return "Anne Donovan";
  if (entry.startsWith("Duck Feet")) return "Duck Feet";
  if (entry.startsWith("Dr Jekyll")) return "Dr Jekyll and Mr Hyde";
  return entry;
};

function EnglishMockPanel({ level, userData }) {
  const [paperChoice, setPaperChoice] = useState(null); // null | "ruae" | "critical"
  const [examState, setExamState] = useState("setup");
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  const isHigher = level === "Higher";
  const isN5 = level === "National 5";
  const prescribedForLevel = ENGLISH_PRESCRIBED[level] || ENGLISH_PRESCRIBED["Higher"];

  // RUAE state
  const [passage1, setPassage1] = useState(null);
  const [passage2, setPassage2] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState("");
  const [results, setResults] = useState(null);

  // Critical Reading state
  const [crTab, setCrTab] = useState("Poetry"); // Drama | Prose | Poetry
  const [selectedAuthor, setSelectedAuthor] = useState("Carol Ann Duffy");
  const [selectedPoem, setSelectedPoem] = useState("Medusa");
  const [selectedStory, setSelectedStory] = useState(null);
  const [crExtract, setCrExtract] = useState(null);
  const [crSSTQuestions, setCrSSTQuestions] = useState(null);
  const [crEssayQuestions, setCrEssayQuestions] = useState(null);
  const [crAnswers, setCrAnswers] = useState({ sst: "", essay: "" });
  const [crResults, setCrResults] = useState(null);
  const [crSection, setCrSection] = useState("sst"); // sst | essay | both

  useEffect(() => {
    if (examState === "in_progress") {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [examState]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const reset = () => {
    setPaperChoice(null); setExamState("setup"); setTimer(0);
    setPassage1(null); setPassage2(null); setQuestions(null); setAnswers(""); setResults(null);
    setCrExtract(null); setCrSSTQuestions(null); setCrEssayQuestions(null);
    setCrAnswers({ sst: "", essay: "" }); setCrResults(null); setCrSection("sst");
    setAhQuestions(null); setAhAnswer(""); setAhResults(null); setAhPassage(null);
  };

  // Reset author/poem when crTab or level changes
  useEffect(() => {
    const authors = prescribedForLevel[crTab] || [];
    const first = authors[0] || null;
    setSelectedAuthor(first);
    if (crTab === "Poetry" && first) {
      setSelectedPoem(POET_POEMS[first]?.[0] || null);
    } else {
      setSelectedPoem(null);
    }
  }, [crTab, level]); // eslint-disable-line

  // ── RUAE Generation ──────────────────────────────────────────────────
  const generateRUAE = async () => {
    setExamState("generating");
    const topic = [
      "street art and public spaces", "the psychology of social media", "rewilding and conservation",
      "the future of work and automation", "loneliness in modern society", "the ethics of AI",
      "extreme sports and risk-taking", "the decline of traditional crafts", "music and identity",
      "urban farming and food security", "sleep science", "the value of failure",
      "memory and nostalgia", "competitive gaming and esports", "climate grief"
    ][Math.floor(Math.random() * 15)];

    try {
      // Generate passage(s) first
      const passageSystem = isHigher
        ? `You are generating TWO thematically linked non-fiction passages for a Higher English RUAE exam. Both must be about: "${topic}".

PASSAGE 1 (~900 words): A detailed, opinion-based non-fiction article. Use paragraph breaks. Number each line group in multiples of 5 in the left margin (e.g. 5, 10, 15...) as real SQA papers do. Write as if from a broadsheet newspaper or literary magazine. Include varied sentence structures, strong word choice, imagery, and clear argument progression.

PASSAGE 2 (~600 words): A second article on the same topic. Must agree OR disagree with Passage 1 on at least 3 clear points. Same formatting. This passage has NO questions — it is read-only.

Format your output EXACTLY as:
---PASSAGE 1---
[full passage with line numbers every 5 lines]
---PASSAGE 2---
[full passage with line numbers every 5 lines]`
        : `You are generating ONE non-fiction passage for a National 5 English RUAE exam about: "${topic}".

The passage should be ~1000 words. Write as if from a newspaper or magazine. Use clear paragraph structure. Number every 5th line in the left margin (e.g. 5, 10, 15...). The language should be accessible but with some challenge — allow weaker candidates a general understanding while giving stronger candidates language to analyse (word choice, imagery, sentence structure).

Format your output EXACTLY as:
---PASSAGE 1---
[full passage with line numbers]`;

      const passageRes = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: passageSystem,
          messages: [{ role: "user", content: `Generate the passage(s) now about: ${topic}` }]
        })
      });
      const passageData = await passageRes.json();
      const passageText = passageData.content?.[0]?.text || "";

      const p1Match = passageText.match(/---PASSAGE 1---([\s\S]*?)(?=---PASSAGE 2---|$)/);
      const p2Match = passageText.match(/---PASSAGE 2---([\s\S]*?)$/);
      const p1 = p1Match ? p1Match[1].trim() : passageText;
      const p2 = p2Match ? p2Match[1].trim() : null;

      setPassage1(p1);
      if (p2) setPassage2(p2);

      // Now generate questions
      const questionSystem = isHigher
        ? `You are writing questions for a Higher English RUAE paper. The passage is about "${topic}". Total: 30 marks.

Generate exactly 9 questions. Questions 1–8 are on Passage 1 only. Question 9 is on BOTH passages.

Rules:
- Always specify the line range: "Read lines X–Y."
- For understanding questions: use "Explain fully" and add "Use your own words in your answer."
- For analysis questions: use "By referring to at least two examples, analyse how the writer uses language to..."
- For identification/summary: use "Identify X points" in bold
- Mark allocations for Q1–8 must add to 25. Use: 2, 3, or 4 marks each. Vary them realistically.
- Q9 = 5 marks: "Look at both passages. [State the relationship — agreement or disagreement]. Identify three key areas on which they [agree/disagree]. Support your points by referring to both passages. You may answer in continuous prose or developed bullet points."
- Use these command words ONLY: explain, explain fully, analyse, identify
- Bold any number of examples required e.g. "By referring to **at least two** examples"
- The final total must be exactly 30 marks.

Output ONLY the numbered questions with marks. No preamble.`
        : `You are writing questions for a National 5 English RUAE paper. The passage is about "${topic}". Total: 30 marks.

Generate exactly 9 questions on the single passage.

Rules:
- Always specify the line range: "Look at lines X–Y."
- For understanding: "Using your own words as far as possible, explain..." — always require own words in bold
- For analysis: "By referring to **two** examples of language, explain how the writer..."
- For identification: "Identify **X** points..."
- Mark allocations must total exactly 30. Use: 2, 3, or 4 marks per question.
- NO 5-mark question on a second passage (this is National 5 — single passage only).
- Command words: explain, summarise, identify only.
- Bold the number of examples/points required.

Output ONLY the numbered questions with marks. No preamble.`;

      const qRes = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: questionSystem,
          messages: [{ role: "user", content: `Generate the questions for this passage:\n\n${p1}\n\n${p2 ? "Passage 2:\n" + p2 : ""}` }]
        })
      });
      const qData = await qRes.json();
      setQuestions(qData.content?.[0]?.text || "");
      setExamState("in_progress");
      setTimer(0);
    } catch (err) {
      console.error(err);
      setExamState("setup");
    }
  };

  // ── RUAE Marking ────────────────────────────────────────────────────
  const markRUAE = async () => {
    if (!answers.trim()) return;
    setExamState("marking");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: `You are marking a ${level} English RUAE paper. For each question: state marks awarded vs available, explain what earns marks, note what was missing. Be specific about own-words compliance for understanding questions and quote+comment structure for analysis questions. End with total /30, a grade estimate (A/B/C/D), and the top 2 skills to practise.`,
          messages: [{ role: "user", content: `Passage 1:\n${passage1}\n\n${passage2 ? "Passage 2:\n" + passage2 + "\n\n" : ""}Questions:\n${questions}\n\nStudent answers (time: ${formatTime(timer)}):\n${answers}` }]
        })
      });
      const data = await res.json();
      setResults(data.content?.[0]?.text || "Marking failed.");
      setExamState("results");
    } catch { setResults("Connection error."); setExamState("results"); }
  };

  // ── Critical Reading Generation ──────────────────────────────────────
  const generateCriticalReading = async () => {
    setExamState("generating");
    const genre = crTab;
    const author = selectedAuthor;
    const isPoet = genre === "Poetry";

    try {
      let extractText = null;

      if (isPoet) {
        // Use the hardcoded poem — no API call needed for the extract
        extractText = getPoemText(resolvePoetKey(author), selectedPoem);
        if (!extractText) {
          setExamState("setup");
          return;
        }
        setCrExtract(extractText);
      }

      // Generate SST questions only (extract already set for poetry)
      const questionPrompt = isPoet
        ? (isHigher
          ? `Generate 4 questions for this Higher English Critical Reading paper on the poem "${selectedPoem}" by ${author}.

The poem text is:
${extractText}

Number the lines of the poem yourself for reference (every line gets a number, starting at 1).

Generate exactly 4 questions (2+4+4+10 = 20 marks):
Q1 (2 marks): "Look at lines X–Y. Analyse how the poet's use of language [specific effect]." Choose lines with strong imagery or tone.
Q2 (4 marks): "Look at lines X–Y. By referring to at least two examples, analyse how the poet's use of language [specific effect]." Choose a substantial section.
Q3 (4 marks): "Look at lines X–Y. By referring to at least two examples, analyse how the poet's use of language [specific effect]." Choose a different section.
Q4 (10 marks): "By referring to this poem and to at least one other poem by ${author}, discuss how [central concern/theme evident in this poem]."

For Q4 note in brackets: (2 marks commonality — a general point comparing across poems; 2 marks from this extract with quotation; 6 marks from elsewhere in ${author}'s work — aim for 3 developed points each worth 2 marks)

Make the questions genuinely interesting and appropriate to Higher level analysis. Use "analyse" as the command word for Q1–Q3, "discuss" for Q4. Reference specific line numbers.

Output ONLY the 4 questions, clearly numbered with mark allocation at the end of each line.`
          : `Generate 4 questions for this National 5 English Critical Reading paper on the poem "${selectedPoem}" by ${author}.

The poem text is:
${extractText}

Number the lines of the poem yourself for reference (every line gets a number, starting at 1).

Generate exactly 4 questions (4+4+4+8 = 20 marks):
Q1 (4 marks): "Look at lines X–Y. By referring to two examples, explain how the poet's use of language [specific effect]." Choose lines with clear word choice or imagery.
Q2 (4 marks): "Look at lines X–Y. By referring to two examples, explain how the poet's use of language [specific effect]." Choose a different section.
Q3 (4 marks): "Look at lines X–Y. By referring to two examples, explain how the poet conveys [theme/feeling]."
Q4 (8 marks): "By referring to this poem and to at least one other poem by ${author}, explain how [central concern/theme evident in this poem]."

For Q4 note in brackets: (2 marks general/commonality point; 2 marks from this poem with quotation; 4 marks from elsewhere — 2 points × 2 marks each)

Use "explain" as the command word throughout. Reference specific line numbers.

Output ONLY the 4 questions, clearly numbered with mark allocation at the end of each line.`)
        : `Generate a Higher English Critical Reading paper extract and 4 questions for ${genre}: ${author}.

${genre === "Drama"
  ? `Write a stage extract of 40–55 lines from a key scene in ${author}. Number every line. Then generate 4 questions.`
  : `Write a prose extract of 40–55 lines from a key moment in ${author}. Number every line. Then generate 4 questions.`}

Questions: Q1 (2 marks) analyse, Q2 (4 marks) analyse with at least 2 examples, Q3 (4 marks) analyse with at least 2 examples, Q4 (10 marks) discuss — referring to this extract and elsewhere.

Format output as:
---EXTRACT---
[extract with line numbers]
---QUESTIONS---
[4 questions]`;

      const qRes = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: `You are an expert SQA Higher English examiner generating Critical Reading questions. Use correct SQA command words and mark allocations. Be specific about line references.`,
          messages: [{ role: "user", content: questionPrompt }]
        })
      });
      const qData = await qRes.json();
      const qText = qData.content?.[0]?.text || "";

      if (!isPoet) {
        const extractMatch = qText.match(/---EXTRACT---([\s\S]*?)(?=---QUESTIONS---|$)/);
        const questionsMatch = qText.match(/---QUESTIONS---([\s\S]*?)$/);
        setCrExtract(extractMatch ? extractMatch[1].trim() : "");
        setCrSSTQuestions(questionsMatch ? questionsMatch[1].trim() : qText);
      } else {
        setCrSSTQuestions(qText);
      }

      // Critical essay questions — Section 2
      const essaySystem = isHigher
        ? `You are generating Section 2 (Critical Essay) questions for a Higher English Critical Reading paper.

Generate exactly 6 sets of questions — one for each genre. Within each genre, generate exactly 3 questions to choose from (as real SQA papers do).

Genres: Drama, Prose Fiction, Prose Non-fiction, Poetry, Film and Television Drama, Language.

Each question must:
- Start with a broad, accessible statement about a text type
- Use "By referring to appropriate techniques, explain/discuss..." format
- Be applicable to ANY text within the genre (not text-specific)
- Use "explain" or "discuss" as the main command word
- Be worth 20 marks (stated at end)

Format EXACTLY as:
---DRAMA---
1. [question] 20
2. [question] 20
3. [question] 20
---PROSE FICTION---
[3 questions]
---PROSE NON-FICTION---
[3 questions]
---POETRY---
[3 questions]
---FILM AND TELEVISION DRAMA---
[3 questions]
---LANGUAGE---
[3 questions]`
        : `You are generating Section 2 (Critical Essay) questions for a National 5 English Critical Reading paper.

Generate exactly 5 sets of questions — one for each genre. Within each genre, generate exactly 2 questions to choose from.

Genres: Drama, Prose, Poetry, Film and Television Drama, Language. (No Prose Non-fiction at National 5.)

Each question must:
- Start with a broad, accessible statement about a text type
- Use "By referring to appropriate techniques, explain how..." format
- Be applicable to ANY text within the genre (not text-specific)
- Use "explain" as the main command word (not "discuss")
- Be worth 20 marks (stated at end)

Format EXACTLY as:
---DRAMA---
1. [question] 20
2. [question] 20
---PROSE---
[2 questions]
---POETRY---
[2 questions]
---FILM AND TELEVISION DRAMA---
[2 questions]
---LANGUAGE---
[2 questions]`;

      const essayRes = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: essaySystem,
          messages: [{ role: "user", content: "Generate the critical essay questions now." }]
        })
      });
      const essayData = await essayRes.json();
      setCrEssayQuestions(essayData.content?.[0]?.text || "");

      setExamState("in_progress");
      setTimer(0);
    } catch (err) {
      console.error(err);
      setExamState("setup");
    }
  };

  // ── Critical Reading Marking ─────────────────────────────────────────
  const markCriticalReading = async () => {
    setExamState("marking");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: isHigher
            ? `You are marking a Higher English Critical Reading paper.

SECTION 1 — Scottish Text (20 marks):
For Q1–Q3 (analysis questions): reference + comment structure. 2 marks = detailed/insightful comment + quotation. 1 mark = basic comment + quotation. 0 for reference alone. Mark each out of its stated total.
For Q4 (10-mark question): 2 marks for general/commonality point, 2 marks for developed point from extract, 6 marks for points from elsewhere. Give specific feedback on each element.

SECTION 2 — Critical Essay (20 marks):
Mark using SQA generic marking: relevance to question, knowledge of text, analysis of technique, evaluation. Give a mark /20 and explain what band it falls in (A=16–20, B=12–15, C=8–11, D=4–7).

End with: total SST /20, total essay /20, overall /40, grade estimate.`
            : `You are marking a National 5 English Critical Reading paper. Be encouraging and constructive.

SECTION 1 — Scottish Text (20 marks):
For Q1–Q3 (explain questions, 4 marks each): quote + comment = 2 marks per developed point. Reward clear explanations using own words alongside quotation. Be encouraging with partial credit.
For Q4 (8-mark question): 2 marks for a general/commonality point comparing across poems; 2 marks for a developed point from this extract with quotation; 4 marks for points from elsewhere (2 points × 2 marks each). Give specific, encouraging feedback on each element.

SECTION 2 — Critical Essay (20 marks):
Mark using SQA generic marking: relevance to question, knowledge of text, use of technique, personal response. Give a mark /20 with band (A=16–20, B=12–15, C=8–11, D=4–7).

End with: SST /20, Essay /20, Overall /40, grade estimate.`,
          messages: [{
            role: "user",
            content: `Extract:\n${crExtract}\n\nSST Questions:\n${crSSTQuestions}\n\nSST Answers:\n${crAnswers.sst}\n\nCritical Essay Answers:\n${crAnswers.essay}\n\nTime taken: ${formatTime(timer)}`
          }]
        })
      });
      const data = await res.json();
      setCrResults(data.content?.[0]?.text || "Marking failed.");
      setExamState("results");
    } catch { setCrResults("Connection error."); setExamState("results"); }
  };

  // ── Advanced Higher state ────────────────────────────────────────────
  const [ahGenre, setAhGenre] = useState("Poetry");
  const [ahTexts, setAhTexts] = useState("");
  const [ahQuestions, setAhQuestions] = useState(null);
  const [ahAnswer, setAhAnswer] = useState("");
  const [ahResults, setAhResults] = useState(null);
  const [ahPassage, setAhPassage] = useState(null);
  const [ahPassageGenre, setAhPassageGenre] = useState("Poetry");

  // ── AH Literary Study Generation ─────────────────────────────────────
  const generateAHLiterary = async () => {
    setExamState("generating");
    try {
      const genreRequirements = {
        "Poetry": "three poems (by one or more poets)",
        "Prose Fiction": "two novels or three short stories",
        "Prose Non-fiction": "at least two non-fiction texts",
        "Drama": "two plays"
      };
      const req = genreRequirements[ahGenre] || "two or three texts";

      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 2000,
          system: `You are generating Advanced Higher English Literary Study exam questions. Generate exactly 7 questions for the ${ahGenre} section, matching the style of real SQA Advanced Higher Literary Study papers.

Requirements for ${ahGenre} questions:
- Each question must require reference to ${req}
- Command words: "discuss", "analyse", "compare and contrast", "to what extent do you agree"
- Questions must focus on literary technique AND thematic concern together
- Questions should be broad enough to apply to ANY texts in the genre
- Do NOT name specific texts or authors in the questions
- Each question worth 20 marks
- Questions should range from thematic (isolation, love, power, identity, mortality) to technical (narrative voice, structure, imagery, form)

Format exactly as:
PART [GENRE IN CAPS] — [GENRE] — 20 marks
Attempt ONE question.

1. [question]
2. [question]
3. [question]
4. [question]
5. [question]
6. [question]
7. [question]

Output ONLY the questions. No preamble.`,
          messages: [{ role: "user", content: `Generate 7 Advanced Higher Literary Study questions for ${ahGenre}.` }]
        })
      });
      const data = await res.json();
      setAhQuestions(data.content?.[0]?.text || "");
      setExamState("in_progress");
      setTimer(0);
    } catch (err) {
      console.error(err);
      setExamState("setup");
    }
  };

  // ── AH Literary Study Marking ─────────────────────────────────────────
  const markAHLiterary = async () => {
    if (!ahAnswer.trim()) return;
    setExamState("marking");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: `You are marking an Advanced Higher English Literary Study critical essay (20 marks).

Mark using these criteria:
- Knowledge and understanding of texts: specific textual evidence, insight into central concerns
- Analysis: range of literary techniques examined, depth of analytical comment
- Evaluation: clear evaluative stance, evidence-based judgement
- Expression: structured argument, appropriate critical vocabulary, technical accuracy

Bands: 20–17 (A), 16–13 (B), 12–9 (C), 8–5 (D), 4–0 (No award)

Give:
1. A mark /20 with band
2. Specific strengths (what earned marks)
3. What was missing or underdeveloped
4. One paragraph of model commentary showing the level of analysis expected at AH
5. Top 2 priorities to improve`,
          messages: [{ role: "user", content: `Question paper:
${ahQuestions}

Student's essay:
${ahAnswer}

Texts used: ${ahTexts}

Time taken: ${formatTime(timer)}` }]
        })
      });
      const data = await res.json();
      setAhResults(data.content?.[0]?.text || "Marking failed.");
      setExamState("results");
    } catch { setAhResults("Connection error."); setExamState("results"); }
  };

  // ── AH Textual Analysis Generation ───────────────────────────────────
  const generateAHTextual = async () => {
    setExamState("generating");
    try {
      const genreInstructions = {
        "Poetry": `Generate a complete, original poem of 30–40 lines suitable for Advanced Higher Textual Analysis. The poem should be by a plausible contemporary or modern poet (invent a name). It should have complex imagery, a discernible but non-obvious theme, interesting use of form and structure, and rich language for analysis. Include a title and the poet's name and date (e.g. 2019).

Then generate this question:
"Write a detailed critical analysis of some of the means by which the poet reflects on [central theme of the poem].
In your response you should consider:
• form and structure
• word choice and imagery
• mood and atmosphere
• any other literary device you consider to be important."`,

        "Prose Fiction": `Generate an original prose fiction extract of approximately 800–1000 words suitable for Advanced Higher Textual Analysis. Set it in an interesting situation — the opening of a novel or a key scene. Include rich characterisation, interesting narrative voice, and language worth analysing. Include a title, author name (invented), date and a brief context sentence as SQA papers do.

Then generate this question:
"Discuss the ways in which the opening of this novel/story is effective in establishing [key aspect — character, atmosphere, or central concern].
In your response you should consider:
• narrative voice and perspective
• characterisation
• setting and atmosphere
• language and imagery."`,

        "Prose Non-fiction": `Generate an original non-fiction extract of approximately 800–1000 words suitable for Advanced Higher Textual Analysis. It should be from a biography, essay, or literary non-fiction piece. Include a clear authorial voice, interesting subject matter, and language rich enough for analysis. Include title, author (invented), date and a brief context sentence.

Then generate this question:
"Analyse some of the ways in which the author conveys [central argument/admiration/concern] in this extract.
In your response you should consider:
• word choice and tone
• use of evidence and example
• structure and progression of ideas
• any other techniques you consider important."`,

        "Drama": `Generate an original drama extract of approximately 600–800 words (stage directions + dialogue) suitable for Advanced Higher Textual Analysis. It should introduce characters in a situation of tension or conflict. Include meaningful stage directions and subtext-rich dialogue. Include title, playwright (invented), date and a brief context paragraph.

Then generate this question:
"Make a detailed analysis of the dramatic techniques used in this extract to introduce the audience to [central tension/character dynamic].
In your response you should consider:
• characterisation through dialogue and action
• dramatic tension and conflict
• setting and stage directions
• language and tone."`
      };

      const instruction = genreInstructions[ahPassageGenre] || genreInstructions["Poetry"];

      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: `You are generating an Advanced Higher English Textual Analysis paper. Generate a sophisticated, original unseen text and question. The text must be complex and rich enough to reward detailed critical analysis at Advanced Higher level. Format your output as:

---TEXT---
[full text with any context note, title, author, date]
---QUESTION---
[the question]`,
          messages: [{ role: "user", content: instruction }]
        })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "";
      const textMatch = raw.match(/---TEXT---([\s\S]*?)(?=---QUESTION---|$)/);
      const qMatch = raw.match(/---QUESTION---([\s\S]*?)$/);
      setAhPassage(textMatch ? textMatch[1].trim() : raw);
      setAhQuestions(qMatch ? qMatch[1].trim() : "Write a detailed critical analysis of this text.");
      setExamState("in_progress");
      setTimer(0);
    } catch (err) {
      console.error(err);
      setExamState("setup");
    }
  };

  // ── AH Textual Analysis Marking ───────────────────────────────────────
  const markAHTextual = async () => {
    if (!ahAnswer.trim()) return;
    setExamState("marking");
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001", max_tokens: 3000,
          system: `You are marking an Advanced Higher English Textual Analysis response (20 marks).

The candidate has written a critical analysis of an unseen text. Mark on:
- Understanding of the text's central concerns and how they are communicated
- Analysis of a range of literary techniques with depth and insight
- Evaluative stance — not just identifying techniques but judging their effect
- Expression: structured argument, critical vocabulary, accuracy

Bands: 20–17 (A), 16–13 (B), 12–9 (C), 8–5 (D), 4–0 (No award)

Give:
1. Mark /20 with band and one-sentence justification
2. Strongest points of analysis (quote from their response where possible)
3. What was missing — what techniques or aspects deserved more attention
4. A model paragraph showing the depth of analysis expected at AH level, using the actual text
5. Two specific improvement points`,
          messages: [{ role: "user", content: `Text:
${ahPassage}

Question:
${ahQuestions}

Student response:
${ahAnswer}

Time: ${formatTime(timer)}` }]
        })
      });
      const data = await res.json();
      setAhResults(data.content?.[0]?.text || "Marking failed.");
      setExamState("results");
    } catch { setAhResults("Connection error."); setExamState("results"); }
  };

  // ── Paper choice screen ──────────────────────────────────────────────
  if (!paperChoice) return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, padding: "40px 32px", textAlign: "center" }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>📝</div>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2", letterSpacing: -0.5 }}>{level} English — Mock exam</div>
      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 360, lineHeight: 1.7, fontWeight: 300 }}>Choose which paper to generate. Both include a full SQA-style structure with marking.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 400 }}>
        {level !== "Advanced Higher" && (
          <button onClick={() => setPaperChoice("ruae")} style={{ padding: "18px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Reading for UAE</div>
              <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>
                {level === "Higher" ? "Two linked passages · 30 marks · ~1 hour" : "One passage · 30 marks · ~1 hour"}
              </div>
            </div>
            <span style={{ color: "#1D9E75", fontSize: 18 }}>→</span>
          </button>
        )}
        {level !== "Advanced Higher" && (
          <button onClick={() => setPaperChoice("critical")} style={{ padding: "18px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Critical Reading</div>
              <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>
                {level === "Higher" ? "Scottish Text + Critical Essay · 40 marks · ~1.5 hours" : "Scottish Text (20 marks) + Critical Essay (20 marks) · 40 marks total"}
              </div>
            </div>
            <span style={{ color: "#1D9E75", fontSize: 18 }}>→</span>
          </button>
        )}
        {level === "Advanced Higher" && (
          <>
            <button onClick={() => setPaperChoice("ah_literary")} style={{ padding: "18px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Literary Study</div>
                <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Critical essay on studied texts · 20 marks · 1.5 hours</div>
              </div>
              <span style={{ color: "#1D9E75", fontSize: 18 }}>→</span>
            </button>
            <button onClick={() => setPaperChoice("ah_textual")} style={{ padding: "18px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Textual Analysis</div>
                <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Unseen text — one detailed critical analysis · 20 marks · 1.5 hours</div>
              </div>
              <span style={{ color: "#1D9E75", fontSize: 18 }}>→</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ── Generating spinner ───────────────────────────────────────────────
  if (examState === "generating") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>
        {paperChoice === "ruae" ? "Generating passage and questions…" : "Generating extract and questions…"}
      </div>
      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>This takes about 20 seconds</div>
    </div>
  );

  // ── Marking spinner ──────────────────────────────────────────────────
  if (examState === "marking") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
      <div style={{ fontSize: 14, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>Marking your answers…</div>
    </div>
  );

  // ── AH Literary Study Setup ──────────────────────────────────────────
  if (paperChoice === "ah_literary" && examState === "setup") return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
      <div style={{ maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => setPaperChoice(null)} style={{ background: "transparent", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>←</button>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2" }}>Literary Study — Advanced Higher</div>
        </div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.4)", marginBottom: 20, lineHeight: 1.6, fontWeight: 300 }}>
          Summit will generate 7 questions for your chosen genre. You write one critical essay on your studied texts. Enter the texts you've studied so the marker can give specific feedback.
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 10, fontWeight: 300, letterSpacing: 0.5 }}>Genre</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {["Poetry", "Prose Fiction", "Prose Non-fiction", "Drama"].map(g => (
              <button key={g} onClick={() => setAhGenre(g)}
                style={{ padding: "8px 16px", borderRadius: 8, fontSize: 13, border: ahGenre === g ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: ahGenre === g ? "rgba(29,158,117,0.15)" : "transparent", color: ahGenre === g ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {g}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", marginTop: 8, fontWeight: 300 }}>
            {ahGenre === "Poetry" && "You'll need to write about three poems"}
            {ahGenre === "Prose Fiction" && "You'll need to write about two novels or three short stories"}
            {ahGenre === "Prose Non-fiction" && "You'll need to write about at least two non-fiction texts"}
            {ahGenre === "Drama" && "You'll need to write about two plays"}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 8, fontWeight: 300, letterSpacing: 0.5 }}>Your texts (optional — helps the marker give specific feedback)</div>
          <textarea value={ahTexts} onChange={e => setAhTexts(e.target.value)}
            placeholder={ahGenre === "Poetry" ? "e.g. Poems by Carol Ann Duffy, Norman MacCaig, Edwin Morgan" : ahGenre === "Drama" ? "e.g. Hamlet by Shakespeare, Death of a Salesman by Arthur Miller" : "e.g. 1984 by George Orwell, Brave New World by Aldous Huxley"}
            rows={3}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#f0f4f2", fontSize: 13, fontFamily: "'DM Sans', sans-serif", resize: "none", boxSizing: "border-box" }} />
        </div>

        <div style={{ padding: "10px 14px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 8, fontSize: 12, color: "rgba(240,244,242,0.5)", marginBottom: 20, lineHeight: 1.6 }}>
          ⚠️ <strong style={{ color: "#EF9F27" }}>AI-generated questions.</strong> Practice only — not from SQA. Always use real past papers from sqa.org.uk.
        </div>

        <button onClick={generateAHLiterary} style={{ padding: "13px 28px", background: "#1D9E75", border: "none", borderRadius: 8, color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}>
          Generate questions →
        </button>
      </div>
    </div>
  );

  // ── AH Textual Analysis Setup ─────────────────────────────────────────
  if (paperChoice === "ah_textual" && examState === "setup") return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
      <div style={{ maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => setPaperChoice(null)} style={{ background: "transparent", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>←</button>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2" }}>Textual Analysis — Advanced Higher</div>
        </div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.4)", marginBottom: 20, lineHeight: 1.6, fontWeight: 300 }}>
          Summit will generate an unseen text and one analytical question. Read the text carefully, annotate it, then write your critical analysis.
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 10, fontWeight: 300, letterSpacing: 0.5 }}>Genre</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {["Poetry", "Prose Fiction", "Prose Non-fiction", "Drama"].map(g => (
              <button key={g} onClick={() => setAhPassageGenre(g)}
                style={{ padding: "8px 16px", borderRadius: 8, fontSize: 13, border: ahPassageGenre === g ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: ahPassageGenre === g ? "rgba(29,158,117,0.15)" : "transparent", color: ahPassageGenre === g ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {g}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: "10px 14px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 8, fontSize: 12, color: "rgba(240,244,242,0.5)", marginBottom: 20, lineHeight: 1.6 }}>
          ⚠️ <strong style={{ color: "#EF9F27" }}>AI-generated unseen text.</strong> Practice only. Always supplement with real SQA past papers.
        </div>

        <button onClick={generateAHTextual} style={{ padding: "13px 28px", background: "#1D9E75", border: "none", borderRadius: 8, color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}>
          Generate unseen text →
        </button>
      </div>
    </div>
  );

  // ── AH Literary Study In Progress ────────────────────────────────────
  if (paperChoice === "ah_literary" && examState === "in_progress") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Choose one question — write your critical essay — then submit</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
      </div>
      <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — AI Practice Paper</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>ADVANCED HIGHER</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>ENGLISH — Literary Study</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 2 }}>Total marks — 20 · 1 hour 30 minutes</div>
            </div>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
              <strong>Instructions:</strong> Attempt ONLY ONE question. Your answer should take the form of a critical essay appropriately structured to meet the demands of your selected question. You may not use the text(s) from your dissertation for this paper.
            </div>
            {renderSQAPaper(ahQuestions)}
          </div>
        </div>
        <div style={{ padding: "14px 24px" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 6, fontWeight: 500 }}>Your critical essay</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginBottom: 8, fontWeight: 300 }}>State the question number, then write your full essay. Aim for a well-structured argument with close textual analysis.</div>
          <textarea value={ahAnswer} onChange={e => setAhAnswer(e.target.value)}
            placeholder="Question: [number]  [Your critical essay — aim for a clear line of argument, close analysis of technique, and relevant textual evidence throughout...]"
            style={{ width: "100%", minHeight: 180, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
          <button onClick={markAHLiterary} disabled={!ahAnswer.trim()}
            style={{ marginTop: 8, width: "100%", padding: "13px", background: ahAnswer.trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: ahAnswer.trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
            Submit for marking →
          </button>
        </div>
      </div>
    </div>
  );

  // ── AH Textual Analysis In Progress ──────────────────────────────────
  if (paperChoice === "ah_textual" && examState === "in_progress") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Read the text carefully — annotate it — then write your analysis</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
      </div>
      <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — AI Practice Paper</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>ADVANCED HIGHER</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>ENGLISH — Textual Analysis</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 2 }}>Total marks — 20 · 1 hour 30 minutes</div>
            </div>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
              <strong>Instructions:</strong> Read the text carefully and attempt the question. Your answer should take the form of a critical analysis appropriately structured to meet the demands of the question.
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 12, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>PART {ahPassageGenre.toUpperCase()} — {ahPassageGenre}</div>
              {renderSQAPaper(ahPassage)}
            </div>
            <div style={{ borderTop: "2px solid #000", paddingTop: 16 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Question</div>
              {renderSQAPaper(ahQuestions)}
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 24px" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 6, fontWeight: 500 }}>Your critical analysis</div>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginBottom: 8, fontWeight: 300 }}>Write a detailed critical analysis. Quote from the text and analyse technique, effect and central concerns.</div>
          <textarea value={ahAnswer} onChange={e => setAhAnswer(e.target.value)}
            placeholder={"[Your critical analysis — engage with form, structure, language and imagery. Be selective and analytical rather than descriptive...]"}
            style={{ width: "100%", minHeight: 180, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
          <button onClick={markAHTextual} disabled={!ahAnswer.trim()}
            style={{ marginTop: 8, width: "100%", padding: "13px", background: ahAnswer.trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: ahAnswer.trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
            Submit for marking →
          </button>
        </div>
      </div>
    </div>
  );

  // ── RUAE Paper Setup ─────────────────────────────────────────────────
  if (paperChoice === "ruae" && examState === "setup") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: "40px 32px", textAlign: "center" }}>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2" }}>Reading for UAE — {level}</div>
      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 360, lineHeight: 1.7, fontWeight: 300 }}>
        {level === "Higher"
          ? "Summit will generate two thematically linked passages and 9 questions (30 marks total). Work through it in your own time, then submit for marking."
          : "Summit will generate one passage and 9 questions (30 marks total). Work through it, then submit for marking."}
      </div>
      <div style={{ padding: "10px 14px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 8, fontSize: 12, color: "rgba(240,244,242,0.5)", maxWidth: 380, lineHeight: 1.6 }}>
        ⚠️ <strong style={{ color: "#EF9F27" }}>AI-generated passage.</strong> For practice only. The text and questions are not from SQA. Always use real past papers from sqa.org.uk too.
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={() => setPaperChoice(null)} style={{ padding: "11px 20px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "rgba(240,244,242,0.4)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>← Back</button>
        <button onClick={generateRUAE} style={{ padding: "11px 24px", background: "#1D9E75", border: "none", borderRadius: 8, color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}>Generate paper →</button>
      </div>
    </div>
  );

  // ── Critical Reading Setup ───────────────────────────────────────────
  if (paperChoice === "critical" && examState === "setup") return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
      <div style={{ maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => setPaperChoice(null)} style={{ background: "transparent", border: "none", color: "rgba(240,244,242,0.3)", cursor: "pointer", fontSize: 20, padding: 0 }}>←</button>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2" }}>Critical Reading — {level}</div>
        </div>

        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.4)", marginBottom: 20, lineHeight: 1.6, fontWeight: 300 }}>
          Select your Scottish Text genre and author. Summit generates the extract, 4 questions (20 marks), and Section 2 critical essay questions across all genres (20 marks).
        </div>

        {/* Genre tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {["Poetry", "Drama", "Prose"].map(g => (
            <button key={g} onClick={() => {
              setCrTab(g);
              const firstAuthor = prescribedForLevel[g][0];
              setSelectedAuthor(firstAuthor);
              if (g === "Poetry") setSelectedPoem(POET_POEMS[firstAuthor]?.[0] || "");
            }}
              style={{ padding: "7px 16px", borderRadius: 8, fontSize: 13, border: crTab === g ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: crTab === g ? "rgba(29,158,117,0.15)" : "transparent", color: crTab === g ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: crTab === g ? 500 : 400 }}>
              {g}
            </button>
          ))}
        </div>

        {/* Author/text picker */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 10, fontWeight: 300, letterSpacing: 0.5 }}>
            {crTab === "Poetry" ? "Poet" : crTab === "Drama" ? "Play" : "Text"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {prescribedForLevel[crTab].map(a => (
              <button key={a} onClick={() => {
                setSelectedAuthor(a);
                if (crTab === "Poetry") {
                  setSelectedPoem(POET_POEMS[resolvePoetKey(a)]?.[0] || "");
                  setSelectedStory(null);
                } else if (crTab === "Prose") {
                  const stories = PROSE_COLLECTION_STORIES[a];
                  setSelectedStory(stories ? stories[0] : null);
                  setSelectedPoem(null);
                }
              }}
                style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: selectedAuthor === a ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: selectedAuthor === a ? "rgba(29,158,117,0.15)" : "transparent", color: selectedAuthor === a ? "#4DC9A0" : "rgba(240,244,242,0.5)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {crTab === "Prose" ? proseShortName(a) : a}
              </button>
            ))}
          </div>
        </div>

        {/* Story picker — for Prose collections with multiple stories */}
        {crTab === "Prose" && selectedAuthor && PROSE_COLLECTION_STORIES[selectedAuthor] && PROSE_COLLECTION_STORIES[selectedAuthor].length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 10, fontWeight: 300, letterSpacing: 0.5 }}>Story</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {PROSE_COLLECTION_STORIES[selectedAuthor].map(s => (
                <button key={s} onClick={() => setSelectedStory(s)}
                  style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: selectedStory === s ? "1px solid #EF9F27" : "1px solid rgba(255,255,255,0.1)", background: selectedStory === s ? "rgba(239,159,39,0.15)" : "transparent", color: selectedStory === s ? "#EF9F27" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Poem picker — only for Poetry tab */}
        {crTab === "Poetry" && POET_POEMS[resolvePoetKey(selectedAuthor)] && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", marginBottom: 10, fontWeight: 300, letterSpacing: 0.5 }}>Poem</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {POET_POEMS[resolvePoetKey(selectedAuthor)].map(p => (
                <button key={p} onClick={() => setSelectedPoem(p)}
                  style={{ padding: "6px 13px", borderRadius: 20, fontSize: 12, border: selectedPoem === p ? "1px solid #EF9F27" : "1px solid rgba(255,255,255,0.1)", background: selectedPoem === p ? "rgba(239,159,39,0.15)" : "transparent", color: selectedPoem === p ? "#EF9F27" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ padding: "10px 14px", background: "rgba(239,159,39,0.07)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 8, fontSize: 12, color: "rgba(240,244,242,0.5)", marginBottom: 20, lineHeight: 1.6 }}>
          ⚠️ <strong style={{ color: "#EF9F27" }}>AI-generated extract.</strong> Treat as practice only. The poem/extract is generated in the style of the author — not reproduced verbatim. Always refer to the real texts from your course.
        </div>

        <button onClick={generateCriticalReading} style={{ padding: "13px 28px", background: "#1D9E75", border: "none", borderRadius: 8, color: "white", fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, cursor: "pointer" }}>
          Generate paper →{crTab === "Poetry" && selectedPoem ? ` (${selectedPoem})` : ""}
        </button>
      </div>
    </div>
  );

  // ── RUAE In Progress ─────────────────────────────────────────────────
  if (paperChoice === "ruae" && examState === "in_progress") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>Read the passage(s), answer all questions, then submit</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
        {/* Paper */}
        <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            {/* Header */}
            <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — AI Practice Paper</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>{level.toUpperCase()}</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>ENGLISH</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 2 }}>Reading for Understanding, Analysis and Evaluation · Total marks: 30</div>
            </div>

            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "10px 14px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
              <strong>Instructions:</strong> Attempt ALL questions. Use your own words wherever specified. For analysis questions, quote from the passage and explain the effect. You may highlight or annotate the passage.
            </div>

            {/* Passage 1 */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: "#000", marginBottom: 8, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Passage 1</div>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: "#333", fontStyle: "italic", marginBottom: 12 }}>Read the passage below and attempt questions 1 to {passage2 ? "8" : "9"}.</div>
              {renderSQAPaper(passage1)}
            </div>

            {/* Passage 2 (Higher only) */}
            {passage2 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: "#000", marginBottom: 8, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Passage 2</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: "#333", fontStyle: "italic", marginBottom: 12 }}>Read the passage below and attempt question 9. While reading, you may wish to make notes on the main ideas and/or highlight key points.</div>
                {renderSQAPaper(passage2)}
              </div>
            )}

            {/* Questions */}
            <div style={{ borderTop: "2px solid #000", paddingTop: 16, marginTop: 8 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: "#000", marginBottom: 12 }}>Questions</div>
              {renderSQAPaper(questions)}
            </div>
          </div>
        </div>

        {/* Answer box */}
        <div style={{ flex: "0 0 auto", padding: "16px 24px" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 10, fontWeight: 500 }}>Your answers</div>
          <textarea value={answers} onChange={e => setAnswers(e.target.value)}
            placeholder={"Q1.\n\nQ2.\n\nQ3.\n\n..."}
            style={{ width: "100%", minHeight: 120, resize: "vertical", padding: "12px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
          <button onClick={markRUAE} disabled={!answers.trim()}
            style={{ marginTop: 10, width: "100%", padding: "13px", background: answers.trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: answers.trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: answers.trim() ? "pointer" : "default" }}>
            Submit for marking →
          </button>
        </div>
      </div>
    </div>
  );

  // ── Critical Reading In Progress ─────────────────────────────────────
  if (paperChoice === "critical" && examState === "in_progress") {
    const genreList = isN5
      ? ["DRAMA", "PROSE", "POETRY", "FILM AND TELEVISION DRAMA", "LANGUAGE"]
      : ["DRAMA", "PROSE FICTION", "PROSE NON-FICTION", "POETRY", "FILM AND TELEVISION DRAMA", "LANGUAGE"];
    const essaySections = crEssayQuestions
      ? genreList.map(genre => {
          const escaped = genre.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const match = crEssayQuestions.match(new RegExp(`---${escaped}---([\\s\\S]*?)(?=---|$)`));
          return { genre, questions: match ? match[1].trim() : "" };
        }).filter(s => s.questions)
      : [];

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {["sst","essay"].map(s => (
              <button key={s} onClick={() => setCrSection(s)}
                style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, border: crSection === s ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: crSection === s ? "rgba(29,158,117,0.15)" : "transparent", color: crSection === s ? "#4DC9A0" : "rgba(240,244,242,0.35)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                {s === "sst" ? "Section 1 — Scottish Text" : "Section 2 — Critical Essay"}
              </button>
            ))}
          </div>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
          {crSection === "sst" ? (
            <>
              {/* SST paper */}
              <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ maxWidth: 680, margin: "0 auto" }}>
                  <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase" }}>Scottish Qualifications Authority — AI Practice Paper</div>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 20, fontWeight: 700, color: "#000", marginTop: 4 }}>HIGHER</div>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 15, fontWeight: 600, color: "#000" }}>ENGLISH — Critical Reading</div>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 2 }}>Section 1 — Scottish Text · 20 marks · Approximately 45 minutes</div>
                  </div>

                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
                    <strong>Instructions:</strong> Read the {crTab === "Poetry" ? "poem" : "extract"} carefully and attempt ALL questions. For the 10-mark question, refer to this text AND at least one other text by the same author.
                  </div>

                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", marginBottom: 6, fontStyle: "italic" }}>
                    Scottish Text — {crTab} · {selectedAuthor}
                  </div>
                  <div style={{ marginBottom: 24 }}>{renderSQAPaper(crExtract)}</div>

                  <div style={{ borderTop: "1px solid #ccc", paddingTop: 16 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 12, color: "#000" }}>Questions <span style={{ fontWeight: 400, color: "#555" }}>[Total: 20 marks]</span></div>
                    {renderSQAPaper(crSSTQuestions)}
                  </div>

                  <div style={{ marginTop: 16, padding: "10px 12px", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 4 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: "#333", marginBottom: 6 }}>
                      {isHigher ? "10-MARK QUESTION GUIDANCE" : "8-MARK QUESTION GUIDANCE"}
                    </div>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#555", lineHeight: 1.6 }}>
                      {isHigher ? (
                        <>
                          2 marks — general/commonality point comparing this text to others by the same author.<br />
                          2 marks — developed, analytical point from this extract with quotation.<br />
                          6 marks — analytical points from elsewhere in the author's work (2 marks each for quote + comment).
                        </>
                      ) : (
                        <>
                          2 marks — general/commonality point comparing this poem to at least one other by the same poet.<br />
                          2 marks — developed point from this poem with quotation.<br />
                          4 marks — points from elsewhere in the poet's work (2 points × 2 marks each).
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SST answer box */}
              <div style={{ padding: "14px 24px" }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 8, fontWeight: 500 }}>Section 1 answers</div>
                <textarea value={crAnswers.sst} onChange={e => setCrAnswers(prev => ({ ...prev, sst: e.target.value }))}
                  placeholder={"Q1.\n\nQ2.\n\nQ3.\n\nQ4 (10 marks — include commonality point, extract point, points from elsewhere):\n"}
                  style={{ width: "100%", minHeight: 110, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
                <button onClick={() => setCrSection("essay")}
                  style={{ marginTop: 8, width: "100%", padding: "11px", background: "rgba(29,158,117,0.15)", color: "#4DC9A0", border: "1px solid rgba(29,158,117,0.3)", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
                  Move to Section 2 — Critical Essay →
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Critical Essay paper */}
              <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: "white", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ maxWidth: 680, margin: "0 auto" }}>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: "#000", marginBottom: 8 }}>SECTION 2 — Critical Essay · 20 marks · Approximately 45 minutes</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 20 }}>
                    <strong>Instructions:</strong> Attempt ONE question from the genres below. Your answer must be on a different genre from that chosen in Section 1 ({crTab}). Answers should refer to appropriate techniques and show detailed knowledge of your chosen text.
                  </div>

                  {essaySections.map(({ genre, questions: qs }) => (
                    <div key={genre} style={{ marginBottom: 24 }}>
                      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: "#000", marginBottom: 10, borderBottom: "1px solid #eee", paddingBottom: 4 }}>
                        PART — {genre}
                      </div>
                      {renderSQAPaper(qs)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Essay answer box */}
              <div style={{ padding: "14px 24px" }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 8, fontWeight: 500 }}>Section 2 — Critical essay answer</div>
                <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginBottom: 8, fontWeight: 300 }}>State the question number you are answering, then write your full essay response.</div>
                <textarea value={crAnswers.essay} onChange={e => setCrAnswers(prev => ({ ...prev, essay: e.target.value }))}
                  placeholder={"Question: [number]\n\n[Your critical essay...]"}
                  style={{ width: "100%", minHeight: 160, resize: "vertical", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", boxSizing: "border-box" }} />
                <button onClick={markCriticalReading} disabled={!crAnswers.sst.trim() && !crAnswers.essay.trim()}
                  style={{ marginTop: 8, width: "100%", padding: "13px", background: (crAnswers.sst.trim() || crAnswers.essay.trim()) ? "#1D9E75" : "rgba(255,255,255,0.05)", color: (crAnswers.sst.trim() || crAnswers.essay.trim()) ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
                  Submit both sections for marking →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Results ──────────────────────────────────────────────────────────
  if (examState === "results") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 13, color: "#4DC9A0", fontWeight: 500 }}>
          {paperChoice === "ruae" && `RUAE results — ${level} English`}
          {paperChoice === "critical" && `Critical Reading results — ${level} English`}
          {paperChoice === "ah_literary" && "Literary Study results — Advanced Higher English"}
          {paperChoice === "ah_textual" && "Textual Analysis results — Advanced Higher English"}
        </div>
        <button onClick={reset} style={{ fontSize: 12, padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.45)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>New paper</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        <div style={{ fontSize: 13, lineHeight: 1.85, color: "rgba(240,244,242,0.8)", fontWeight: 300 }}>
          {renderMessage(
            paperChoice === "ruae" ? results :
            (paperChoice === "ah_literary" || paperChoice === "ah_textual") ? ahResults :
            crResults
          )}
        </div>
      </div>
    </div>
  );

  return null;
}

// ── SQA Maths Formulae Lists ─────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const MATHS_FORMULAE = {
  "National 5": "Quadratic formula: $x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$ | Sine rule: $\\frac{a}{\\sin A} = \\frac{b}{\\sin B} = \\frac{c}{\\sin C}$ | Cosine rule: $a^2 = b^2 + c^2 - 2bc\\cos A$ | Area of triangle: $A = \\frac{1}{2}ab\\sin C$ | Volume of sphere: $V = \\frac{4}{3}\\pi r^3$ | Volume of cone: $V = \\frac{1}{3}\\pi r^2 h$ | Volume of pyramid: $V = \\frac{1}{3}Ah$ | Standard deviation: $s = \\sqrt{\\frac{\\sum(x-\\bar{x})^2}{n-1}}$",
  "Higher": "Circle: $x^2+y^2+2gx+2fy+c=0$ centre $(-g,-f)$ radius $\\sqrt{g^2+f^2-c}$ | Scalar product: $\\mathbf{a}\\cdot\\mathbf{b} = a_1b_1+a_2b_2+a_3b_3$ | $\\sin(A\\pm B) = \\sin A\\cos B \\pm \\cos A\\sin B$ | $\\cos(A\\pm B) = \\cos A\\cos B \\mp \\sin A\\sin B$ | $\\sin 2A = 2\\sin A\\cos A$ | $\\cos 2A = \\cos^2 A - \\sin^2 A = 2\\cos^2 A-1 = 1-2\\sin^2 A$ | $\\frac{d}{dx}(\\sin ax) = a\\cos ax$ | $\\frac{d}{dx}(\\cos ax) = -a\\sin ax$ | $\\int\\sin ax\\,dx = -\\frac{1}{a}\\cos ax+c$ | $\\int\\cos ax\\,dx = \\frac{1}{a}\\sin ax+c$",
  "Advanced Higher": "$\\frac{d}{dx}(\\tan x)=\\sec^2 x$ | $\\frac{d}{dx}(\\cot x)=-\\csc^2 x$ | $\\frac{d}{dx}(\\sec x)=\\sec x\\tan x$ | $\\frac{d}{dx}(\\sin^{-1}x)=\\frac{1}{\\sqrt{1-x^2}}$ | $\\frac{d}{dx}(\\tan^{-1}x)=\\frac{1}{1+x^2}$ | $\\frac{d}{dx}(\\ln x)=\\frac{1}{x}$ | $\\int\\sec^2(ax)\\,dx=\\frac{1}{a}\\tan(ax)+c$ | $\\int\\frac{1}{a^2+x^2}\\,dx=\\frac{1}{a}\\tan^{-1}\\frac{x}{a}+c$ | $\\int e^{ax}\\,dx=\\frac{1}{a}e^{ax}+c$ | $\\sum_{r=1}^n r=\\frac{n(n+1)}{2}$ | $\\sum_{r=1}^n r^2=\\frac{n(n+1)(2n+1)}{6}$ | $(a+b)^n=\\sum_{r=0}^n\\binom{n}{r}a^{n-r}b^r$ | De Moivre: $[r(\\cos\\theta+i\\sin\\theta)]^n=r^n(\\cos n\\theta+i\\sin n\\theta)$"
};

// ── Standard MockExamPanel (non-English subjects) ─────────────────────
function MockExamPanel({ subject, level, userData }) {
  const [examState, setExamState] = useState("setup");
  const [paperType, setPaperType] = useState(null);
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(null);
  const timerRef = useRef(null);

  const isScience = SCIENCE_SUBJECTS.includes(subject);
  const isMaths = MATHS_SUBJECTS.includes(subject);
  const isEnglish = subject === "English";

  const isAdmin = subject === "Administration and IT";
  const isDrama = subject === "Drama";
  const isComputing = subject === "Computing Science";

  // ── Multi-paper subjects: level-aware paper options ──────────────────
  const MOCK_PAPER_OPTIONS = {
    "Religious, Moral and Philosophical Studies": {
      "National 5": [
        { type: "n5_full", title: "N5 Question Paper", sub: "80 marks · 2h 20min · 3 sections" }
      ],
      "Higher": [
        { type: "rmps_p1", title: "Paper 1 — World Religion & Morality", sub: "60 marks · 2h 15min" },
        { type: "rmps_p2", title: "Paper 2 — Religious & Philosophical Questions", sub: "20 marks · 45 min" }
      ],
      "Advanced Higher": [
        { type: "ah_full", title: "Advanced Higher Paper", sub: "90 marks · 3 hours" }
      ]
    },
    "History": {
      "National 5": [
        { type: "n5_full", title: "N5 Question Paper", sub: "80 marks · 2h 20min · 3 sections" }
      ],
      "Higher": [
        { type: "hist_p1", title: "Paper 1 — Scottish History", sub: "36 marks · 1h 30min · source questions" },
        { type: "hist_p2", title: "Paper 2 — World History", sub: "44 marks · 1h 30min · essays" }
      ],
      "Advanced Higher": [
        { type: "ah_full", title: "Advanced Higher Paper", sub: "90 marks · 3 hours" }
      ]
    },
    "Modern Studies": {
      "National 5": [
        { type: "n5_full", title: "N5 Question Paper", sub: "80 marks · 2h 20min · 3 sections" }
      ],
      "Higher": [
        { type: "ms_p1", title: "Paper 1", sub: "52 marks · 1h 45min · extended essays" },
        { type: "ms_p2", title: "Paper 2", sub: "28 marks · 1h 15min · source evaluation" }
      ],
      "Advanced Higher": [
        { type: "ah_full", title: "Advanced Higher Paper", sub: "90 marks · 3 hours" }
      ]
    },
    "Graphic Communication": {
      "National 5": [{ type: "full", title: "Question Paper", sub: "80 marks · 2 hours" }],
      "Higher": [{ type: "full", title: "Question Paper", sub: "90 marks · 2h 30min" }],
      "Advanced Higher": [{ type: "full", title: "Question Paper", sub: "90 marks · 2h 30min" }]
    }
  };

  const multiPaperSubjects = Object.keys(MOCK_PAPER_OPTIONS);
  const isMultiPaper = multiPaperSubjects.includes(subject);
  const mockPaperOptions = isMultiPaper ? (MOCK_PAPER_OPTIONS[subject]?.[level] || [{ type: "full", title: "Question Paper", sub: "" }]) : [];

  // ── Prompt resolver for multi-paper mock types ────────────────────────
  const getMultiPaperPrompt = (type) => {
    // RMPS
    if (type === "rmps_p1") return `Generate Higher RMPS Paper 1 — World Religion, Morality and Belief (60 marks, 2h 15min).

Generate ALL parts in BOTH sections exactly as in the real SQA paper — the student chooses which to answer based on what they studied. Use only these exact question wordings.

HIGHER
RELIGIOUS, MORAL AND PHILOSOPHICAL STUDIES
Total marks — 60 | Time: 2 hours 15 minutes

SECTION 1 — WORLD RELIGION — 30 marks
Attempt ONE part only.

Part A — Buddhism (attempt if you have studied Buddhism)
1. Analyse the relationship between tanha and the Three Root Poisons. [10]
2. Evaluate the relevance of meditation to Buddhists. [20]

Part B — Christianity (attempt if you have studied Christianity)
3. Analyse the relationship between sin and stewards. [10]
4. Evaluate the relevance of worship to Christians. [20]

Part C — Hinduism (attempt if you have studied Hinduism)
5. Analyse the relationship between dukkha and avidya. [10]
6. Evaluate the relevance of worship to Hindus. [20]

Part D — Islam (attempt if you have studied Islam)
7. Analyse the relationship between suffering and khalifas. [10]
8. Evaluate the relevance of worship to Muslims. [20]

Part E — Judaism (attempt if you have studied Judaism)
9. Analyse the relationship between yetzer harah and suffering. [10]
10. Evaluate the relevance of worship to Jews. [20]

Part F — Sikhism (attempt if you have studied Sikhism)
11. Analyse the relationship between atma and free will. [10]
12. Evaluate the relevance of worship to Sikhs. [20]

[NOW GO TO SECTION 2]

SECTION 2 — MORALITY AND BELIEF — 30 marks
Attempt ONE part only.

Part A — Morality and justice (attempt if you have studied this topic)
13. Analyse non-religious responses to moral issues arising from deterrence. [10]
14. Evaluate religious responses to moral issues arising from responses to crime. [20]

Part B — Morality and relationships (attempt if you have studied this topic)
15. Analyse non-religious responses to moral issues arising from forced marriage. [10]
16. Evaluate religious responses to moral issues arising from relationships. [20]

Part C — Morality, environment and global issues (attempt if you have studied this topic)
17. Analyse non-religious responses to moral issues arising from biodiversity. [10]
18. Evaluate religious responses to moral issues arising from economic issues. [20]

Part D — Morality, medicine and the human body (attempt if you have studied this topic)
19. Analyse non-religious responses to moral issues arising from the therapeutic use of embryos. [10]
20. Evaluate religious responses to moral issues arising from organ donation. [20]

Part E — Morality and conflict (attempt if you have studied this topic)
21. Analyse non-religious responses to moral issues arising from using weapons of mass destruction. [10]
22. Evaluate religious responses to moral issues arising from conflict. [20]

[END OF QUESTION PAPER]

MARKING GUIDANCE:
10-mark questions: KU up to 6 marks + Analysis up to 4 marks. Capped at 4/10 if no analysis.
20-mark questions: KU up to 10 marks + Analysis up to 5 marks + Evaluation up to 5 marks. Capped at 8/20 if no analysis AND no evaluation.

After generating, tell the student: "Answer ONE part from Section 1 (your studied religion) and ONE part from Section 2 (your studied morality topic). Paste your answers and I'll mark them."`;
    if (type === "rmps_p2") return `Generate Higher RMPS Paper 2 — Religious and Philosophical Questions (20 marks, 45 min).

ONE question only. Choose the topic most due for return from:
"To what extent do you agree with religious explanations for the origins of the universe?"
"To what extent do you agree with religious arguments for the existence of God?"
"To what extent do you agree that God is responsible for suffering and evil in the world?"
"To what extent do you agree with religious explanations for miracles?"

Format as a clean SQA exam paper showing [20 marks]. Then provide a mark scheme: KU up to 10 marks (religious AND non-religious views, relevant philosophers/sources), Analysis up to 5 marks (implications, links between arguments), Evaluation up to 5 marks (supported judgements, reasoned conclusion). Capped at 8 if no analysis AND no evaluation.

After generating, tell the student: "Write your essay response and paste it — I'll mark it against the SQA criteria."`;
    if (type === "n5_full" && subject === "Religious, Moral and Philosophical Studies") return `Generate N5 RMPS — 80 marks, 2h 20min. THREE sections (27+27+26 marks). For each section generate ONE part:\nSection 1 World Religion (27 marks): Q1 (5), Q2a (4), Q2b (2), Q3a (4), Q3b (8) — include 8-mark "How far do you agree?" requiring personal view + reasons + counter-argument + conclusion.\nSection 2 Morality and Belief (27 marks): same structure.\nSection 3 Religious & Philosophical Questions (26 marks): Q1a (5), Q1b (4), Q2 (5), Q3a (4), Q3b (8).\nGenerate actual questions. Show mark allocations clearly.`;
    if (type === "ah_full" && subject === "Religious, Moral and Philosophical Studies") return `Generate AH RMPS — 90 marks, 3 hours. Choose ONE section (Philosophy of Religion / Religious Experience / Medical Ethics). Generate: 2 × 30-mark "Discuss" essays (student attempts both) + 1 × 15-mark source question (5+5+5: describe/analyse/evaluate a philosophical extract). Include marking grid: Analysis (0-8), Comparison (0-6), Evaluation (0-8), Synthesis/Line of Argument (0-8).`;

    // History
    if (type === "hist_p1") return `Generate Higher History Paper 1 — Scottish History (36 marks, 1h 30min).

Generate ALL FIVE parts so the student can attempt the one they have studied. Each part has 4 sources + 4 questions. Use the exact question format from the real SQA paper.

HIGHER HISTORY
SCOTTISH HISTORY — 36 marks
Attempt ONE part only.

PART A — The Wars of Independence, 1249–1328
[Generate 4 sources: Source A (named modern historian, ~150 words on Alexander III/succession), Source B (~150 words on Wallace/Murray), Source C (~150 words on Wallace — contrasting interpretation to B), Source D (~150 words, primary/near-contemporary source on Bruce)]
Q1. Explain the reasons why there was a succession problem, 1286–1292. [8]
Q2. How fully does Source A explain the reasons why there were difficulties in the relationship between John Balliol and Edward I, 1292–1296? Use the source and recalled knowledge. [10]
Q3. How much do Sources B and C reveal about differing interpretations of the roles of William Wallace and Andrew Murray? Use the sources and recalled knowledge. [10]
Q4. Evaluate the usefulness of Source D as evidence of Bruce's conflict with his Scottish opponents. Refer to: the origin and possible purpose of the source; the content of the source; recalled knowledge. [8]

[Now go to PART B]

PART B — The Age of the Reformation, 1542–1603
[Generate 4 sources on Mary Queen of Scots / James VI and the Kirk]
Q5. Explain the reasons why there was a Reformation in Scotland in 1560. [8]
Q6. How fully does Source A explain the difficulties faced by Mary, Queen of Scots, during her reign, 1561–1567? [10]
Q7. How much do Sources B and C reveal about differing interpretations of the relationship between the Crown and the Kirk in the later part of the reign of James VI, 1585–1603? [10]
Q8. Evaluate the usefulness of Source D as evidence of the impact of the Reformation on Scottish culture to 1603. [8]

PART C — The Treaty of Union, 1689–1740
[Generate 4 sources on arguments for/against Union, negotiations, Jacobitism]
Q9. Explain the reasons why Scottish relations with England worsened, from 1689. [8]
Q10. How fully does Source A explain the arguments for and against Union with England? [10]
Q11. How much do Sources B and C reveal about differing interpretations of the negotiations leading to the Act of Union of 1707? [10]
Q12. Evaluate the usefulness of Source D as evidence of the political effects of the Union, up to 1740. [8]

PART D — Migration and Empire, 1830–1939
[Generate 4 sources on Irish immigration, Scottish emigration, empire]
Q13. Explain the reasons for the migration of Scots. [8]
Q14. How fully does Source A explain the experience of immigrants in Scotland? [10]
Q15. How much do Sources B and C reveal about differing interpretations of the impact of Scots emigrants on Canada? [10]
Q16. Evaluate the usefulness of Source D as evidence of the impact of the empire on Scotland, to 1939. [8]

PART E — The Impact of the Great War, 1914–1928
[Generate 4 sources on recruitment, home front, industry, Red Clydeside]
Q17. Explain the reasons why Scots played an important role on the Western Front. [8]
Q18. How fully does Source A explain the domestic impact of the Great War on Scottish society and culture? [10]
Q19. How much do Sources B and C reveal about differing interpretations of the impact of the Great War on Scottish industry? [10]
Q20. Evaluate the usefulness of Source D as evidence of events on Red Clydeside. [8]

[END OF QUESTION PAPER]

MARKING GUIDANCE:
Q1/5/9/13/17 (8 marks — Explain): recalled knowledge only. Up to 3 marks per factor (1 = identify, 2 = develop, 3 = exemplify). Need at least 3 different factors for full marks.
Q2/6/10/14/18 (10 marks — How fully/Source A): up to 4 marks from source + up to 7 marks recalled knowledge. Must use both. Balanced conclusion on how fully required.
Q3/7/11/15/19 (10 marks — Differing interpretations B&C): compare overall AND in detail. Point of comparison = 1 mark per developed comparison (similarity or difference). Must use both sources. Recalled knowledge adds context.
Q4/8/12/16/20 (8 marks — Evaluate usefulness Source D): origin (who, when, why written) = up to 2 marks. Purpose = up to 2 marks. Content = up to 2 marks. Recalled knowledge to agree/disagree = up to 2 marks.

After generating, tell the student: "Attempt all four questions in the part for your studied Scottish context, then paste your answers and I'll mark them."`;

    if (type === "hist_p2") return `Generate Higher History Paper 2 — British, European and World History (44 marks, 1h 30min).

Generate ALL parts in BOTH sections so the student can attempt the one they have studied. Every question is worth 22 marks. The real paper has ONLY essay questions — no sources.

HIGHER HISTORY
BRITISH, EUROPEAN AND WORLD HISTORY — 44 marks
SECTION 1 — BRITISH — 22 marks: Attempt ONE question from the part you have chosen.
SECTION 2 — EUROPEAN AND WORLD — 22 marks: Attempt ONE question from the part you have chosen.

SECTION 1 — BRITISH — 22 marks

PART A — Church, state and feudal society, 1066–1406
1. How important was the role of the king in feudal society? [22]
2. To what extent was law and order the main reason for the increase of central royal power in the reign of Henry II in England? [22]
3. King John was successful in increasing royal authority in England. How valid is this view? [22]

PART B — The century of revolutions, 1603–1702
4. To what extent was the Divine Right of Kings the main reason for the problems faced by King James after the Union of the Crowns in 1603? [22]
5. Foreign issues were the main reason for the failure to find an alternative form of government, 1649–1658. How valid is this view? [22]
6. How important were religious issues as a reason for the Revolution Settlement of 1688–1689? [22]

PART C — The trade in enslaved African people
7. To what extent were military factors the main reason for the development of the trade in enslaved Africans? [22]
8. The trade in enslaved Africans had a negative impact on African societies. How valid is this view? [22]
9. How important was enslaved Africans' fight for freedom in St Domingue as an obstacle to abolition? [22]

PART D — Britain, 1851–1951
10. How important were pressure groups as a reason why Britain became more democratic, 1851–1928? [22]
11. The rise of Labour was the main reason why the Liberals introduced social welfare reforms, 1906–1914. How valid is this view? [22]
12. To what extent were the Liberal social welfare reforms effective in meeting the needs of the British people? [22]

PART E — Britain and Ireland, 1900–1985
13. How important was the Irish Cultural Revival as a reason for the growth of tension in Ireland, to 1914? [22]
14. To what extent were divisions in the republican movement the main reason for the outbreak of the Irish Civil War? [22]
15. Economic issues were the main reason for the developing crisis in Northern Ireland, by 1968. How valid is this view? [22]

[Now go to SECTION 2]

SECTION 2 — EUROPEAN AND WORLD — 22 marks

PART A — The crusades, 1071–1204
16. To what extent was papal desire to channel the aggressive nature of feudal society the main reason for the calling of the First Crusade? [22]
17. How important were divisions among the Crusaders as a reason for the fall of Jerusalem in 1187? [22]
18. Richard's use of diplomacy was the main reason for the resolution of the Third Crusade. How valid is this view? [22]

PART B — The American Revolution, 1763–1787
19. How important was British neglect of the colonies as a reason for colonial resentment towards Britain by 1763? [22]
20. The American War of Independence was global in nature. How valid is this view? [22]
21. To what extent was the role of George Washington the main reason for the colonists' victory in the War of Independence? [22]

PART C — The French Revolution, to 1799
22. How important was the role of the royal family as a threat to the security of the Ancien Régime before 1789? [22]
23. The actions of the Committee of Public Safety were the main reason for the Terror, 1792–1795. How valid is this view? [22]
24. To what extent was the role of Sieyès the main reason for the establishment of the Consulate? [22]

PART D — Germany, 1815–1939
25. The effects of the French Revolution and Napoleonic Wars were the main reason for the growth of German nationalism, 1815–1850. How valid is this view? [22]
26. To what extent was the role of Bismarck the main reason why German unification was achieved by 1871? [22]
27. How important was the appeal of Hitler and the Nazis after 1928 as a reason why the Nazis achieved power in 1933? [22]

PART E — Italy, 1815–1939
28. How important were the effects of the French Revolution and Napoleonic Wars as a reason for the growth of Italian nationalism, 1815–1850? [22]
29. The rise of Piedmont was the main reason why unification was achieved in Italy, by 1870. How valid is this view? [22]
30. To what extent was the resentment of the Peace Settlement the main reason why the Fascists achieved power in Italy, 1919–1925? [22]

PART F — Russia, 1881–1921
31. To what extent was the security of the Tsarist state challenged before 1905? [22]
32. How important was discontent among the working class as a reason for the February Revolution, 1917? [22]
33. Dual Power was the main reason for the success of the October Revolution, 1917. How valid is this view? [22]

PART G — USA, 1918–1968
34. Economic fears were the main reason for changing attitudes towards immigration in the 1920s. How valid is this view? [22]
35. To what extent was the New Deal effective in solving America's problems in the 1930s? [22]
36. How important were prejudice and discrimination as a reason for the development of the Civil Rights campaign, after 1945? [22]

PART H — Appeasement and the road to war, to 1939
37. How important was the Peace Settlement of 1919 as a reason for the aggressive nature of the foreign policies of Germany and Italy in the 1930s? [22]
38. To what extent was British foreign policy unsuccessful in containing Fascist aggression, 1935 to March 1938? [22]
39. The Munich Agreement was a success for the countries involved. How valid is this view? [22]

PART I — The Cold War, 1945–1989
40. How important was the crisis over Korea as a reason for the emergence of the Cold War, up to 1955? [22]
41. To what extent were the strengths of North Vietnam the main reason why the US lost the war in Vietnam? [22]
42. The development of Détente was the main reason why the superpowers attempted to manage the Cold War, 1962–1979. How valid is this view? [22]

[END OF QUESTION PAPER]

MARKING GUIDANCE (22 marks each):
These are extended essay questions. A well-structured response should: introduce with a thesis, analyse multiple factors with developed explanation and evidence, reach a reasoned conclusion directly addressing the question. Command words: "How important/significant" = assess ONE factor against others. "To what extent" = assess the main factor against others. "How valid is this view" = assess the stated view with evidence for and against.
Award guidance: 0–6 = limited, 7–13 = adequate, 14–18 = good, 19–22 = excellent with sustained analysis and balanced conclusion.

After generating, tell the student: "Attempt ONE question from Section 1 (your British topic) and ONE question from Section 2 (your European/World topic). Write your essays and paste them — I'll mark both."`;

    if (type === "n5_full" && subject === "History") return `Generate N5 History — 80 marks, 2h 20min.

Generate ALL parts in ALL three sections so the student can attempt the one they have studied.

N5 HISTORY — 80 marks
SECTION 1 — SCOTTISH CONTEXTS — 25 marks: Attempt ONE part.
SECTION 2 — BRITISH CONTEXTS — 26 marks: Attempt ONE part.
SECTION 3 — EUROPEAN AND WORLD CONTEXTS — 29 marks: Attempt ONE part.

SECTION 1 — SCOTTISH CONTEXTS — 25 marks

For EACH of the 5 parts below, generate ONE source (100-150 words, "from a textbook written by a modern historian in [year]") and 5 questions following this EXACT pattern:
Q1 (5): Evaluate the usefulness of Source A as evidence of [topic]. (You may want to comment on what type of source it is, who wrote it, when they wrote it, why they wrote it, what they say and what has been missed out.)
Q2 (4): Describe [aspect of topic].
[Then generate Sources B and C — contrasting views, 60-80 words each]
Q3 (4): Compare the views of Sources B and C about [topic]. (Compare the sources overall and/or in detail.)
[Then generate Source D — 80-100 words explaining reasons]
Q4 (6): How fully does Source D explain [reasons/topic]? (Use the source and recall to reach a judgement.)
Q5 (6): Explain the reasons why [event/development].

Parts to generate:
PART A — The Wars of Independence, 1286–1328 (source on succession crisis, Q2 on Great Cause, Q5 on Bruce achieving victory 1306–1314)
PART B — Mary Queen of Scots, and the Reformation, 1542–1587 (source on Mary's move to France, Q2 on Scottish society/Church 1540s, Q5 on reasons for Mary's execution 1587)
PART C — The Treaty of Union, 1689–1715 (source on Darien Scheme, Q2 on economic problems 1689–1707, Q5 on reasons for 1715 Jacobite rebellion)
PART D — Migration and Empire, 1830–1939 (source on Jewish immigration, Q2 on empire's impact on Scotland's industry, Q5 on Scots migrants' role in British Empire)
PART E — The Era of the Great War, 1900–1928 (source on Scottish recruitment, Q2 on life in the trenches, Q5 on reasons franchise extended 1918)

SECTION 2 — BRITISH CONTEXTS — 26 marks

For EACH of the 5 parts, generate questions following this EXACT pattern:
Q1 (9): "To what extent..." or "How important were..." essay — recalled knowledge, balanced assessment, reasoned conclusion.
[Generate Source A — 80-100 words on a specific event/development]
Q2 (6): How fully does Source A describe [topic]? (Use the source and recall.)
[Generate Source B — 60-80 words, a primary/near-contemporary source]
Q3 (5): Evaluate the usefulness of Source B as evidence of [topic]. (Comment on type, author, date, purpose, content, omissions.)
Q4 (6): Explain the reasons why [development].

Parts: A (Medieval Kingdoms 1066–1406), B (War of Three Kingdoms 1603–1651), C (Trade in Enslaved African People 1770–1807), D (Changing Britain 1760–1914), E (Making of Modern Britain 1880–1951).

SECTION 3 — EUROPEAN AND WORLD CONTEXTS — 29 marks

For EACH of the 10 parts, generate questions following this EXACT pattern:
Q1 (4): Describe [aspect].
Q2 (6): Explain the reasons why [event/development].
[Generate Sources A and B — 60-80 words each, similar but slightly different views]
Q3 (4): Compare the views of Sources A and B about [topic]. (Compare overall and/or in detail.)
Q4 (9): "To what extent..." essay — recalled knowledge, balanced assessment, reasoned conclusion.
[Generate Source C — 80-100 words]
Q5 (6): How fully does Source C describe [topic]? (Use the source and recall.)

Parts: A (Crusades 1071–1192), B (American Revolution 1774–1783), C (USA 1850–1880), D (Hitler and Nazi Germany 1919–1939), E (Lenin and Russian Revolution 1894–1921), F (Mussolini and Fascist Italy 1919–1939), G (Civil Rights USA 1918–1968), H (Appeasement 1918–1939), I (World War II 1939–1945), J (Cold War 1945–1989).

Show all mark allocations clearly. After generating, tell the student: "Attempt ONE part from each section — the contexts you have studied. Paste all your answers and I'll mark the full paper."`;

    if (type === "ah_full" && subject === "History") return `Generate AH History — 90 marks, 3 hours.

Generate ALL 10 sections so the student can attempt the one they have studied. Each section has Part A (Historical Issues, 50 marks) and Part B (Historical Sources, 40 marks).

ADVANCED HIGHER HISTORY — 90 marks
Attempt ONE section only.

For EACH of the 10 sections below, generate:

PART A — HISTORICAL ISSUES — 50 marks
Generate 5 essay questions (25 marks each). Student attempts TWO. Use command words: "How valid is this view?", "To what extent...", "How justified is the view that...", "How far can it be argued that...". Questions should cover different aspects of the section topic.

PART B — HISTORICAL SOURCES — 40 marks
Generate 4 sources (100-200 words each, from named historians with dates and publication titles). Then generate these THREE questions (always in this mark split — varies by section):
Either: Q (12) Evaluate usefulness + Q (12) How fully + Q (16) Differing interpretations
Or: Q (16) Differing interpretations + Q (12) How fully + Q (12) Evaluate usefulness

Sections to generate:
1. Northern Britain: from the Iron Age to 1034
2. Scotland: independence and kingship, 1249–1334
3. Scotland: from the Treaty of Union to the Enlightenment, 1707–1815
4. USA: 'a house divided', 1850–1865
5. Japan: the modernisation of a nation, 1840–1920
6. Germany: from democracy to dictatorship, 1918–1939
7. South Africa: race and power, 1902–1984
8. Russia: from Tsarism to Stalinism, 1914–1945
9. The Spanish Civil War: causes, conflict and consequences, 1923–1945
10. Britain: at war and peace, 1938–1951

MARKING GUIDANCE:
Part A essays (25 marks): sustained analysis, multiple factors, evaluation of relative importance, reasoned conclusion directly addressing the question. No bullet points — continuous argument.
Part B source questions:
- Evaluate usefulness (12): origin up to 3 + purpose up to 3 + content up to 3 + recalled knowledge up to 3.
- How fully (12): from source up to 4 + recalled knowledge up to 8. Conclusion on how fully required.
- Differing interpretations (16): compare both sources in detail + recalled knowledge to contextualise each interpretation. Overall conclusion on extent of difference required.

After generating, tell the student: "Attempt ALL questions in the ONE section for your studied topic. Paste your answers — I'll mark both parts."`;


    // Modern Studies
    if (type === "ms_p1") return `Generate Higher Modern Studies Paper 1 (52 marks, 1h 45min). THREE sections:\nSection 1 Democracy (20 marks): ONE "To what extent..." question — must argue BOTH sides then give a supported conclusion.\nSection 2 Social Issues (20 marks): ONE "Discuss." question (Social Inequality OR Crime and the Law) requiring balanced argument with conclusion.\nSection 3 International Issues (12 marks): ONE "Analyse..." or "Evaluate..." question.\n\nAfter generating the paper, tell the student: "This is an essay exam. Write full prose essays for all three sections — no bullet points. When you paste your answers I will mark each one using the SQA grids: 20-mark essays = KU (up to 8, capped at 6 without exemplification) + Analysis (up to 6) + Conclusions/Evaluation (up to 4) + Structure (up to 2). 12-mark = KU (up to 8) + Analysis/Evaluation (up to 4). Formula: point → explain (because/therefore) → real example → analytical comment → evaluative conclusion."`;
    if (type === "ms_p2") return `Generate Higher Modern Studies Paper 2 (28 marks, 1h 15min). THREE compulsory questions. Write actual source content:\nQ1 (10 marks) Conclusions: Provide 4 sources (written passage, statistics table, graph description, named viewpoint). "What conclusions can be drawn about [topic]? Reach a conclusion about each: [4 bullet points]. Link information within and between sources."\nQ2 (10 marks) Accuracy: Provide 4 sources. "To what extent is it accurate to state that [claim]?" — must support AND oppose with overall conclusion.\nQ3 (8 marks) Reliability: Provide 3 sources (different types). "To what extent are Sources A, B and C reliable? Give an overall conclusion on the most reliable." Show mark allocations.`;
    if (type === "n5_full" && subject === "Modern Studies") return `Generate N5 Modern Studies — 80 marks, 2h 20min. THREE sections (26+26+28 marks). For each section generate Part A OR Part B + the source question:\nSection 1 Democracy (26 marks): 3 KU questions (4+6+6) + 10-mark decision/recommendation source question (3 sources, choose option and justify).\nSection 2 Social Issues (26 marks): 3 KU questions (4+6+6) + 10-mark support-and-oppose source question.\nSection 3 International Issues (28 marks): 3 KU questions (4+6+8) + 10-mark conclusions source question (4 bullet-point conclusions from 3 sources).\nGenerate actual sources. Show mark allocations clearly.`;
    if (type === "ah_full" && subject === "Modern Studies") return `Generate AH Modern Studies — 90 marks, 3 hours. ONE section (Political Issues / Law and Order / Social Inequality). Part A (60 marks): 3 × 30-mark "Discuss" essays (student attempts 2). Part B (30 marks): Q1 (15 marks) focus groups research methods question for a specific scenario. Q2 (15 marks) source trustworthiness question with a realistic extract. Marking: 30-mark essays = Analysis (0-8) + Comparison (0-6) + Evaluation (0-8) + Synthesis (0-8). Show mark allocations.`;

    // Graphic Comm
    if (type === "full" && subject === "Graphic Communication") return `Generate a ${level} Graphic Communication knowledge mock (${level === "National 5" ? "80 marks, 2 hours" : "90 marks, 2h 30min"}). IMPORTANT: Only generate questions answerable in writing — describe, explain, state, justify, identify. Do NOT ask students to draw or complete diagrams. Cover: design elements and principles, DTP features and techniques, 3D CAD modelling operations, building/construction graphics, assembly constraints. Show mark allocations clearly.`;

    return null;
  };

  const getAdminHigherPrompt = () => `You are generating a PREDICTED SQA Higher Administration and IT Question Paper (50 marks, 1 hour 30 minutes).

HIGHER
ADMINISTRATION AND IT
Total marks — 50
1 hour 30 minutes

SECTION 1 — 10 marks
Attempt ALL questions.

[Write a scenario passage of 250-350 words about a named manager at a fictional Scottish organisation. Include one communication/meeting failure, one data/security concern, one staff management breakdown, one customer service failure. Use specific names and concrete details.]

Questions based on ALL the information provided:
1. Outline ways in which [manager name] could address the issues identified above. 5
2. [Justify OR Describe OR Compare — one specific aspect of the scenario. 3 marks]
3. Compare [two relevant methods or approaches]. 2

SECTION 2 — 40 marks
Attempt ALL questions.

Generate exactly 9 standalone questions NOT referencing the scenario. Use this EXACT mark allocation for the 9 questions in order: 4, 5, 4, 4, 5, 4, 4, 6, 4 — these add to exactly 40. Do not change these values.

Topics to cover across the 9 questions (one topic per question, in any order):
- Time management tools (Gantt chart, action plan, e-diary, to-do list)
- GDPR responsibilities and consequences of a data breach
- One other Act: Computer Misuse Act 1990 OR Health & Safety at Work Act 1974 OR Fire Safety Regulations
- Electronic communication methods in the workplace
- Customer service features, benefits, and consequences of poor service
- One IT software feature: database OR spreadsheet OR presentation software
- Flexible working arrangements and impact on staff/organisation
- Effective teams (features, Belbin roles, OR Tuckman stages)
- Meeting documentation (notice, agenda, minutes) OR file management OR health and safety responsibilities

Command words to rotate across the 9 questions — no two consecutive questions should use the same one: outline, describe, explain, justify, discuss, compare.

Write the mark allocation in brackets at the end of each question e.g. [4 marks].

[END OF QUESTION PAPER]`;

  const getAdminN5Prompt = () => `You are generating a theory preview for SQA National 5 Administration and IT.

IMPORTANT: The real N5 exam is a 2-hour PRACTICAL computer-based exam. Students work with real database, spreadsheet, and word-processing files and print evidence. This preview covers only the theory questions typed inside a word-processing document. Use sqa.org.uk past paper files to practise the practical tasks.

NATIONAL 5 ADMINISTRATION AND IT — THEORY PREVIEW

SECTION A — DATABASE FEATURES
Outline a use of each of the following:
1. Query [Space for answer]
2. Report [Space for answer]
3. Form [Space for answer]

SECTION B — INFORMATION SECURITY
Describe four ways an organisation can keep its electronic information secure.
[Space for answer]

SECTION C — LEGISLATION
[Generate one of: Explain three employer health & safety responsibilities. OR Outline two offences under the Computer Misuse Act. OR Describe three ways an organisation complies with GDPR. OR Outline three employer responsibilities under Fire Safety Regulations.]
[Space for answer]

[END OF THEORY PREVIEW]
Note: The real exam also includes ~39 marks of practical database and spreadsheet work not reproducible here.`;

  const getDramaHigherPrompt = () => `You are generating a PREDICTED SQA Higher Drama Question Paper.

Match the EXACT structure of the real SQA Higher Drama specimen paper (May 2025 version):

HIGHER
DRAMA
Total marks — 40
2 hours

SECTION 1 — THEATRE PRODUCTION: TEXT IN CONTEXT — 20 marks
Attempt ONE question.

In this section you are asked a question that allows you to answer from the perspective of a director OR an actor OR a designer preparing for an intended production of your selected text. You should refer to your selected text in your answer and must make a minimum of three textual references and/or quotations in your answer.

Generate exactly 6 questions — two for director, two for actor, two for designer. Each is worth 20 marks. Use this structure for each question: first part asks candidate to DESCRIBE aspects of the text with textual references; second part asks them to EXPLAIN the concepts they would use, which must relate to the first part. Use the exact question style below as your model:

1. As a director, describe in detail the different changes in mood and atmosphere in your selected text. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the ways in which you would use directing concepts to highlight these changes in your final production. These must be related to the first part of the question. 20

2. As a director, [choose a different director focus — e.g. dramatic features that create climax, use of staging, social/historical context, creation of tension, use of dramatic conventions]. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the ways in which you would use directing concepts to achieve your desired dramatic impact. These must be related to the first part of the question. 20

3. As an actor, choose one of the characters from your selected text and describe in detail [character feelings towards other characters OR aspects of their personality OR character development]. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the ways in which you would use acting concepts to portray this [aspect] in your final production. These must be related to the first part of this question. 20

4. As an actor, choose one complex character from your selected text and describe in detail [a different character focus from Q3]. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the ways in which you would use acting concepts to portray this character. These must be related to the first part of this question. 20

5. As a designer, describe in detail [different moments that convey themes/issues OR key scenes OR mood and atmosphere OR character characteristics] in your selected text. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the ways in which you would use design concepts to [highlight/communicate these]. These must be related to the first part of this question. 20

6. As a designer, [choose a different design focus from Q5 — e.g. choose one character and describe their characteristics; OR moments showing status/relationships; OR moments showing historical/social context]. You must use textual references and/or quotations from the play to justify your answer.
Explain, in detail, the design concepts that would help you to communicate these. These must be related to the first part of this question. 20

---

SECTION 2 — PERFORMANCE ANALYSIS — 20 marks
Attempt ONE question.

In this section you are asked to give a performance analysis of a theatrical performance that you have seen live or recorded. You are asked to think about individual aspects of a production and analyse the extent to which these helped the audience to understand the performance.

The play you select for this performance analysis must be different from your selected text in Section 1.

In your analysis, you should include reference to a minimum of two production areas from the list below:
• choice and use of the performance space
• director's intentions and effectiveness
• acting and development of characters
• design concepts and their effectiveness.

You must state the name of the performance in the introduction to your response.

Generate exactly 2 questions for Section 2. Each is worth 20 marks. Each question must follow this structure: first ask the candidate to IDENTIFY AND DESCRIBE ways in which [a specific aspect] was created/communicated; then ANALYSE the extent to which their selected production areas helped the audience understand [that aspect]. Use different focuses for Q7 and Q8 — choose from: contrasts, social/historical context, themes and issues, key relationships, dramatic tension, character development, mood and atmosphere, the director's intentions.

7. Identify and describe in detail the ways [focus 1] were created/communicated in a performance you have seen recently.
Analyse the extent to which your selected production areas helped the audience to understand [focus 1]. 20

8. Identify and describe in detail the ways in which [focus 2] was communicated in a performance you have seen recently.
Analyse the extent to which your selected production areas helped the audience understand [focus 2]. 20

[END OF QUESTION PAPER]`;

  const getDramaN5Prompt = () => `You are generating a PREDICTED SQA National 5 Drama Question Paper (60 marks, 1 hour 30 minutes).

NATIONAL 5
DRAMA
Total marks — 60
1 hour 30 minutes

SECTION 1 — 20 marks
Attempt ALL questions.
Consider a performance you have taken part in during your course as either an actor or in a production role.
[Tick: Actor / Lighting / Costume / Set / Props and set dressing / Sound / Make-up and hair]

1. Identify the genre of your drama. Justify your answer. 2
2. Describe two ways you contributed during the rehearsal process. 4
3. (a) Identify the mood(s) and/or atmosphere(s) in your drama. Justify your answer. 2
   (b) Evaluate your effectiveness in highlighting the mood(s) and/or atmosphere(s). If an actor, include comments on performance concepts using appropriate terminology. If in a production role, include comments on design concepts. 4

Consider a performance one other drama student has taken part in.

4. Describe one activity they carried out during the rehearsal process. 2
5. Identify a suitable target audience for this student's drama. Justify your answer. 2
6. Evaluate the effectiveness of this student's final performance. If an actor, include performance concept comments. If a production role, include design concept comments. 4

SECTION 2 — 40 marks
Choose one of the following stimuli to develop ideas for a drama with two or more characters suitable for live performance.

Stimulus A — [Write an original poem or dramatic extract, 15-25 lines, with clear dramatic potential around themes of isolation, conflict, fear, identity, or change.]
Stimulus B — [Write a philosophical quote, 1-3 sentences, about human experience — loss, courage, identity, or belonging. Include a plausible source.]
Stimulus C — [Describe a striking black-and-white photograph as a drama stimulus. E.g. two figures on an empty pier at dawn, one facing the water, one looking back, wide space between them.]

[Tick: Stimulus A / Stimulus B / Stimulus C]

7. (a) State the purpose of your drama. 1
   (b) Describe a scene highlighting this purpose. 3
8. (a) Identify the time period. Give a reason. 2
   (b) Describe how you would use set and costume to communicate this time period. 4
9. (a) State the main theme/issue. Justify. 2
   (b) As a designer, describe ONE production skill to communicate this: Lighting / Sound / Props / Make-up and hair. 3
10. (a) Identify the character with the highest status. Justify. 2
    (b) Describe how an actor would use voice and movement to communicate this status. 4
11. Describe how you would use TWO rehearsal techniques to develop characters. Justify. Improvisation / Thought-tracking / Voices in the head. 4
12. State the staging you would use. Give a reason. 2
13. (a) Choose two characters in the same scene. Describe their relationship. 2
    (b) As a director, describe stage positioning to communicate this relationship. Justify. 4
14. (a) Describe a key moment. Give a reason. 2
    (b) Describe the desired audience emotional response. 2
    (c) Describe how ONE convention would create this response: Voice-over / Slow motion / Mime / Monologue. 3

[END OF QUESTION PAPER]`;

  useEffect(() => {
    if (examState === "in_progress") {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
      window.__summitMockInProgress = true;
    } else {
      clearInterval(timerRef.current);
      window.__summitMockInProgress = false;
    }
    return () => {
      clearInterval(timerRef.current);
      window.__summitMockInProgress = false;
    };
  }, [examState]);

  // English gets its own dedicated panel — must be after all hooks
  if (isEnglish) return <EnglishMockPanel level={level} userData={userData} />;

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  // ── Maths paper system prompts ─────────────────────────────────────
  const getMathsPrompt = (type, level, subject) => {
    const isN5 = level === "National 5";
    const isHigher = level === "Higher";
    const isAH = level === "Advanced Higher";
    const isAppMaths = subject === "Applications of Mathematics";

    const FMT = `CRITICAL: Write ALL mathematical expressions in LaTeX inside $...$. Never write the same expression twice. Never mix plain text and LaTeX for the same expression. Coordinates: $A(2, 5)$. Equations: $y = 2x^3 - x^2 + 5$. After each question add [Space for working] (2-3 marks) or [Large working space] (4+ marks). Write mark allocation at end e.g. [4 marks]. For diagrams: describe in words only.`;

    if (isN5 && !isAppMaths) {
      if (type === "noncalc") return `You are generating a National 5 Mathematics Paper 1 (Non-calculator) mock exam matching real SQA N5 past papers exactly.

Total marks: 40 | Time: 1 hour | No calculator | All questions compulsory
Mark split: ~35% algebra, ~25% geometry, ~20% trig, ~10% numerical, ~10% statistics

Generate exactly 13-15 questions totalling 40 marks. Use a mix of 2, 3, and 4 mark questions.

TOPICS TO INCLUDE:
1. Fraction arithmetic with mixed numbers e.g. $4\frac{2}{5} - 2\frac{2}{7}$ [2 marks]
2. Expand and simplify double brackets e.g. $(x+3)(x-5) + 2x$ [3 marks]
3. Interquartile range from 10 values given as a list [2 marks]
4. Reverse percentage — sale price given, find original [3 marks]
5. Area of triangle using $A = \frac{1}{2}ab\sin C$ with given values [2 marks]
6. Straight line equation through two given points [3 marks]
7. Trig graph — state amplitude and period from $y = a\sin(bx°)$ [2 marks]
8. Parabola — state values of $a$ and $b$ from $y = (x-a)^2 + b$ [2 marks]
9. Simplify using laws of indices e.g. $\frac{(2n^2)(7n^3)}{n^4}$ [3 marks]
10. Nature of roots — calculate discriminant $b^2 - 4ac$ and state nature [2 marks]
11. Rationalise denominator e.g. $\frac{6}{\sqrt{10}}$ [2 marks]
12. Algebraic fraction — express as single fraction e.g. $\frac{5}{x+1} - \frac{4}{x(x+1)}$ [3 marks]
13. Simultaneous equations — solve algebraically [3 marks]
14. Surd simplification e.g. $\sqrt{75} - \sqrt{3}$ [2 marks]

${FMT}

[FORMULAE NOTE: Real exam provides quadratic formula, sine rule, cosine rule, area of triangle, volumes, standard deviation.]

Begin with:
NATIONAL 5 MATHEMATICS
Paper 1 (Non-calculator) — AI Practice Paper
Total marks: 40 | Time: 1 hour
You must NOT use a calculator. To earn full marks you must show your working.`;

      if (type === "calc") return `You are generating a National 5 Mathematics Paper 2 (Calculator) mock exam matching real SQA N5 past papers exactly.

Total marks: 50 | Time: 1 hour 30 minutes | Calculator permitted | All questions compulsory

Generate exactly 14-16 questions totalling 50 marks.

TOPICS TO INCLUDE:
1. Appreciation or depreciation over multiple years [3 marks]
2. Volume of sphere, cone or pyramid using formula [3 marks]
3. Scientific notation calculation [2 marks]
4. Mean and standard deviation of 7 values [4 marks]
5. Completing the square: $x^2 + bx + c$ into $(x+p)^2 + q$ [2 marks]
6. Sector area — given radius and angle [3 marks]
7. Angle in regular polygon [2 marks]
8. 3D coordinates — cuboid vertices, length of space diagonal [1+3 marks]
9. Change subject of formula involving fraction or square root [3 marks]
10. Simultaneous equations from context — write equations then solve [1+1+4 marks]
11. Similar shapes — find area using scale factor [3 marks]
12. Bearing problem using sine/cosine rule [4 marks]
13. Solve equation with algebraic fractions [3 marks]
14. Trig equation — find two values of $x$ in given domain [4 marks]
15. Vectors — express $\overrightarrow{GF}$ in terms of given vectors [2 marks]

${FMT}

Begin with:
NATIONAL 5 MATHEMATICS
Paper 2 (Calculator) — AI Practice Paper
Total marks: 50 | Time: 1 hour 30 minutes
You may use a calculator. To earn full marks you must show your working.`;
    }

    if (isHigher && !isAppMaths) {
      if (type === "noncalc") return `You are generating a Higher Mathematics Paper 1 (Non-calculator) mock exam matching real SQA Higher past papers exactly.

Total marks: 55 | Time: 1 hour 15 minutes | No calculator | All questions compulsory
Mark split: ~35% calculus, ~30% algebra/trig, ~20% geometry, ~15% other

Generate 12-14 questions totalling 55 marks.

TOPICS TO INCLUDE (in roughly this order):
1. Differentiate curve, find equation of tangent at given point [4 marks]
2. Find equation of perpendicular bisector of line segment $AB$ [4 marks]
3. Evaluate indefinite integral with negative/fractional powers e.g. $\int\left(12x^{\frac{1}{2}} + \frac{2}{x^2}\right)dx$ [4 marks]
4. Evaluate log expression using laws e.g. $\log_3\frac{1}{3} + 2\log_3 24$ [3 marks]
5. Graph transformation — given stationary points of $y=f(x)$, state stationary points of $y=f(x-3)+2$ [2 marks]
6. Double angle formulae — given right-angled triangle, find $\sin 2\theta$, $\cos 2\theta$, then $\sin(2\theta + 30°)$ [3+1 marks]
7. Factor theorem — show $(x+a)$ is factor of cubic, hence solve [2+3 marks]
8. Solve logarithmic equation e.g. $\log_a 75 + 2 = 3\log_a 5$ [3 marks]
9. Line and circle intersection — find coordinates [4 marks]
10. Scalar product — find angle between two 3D vectors [5 marks]
11. Discriminant — find range of $k$ for two real distinct roots [4 marks]
12. Integration with initial condition — find $y$ given $\frac{dy}{dx}$ and a point [4 marks]
13. Surd question e.g. expand $(\sqrt{3}-1)^2(\sqrt{3}+2)$ [2 marks]

${FMT}

[FORMULAE NOTE: Real exam provides circle equations, scalar product, trig addition/double angle formulae, standard derivatives and integrals for sin ax and cos ax.]

Begin with:
HIGHER MATHEMATICS
Paper 1 (Non-calculator) — AI Practice Paper
Total marks: 55 | Time: 1 hour 15 minutes
You must NOT use a calculator. To earn full marks you must show your working.`;

      if (type === "calc") return `You are generating a Higher Mathematics Paper 2 (Calculator) mock exam matching real SQA Higher past papers exactly.

Total marks: 65 | Time: 1 hour 30 minutes | Calculator permitted | All questions compulsory

Generate 13-15 questions totalling 65 marks.

TOPICS TO INCLUDE (in roughly this order):
1. Triangle with three vertices — find altitude through $A$ (3 marks), median through $B$ (3 marks), intersection point (2 marks) [8 marks total]
2. Completing the square with non-unitary coefficient e.g. $2x^2 - 8x + 5$ into $p(x+q)^2 + r$ [3 marks]
3. Area between two curves — integrate between intersection points [4 marks]
4. Find inverse function $f^{-1}(x)$ [3 marks]
5. Show three points are collinear, state ratio $PQ:QR$ [3+1 marks]
6. Wave function — express $a\cos x° + b\sin x°$ as $k\cos(x°+\alpha)$, hence solve equation [4+3 marks]
7. Evaluate definite trig integral e.g. $\int_0^{\frac{\pi}{7}}\cos 5x\,dx$ [2 marks]
8. Pyramid with vectors — express $\overrightarrow{BE}$ in terms of $\mathbf{i}$, $\mathbf{j}$, $\mathbf{k}$ [2+2 marks]
9. Recurrence relation $u_{n+1} = mu_n + b$ — find limit, calculate $u_0$ [2+1 marks]
10. Optimisation — show surface area/perimeter formula, find minimum by differentiation [3+6 marks]
11. Solve trig equation using identity e.g. $3\sin^2 x° - 4\cos x° = 0$ [4 marks]
12. Composite function $h(x) = f(g(x))$, find $h'(x)$ [2+2 marks]
13. Exponential model $N = N_0 e^{kt}$ — find initial value, find time for given amount [1+4 marks]
14. Two circles — find centres/radii, determine equation of tangent or show relationship [2+2+3 marks]

${FMT}

Begin with:
HIGHER MATHEMATICS
Paper 2 (Calculator) — AI Practice Paper
Total marks: 65 | Time: 1 hour 30 minutes
You may use a calculator. To earn full marks you must show your working.`;
    }

    if (isAH && !isAppMaths) {
      if (type === "noncalc") return `You are generating an Advanced Higher Mathematics Paper 1 (Non-calculator) mock exam matching real SQA AH past papers exactly.

Total marks: 35 | Time: 1 hour | No calculator
Mark split: ~45% calculus, ~30% algebra/proof, ~25% matrices/complex numbers

Generate 8-10 questions totalling 35 marks.

TOPICS TO INCLUDE:
1. Binomial theorem — expand $(p + qx^n)^r$ or find specific term/coefficient [4 marks]
2. Differentiate complex function — simplify first e.g. $f(x) = \frac{x^2 + 3x - 2}{x^{\frac{3}{2}}}$ [3 marks]
3. Complex numbers — express $\frac{z}{w}$ in form $a+bi$ where $z = 11+10i$, $w = 3+2i$ [2 marks]
4. Matrices — find $3A - 2B$ [1 mark], find $A'B$ [2 marks], find determinant expression [1 mark], find $\lambda$ making matrix singular [1 mark]
5. Evaluate $\int_1^2 \frac{1}{1+4x^2}\,dx$ [2 marks]
6. Express $f(x) = \frac{x^2+5}{x-2}$ in form $Ax + B + \frac{C}{x-2}$, state asymptotes [2+2 marks]
7. Solve differential equation e.g. $\frac{dy}{dx} = \frac{y}{2x-1}$ with initial condition [5 marks]
8. Gaussian elimination on 3×3 system — find intersection point [4 marks], find point where line meets plane [3 marks], find parametric equations of line through two points [2 marks]

${FMT}

[FORMULAE NOTE: AH Paper 1 provides standard derivatives including $\tan x$, $\cot x$, $\sec x$, $\csc x$, inverse trig, $\ln x$, $e^x$; standard integrals; summation formulae; binomial theorem; Maclaurin expansion; de Moivre's theorem; vector product; rotation matrix.]

Begin with:
ADVANCED HIGHER MATHEMATICS
Paper 1 (Non-calculator) — AI Practice Paper
Total marks: 35 | Time: 1 hour
You must NOT use a calculator. To earn full marks you must show your working.`;

      if (type === "calc") return `You are generating an Advanced Higher Mathematics Paper 2 (Calculator) mock exam matching real SQA AH past papers exactly.

Total marks: 80 | Time: 2 hours 30 minutes | Calculator permitted

Generate 14-17 questions totalling 80 marks.

TOPICS TO INCLUDE:
1. Differentiate $f(x) = \cos^{-1}(x^4) - 1$ [2 marks]
2. Implicit differentiation — find $\frac{dy}{dx}$ for $2y^2 + 2xe^{4x} = 3$ [3 marks]
3. Partial fractions — decompose $\frac{2x^2-18}{(x-1)(3+x)(5-x)}$ [3 marks]
4. Euclidean algorithm — find GCD of 1118 and 416 [1 mark], find integers $a,b$ such that $d = 1118a + 416b$ [2 marks]
5. Logarithmic differentiation — find $\frac{dy}{dx}$ for $y = x^{\cot x}$ [5 marks]
6. Maclaurin expansion for $\cos 3x$ up to $x^4$ [2 marks], hence find for $\cos^2 3x$ [2 marks]
7. Parametric equations $x = t^2$, $y = \tan t$ — find $\frac{dy}{dx}$ [2 marks] and $\frac{d^2y}{dx^2}$ [3 marks]
8. Matrix property $A^2 = 6A - I$ — express $A^3$ as $pA + qI$ [2 marks], find $A^{-1}$ [2 marks]
9. Velocity $v = te^{-2t}$ — find displacement given $s=0$ at $t=0$ [2 marks], show acceleration always positive [2 marks]
10. Summation — find and factorise $\sum_{r=1}^{n} r(r^3-1)$ [2 marks]
11. Integration by substitution then volume of revolution [2+3 marks]
12. Second order ODE with initial conditions [9 marks]
13. Geometric series — find ratio, first term, explain convergence, find sum to infinity, effect of multiplying by $k$ [2+1+1+1+1 marks]
14. Differential equation — find general solution [5 marks]
15. Proof by induction for $\sum_{r=1}^{n}\frac{1}{(2r-1)(2r+1)} = \frac{n}{2n+1}$ [5 marks]
16. Integration by parts — $\int e^{2x}\sin 5x\,dx$ [5 marks]
17. Complex numbers — express $z + i\bar{z}$ in Cartesian form, find argument, use de Moivre [2+1+2 marks]

${FMT}

Begin with:
ADVANCED HIGHER MATHEMATICS
Paper 2 (Calculator) — AI Practice Paper
Total marks: 80 | Time: 2 hours 30 minutes
You may use a calculator. To earn full marks you must show your working.`;
    }

    // Applications of Mathematics or fallback
    return null;
  };

  const generatePaper = async (type) => {
    setRateLimitError(null);
    try {
      const limitRes = await fetch(`${BACKEND}/check-mock-limit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: userData?.id || null })
      });
      const limitData = await limitRes.json();
      if (!limitData.allowed) {
        setRateLimitError(limitData.message || "Rate limit reached. Try again later.");
        return;
      }
    } catch(e) { console.warn("Rate limit check failed:", e); }
    setPaperType(type);
    setLoading(true); setExamState("generating");
    try {
      const mathsPrompt = getMathsPrompt(type, level, subject);
      const multiPaperPrompt = !mathsPrompt ? getMultiPaperPrompt(type) : null;
      const prompt = mathsPrompt || multiPaperPrompt || (() => {
        if (type === "p1_mc") return `Generate a Paper 1 multiple choice paper for ${level} ${subject}. Include 20-25 multiple choice questions (A, B, C, D options) covering all topics across the course. Format exactly like an SQA paper with question numbers, clear question text, and lettered options. State the total marks at the top.`;
        if (type === "p2") return `Generate a Paper 2 extended answer paper for ${level} ${subject}. Include a mix of short answer (2-4 mark) and extended response (6-8 mark) questions. Use correct SQA command words. State marks for each question. Realistic SQA numbering.`;
        if (type === "noncalc") return `Generate a non-calculator paper for ${level} ${subject}. Include questions that can be answered without a calculator. Mix of short (2-3 mark) and longer questions (4-6 marks). State marks clearly. Use SQA-style numbering.`;
        if (type === "calc") return `Generate a calculator paper for ${level} ${subject}. Include questions that may require a calculator. Mix of short (2-3 mark) and longer questions (4-6 marks). State marks clearly. Use SQA-style numbering.`;
        if (type === "cs_ddd") return `Generate the paper now. IMPORTANT: Generate Section 1 (SDD + Computer Systems) in full, then generate Section 2 (Database Design and Development) ONLY. Do NOT generate Section 3 (Web Design and Development).`;
        if (type === "cs_wdd") return `Generate the paper now. IMPORTANT: Generate Section 1 (SDD + Computer Systems) in full, then generate Section 3 (Web Design and Development) ONLY. Do NOT generate Section 2 (Database Design and Development).`;
        return `Generate a complete ${level} ${subject} mock exam paper. Include a mix of question types covering all major topics. State marks for each question. Use correct SQA command words and numbering.`;
      })();
      const isMaths = ["Mathematics", "Applications of Mathematics"].includes(subject);
      const isComputingScience = subject === "Computing Science";
      const res = await fetch(`${BACKEND}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: isMaths ? 8000 : isComputingScience ? 12000 : 3000, system: mathsPrompt ? `You are an expert SQA Mathematics examiner. Generate questions that exactly match the style, difficulty and notation of real SQA ${level} Mathematics past papers. Use LaTeX math notation ($...$) for all mathematical expressions. Always include working space boxes after questions. Be precise with mark allocations.` : isAdmin ? `You are an expert SQA examiner generating a ${level} Administration and IT exam. Follow the format exactly.` : isDrama ? `You are an expert SQA examiner generating a ${level} Drama exam. Follow the format exactly.` : getSystemPrompt(subject, level, "mock", userData), messages: [{ role: "user", content: isAdmin ? (level === "National 5" ? getAdminN5Prompt() : getAdminHigherPrompt()) : isDrama ? (level === "National 5" ? getDramaN5Prompt() : getDramaHigherPrompt()) : prompt }] }) });
      const data = await res.json();
      setPaper(data.content?.[0]?.text || "Failed to generate paper.");
      setExamState("in_progress"); setTimer(0);
    } catch { setExamState("setup"); }
    setLoading(false);
  };

  const submitPaper = async () => {
    if (!answers.trim()) return;
    setLoading(true); setExamState("submitting");
    try {
      const isMultiChoice = paperType === "p1_mc";
      const isMathsSubject = ["Mathematics", "Applications of Mathematics"].includes(subject);
      const mathsMarkingPrompt = `You are marking a ${level} ${subject} mock exam paper using SQA marking principles.

CRITICAL: Your response must be plain text only. Do not use markdown headers, bold (**), or LaTeX ($). Write mathematical expressions in plain text e.g. "x = 3" not "$x = 3$".

SQA MATHS MARKING PHILOSOPHY:
- Strategy mark (ss): awarded for selecting the correct overall approach
- Process mark (pd): awarded for carrying out the correct mathematical process  
- Answer mark (ans): awarded for the correct final answer
- Follow-through (ft): if a student makes an early error but carries it through correctly, award process and answer marks for subsequent correct working
- NEVER penalise the same error twice

For EACH question in the paper:
Write "Q[number]: [marks awarded]/[marks available]"
Then on the next line briefly explain what earned marks and what was missed.
If working is shown but the answer is wrong, award method marks where the process is correct.
If a question is blank or not attempted, write "Q[number]: 0/[marks available] - Not attempted"

After ALL questions, write:
"TOTAL: [X]/[total marks available]"
"GRADE ESTIMATE: A (roughly 70%+) / B (roughly 60%) / C (roughly 50%) / D (roughly 40%)"
"TOP 2 PRIORITIES: [specific topic 1], [specific topic 2]"
"STRENGTH: [one genuine thing they did well]"

Keep each question's feedback to 2-3 lines maximum. Be encouraging but honest.`;
      const markingSystem = isMultiChoice
        ? `You are marking a multiple choice Paper 1. For each question give: correct answer, whether student got it right, and brief explanation of the correct answer. End with total score out of total questions, grade estimate, and top 3 topics to review.`
        : isMathsSubject
        ? mathsMarkingPrompt
        : `You are Summit, marking a complete ${level} ${subject} mock exam paper. For each question write "Q[number]: [awarded]/[available] marks" then 1-2 sentences on what earned marks and what was missing. End with: TOTAL: X/Y, GRADE ESTIMATE: A/B/C/D, TOP 2 PRIORITIES: [topics], STRENGTH: [one positive].`;
      const res = await fetch(`${BACKEND}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 6000, system: markingSystem, messages: [{ role: "user", content: `Paper:\n\n${paper}\n\n---\n\nAnswers (time: ${formatTime(timer)}):\n\n${answers}` }] }) });
      const data = await res.json();
      setResults(data.content?.[0]?.text || "Marking failed."); setExamState("results");
    } catch { setResults("Connection error. Try again."); setExamState("results"); }
    setLoading(false);
  };

  const reset = () => { setExamState("setup"); setPaper(null); setAnswers(""); setResults(null); setTimer(0); setPaperType(null); };

  if (examState === "setup") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 16, padding: "40px 32px" }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>📝</div>
      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2", letterSpacing: -0.5 }}>Mock exam</div>
      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 360, lineHeight: 1.7, fontWeight: 300 }}>Choose your paper type below. Work through it at your own pace, then submit for a full breakdown.</div>
      {!disclaimerDismissed && <div style={{ maxWidth: 420, width: "100%" }}><MockDisclaimer onDismiss={() => setDisclaimerDismissed(true)} /></div>}
      {rateLimitError && (
        <div style={{ maxWidth: 420, width: "100%", padding: "10px 14px", background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 8, fontSize: 13, color: "#E24B4A", fontWeight: 300 }}>
          ⏱ {rateLimitError}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 380, marginTop: 8 }}>
        {isScience && (
          <>
            {[["p1_mc","Paper 1 — Multiple Choice","20-25 questions · SQA format"],["p2","Paper 2 — Extended Answers","Short & extended questions"]].map(([type, title, sub]) => (
              <button key={type} onClick={() => generatePaper(type)} style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                <div><div style={{ fontWeight: 500 }}>{title}</div><div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>{sub}</div></div>
                <span style={{ color: "#1D9E75" }}>→</span>
              </button>
            ))}
          </>
        )}
        {isMaths && (
          <>
            {[["noncalc","Non-calculator paper","No calculator allowed"],["calc","Calculator paper","Calculator permitted"]].map(([type, title, sub]) => (
              <button key={type} onClick={() => generatePaper(type)} style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                <div><div style={{ fontWeight: 500 }}>{title}</div><div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>{sub}</div></div>
                <span style={{ color: "#1D9E75" }}>→</span>
              </button>
            ))}
          </>
        )}
        {isAdmin && (
          <button onClick={() => generatePaper("full")}
            style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {level === "National 5" ? "Theory Questions (Practical preview)" : "Question Paper"}
              </div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>
                {level === "National 5"
                  ? "50 marks · Note: real exam is practical computer-based"
                  : "50 marks · 1 hour 30 minutes"}
              </div>
            </div>
            <span style={{ color: "#1D9E75" }}>→</span>
          </button>
        )}
        {isDrama && (
          <button onClick={() => generatePaper("full")}
            style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Question Paper</div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>
                {level === "National 5" ? "60 marks · 1 hour 30 minutes · 2 sections" : "40 marks · 2 hours · 2 sections"}
              </div>
            </div>
            <span style={{ color: "#1D9E75" }}>→</span>
          </button>
        )}
        {isMultiPaper && mockPaperOptions.map(({ type, title, sub }) => (
          <button key={type} onClick={() => generatePaper(type)}
            style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
            <div><div style={{ fontWeight: 500 }}>{title}</div><div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>{sub}</div></div>
            <span style={{ color: "#1D9E75" }}>→</span>
          </button>
        ))}
        {isComputing && (
          <>
            {[
              ["cs_ddd", "Database Development section", "Section 1 (SDD) + Section 2 (DDD)"],
              ["cs_wdd", "Web Development section", "Section 1 (SDD) + Section 3 (WDD)"]
            ].map(([type, title, sub]) => (
              <button key={type} onClick={() => generatePaper(type)} style={{ padding: "14px 20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4f2", fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.4)"; e.currentTarget.style.background = "rgba(29,158,117,0.06)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                <div><div style={{ fontWeight: 500 }}>{title}</div><div style={{ fontSize: 11, color: "rgba(240,244,242,0.35)", marginTop: 2 }}>{sub}</div></div>
                <span style={{ color: "#1D9E75" }}>→</span>
              </button>
            ))}
          </>
        )}
        {!isScience && !isMaths && !isAdmin && !isDrama && !isMultiPaper && !isComputing && (
          <button onClick={() => generatePaper("full")} style={{ padding: "14px 20px", background: "#1D9E75", border: "none", borderRadius: 10, color: "white", fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>Generate paper →</button>
        )}
      </div>
    </div>
  );

  if (examState === "generating" || (examState === "submitting" && loading)) return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
      <div style={{ fontSize: 14, color: "rgba(240,244,242,0.4)", fontWeight: 300 }}>{examState === "generating" ? "Building your paper…" : "Marking your answers…"}</div>
    </div>
  );

  if (examState === "in_progress") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>{paperType === "p1_mc" ? "Circle your answers below, then submit" : isMaths ? "Work through each question — show all working — then submit" : "Work through the paper, then paste all your answers"}</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#1D9E75" }}>⏱ {formatTime(timer)}</div>
      </div>
      <div style={{ padding: "10px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}><MockDisclaimer /></div>
      <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "24px", borderBottom: "1px solid rgba(255,255,255,0.07)", background: "white" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Scottish Qualifications Authority</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: "#000" }}>{level.toUpperCase()}</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 16, fontWeight: 600, color: "#000", marginTop: 2 }}>{subject.toUpperCase()}</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#555", marginTop: 4 }}>
                    {paperType === "p1_mc" ? "Paper 1 — Multiple Choice" : paperType === "p2" ? "Paper 2 — Extended Answers" : paperType === "noncalc" ? "Paper 1 — Non-calculator" : paperType === "calc" ? "Paper 2 — Calculator" : "Practice Paper"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333" }}>AI-Generated Practice Paper</div>
                  <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#333", marginTop: 2 }}>Summit — usesummit.co.uk</div>
                </div>
              </div>
            </div>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#333", marginBottom: 20, padding: "10px 14px", border: "1px solid #ccc", borderRadius: 4 }}>
              <strong>Instructions:</strong> {paperType === "p1_mc" ? "Circle the letter of the correct answer for each question." : "Answer all questions. Show all working where required. Marks for each question are shown in brackets."} Time yourself and work under exam conditions.
            </div>
            {["Mathematics","Applications of Mathematics"].includes(subject) && MATHS_FORMULAE[level] && (
              <div style={{ margin: "0 0 20px 0", padding: "12px 16px", border: "2px solid #000", fontFamily: "'Trebuchet MS', sans-serif", color: "#111", background: "white" }}>
                <div style={{ fontSize: 13, fontWeight: 700, borderBottom: "1px solid #ccc", paddingBottom: 6, marginBottom: 10 }}>FORMULAE LIST</div>
                <div style={{ fontSize: 12, lineHeight: 2, color: "#111" }} dangerouslySetInnerHTML={{ __html: MATHS_FORMULAE[level].split(" | ").map(f => renderMath(f)).join("<br/>") }} />
              </div>
            )}
            {renderSQAPaper(paper)}
          </div>
        </div>
        <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", padding: "16px 24px" }}>
          {(paperType !== "p1_mc" && isMaths) ? (
            <>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 6, fontWeight: 500 }}>Your working and answers</div>
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginBottom: 8, fontWeight: 300 }}>Show your working for each question — marks are awarded for working even if the final answer is wrong.</div>
              <textarea value={answers} onChange={e => setAnswers(e.target.value)}
                placeholder={"Q1.\nWorking: ...\nAnswer: ...\n\nQ2.\nWorking: ...\nAnswer: ...\n\n(Show all working — SQA award marks for correct working even with wrong final answer)"}
                style={{ minHeight: 180, resize: "vertical", padding: "12px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none", width: "100%", boxSizing: "border-box" }} />
              <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", marginTop: 6, fontWeight: 300 }}>
                💡 You can write fractions as e.g. "3/4", powers as "x^2", square roots as "sqrt(x)". The marker understands standard notation.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 10, fontWeight: 500 }}>Your answers</div>
              <textarea value={answers} onChange={e => setAnswers(e.target.value)}
                placeholder={paperType === "p1_mc" ? "Q1: A\nQ2: C\nQ3: B\n..." : "Q1.\n\nQ2.\n\nQ3.\n\n(Write all answers here)"}
                style={{ minHeight: 100, resize: "vertical", padding: "12px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", color: "#f0f4f2", outline: "none" }} />
            </>
          )}
          <button onClick={submitPaper} disabled={!answers.trim()} style={{ marginTop: 10, padding: "13px", background: answers.trim() ? "#1D9E75" : "rgba(255,255,255,0.05)", color: answers.trim() ? "white" : "rgba(240,244,242,0.2)", border: "none", borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: answers.trim() ? "pointer" : "default" }}>Submit for marking →</button>
        </div>
      </div>
    </div>
  );

  if (examState === "results") return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 13, color: "#4DC9A0", fontWeight: 500 }}>Mock results — {level} {subject}</div>
        <button onClick={reset} style={{ fontSize: 12, padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(240,244,242,0.45)", fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>New paper</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        <div style={{ fontSize: 13, lineHeight: 1.85, color: "rgba(240,244,242,0.8)", fontWeight: 300 }}>{renderMessage(results)}</div>
      </div>
    </div>
  );

  return null;
}

// ── Route-level components ────────────────────────────────────────────
function LandingPage() {
  const navigate = useNavigate();
  return <Landing onGetStarted={() => navigate("/auth")} onSignIn={() => navigate("/auth")} />;
}

function StudyApp({ session, profile, setProfile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [subject, setSubject] = useState(profile?.subjects?.[0] || "");
  const [mode, setMode] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [activeScreen, setActiveScreen] = useState(null);
  const [lastMessageTimes, setLastMessageTimes] = useState([]);
  const [rateLimited, setRateLimited] = useState(false);
  const [showProWelcome, setShowProWelcome] = useState(false);
  const [showCoachAnnouncement, setShowCoachAnnouncement] = useState(false);
  const [lockedSubjectModal, setLockedSubjectModal] = useState(null); // { name, level }
  const [checkinDismissed, setCheckinDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const bottomRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Boost purchase success — poll until webhook updates Supabase
    if (params.get("boost_success") === "true") {
      navigate("/app", { replace: true });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const { data } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
        if (data) {
          if (data.coach_unlocked || attempts >= 10) {
            setProfile(data);
            setActiveScreen("coach");
          } else {
            setTimeout(poll, 1000);
          }
        }
      };
      poll();
      return;
    }

    // Pro upgrade success
    if (params.get("upgrade") === "success") {
      loadProfile(session.user.id);
      setShowProWelcome(true);
      navigate("/app", { replace: true });
      return;
    }

    // Predicted paper purchase success — poll until webhook updates Supabase
    if (params.get("paper_success") === "true") {
      navigate("/app", { replace: true });
      const subjectParam = params.get("subject") || "";
      const levelParam = params.get("level") || "";
      const paperKey = subjectParam + "__" + levelParam;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const { data } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
        if (data) {
          const hasPurchase = Array.isArray(data.purchased_papers) && data.purchased_papers.includes(paperKey);
          if (hasPurchase || attempts >= 8) {
            setProfile(data);
            if (data.subjects?.[0]) setSubject(data.subjects[0]);
            setActiveScreen("predicted");
          } else {
            setTimeout(poll, 1000);
          }
        }
      };
      poll();
    }
  }, [location.search]); // eslint-disable-line

  const loadProfile = async (userId) => {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();
    if (!error && data) { setProfile(data); setSubject(data.subjects?.[0] || ""); }
  };

  const triggerCheckout = async (userId, userEmail) => {
    setUpgradeLoading(true);
    try {
      const res = await fetch(`${BACKEND}/create-checkout-session`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId, userEmail }) });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setUpgradeLoading(false);
  };


  // eslint-disable-next-line no-unused-vars
  const triggerEliteCheckout = async (userId, userEmail) => {
    setUpgradeLoading(true);
    try {
      // If already on Pro, upgrade the existing subscription (no double charge)
      if (profile?.plan === "pro" && profile?.stripe_subscription_id) {
        const res = await fetch(`${BACKEND}/upgrade-to-elite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId })
        });
        const data = await res.json();
        if (data.ok) {
          // Payment pending - poll Supabase for up to 10s for the webhook to fire
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            const { data: updated } = await supabase.from("profiles").select("*").eq("id", userId).single();
            if (updated?.plan === "elite" || attempts >= 10) {
              clearInterval(poll);
              if (updated) setProfile(updated);
              setUpgradeLoading(false);
            }
          }, 1000);
          return;
        }
        // If no subscription found, fall through to new checkout
        if (data.error !== "no_subscription") {
          console.error("upgrade-to-elite error:", data.error);
          setUpgradeLoading(false);
          return;
        }
      }
      // New checkout (free plan or no existing subscription)
      const res = await fetch(`${BACKEND}/create-elite-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, userEmail })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setUpgradeLoading(false);
  };

  const triggerBoostCheckout = async (userId, userEmail) => {
    setUpgradeLoading(true);
    try {
      const res = await fetch(`${BACKEND}/create-boost-checkout`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId, userEmail }) });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setUpgradeLoading(false);
  };

  const handleUpgrade = () => { if (session?.user?.id) triggerCheckout(session.user.id, session.user.email); };
  const signOut = async () => { await supabase.auth.signOut(); navigate("/"); };

  const handleAddSubject = async (newSubject, newLevel) => {
    const updatedSubjects = [...(profile.subjects || []), newSubject];
    const updatedLevels = { ...(profile.levels || {}), [newSubject]: newLevel };
    await supabase.from("profiles").update({ subjects: updatedSubjects, levels: updatedLevels }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, subjects: updatedSubjects, levels: updatedLevels }));
    setShowAddSubject(false);
  };

  const handleRemoveSubject = async (subjectToRemove) => {
    if (profile.subjects.length <= 1) return;
    const updatedSubjects = profile.subjects.filter(s => s !== subjectToRemove);
    const updatedLevels = { ...profile.levels };
    delete updatedLevels[subjectToRemove];
    await supabase.from("profiles").update({ subjects: updatedSubjects, levels: updatedLevels }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, subjects: updatedSubjects, levels: updatedLevels }));
    if (subject === subjectToRemove) { setSubject(updatedSubjects[0]); setMessages([]); setHistory([]); }
  };

  const handleDeleteWeakTopic = async (topicToDelete) => {
    const updated = (profile?.weak_topics || []).filter(t =>
      !(t.subject === topicToDelete.subject && t.topic === topicToDelete.topic && t.date === topicToDelete.date)
    );
    await supabase.from("profiles").update({ weak_topics: updated }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, weak_topics: updated }));
  };

  const handleClearAllWeakTopics = async () => {
    await supabase.from("profiles").update({ weak_topics: [] }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, weak_topics: [] }));
  };

  const saveWeakTopic = async (scoreText) => {
    const weak = extractWeakTopic(scoreText, subject);
    if (!weak || !session?.user?.id) return;
    const current = profile?.weak_topics || [];
    const updated = [...current, weak].slice(-50);
    await supabase.from("profiles").update({ weak_topics: updated }).eq("id", session.user.id);
    setProfile(prev => ({ ...prev, weak_topics: updated }));
  };

  const checkAndIncrementMessages = async () => {
    if (!profile || profile.plan === "pro" || profile.plan === "elite") return true;
    const today = new Date().toDateString();
    let count = profile.messages_today || 0;
    if (profile.messages_reset_date !== today) count = 0;
    if (count >= FREE_DAILY_LIMIT) { setUpgradePrompt("You've used your 10 free messages today. Upgrade to Pro for unlimited messages across all study modes."); return false; }
    const newCount = count + 1;
    setProfile(prev => ({ ...prev, messages_today: newCount, messages_reset_date: today }));
    await supabase.from("profiles").update({ messages_today: newCount, messages_reset_date: today }).eq("id", session.user.id);
    return true;
  };

  const handleModeSwitch = (m) => {
    if (profile?.plan !== "pro" && profile?.plan !== "elite" && m !== "chat") {
      setUpgradePrompt(m === "paper" ? ((subject === "History" || subject === "Modern Studies") ? "Essay mode is a Pro feature. Upgrade to get SQA-accurate essay marking with mark breakdowns, worked examples, and the try-again loop." : "Past paper mode is a Pro feature. Upgrade to get mark scheme breakdowns, worked examples, and the try-again loop.") : m === "drill" ? "Drill mode is a Pro feature. Upgrade to get targeted SQA-style questions fired at your weak spots." : "Mock exam mode is a Pro feature. Upgrade to generate full SQA-style papers and get a comprehensive marked breakdown.");
      return;
    }
    if (window.__summitMockInProgress && m !== "mock") {
      if (!window.confirm("Your mock paper will be lost if you switch tabs. Are you sure?")) return;
      window.__summitMockInProgress = false;
    }
    if (window.__summitMockInProgress && m !== "mock") {
      if (!window.confirm("Your mock paper will be lost if you switch tabs. Are you sure?")) return;
      window.__summitMockInProgress = false;
    }
    setMode(m); setMessages([]); setHistory([]);
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    const now = Date.now();
    const recentMessages = lastMessageTimes.filter(t => now - t < 60000);
    if (recentMessages.length >= 3) {
      const oldestInWindow = Math.min(...recentMessages);
      const secondsLeft = Math.ceil((60000 - (now - oldestInWindow)) / 1000);
      setRateLimited(secondsLeft);
      setTimeout(() => setRateLimited(false), secondsLeft * 1000);
      return;
    }
    setLastMessageTimes([...recentMessages, now]);
    const allowed = await checkAndIncrementMessages();
    if (!allowed) return;
    setInput("");
    const userMsg = { role: "user", content: msg };
    setMessages(prev => [...prev, { from: "user", text: msg }]);
    const newHistory = [...history, userMsg];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: subject === "History" && mode === "paper" ? "claude-sonnet-4-5" : "claude-haiku-4-5-20251001", max_tokens: subject === "History" && mode === "paper" ? 4000 : 2000, system: getSystemPrompt(subject, currentLevel, mode, profile), messages: newHistory }) });
      const data = await res.json();
      const reply = data.content?.[0]?.text || (data.error?.type === "overloaded_error" ? "Summit is busy right now — please try again in a moment." : "Something went wrong.");
      setMessages(prev => [...prev, { from: "ai", text: reply, mode }]);
      setHistory(prev => [...prev, { role: "assistant", content: reply }]);
      if (mode === "paper") { const parsed = parsePaperResponse(reply); if (parsed.studentScore) saveWeakTopic(parsed.studentScore); }
    } catch { setMessages(prev => [...prev, { from: "ai", text: "Connection error. Try again.", mode }]); }
    setLoading(false);
  };

  const handleTryAgain = (question) => {
    setMessages(prev => [...prev, { from: "ai", text: `Try again 👇\n\n${question}`, mode: "paper", isRetry: true }]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleDrillWeakness = (weakSubject, topic) => {
    setActiveScreen(null); setSubject(weakSubject); setMode("drill"); setMessages([]); setHistory([]);
    setSidebarOpen(false);
    setTimeout(() => send(`Drill me specifically on this topic: ${topic}`), 300);
  };

  const switchSubject = (s) => { setSubject(s); setMessages([]); setHistory([]); setSidebarOpen(false); };
  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  useEffect(() => {
    if (!profile || profile.plan === "elite" || profile.coach_unlocked) return;
    const dismissed = profile?.dismissed_announcements || [];
    if (!dismissed.includes("coach_v1")) {
      const timer = setTimeout(() => setShowCoachAnnouncement(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [profile]);


  if (showProWelcome) return <ProWelcome onContinue={() => setShowProWelcome(false)} examDate={profile?.exam_date} plan={profile?.plan} displayName={profile?.display_name} />;

  const subjects = profile?.subjects || [];
  const levels = profile?.levels || {};
  const examDate = profile?.exam_date;
  const plan = profile?.plan || "free";
  const daysUntil = examDate ? Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const currentLevel = levels[subject] || "Higher";
  const conf = profile?.confidence?.[subject];
  const confColor = conf <= 2 ? "#E24B4A" : conf === 3 ? "#EF9F27" : "#1D9E75";
  const messagesLeft = plan === "free" ? Math.max(0, FREE_DAILY_LIMIT - (profile?.messages_today || 0)) : null;
  const isMockMode = mode === "mock";
  const weakTopics = profile?.weak_topics || [];
  const weakCount = weakTopics.length;
  const flashcards = profile?.flashcards || [];
  const flashcardCount = flashcards.length;

  return (
    <div style={{ display: "flex", height: "100vh", background: "#09100f", color: "#f0f4f2", fontFamily: "'DM Sans', sans-serif", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap');
        @import url('https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css');
        .katex { font-size: 1em; }
        .math-block { display: block; margin: 8px 0; overflow-x: auto; }
        .working-box { border: 1px solid #ccc; min-height: 80px; margin: 8px 0 16px; background: #fafafa; padding: 8px; border-radius: 2px; }
        .formula-note { font-style: italic; color: #555; font-size: 12px; font-family: 'Trebuchet MS', sans-serif; margin: 4px 0; }
        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideInLeft { from{opacity:0;transform:translateX(-14px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideInRight { from{opacity:0;transform:translateX(14px)} to{opacity:1;transform:translateX(0)} }
        @keyframes typingPulse { 0%,100%{transform:translateY(0);opacity:0.4} 50%{transform:translateY(-3px);opacity:1} }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        textarea:focus { outline: none; border-color: rgba(29,158,117,0.6) !important; box-shadow: 0 0 0 3px rgba(29,158,117,0.08) !important; }
        textarea { color: #f0f4f2 !important; } textarea::placeholder { color: rgba(240,244,242,0.25) !important; }
        input { color: #f0f4f2 !important; } input::placeholder { color: rgba(240,244,242,0.25) !important; }
        select option { background: #09100f; color: #f0f4f2; }
        .remove-btn { opacity: 0; transition: opacity 0.15s; } .subject-row:hover .remove-btn { opacity: 1; }
        .tool-btn { transition: all 0.15s ease !important; }
        .tool-btn:hover { background: rgba(255,255,255,0.05) !important; color: rgba(240,244,242,0.7) !important; transform: translateX(2px); }
      `}</style>

      {upgradePrompt === "elite_coach" && (
        <EliteCoachUpgradePrompt
          onDismiss={() => setUpgradePrompt(null)}
          onUpgrade={() => { if (session?.user?.id) triggerBoostCheckout(session.user.id, session.user.email); }}
          upgradeLoading={upgradeLoading}
        />
      )}
      {lockedSubjectModal && (
        <SubjectUpgradeModal
          subjectName={lockedSubjectModal.name}
          level={lockedSubjectModal.level}
          onDismiss={() => setLockedSubjectModal(null)}
          onUpgrade={() => { setLockedSubjectModal(null); handleUpgrade(); }}
          onUpgradeElite={() => { setLockedSubjectModal(null); if (session && session.user) triggerEliteCheckout(session.user.id, session.user.email); }}
          upgradeLoading={upgradeLoading}
        />
      )}
      {showPlanModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#0d1a18", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 22, maxWidth: 620, width: "100%", animation: "fadeUp 0.4s cubic-bezier(0.16,1,0.3,1)", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "32px 32px 28px" }}>

              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
                    {daysUntil !== null && daysUntil <= 14 ? `${daysUntil} days until your exam` : "Upgrade Summit"}
                  </div>
                  <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 30, color: "#f0f4f2", lineHeight: 1.15, letterSpacing: -0.5 }}>
                    {daysUntil !== null && daysUntil <= 14
                      ? <>The difference between<br />a B and an A.</>
                      : <>Unlock everything<br />Summit has to offer.</>}
                  </div>
                </div>
                <button onClick={function() { setShowPlanModal(false); }} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.25)", cursor: "pointer", fontSize: 24, padding: 0, lineHeight: 1, marginLeft: 16, flexShrink: 0 }}>×</button>
              </div>

              {/* Side by side plans */}
              <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>

                {/* Pro */}
                <div style={{ flex: "1 1 200px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "20px 18px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", color: "rgba(240,244,242,0.35)", fontWeight: 500, marginBottom: 6 }}>Pro</div>
                  <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 30, color: "#f0f4f2", marginBottom: 18, letterSpacing: -0.5 }}>
                    £6.99<span style={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 20 }}>
                    {["Unlimited messages", "All 4 study modes", "All SQA subjects", "Weakness tracker + planner"].map(f => (
                      <div key={f} style={{ fontSize: 13, color: "rgba(240,244,242,0.5)", display: "flex", alignItems: "center", gap: 8, fontWeight: 300 }}>
                        <span style={{ color: "#1D9E75", fontSize: 11, flexShrink: 0 }}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                  <button onClick={function() { setShowPlanModal(false); handleUpgrade(); }} disabled={upgradeLoading}
                    style={{ width: "100%", padding: "13px", background: "rgba(255,255,255,0.05)", color: "rgba(240,244,242,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, cursor: "pointer" }}>
                    {upgradeLoading ? "Redirecting…" : "Get Pro →"}
                  </button>
                </div>

                {/* Coach Boost */}
                <div style={{ flex: "1 1 200px", border: "2px solid rgba(167,139,250,0.5)", borderRadius: 16, padding: "20px 18px", position: "relative", background: "rgba(124,58,237,0.05)" }}>
                  <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", fontSize: 10, fontWeight: 700, padding: "3px 14px", borderRadius: 20, whiteSpace: "nowrap", letterSpacing: 1 }}>AI COACH</div>
                  <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", color: "#a78bfa", fontWeight: 500, marginBottom: 6 }}>Exam Boost</div>
                  <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 30, color: "#f0f4f2", marginBottom: 4, letterSpacing: -0.5 }}>
                    £7.99<span style={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}> once</span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", marginBottom: 18, fontWeight: 300 }}>Add to Pro — pay once, keep forever</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 20 }}>
                    {["AI Study Coach", "Progress dashboard", "Knows your weak topics", "Day-by-day revision plan"].map(f => (
                      <div key={f} style={{ fontSize: 13, color: "rgba(240,244,242,0.5)", display: "flex", alignItems: "center", gap: 8, fontWeight: 300 }}>
                        <span style={{ color: "#a78bfa", fontSize: 11, flexShrink: 0 }}>✦</span>{f}
                      </div>
                    ))}
                  </div>
                  <button onClick={function() { setShowPlanModal(false); if (session && session.user) triggerBoostCheckout(session.user.id, session.user.email); }} disabled={upgradeLoading}
                    style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 20px rgba(124,58,237,0.35)", letterSpacing: 0.2 }}>
                    {upgradeLoading ? "Redirecting…" : "Unlock Coach — £7.99 →"}
                  </button>
                </div>

              </div>

              <div style={{ textAlign: "center" }}>
                <button onClick={function() { setShowPlanModal(false); }} style={{ fontSize: 13, color: "rgba(240,244,242,0.18)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  Maybe later
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
      {showCoachAnnouncement && (() => {
        const dismissCoachAnnouncement = async () => {
          const current = profile?.dismissed_announcements || [];
          if (!current.includes("coach_v1")) {
            const updated = [...current, "coach_v1"];
            await supabase.from("profiles").update({ dismissed_announcements: updated }).eq("id", session.user.id);
            setProfile(prev => ({ ...prev, dismissed_announcements: updated }));
          }
          setShowCoachAnnouncement(false);
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div style={{ background: "#0d0f1a", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 24, padding: "0", maxWidth: 460, width: "100%", boxShadow: "0 0 100px rgba(124,58,237,0.2)", animation: "fadeUp 0.45s cubic-bezier(0.16,1,0.3,1)", overflow: "hidden" }}>
              {/* Purple gradient header */}
              <div style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.25), rgba(167,139,250,0.1))", padding: "32px 36px 28px", borderBottom: "1px solid rgba(167,139,250,0.15)", textAlign: "center" }}>
                <div style={{ width: 60, height: 60, borderRadius: 18, background: "linear-gradient(135deg, rgba(167,139,250,0.3), rgba(124,58,237,0.2))", border: "1px solid rgba(167,139,250,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28, boxShadow: "0 0 40px rgba(124,58,237,0.3)" }}>✦</div>
                <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: "#a78bfa", fontWeight: 600, marginBottom: 8 }}>Just launched</div>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 28, color: "#f0f4f2", letterSpacing: -0.5, lineHeight: 1.15 }}>
                  Meet your AI<br /><em style={{ fontStyle: "italic", color: "#c4b5fd" }}>Study Coach.</em>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: "28px 36px 32px" }}>
                <p style={{ fontSize: 14, color: "rgba(240,244,242,0.5)", lineHeight: 1.75, fontWeight: 300, marginBottom: 24, textAlign: "center" }}>
                  The tutor that knows your exact weak spots, generates mock papers, marks your answers, and tells you what to study today. Always on. Always personal.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                  {[
                    ["✦", "Knows your weak topics", "Tracks every topic you’ve dropped marks on"],
                    ["🎯", "Full mock papers + marking", "Generates papers, marks them, tells you what to fix"],
                    ["📅", "Day-by-day revision plan", "Built around your actual exam dates"],
                  ].map(([icon, title, sub]) => (
                    <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.12)", borderRadius: 10 }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#f0f4f2", marginBottom: 2 }}>{title}</div>
                        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>{sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Price anchor */}
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, color: "#f0f4f2", letterSpacing: -1, lineHeight: 1 }}>
                    £7.99<span style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}> one-off</span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", marginTop: 4, fontWeight: 300 }}>Pay once, yours until your last exam</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={() => { dismissCoachAnnouncement(); if (session?.user?.id) triggerBoostCheckout(session.user.id, session.user.email); }}
                    style={{ padding: "15px", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", border: "none", borderRadius: 12, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 24px rgba(124,58,237,0.35)", letterSpacing: 0.2 }}
                    onMouseOver={e => e.currentTarget.style.opacity = "0.9"}
                    onMouseOut={e => e.currentTarget.style.opacity = "1"}>
                    Unlock AI Study Coach →
                  </button>
                  <button onClick={dismissCoachAnnouncement}
                    style={{ padding: "11px", background: "transparent", color: "rgba(240,244,242,0.25)", border: "none", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
                    I’ll stick with {plan === "pro" ? "Pro" : "the free plan"} for now
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {upgradePrompt && upgradePrompt !== "elite_coach" && <UpgradePrompt reason={upgradePrompt} onDismiss={() => setUpgradePrompt(null)} onUpgrade={handleUpgrade} upgradeLoading={upgradeLoading} />}
      {showAddSubject && <AddSubjectModal currentSubjects={subjects} levels={levels} onAdd={handleAddSubject} onClose={() => setShowAddSubject(false)} />}
      {isMobile && sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 40 }} />}

      {/* Sidebar */}
      <div style={{ width: 220, borderRight: "1px solid rgba(255,255,255,0.07)", display: "flex", flexDirection: "column", flexShrink: 0, position: isMobile ? "fixed" : "relative", transform: isMobile ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)") : "translateX(0)", transition: "transform 0.25s cubic-bezier(0.16,1,0.3,1)", top: 0, left: 0, height: "100%", zIndex: 50, background: "#09100f" }}>
        <div style={{ padding: "20px 16px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <img src={SummitSubmark} alt="Summit" style={{ height: 100, width: "auto", display: "block", marginBottom: 4 }} />
          <div style={{ fontSize: 11, color: "rgba(240,244,242,0.25)", fontWeight: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.user.email}</div>
        </div>
        {daysUntil !== null && daysUntil > 0 && (
          <div style={{ margin: "12px 12px 0", padding: "10px 12px", background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: "#1D9E75", fontWeight: 500, marginBottom: 2 }}>EXAM COUNTDOWN</div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#f0f4f2", letterSpacing: -0.5 }}>{daysUntil} days</div>
            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>until first exam</div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 8px" }}>
          {/* AI Coach — Elite feature */}
          <button
            onClick={() => {
              if (plan !== "elite" && !profile?.coach_unlocked) {
                setUpgradePrompt("elite_coach");
                return;
              }
              setActiveScreen(activeScreen === "coach" ? null : "coach");
              setSidebarOpen(false);
            }}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 10px",
              borderRadius: 8,
              background: activeScreen === "coach"
                ? "rgba(167,139,250,0.12)"
                : (plan === "elite" || profile?.coach_unlocked)
                  ? "rgba(167,139,250,0.06)"
                  : "transparent",
              border: activeScreen === "coach"
                ? "1px solid rgba(167,139,250,0.4)"
                : (plan === "elite" || profile?.coach_unlocked)
                  ? "1px solid rgba(167,139,250,0.2)"
                  : "1px dashed rgba(255,255,255,0.08)",
              color: (plan === "elite" || profile?.coach_unlocked) ? (activeScreen === "coach" ? "#f0f4f2" : "#c4b5fd") : "rgba(240,244,242,0.2)",
              fontSize: 12,
              fontFamily: "'DM Sans', sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
              transition: "all 0.2s"
            }}
          >
            <span style={{ fontSize: 15 }}>✦</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>AI Study Coach</div>
              <div style={{ fontSize: 10, fontWeight: 300, opacity: 0.6, marginTop: 1 }}>
                {(plan === "elite" || profile?.coach_unlocked) ? "Your personal tutor" : "£7.99 one-off"}
              </div>
            </div>
            {plan !== "elite" && !profile?.coach_unlocked && <span style={{ fontSize: 9 }}>🔒</span>}
            {(plan === "elite" || profile?.coach_unlocked) && activeScreen === "coach" && (
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
            )}
          </button>

          <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(240,244,242,0.25)", padding: "4px 8px", marginBottom: 4 }}>Your subjects</div>
          {/* Active (unlocked) subjects */}
          {subjects.map(s => {
            const c = profile?.confidence?.[s];
            const dot = c <= 2 ? "#E24B4A" : c === 3 ? "#EF9F27" : "#1D9E75";
            return (
              <div key={s} className="subject-row" style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                <button onClick={() => { switchSubject(s); setActiveScreen(null); }} style={{ flex: 1, textAlign: "left", padding: "8px 10px", borderRadius: 6, background: subject === s && !activeScreen ? "rgba(29,158,117,0.12)" : "transparent", border: subject === s && !activeScreen ? "1px solid rgba(29,158,117,0.25)" : "1px solid transparent", color: subject === s && !activeScreen ? "#f0f4f2" : "rgba(240,244,242,0.45)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: subject === s && !activeScreen ? 500 : 400 }}>{s}</div>
                    <div style={{ fontSize: 10, color: "rgba(240,244,242,0.25)", fontWeight: 300 }}>{levels[s]}</div>
                  </div>
                </button>
                {(plan === "pro" || plan === "elite") && subjects.length > 1 && (
                  <button className="remove-btn" onClick={() => handleRemoveSubject(s)} style={{ width: 20, height: 20, borderRadius: 4, background: "rgba(226,75,74,0.15)", border: "none", color: "#E24B4A", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 2 }}>×</button>
                )}
              </div>
            );
          })}

          {/* Locked subjects (free plan only — subjects selected during onboarding but not active) */}
          {plan === "free" && (profile?.all_subjects || []).filter(s => !subjects.includes(s)).map(s => (
            <div key={s} style={{ marginBottom: 2 }}>
              <button
                onClick={() => setLockedSubjectModal({ name: s, level: levels[s] || "Higher" })}
                style={{
                  width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6,
                  background: "rgba(255,255,255,0.015)",
                  border: "1px dashed rgba(255,255,255,0.07)",
                  color: "rgba(240,244,242,0.22)",
                  fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s"
                }}
                onMouseOver={e => { e.currentTarget.style.background = "rgba(29,158,117,0.05)"; e.currentTarget.style.borderColor = "rgba(29,158,117,0.2)"; e.currentTarget.style.color = "rgba(240,244,242,0.45)"; }}
                onMouseOut={e => { e.currentTarget.style.background = "rgba(255,255,255,0.015)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(240,244,242,0.22)"; }}
              >
                <span style={{ fontSize: 10, flexShrink: 0 }}>🔒</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12 }}>{s}</div>
                  <div style={{ fontSize: 10, color: "rgba(240,244,242,0.18)", fontWeight: 300 }}>{levels[s] || "Higher"} · Pro/Elite only</div>
                </div>
              </button>
            </div>
          ))}
          {(plan === "pro" || plan === "elite") ? (
            <button onClick={() => setShowAddSubject(true)} style={{ width: "100%", textAlign: "left", padding: "7px 10px", borderRadius: 6, background: "transparent", border: "1px dashed rgba(29,158,117,0.25)", color: "rgba(29,158,117,0.6)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.5)"; e.currentTarget.style.color = "#4DC9A0"; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(29,158,117,0.25)"; e.currentTarget.style.color = "rgba(29,158,117,0.6)"; }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add subject
            </button>
          ) : (
            <button onClick={() => setUpgradePrompt("Add more subjects with a Pro plan. Upgrade to study all your SQA subjects in one place.")} style={{ width: "100%", textAlign: "left", padding: "7px 10px", borderRadius: 6, background: "transparent", border: "1px dashed rgba(255,255,255,0.08)", color: "rgba(240,244,242,0.2)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 9 }}>🔒</span> Add subject
            </button>
          )}

          <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(240,244,242,0.25)", padding: "4px 8px", marginBottom: 4, marginTop: 16 }}>Tools</div>

          {/* Daily Check-in */}
          {!checkinDismissed && (profile?.subjects?.length > 0) && (
            <DailyCheckin
              profile={profile}
              plan={plan}
              onOpenCoach={() => { setActiveScreen("coach"); setSidebarOpen(false); }}
              onDismiss={() => setCheckinDismissed(true)}
            />
          )}

          {/* Progress Dashboard — Elite only */}
          <button className="tool-btn" onClick={() => {
            if (plan !== "elite" && !profile?.coach_unlocked) { setUpgradePrompt("elite_coach"); return; }
            setActiveScreen(activeScreen === "progress" ? null : "progress"); setSidebarOpen(false);
          }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, background: activeScreen === "progress" ? "rgba(167,139,250,0.08)" : "transparent", border: activeScreen === "progress" ? "1px solid rgba(167,139,250,0.25)" : "1px solid transparent", color: activeScreen === "progress" ? "#f0f4f2" : (plan === "elite" || profile?.coach_unlocked) ? "rgba(240,244,242,0.4)" : "rgba(240,244,242,0.2)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s", marginBottom: 2 }}>
            <span style={{ fontSize: 14 }}>📊</span>
            <span style={{ flex: 1 }}>Progress</span>
            {plan !== "elite" && !profile?.coach_unlocked && <span style={{ fontSize: 9, color: "rgba(167,139,250,0.4)" }}>🔒</span>}
          </button>

          {/* Flashcards */}
          <button className="tool-btn" onClick={() => { setActiveScreen(activeScreen === "flashcards" ? null : "flashcards"); setSidebarOpen(false); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, background: activeScreen === "flashcards" ? "rgba(167,139,250,0.08)" : "transparent", border: activeScreen === "flashcards" ? "1px solid rgba(167,139,250,0.25)" : "1px solid transparent", color: activeScreen === "flashcards" ? "#f0f4f2" : "rgba(240,244,242,0.4)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s", marginBottom: 2 }}>
            <span style={{ fontSize: 14 }}>🃏</span><span style={{ flex: 1 }}>Flashcards</span>
            {flashcardCount > 0 && <span style={{ background: "rgba(167,139,250,0.2)", color: "#a78bfa", fontSize: 10, padding: "1px 6px", borderRadius: 10, fontWeight: 600 }}>{flashcardCount}</span>}
          </button>

          {/* Weakness tracker */}
          <button className="tool-btn" onClick={() => { setActiveScreen(activeScreen === "weaknesses" ? null : "weaknesses"); setSidebarOpen(false); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, background: activeScreen === "weaknesses" ? "rgba(226,75,74,0.08)" : "transparent", border: activeScreen === "weaknesses" ? "1px solid rgba(226,75,74,0.2)" : "1px solid transparent", color: activeScreen === "weaknesses" ? "#f0f4f2" : "rgba(240,244,242,0.4)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s", marginBottom: 2 }}>
            <span style={{ fontSize: 14 }}>📉</span><span style={{ flex: 1 }}>Weakness tracker</span>
            {weakCount > 0 && <span style={{ background: "rgba(226,75,74,0.2)", color: "#E24B4A", fontSize: 10, padding: "1px 6px", borderRadius: 10, fontWeight: 600 }}>{weakCount}</span>}
          </button>

          {/* Revision planner */}
          <button className="tool-btn" onClick={() => { setActiveScreen(activeScreen === "planner" ? null : "planner"); setSidebarOpen(false); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, background: activeScreen === "planner" ? "rgba(29,158,117,0.08)" : "transparent", border: activeScreen === "planner" ? "1px solid rgba(29,158,117,0.2)" : "1px solid transparent", color: activeScreen === "planner" ? "#f0f4f2" : "rgba(240,244,242,0.4)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s", marginBottom: 2 }}>
            <span style={{ fontSize: 14 }}>📅</span><span>Revision planner</span>
          </button>

          {/* Predicted papers */}
          <button className="tool-btn" onClick={() => { setActiveScreen(activeScreen === "predicted" ? null : "predicted"); setSidebarOpen(false); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, background: activeScreen === "predicted" ? "rgba(239,159,39,0.1)" : "transparent", border: activeScreen === "predicted" ? "1px solid rgba(239,159,39,0.3)" : "1px solid transparent", color: activeScreen === "predicted" ? "#f0f4f2" : "rgba(240,244,242,0.4)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }}>
            <span style={{ fontSize: 14 }}>🎯</span>
            <span style={{ flex: 1 }}>Predicted papers</span>
            {plan !== "pro" && <span style={{ background: "rgba(239,159,39,0.15)", color: "#EF9F27", fontSize: 9, padding: "1px 5px", borderRadius: 4, fontWeight: 600, letterSpacing: 0.3 }}>£4.99</span>}
            {(plan === "pro" || plan === "elite") && <span style={{ background: "rgba(29,158,117,0.15)", color: "#4DC9A0", fontSize: 9, padding: "1px 5px", borderRadius: 4, fontWeight: 600, letterSpacing: 0.3 }}>FREE</span>}
          </button>
        </div>

        <div style={{ padding: "12px 8px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", flexDirection: "column", gap: 8 }}>
          {plan === "free" && <div style={{ padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 6, fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}><span style={{ color: messagesLeft <= 3 ? "#E24B4A" : "rgba(240,244,242,0.5)", fontWeight: 500 }}>{messagesLeft}</span> messages left today</div>}
          {plan === "free" && <button onClick={() => setShowPlanModal(true)} style={{ padding: "8px 10px", background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.25)", borderRadius: 6, fontSize: 11, color: "#4DC9A0", fontFamily: "'DM Sans', sans-serif", cursor: "pointer", fontWeight: 500 }}>✦ Upgrade plan</button>}
          {plan === "pro" && !profile?.coach_unlocked && <button onClick={() => { if (session?.user?.id) triggerBoostCheckout(session.user.id, session.user.email); }} disabled={upgradeLoading} style={{ padding: "8px 10px", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, fontSize: 11, color: "#c4b5fd", fontFamily: "'DM Sans', sans-serif", cursor: "pointer", fontWeight: 500 }}>✦ Unlock Coach + Progress — £7.99</button>}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px" }}>
            <div style={{ fontSize: 11, color: (plan === "pro" || plan === "elite") ? "#1D9E75" : "rgba(240,244,242,0.2)", fontWeight: (plan === "pro" || plan === "elite") ? 500 : 300 }}>{plan === "pro" ? "✦ Pro plan" : plan === "elite" ? "✦ Pro plan" : "Free plan"}</div>
            <button onClick={signOut} style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", padding: 0 }}>Sign out</button>
          </div>
          {(plan === "pro" || plan === "elite") && <button onClick={async () => { const res = await fetch(`${BACKEND}/create-portal-session`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ customerId: profile.stripe_customer_id }) }); const data = await res.json(); if (data.url) window.location.href = data.url; }} style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", padding: "0 2px", textAlign: "left" }}>Manage subscription</button>}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: "10%", width: "80%", height: 250, background: "radial-gradient(ellipse at top, rgba(29,158,117,0.045) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
        {activeScreen === "coach" && <CoachScreen profile={profile} session={session} setProfile={setProfile} onClose={() => setActiveScreen(null)} />}
        {activeScreen === "progress" && <ProgressDashboard profile={profile} session={session} onClose={() => setActiveScreen(null)} />}
        {activeScreen === "flashcards" && <FlashcardScreen flashcards={flashcards} subjects={subjects} levels={levels} session={session} profile={profile} setProfile={setProfile} onClose={() => setActiveScreen(null)} />}
        {activeScreen === "weaknesses" && <WeaknessScreen weakTopics={weakTopics} subjects={subjects} onDrill={handleDrillWeakness} onDelete={handleDeleteWeakTopic} onClearAll={handleClearAllWeakTopics} onClose={() => setActiveScreen(null)} />}
        {activeScreen === "planner" && <PlannerScreen subjects={subjects} levels={levels} examDate={examDate} session={session} setProfile={setProfile} savedPlan={profile?.revision_plan} onClose={() => setActiveScreen(null)} />}
        {activeScreen === "predicted" && <PredictedPaperScreen profile={profile} session={session} setProfile={setProfile} onClose={() => setActiveScreen(null)} />}

        {!activeScreen && (
          <>
            <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", gap: 10, background: "rgba(9,16,15,0.95)", backdropFilter: "blur(8px)", flexShrink: 0 }}>
              {isMobile && (
                <button onClick={() => setSidebarOpen(v => !v)} style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0 }}>
                  <div style={{ width: 16, height: 1.5, background: "rgba(240,244,242,0.6)", borderRadius: 1 }} />
                  <div style={{ width: 16, height: 1.5, background: "rgba(240,244,242,0.6)", borderRadius: 1 }} />
                  <div style={{ width: 16, height: 1.5, background: "rgba(240,244,242,0.6)", borderRadius: 1 }} />
                </button>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#f0f4f2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentLevel} {subject}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: confColor, flexShrink: 0 }} />
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>Confidence: {conf}/5</div>
                </div>
              </div>
              <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 3, border: "1px solid rgba(255,255,255,0.07)", overflowX: "auto", flexShrink: 0, maxWidth: isMobile ? "calc(100vw - 180px)" : "none" }}>
                {[["chat","Concept"],["paper", (subject === "History" || subject === "Modern Studies") ? "Essay mode" : "Past paper"],["drill","Drill"],["mock","Mock"]].map(([m, label]) => {
                  const locked = plan !== "pro" && plan !== "elite" && m !== "chat";
                  return (
                    <button key={m} onClick={() => handleModeSwitch(m)} style={{ padding: "5px 10px", borderRadius: 6, fontSize: isMobile ? 11 : 12, border: "none", background: mode === m ? (m === "mock" ? "rgba(239,159,39,0.2)" : "rgba(29,158,117,0.2)") : "transparent", color: locked ? "rgba(240,244,242,0.2)" : mode === m ? (m === "mock" ? "#EF9F27" : "#4DC9A0") : "rgba(240,244,242,0.35)", fontWeight: mode === m ? 500 : 400, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
                      {locked && <span style={{ fontSize: 9 }}>🔒</span>}{label}
                    </button>
                  );
                })}
              </div>
            </div>
            {isMockMode ? <MockExamPanel subject={subject} level={currentLevel} userData={profile} /> : (
              <>
                <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
                  {messages.length === 0 && (
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 10, paddingTop: 60, animation: "fadeUp 0.5s cubic-bezier(0.16,1,0.3,1)" }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img src={SummitSubmark} alt="Summit" style={{ height: 100, width: "auto" }} />
                      </div>
                      <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: "#f0f4f2", letterSpacing: -0.3 }}>Ready to study</div>
                      <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", maxWidth: 300, lineHeight: 1.6, fontWeight: 300 }}>
                        {mode === "chat" && "Ask anything about your course. Summit explains it at the right SQA level."}
                        {mode === "paper" && subject !== "History" && "Paste a past paper question and your answer. Get mark scheme, score, and a model answer."}
                        {mode === "paper" && subject === "History" && "Select your topic below, then paste your essay or source question. Summit marks it against the real SQA criteria."}
                        {mode === "drill" && "Summit will fire questions at you and score your answers against real marking logic."}
                      </div>
                    </div>
                  )}
                  {messages.map((msg, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, flexDirection: msg.from === "user" ? "row-reverse" : "row", animation: `${msg.from === "user" ? "slideInRight" : "slideInLeft"} 0.3s cubic-bezier(0.16,1,0.3,1)` }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: msg.from === "ai" ? "rgba(29,158,117,0.12)" : "rgba(29,158,117,0.2)", border: msg.from === "ai" ? "1px solid rgba(29,158,117,0.2)" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        {msg.from === "ai" ? <img src={SummitSubmark} alt="Summit" style={{ height: 100, width: "auto" }} /> : <span style={{ fontSize: 11, fontWeight: 500, color: "#4DC9A0" }}>C</span>}
                      </div>
                      <div style={{ maxWidth: "82%", padding: msg.from === "user" ? "11px 15px" : "14px 16px", borderRadius: msg.from === "user" ? "12px 3px 12px 12px" : "3px 12px 12px 12px", background: msg.from === "user" ? "#1D9E75" : "rgba(255,255,255,0.05)", border: msg.from === "user" ? "none" : "1px solid rgba(255,255,255,0.07)", color: msg.from === "user" ? "white" : "rgba(240,244,242,0.85)", fontSize: 13, lineHeight: 1.7 }}>
                        {msg.from === "ai" && msg.mode === "paper" && !msg.isRetry ? <PaperFeedback text={msg.text} onTryAgain={handleTryAgain} /> : <div style={{ fontWeight: 300, lineHeight: 1.7 }}>{renderMessage(msg.text)}</div>}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div style={{ display: "flex", gap: 10, animation: "slideInLeft 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(29,158,117,0.12)", border: "1px solid rgba(29,158,117,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img src={SummitSubmark} alt="Summit" style={{ height: 100, width: "auto" }} />
                      </div>
                      <div style={{ padding: "14px 18px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "3px 12px 12px 12px", display: "flex", gap: 5, alignItems: "center" }}>
                        {[0,1,2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>
                <div style={{ padding: "0 16px 8px", display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0 }}>
                  {(SUBJECT_CHIPS[subject]?.[mode] || DEFAULT_CHIPS[mode])?.map(c => (
                    <button key={c} onClick={() => send(c)} style={{ fontSize: 11, padding: "4px 12px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", cursor: "pointer", color: "rgba(240,244,242,0.4)", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}
                      onMouseOver={e => { e.target.style.borderColor = "rgba(29,158,117,0.4)"; e.target.style.color = "#4DC9A0"; }}
                      onMouseOut={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.color = "rgba(240,244,242,0.4)"; }}>
                      {c}
                    </button>
                  ))}
                </div>
                {rateLimited && <div style={{ padding: "6px 16px", fontSize: 12, color: "#EF9F27", fontWeight: 300 }}>⏱ Slow down — wait {rateLimited}s before sending another message.</div>}
                <div style={{ padding: "8px 16px 20px", display: "flex", flexDirection: "column", gap: 6, borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
                  {/* History Essay Mode — topic selector + bigger textarea */}
                  {subject === "History" && mode === "paper" ? (
                    <>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 2 }}>
                        <span style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>Topic:</span>
                        {[
                          "Wars of Independence", "Reformation", "Treaty of Union", "Migration & Empire", "Great War",
                          "Nazi Germany", "Russia", "Cold War", "Appeasement", "USA", "Italy", "Germany 1815–1939",
                          "Crusades", "American Revolution", "French Revolution", "Ireland", "Britain 1851–1951", "Enslaved Africans"
                        ].map(t => (
                          <button key={t} onClick={() => setInput(prev => {
                            // Prepend topic context if not already there
                            const tag = `[Topic: ${t}]\n`;
                            if (prev.startsWith("[Topic:")) return tag + prev.replace(/^\[Topic:[^\]]*\]\n?/, "");
                            return tag + prev;
                          })}
                          style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, border: input.includes(`[Topic: ${t}]`) ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)", background: input.includes(`[Topic: ${t}]`) ? "rgba(29,158,117,0.15)" : "transparent", color: input.includes(`[Topic: ${t}]`) ? "#4DC9A0" : "rgba(240,244,242,0.4)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s", whiteSpace: "nowrap" }}>
                            {t}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 10 }}>
                        <textarea
                          value={input}
                          onChange={e => setInput(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) { e.preventDefault(); send(); } }}
                          placeholder={"Paste your essay question and full essay here...\n\nTip: Select your topic above first, then paste your question and full essay. Summit will mark it against the SQA criteria."}
                          rows={8}
                          style={{ flex: 1, resize: "vertical", padding: "14px 16px", fontSize: 13, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, background: "rgba(255,255,255,0.04)", minHeight: 160 }}
                        />
                        <button
                          onClick={() => send()}
                          disabled={loading}
                          style={{ width: 38, height: 38, borderRadius: "50%", background: loading ? "rgba(29,158,117,0.3)" : "#1D9E75", border: "none", cursor: loading ? "default" : "pointer", color: "white", fontSize: 16, alignSelf: "flex-end", transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)", boxShadow: loading ? "none" : "0 2px 12px rgba(29,158,117,0.3)" }}
                          onMouseOver={e => { if (!loading) { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(29,158,117,0.45)"; }}}
                          onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = loading ? "none" : "0 2px 12px rgba(29,158,117,0.3)"; }}>
                          ↑
                        </button>
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300 }}>
                        Press Ctrl+Enter to submit · Summit marks against real SQA criteria (context, knowledge, analysis, evaluation, conclusion)
                      </div>
                    </>
                  ) : (
                    <div style={{ display: "flex", gap: 10 }}>
                      <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder={mode === "paper" ? "Paste a question and your answer..." : "Ask a question..."} rows={2} style={{ flex: 1, resize: "none", padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, background: "rgba(255,255,255,0.04)" }} />
                      <button onClick={() => send()} disabled={loading} style={{ width: 38, height: 38, borderRadius: "50%", background: loading ? "rgba(29,158,117,0.3)" : "#1D9E75", border: "none", cursor: loading ? "default" : "pointer", color: "white", fontSize: 16, alignSelf: "flex-end", transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)", boxShadow: loading ? "none" : "0 2px 12px rgba(29,158,117,0.3)" }} onMouseOver={e => { if (!loading) { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(29,158,117,0.45)"; }}} onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = loading ? "none" : "0 2px 12px rgba(29,158,117,0.3)"; }}>↑</button>
                    </div>
                  )}
                  {MATHS_SUBJECTS.includes(subject) && (
                    <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", fontWeight: 300, lineHeight: 1.5 }}>
                      💡 You can write maths in plain text: fractions as "3/4", powers as "x^2", roots as "sqrt(x)", integrals as "int", pi as "pi", theta as "theta", infinity as "inf", sin/cos/tan, log/ln.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────
function AppRoutes() {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) { loadProfile(session.user.id); } else { setProfileLoaded(true); }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
  if (event === "PASSWORD_RECOVERY") {
    navigate("/auth?reset=true");
    return;
  }
  setSession(session);
  if (session) {
    // Don't re-trigger full loadProfile on tab focus events — only on actual sign in
    if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
      loadProfile(session.user.id);
    }
  } else {
    setProfile(null);
    setProfileLoaded(true);
  }
});
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  const loadProfile = async (userId) => {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();
    if (!error && data) { setProfile(data); }
    setProfileLoaded(true);
  };

  const saveProfile = async (data, wantsPro = false, wantsBoost = false) => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
const userId = currentSession?.user?.id;
    if (!userId) return;
    const profileData = {
      id: userId,
      plan: "free",
      subjects: data.subjects,
      all_subjects: data.all_subjects || data.subjects,
      levels: data.levels,
      exam_date: data.examDate || data.exam_date || "",
      confidence: data.confidence || {},
      messages_today: 0,
      messages_reset_date: new Date().toDateString(),
      display_name: data.display_name || "",
      grade_target: data.grade_target || "",
      biggest_struggle: data.biggest_struggle || ""
    };
    setProfile(profileData);
    try {
      const res = await fetch(`${BACKEND}/save-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileData)
      });
      const result = await res.json();
      if (!result.ok) { console.error("save-profile error:", result.error); }
    } catch (err) {
      console.error("save-profile fetch failed:", err);
    }
    if (wantsBoost) {
      // Go to Pro checkout first — after Pro success, user gets the boost upsell in-app
      try {
        const res = await fetch(`${BACKEND}/create-checkout-session`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId, userEmail: session.user.email }) });
        const resData = await res.json();
        if (resData.url) window.location.href = resData.url;
        else navigate("/app");
      } catch (err) { console.error(err); navigate("/app"); }
    } else if (wantsPro) {
      try {
        const res = await fetch(`${BACKEND}/create-checkout-session`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId, userEmail: session.user.email }) });
        const resData = await res.json();
        if (resData.url) window.location.href = resData.url;
        else navigate("/app");
      } catch (err) { console.error(err); navigate("/app"); }
    } else {
      navigate("/app");
    }
  };

  if (session === undefined || !profileLoaded) return (
    <div style={{ minHeight: "100vh", background: "#080e0d", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }`}</style>
      <div style={{ display: "flex", gap: 6 }}>{[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", animation: "bounce 1s infinite", animationDelay: `${i*0.15}s` }} />)}</div>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={!session ? <LandingPage /> : <Navigate to="/app" replace />} />
      <Route path="/auth" element={
  new URLSearchParams(window.location.search).get("reset") === "true"
    ? <Auth />
    : !session ? <Auth /> : <Navigate to="/app" replace />
} />
      <Route path="/onboarding" element={
        !session ? <Navigate to="/auth" replace /> :
        profile ? <Navigate to="/app" replace /> :
        !profileLoaded ? null :
        <Onboarding onComplete={(data, wantsPro, wantsBoost) => saveProfile(data, wantsPro, wantsBoost)} />
      } />
      <Route path="/app" element={
        !session ? <Navigate to="/auth" replace /> :
        (!profile && profileLoaded) ? <Navigate to="/onboarding" replace /> :
        profile ? <StudyApp session={session} profile={profile} setProfile={setProfile} /> :
        null
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}